import { useState, useRef, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

// ═══════════════════════════════════════════════════════════════════════
// SUPABASE — backend connection (fire-and-forget; localStorage is source of truth)
// ═══════════════════════════════════════════════════════════════════════
const SUPABASE_URL = "https://hwjfrnubidvcczaupoft.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_Sztp3yr1t02pUhSD--xeZw_UXC3hv_0";
const COMPANY_ID = "d15e670f-0cd5-43d4-9fe6-284390fbc7ad"; // BAC Drywall
// Paste BAC's company key here (Proton Pass: "SiteVouch — BAC company key").
// This is the per-company JWT that lets fetchBranding() past RLS on companies/company_sites.
const COMPANY_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh3amZybnViaWR2Y2N6YXVwb2Z0Iiwicm9sZSI6ImFub24iLCJjb21wYW55X2lkIjoiZDE1ZTY3MGYtMGNkNS00M2Q0LTlmZTYtMjg0MzkwZmJjN2FkIiwiaWF0IjoxNzg0MjI5Mjg4LCJleHAiOjIwOTk1ODkyODh9.ieqbVwFGl6mYPclcwD_fny_tnSQx5AwGmLdM8w_6Cfk";
// v18.50 — the MAIN client now carries the company key too. apikey stays the public
// project key; Authorization carries the company-scoped JWT so RLS can resolve
// company_id on every table, not just branding. No visible change until the legacy
// tables get their policies — this is the prerequisite for that work.
const sb = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  global: { headers: { Authorization: `Bearer ${COMPANY_KEY}` } },
});
// Kept as a separate name for the branding + cards reads that already use it.
const sbBrand = sb;

// ═══════════════════════════════════════════════════════════════════════
// v17.94 — SUPERVISOR/WORKER OFFLINE QUEUE (talks, notices, messages, snags)
// Generic store-and-forward. Each op: {table, action, payload, photo?}.
// Photos (snags) stored as base64 in the op; uploaded on reconnect.
// Drained by flushSupQueue() on 'online' event + app focus.
// ═══════════════════════════════════════════════════════════════════════
const SUP_QUEUE_KEY = "bac_sup_queue";
const getSupQueue = () => { try { return JSON.parse(localStorage.getItem(SUP_QUEUE_KEY) || "[]"); } catch (e) { return []; } };
const setSupQueue = (q) => { try { localStorage.setItem(SUP_QUEUE_KEY, JSON.stringify(q)); } catch (e) {} };
const enqueueSupOp = (op) => { const q = getSupQueue(); q.push({ ...op, _ts: Date.now() }); setSupQueue(q); };

// Cache last-known fetch results so screens hydrate offline instead of blank.
const cacheSet = (key, val) => { try { localStorage.setItem("bac_cache_" + key, JSON.stringify(val)); } catch (e) {} };
const cacheGet = (key) => { try { const v = localStorage.getItem("bac_cache_" + key); return v ? JSON.parse(v) : null; } catch (e) { return null; } };

// dataURL -> Blob (for queued snag photos on reconnect)
const dataUrlToBlob = (dataUrl) => {
  const [head, b64] = dataUrl.split(",");
  const mime = (head.match(/:(.*?);/) || [])[1] || "image/jpeg";
  const bin = atob(b64); const len = bin.length; const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
};
const blobToDataUrl = (blob) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });

// Drain the queue. Returns number of ops successfully synced.
const flushSupQueue = async () => {
  let q = getSupQueue();
  if (!q.length) return 0;
  const remaining = [];
  let synced = 0;
  for (const op of q) {
    try {
      if (op.table === "snags" && op.action === "insert" && op.photo) {
        // Re-upload queued photo(s) then insert
        const ts = op.photo.ts || Date.now();
        let payload = { ...op.payload };
        const origPath = COMPANY_ID + "/" + ts + "-original.jpg";
        const { error: e1 } = await sb.storage.from("snag-photos").upload(origPath, dataUrlToBlob(op.photo.original), { contentType: "image/jpeg" });
        if (e1) throw e1;
        payload.photo_url = sb.storage.from("snag-photos").getPublicUrl(origPath).data.publicUrl;
        if (op.photo.annotated) {
          const annPath = COMPANY_ID + "/" + ts + "-annotated.jpg";
          const { error: e2 } = await sb.storage.from("snag-photos").upload(annPath, dataUrlToBlob(op.photo.annotated), { contentType: "image/jpeg" });
          if (e2) throw e2;
          payload.annotated_photo_url = sb.storage.from("snag-photos").getPublicUrl(annPath).data.publicUrl;
        }
        const { error: e3 } = await sb.from("snags").insert(payload);
        if (e3) throw e3;
      } else if (op.action === "insert") {
        const { error } = await sb.from(op.table).insert(op.payload);
        if (error) throw error;
      } else if (op.action === "update") {
        const { error } = await sb.from(op.table).update(op.payload).eq("id", op.id);
        if (error) throw error;
      } else if (op.action === "delete") {
        const { error } = await sb.from(op.table).delete().eq("id", op.id);
        if (error) throw error;
      }
      synced++;
    } catch (e) {
      remaining.push(op); // keep failed op for next attempt
    }
  }
  setSupQueue(remaining);
  return synced;
};

// Push a clock event (in/out). Returns the new row id, or null if failed.
const pushClockEvent = async (type, workerId, workerName, siteName, gps) => {
  try {
    const { data, error } = await sb.from("clock_events").insert({
      company_id: COMPANY_ID,
      worker_id: workerId || null,
      worker_name: workerName,
      site_name: siteName,
      event_type: type,
      gps_lat: gps ? parseFloat(gps.lat) : null,
      gps_lng: gps ? parseFloat(gps.lng) : null,
      gps_accuracy: gps ? gps.acc : null,
      clocked_at: new Date().toISOString()
    }).select().single();
    if (error) { console.error("clock_event push failed:", error.message); return null; }
    return data.id;
  } catch (e) { console.error("clock_event exception:", e); return null; }
};

// Push a completed shift + its plot entries. Returns true/false.
const pushShift = async (shiftRecord, entries) => {
  try {
    const isoDate = (() => {
      const [d,m,y] = shiftRecord.date.split("/");
      return y+"-"+m.padStart(2,"0")+"-"+d.padStart(2,"0");
    })();
    const { data: shift, error: sErr } = await sb.from("shifts").insert({
      company_id: COMPANY_ID,
      worker_id: shiftRecord.workerId || null,
      worker_name: shiftRecord.name,
      site_name: shiftRecord.site,
      shift_date: isoDate,
      time_in: shiftRecord.tIn,
      time_out: shiftRecord.tOut,
      total_mins: shiftRecord.totalMins || 0,
      ws_hours: shiftRecord.mgH ? parseFloat(shiftRecord.mgH) : null,
      si_hours: shiftRecord.siH ? parseFloat(shiftRecord.siH) : null,
      status: "submitted",
      submitted_at: new Date().toISOString(),
      manual_entry: shiftRecord.manualEntry || false,
      assigned_supervisor_id: shiftRecord.assignedSupervisorId || null,
      assigned_supervisor_name: shiftRecord.assignedSupervisorName || null
    }).select().single();
    if (sErr) { console.error("shift push failed:", sErr.message); return false; }
    if (entries && entries.length > 0) {
      const rows = entries.map(e => ({
        company_id: COMPANY_ID,
        shift_id: shift.id,
        plot_number: e.plot,
        description: e.desc,
        time_spent_mins: e.mins || 0,
        section: e.sec || "mg"
      }));
      const { error: eErr } = await sb.from("plot_entries").insert(rows);
      if (eErr) console.error("plot_entries push failed:", eErr.message);
    }
    return true;
  } catch (e) { console.error("shift exception:", e); return false; }
};

// Push a request (site missing, time edit, manual shift add, etc.)
const pushRequest = async (req) => {
  try {
    const { error } = await sb.from("requests").insert({
      company_id: COMPANY_ID,
      worker_id: req.workerId || null,
      worker_name: req.workerName || req.name || "",
      request_type: req.type,
      note: req.note || "",
      requested_time_in: req.tIn || null,
      requested_time_out: req.tOut || null,
      status: "pending",
      gps_lat: req.gps ? parseFloat(req.gps.lat) : null,
      gps_lng: req.gps ? parseFloat(req.gps.lng) : null
    });
    if (error) { console.error("request push failed:", error.message); return false; }
    return true;
  } catch (e) { console.error("request exception:", e); return false; }
};

// ── v18.48 — Fetch this worker's own requests, so the app can tell them what
// the office decided. Before this the office could approve or reject a time
// edit and the worker was never told — no proof either way. Excludes
// toolbox_message rows (those are the supervisor messaging channel).
const fetchMyRequests = async (workerIdArg) => {
  if (!workerIdArg) return [];
  try {
    const { data, error } = await sb.from("requests")
      .select("id,request_type,note,status,requested_time_in,requested_time_out,created_at")
      .eq("company_id", COMPANY_ID)
      .eq("worker_id", workerIdArg)
      .order("created_at", { ascending: false })
      .limit(40);
    if (error) { console.error("fetchMyRequests failed:", error.message); return []; }
    return (data || []).filter(r => r.request_type !== "toolbox_message");
  } catch (e) { console.error("fetchMyRequests exception:", e); return []; }
};

// ── v18.24 — Push a Price Work claim (one sheet per plot) to Supabase. ──
// Parent row → price_work_claims; one child row per line → price_work_claim_lines.
// Mirrors pushShift: returns true only if the parent insert lands. Office reads
// these for office-qty-vs-worker-qty reconciliation + margin.
const pushPwClaim = async (rec) => {
  try {
    const lineTot = (ln) => ln.formula === "flat" ? (Number(ln.flatPrice) || 0) : (Number(ln.qty) || 0) * (Number(ln.rate) || 0);
    const isoDate = (() => {
      if (!rec.date) return null;
      const [d,m,y] = rec.date.split("/");
      if (!y) return null;
      return y+"-"+m.padStart(2,"0")+"-"+d.padStart(2,"0");
    })();
    const { data: claim, error: cErr } = await sb.from("price_work_claims").insert({
      company_id: COMPANY_ID,
      worker_id: rec.workerId || null,
      worker_name: rec.name,
      site_name: rec.site,
      plot_number: String(rec.plot),
      claim_date: isoDate,
      sheet_number: rec.sheetNumber,
      worker_invoice_number: rec.worker_invoice_number || null,
      sheet_total: rec.sheetTotal || 0,
      status: "submitted",
      submitted_at: rec.submitted || new Date().toISOString(),
      assigned_supervisor_id: rec.assignedSupervisorId || null,
      assigned_supervisor_name: rec.assignedSupervisorName || null
    }).select().single();
    if (cErr) { console.error("pw claim push failed:", cErr.message); return false; }
    const lines = rec.lines || [];
    if (lines.length > 0) {
      const rows = lines.map(ln => ({
        company_id: COMPANY_ID,
        claim_id: claim.id,
        plot_number: String(rec.plot),
        operation: ln.name || "",
        operation_id: ln.opId != null ? String(ln.opId) : null,
        unit: ln.unit || null,
        formula_type: ln.formula === "flat" ? "flat" : "qty",
        qty: ln.formula === "flat" ? null : (Number(ln.qty) || 0),
        rate: ln.formula === "flat" ? null : (Number(ln.rate) || 0),
        flat_price: ln.formula === "flat" ? (Number(ln.flatPrice) || 0) : null,
        value: lineTot(ln),
        is_custom: !!ln.custom
      }));
      const { error: lErr } = await sb.from("price_work_claim_lines").insert(rows);
      if (lErr) console.error("pw claim_lines push failed:", lErr.message);
    }
    return true;
  } catch (e) { console.error("pw claim exception:", e); return false; }
};

// Look up worker by PIN. Returns worker object {id,name,role,pin_temporary} or null.
// v17.97 — caches successful logins per-device so a previously-seen worker can
// log in offline. Online path unchanged; cache only consulted when offline or
// when the network query fails.
const KNOWN_WORKERS_KEY = "bac_known_workers";
const getKnownWorkers = () => { try { return JSON.parse(localStorage.getItem(KNOWN_WORKERS_KEY) || "[]"); } catch (e) { return []; } };
const rememberWorker = (w, pin) => {
  try {
    const list = getKnownWorkers().filter(x => x.id !== w.id);
    list.push({ id: w.id, name: w.name, role: w.role || "worker", pin: pin, _cachedAt: Date.now() });
    localStorage.setItem(KNOWN_WORKERS_KEY, JSON.stringify(list));
  } catch (e) {}
};
const loginByPinOffline = (pin) => {
  const hit = getKnownWorkers().find(w => w.pin === pin);
  return hit ? { id: hit.id, name: hit.name, role: hit.role, pin_temporary: false, _offline: true } : null;
};
const loginByPin = async (pin) => {
  // Offline → verify against this device's cached logins.
  if (!navigator.onLine) return loginByPinOffline(pin);
  try {
    const { data, error } = await sb.from("workers")
      .select("id,name,role,pin_temporary")
      .eq("company_id", COMPANY_ID)
      .eq("pin", pin)
      .eq("active", true)
      .maybeSingle();
    if (error) { console.error("login failed:", error.message); return loginByPinOffline(pin); }
    if (data) rememberWorker(data, pin); // refresh device cache on every successful online login
    return data;
  } catch (e) { console.error("login exception:", e); return loginByPinOffline(pin); }
};

// Update a worker's PIN (and clear the temporary flag).
const updateWorkerPin = async (workerId, newPin) => {
  try {
    const { error } = await sb.from("workers").update({
      pin: newPin,
      pin_temporary: false,
      pin_changed_at: new Date().toISOString()
    }).eq("id", workerId);
    if (error) { console.error("pin update failed:", error.message); return false; }
    return true;
  } catch (e) { console.error("pin update exception:", e); return false; }
};

// ── v17.18 — Fetch list of supervisors for this company ──
// Used by worker-side "Send to Supervisor" picker on submit.
const fetchSupervisors = async () => {
  try {
    const { data, error } = await sb.from("workers")
      .select("id,name")
      .eq("company_id", COMPANY_ID)
      .eq("role", "supervisor")
      .eq("active", true);
    if (error) { console.error("fetchSupervisors failed:", error.message); return []; }
    return data || [];
  } catch (e) { console.error("fetchSupervisors exception:", e); return []; }
};

// ── v17.18 — Fetch signed shifts for a specific worker ──
// Used by worker-side "My Signed Sheets" section.
// ── v17.59 — also matches by worker_name (handles legacy rows where worker_id is null or mismatched). ──
// ── v17.60 — returns {sheets, diag} where diag is a debug snapshot for the on-screen strip. ──
const fetchSignedShifts = async (workerId, workerName) => {
  const empty = { sheets: [], diag: { byId: 0, byName: 0, companyTotal: 0, latestInCompany: null, errMsg: "" } };
  if (!workerId && !workerName) return empty;
  const COLS = "id,worker_id,worker_name,site_name,shift_date,time_in,time_out,total_mins,ws_hours,si_hours,signed_at,signed_by_name,supervisor_notes,signature_data,assigned_supervisor_name";
  try {
    const qById = workerId ? sb.from("shifts").select(COLS)
      .eq("company_id", COMPANY_ID).eq("worker_id", workerId).eq("status", "signed")
      .order("signed_at", { ascending: false }).limit(50) : null;
    const qByName = workerName ? sb.from("shifts").select(COLS)
      .eq("company_id", COMPANY_ID).eq("worker_name", workerName).eq("status", "signed")
      .order("signed_at", { ascending: false }).limit(50) : null;
    // Diagnostic: latest signed shift in the company (regardless of worker) — helps us see if
    // sign-off is actually persisting, and what worker_id / worker_name look like on the row.
    const qCompany = sb.from("shifts").select("id,worker_id,worker_name,status,signed_at,company_id")
      .eq("company_id", COMPANY_ID).eq("status", "signed")
      .order("signed_at", { ascending: false }).limit(1);
    const [rById, rByName, rCompany] = await Promise.all([qById, qByName, qCompany].map(q => q ? q : Promise.resolve({ data: [], error: null })));
    const byId = {};
    let errMsg = "";
    [rById, rByName].forEach(r => {
      if (r && r.error) { errMsg = r.error.message; return; }
      for (const row of (r.data || [])) byId[row.id] = row;
    });
    const merged = Object.values(byId).sort((a,b) => (b.signed_at||"").localeCompare(a.signed_at||""));
    const diag = {
      byId: (rById.data||[]).length,
      byName: (rByName.data||[]).length,
      companyTotal: (rCompany.data||[]).length, // 0 or 1 (it's a peek, not a count)
      latestInCompany: (rCompany.data||[])[0] || null,
      errMsg
    };
    console.log("[v17.60] fetchSignedShifts →", { workerId, workerName, diag, merged });
    return { sheets: merged.slice(0, 50), diag };
  } catch (e) { console.error("fetchSignedShifts exception:", e); return empty; }
};

// ── v17.21 — Fetch active notices for this company ──
// Uses select(*) and client-side filtering so it works regardless of table schema.
const fetchNotices = async () => {
  try {
    const { data, error } = await sb.from("notices")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);
    if (error) { console.error("fetchNotices failed:", error.message); return []; }
    return (data || []).filter(n => {
      // If company_id column exists, filter by it; otherwise show all
      if (n.company_id && n.company_id !== COMPANY_ID) return false;
      // If active column exists, respect it
      if (n.active === false) return false;
      // If expires_at column exists, respect it
      if (n.expires_at && new Date(n.expires_at) < new Date()) return false;
      return true;
    });
  } catch (e) { console.error("fetchNotices exception:", e); return []; }
};

// ═══════════════════════════════════════════════════════════════════════
// v17.80 — TOOLBOX TALK PRESETS (one-tap fill for Create Talk)
// White-label: swap these out per trade. Video URLs are public YouTube safety
// clips; supervisor can switch to their own uploaded mp4 after filling.
// ═══════════════════════════════════════════════════════════════════════
const TOOLBOX_PRESETS = [
  {
    key: "ladders",
    label: "Ladder Safety",
    title: "Working at Height — Ladder Safety",
    description: "Watch the short clip on safe ladder use, then answer the question to sign off.",
    videoUrl: "",
    question: "How many points of contact must you keep when climbing a ladder?",
    optionA: "Three points of contact",
    optionB: "One hand only",
    optionC: "No contact needed",
    correctAnswer: "a"
  },
  {
    key: "manual",
    label: "Manual Handling",
    title: "Manual Handling on Site",
    description: "Covers safe lifting technique and weight limits. Watch, then answer to sign off.",
    videoUrl: "",
    question: "What is the first thing you should do before lifting a heavy load?",
    optionA: "Lift quickly to get it over with",
    optionB: "Assess the load and plan the lift",
    optionC: "Twist your back to test the weight",
    correctAnswer: "b"
  },
  {
    key: "ppe",
    label: "PPE",
    title: "PPE on Site — Daily Check",
    description: "Why PPE matters and what to check before starting. Watch, then answer to sign off.",
    videoUrl: "",
    question: "What should you do if your hard hat is cracked?",
    optionA: "Carry on, it still fits",
    optionB: "Tape over the crack",
    optionC: "Replace it before starting work",
    correctAnswer: "c"
  }
];

// ═══════════════════════════════════════════════════════════════════════
// v17.28 — TOOLBOX TALKS
// Fetch pending talks for this worker: talks assigned to their current site
// that they haven't yet signed off on. Returns [] when nothing pending.
// ═══════════════════════════════════════════════════════════════════════
const fetchPendingToolboxTalks = async (workerId, currentSite) => {
  if (!workerId || !currentSite) return [];
  try {
    const { data: talks, error: tErr } = await sb
      .from("toolbox_talks")
      .select("*")
      .eq("company_id", COMPANY_ID)
      .eq("archived", false)
      .contains("assigned_sites", [currentSite])
      .order("posted_at", { ascending: false });
    if (tErr) { console.error("fetchPendingToolboxTalks talks failed:", tErr.message); return []; }
    if (!talks || talks.length === 0) return [];

    const { data: sigs, error: sErr } = await sb
      .from("toolbox_signatures")
      .select("talk_id")
      .eq("worker_id", workerId);
    if (sErr) { console.error("fetchPendingToolboxTalks sigs failed:", sErr.message); return []; }

    const signedIds = new Set((sigs || []).map(s => s.talk_id));
    return talks.filter(t => !signedIds.has(t.id));
  } catch (e) { console.error("fetchPendingToolboxTalks exception:", e); return []; }
};

// Submit a toolbox talk signature. Returns true on success.
const pushToolboxSignature = async ({ talkId, workerId, workerName, siteName, signatureData, gps, answerGiven }) => {
  try {
    const row = {
      company_id: COMPANY_ID, // required once toolbox_signatures gets its RLS policy
      talk_id: talkId,
      worker_id: workerId,
      worker_name: workerName,
      site_name: siteName,
      signed_at: new Date().toISOString(),
      signature_data: signatureData,
      gps_lat: gps ? gps.lat : null,
      gps_lng: gps ? gps.lng : null,
      answer_given: answerGiven
    };
    const { error } = await sb.from("toolbox_signatures").insert(row);
    if (error) { enqueueSupOp({ table: "toolbox_signatures", action: "insert", payload: row }); return true; }
    return true;
  } catch (e) {
    try { enqueueSupOp({ table: "toolbox_signatures", action: "insert", payload: {
      company_id: COMPANY_ID,
      talk_id: talkId, worker_id: workerId, worker_name: workerName, site_name: siteName,
      signed_at: new Date().toISOString(), signature_data: signatureData,
      gps_lat: gps ? gps.lat : null, gps_lng: gps ? gps.lng : null, answer_given: answerGiven
    } }); return true; } catch (e2) { return false; }
  }
};

// Send a message to the supervisor about a toolbox talk (lands in requests table).
const pushToolboxMessage = async ({ talk, workerId, workerName, message, gps }) => {
  try {
    const note =
      "[Toolbox Talk: " + (talk.title || "") + "] " +
      "Re: " + (talk.posted_by || "supervisor") + "\n" +
      message;
    const msgPayload = {
      company_id: COMPANY_ID,
      worker_id: workerId,
      worker_name: workerName,
      request_type: "toolbox_message",
      note: note,
      target_supervisor_name: talk.posted_by || null,
      status: "pending",
      gps_lat: gps ? gps.lat : null,
      gps_lng: gps ? gps.lng : null
    };
    const { error } = await sb.from("requests").insert(msgPayload);
    if (error) { enqueueSupOp({ table: "requests", action: "insert", payload: msgPayload }); return true; }
    return true;
  } catch (e) {
    try {
      const note2 = "[Toolbox Talk: " + (talk.title || "") + "] Re: " + (talk.posted_by || "supervisor") + "\n" + message;
      enqueueSupOp({ table: "requests", action: "insert", payload: {
        company_id: COMPANY_ID, worker_id: workerId, worker_name: workerName,
        request_type: "toolbox_message", note: note2, target_supervisor_name: talk.posted_by || null,
        status: "pending", gps_lat: gps ? gps.lat : null, gps_lng: gps ? gps.lng : null
      } }); return true;
    } catch (e2) { return false; }
  }
};

// Look up a supervisor's phone number by name (for tel: link).
const fetchSupervisorPhone = async (name) => {
  if (!name) return null;
  try {
    const { data, error } = await sb.from("workers")
      .select("phone")
      .eq("company_id", COMPANY_ID)
      .eq("name", name)
      .maybeSingle();
    if (error || !data) return null;
    return data.phone || null;
  } catch (e) { return null; }
};

// ═══════════════════════════════════════════════════════════════════════
// v17.65 — SUPERVISOR TOOLBOX TALK HELPERS
// ═══════════════════════════════════════════════════════════════════════

// Upload a video file to the toolbox-videos bucket. Returns public URL or null.
const uploadToolboxVideo = async (file) => {
  if (!file) return null;
  try {
    const safeName = (file.name || "video.mp4").replace(/[^a-z0-9.\-_]/gi, "_");
    const path = Date.now() + "_" + safeName;
    const { error: upErr } = await sb.storage.from("toolbox-videos").upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || "video/mp4"
    });
    if (upErr) { console.error("uploadToolboxVideo failed:", upErr.message); return null; }
    const { data } = sb.storage.from("toolbox-videos").getPublicUrl(path);
    return data?.publicUrl || null;
  } catch (e) { console.error("uploadToolboxVideo exception:", e); return null; }
};

// Insert a new toolbox talk. Returns the inserted row or null.
const pushToolboxTalk = async ({ title, description, videoUrl, question, optionA, optionB, optionC, correctAnswer, assignedSites, postedBy }) => {
  try {
    const row = {
      company_id: COMPANY_ID,
      title: title || "",
      description: description || "",
      video_url: videoUrl || "",
      question: question || "",
      option_a: optionA || "",
      option_b: optionB || "",
      option_c: optionC || "",
      correct_answer: (correctAnswer || "").toLowerCase(),
      assigned_sites: Array.isArray(assignedSites) ? assignedSites : [],
      posted_by: postedBy || "",
      posted_at: new Date().toISOString(),
      archived: false
    };
    const { data, error } = await sb.from("toolbox_talks").insert(row).select().single();
    if (error) { console.error("pushToolboxTalk failed:", error.message); return null; }
    return data || null;
  } catch (e) { console.error("pushToolboxTalk exception:", e); return null; }
};

// v17.85 — Fetch unsigned RAMS for a worker at a given site.
// Returns the first unsigned RAMS doc, or null if all signed.
// rams_documents.assigned_sites is array; supports "all" or specific site names.
const fetchUnsignedRamsForSite = async (workerIdArg, siteName, excludeSet) => {
  try {
    const { data: docs, error: dErr } = await sb.from("rams_documents")
      .select("id,title,file_url,assigned_sites,posted_at")
      .eq("company_id", COMPANY_ID)
      // v18.44 — archived docs never prompt. The office re-send flow archives
      // the old revision and inserts a new row, so this filter is what makes
      // workers sign the new revision instead of both.
      .eq("archived", false);
    if (dErr) { console.error("rams docs fetch", dErr); return null; }
    const relevant = (docs || []).filter(d => {
      const s = d.assigned_sites || [];
      return s.includes("all") || s.includes(siteName);
    });
    if (relevant.length === 0) return null;
    const { data: sigs, error: sErr } = await sb.from("rams_signatures")
      .select("rams_id")
      .eq("worker_id", workerIdArg)
      .eq("site_name", siteName);
    if (sErr) { console.error("rams sigs fetch", sErr); return null; }
    const signedIds = new Set((sigs || []).map(s => s.rams_id));
    // v18.11 — also exclude everything signed this session. Read-replica lag means a
    // just-inserted signature may not be in `sigs` yet; the session Set guarantees a
    // doc signed moments ago can never re-surface and trap the worker in a sign loop.
    const ex = excludeSet || new Set();
    const unsigned = relevant.find(d => !signedIds.has(d.id) && !ex.has(d.id));
    return unsigned || null;
  } catch (e) { console.error("fetchUnsignedRamsForSite", e); return null; }
};

// ═══ v18.45 — WORKER CARDS (CSCS / SSSTS / SMSTS) ═══
// Cards live in worker_cards, isolated per company via the company key
// (sbBrand) — same model as branding, NOT the open legacy-table pattern.
// One row per worker per card type; re-uploading a renewed card upserts
// over the old row. Deletes are blocked by RLS on purpose.
const fetchWorkerCards = async (workerIdArg) => {
  try {
    // select * so new optional columns (image_back) flow through without a
    // version lockstep between app and schema
    const { data, error } = await sbBrand.from("worker_cards")
      .select("*")
      .eq("worker_id", workerIdArg);
    if (error) { console.error("fetchWorkerCards", error); return null; }
    return data || [];
  } catch (e) { console.error("fetchWorkerCards", e); return null; }
};
const pushWorkerCard = async ({ workerIdArg, workerName, cardType, cardNumber, expiryDate, imageData, imageBack }) => {
  try {
    const row = {
      company_id: COMPANY_ID,
      worker_id: workerIdArg,
      worker_name: workerName,
      card_type: cardType,
      card_number: cardNumber || null,
      expiry_date: expiryDate || null,
      image_data: imageData || null,
      updated_at: new Date().toISOString()
    };
    // v18.46 — back-of-card photo. Only included when set, so a save without
    // a back photo still works even if the column hasn't been added yet.
    if (imageBack !== undefined) row.image_back = imageBack || null;
    const { error } = await sbBrand.from("worker_cards").upsert(row, { onConflict: "worker_id,card_type" });
    if (error) { console.error("pushWorkerCard", error); return false; }
    return true;
  } catch (e) { console.error("pushWorkerCard", e); return false; }
};
// Shrink a card photo so it fits comfortably in a DB row: long edge capped
// at 1000px, JPEG. A phone photo lands around 100-200KB as a data URL.
const resizeCardPhoto = (file) => new Promise((resolve) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, 1000 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    resolve(c.toDataURL("image/jpeg", 0.82));
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
  img.src = url;
});
const CARD_TYPES = [
  { t: "cscs",  label: "CSCS Card" },
  { t: "sssts", label: "SSSTS Certificate" },
  { t: "smsts", label: "SMSTS Certificate" },
];

// Fetch toolbox talks. If supervisor (not admin), filter by posted_by name.
const fetchToolboxTalksForSupervisor = async (supervisorName, isAdmin) => {
  try {
    let q = sb.from("toolbox_talks")
      .select("id,title,description,video_url,question,option_a,option_b,option_c,correct_answer,assigned_sites,posted_by,posted_at,archived")
      .eq("company_id", COMPANY_ID)
      .eq("archived", false)
      .order("posted_at", { ascending: false });
    if (!isAdmin && supervisorName) q = q.eq("posted_by", supervisorName);
    const { data, error } = await q;
    if (error) { console.error("fetchToolboxTalksForSupervisor failed:", error.message); return []; }
    return data || [];
  } catch (e) { console.error("fetchToolboxTalksForSupervisor exception:", e); return []; }
};

// v17.81 — Count signatures across this supervisor's talks newer than `sinceIso`.
// Used for the Tracker tile "new signed" badge. Admin counts all talks.
const fetchNewSignatureCount = async (supervisorName, isAdmin, sinceIso) => {
  try {
    let tq = sb.from("toolbox_talks").select("id").eq("company_id", COMPANY_ID).eq("archived", false);
    if (!isAdmin && supervisorName) tq = tq.eq("posted_by", supervisorName);
    const { data: talks, error: tErr } = await tq;
    if (tErr || !talks || talks.length === 0) return 0;
    const ids = talks.map(t => t.id);
    let sq = sb.from("toolbox_signatures").select("id", { count: "exact", head: true }).in("talk_id", ids);
    if (sinceIso) sq = sq.gt("signed_at", sinceIso);
    const { count, error: sErr } = await sq;
    if (sErr) { console.error("fetchNewSignatureCount failed:", sErr.message); return 0; }
    return count || 0;
  } catch (e) { console.error("fetchNewSignatureCount exception:", e); return 0; }
};

// Fetch all signatures for one talk (for the tracker breakdown).
const fetchSignaturesForTalk = async (talkId) => {
  if (!talkId) return [];
  try {
    const { data, error } = await sb.from("toolbox_signatures")
      .select("id,worker_id,worker_name,site_name,signed_at,answer_given")
      .eq("talk_id", talkId);
    if (error) { console.error("fetchSignaturesForTalk failed:", error.message); return []; }
    return data || [];
  } catch (e) { console.error("fetchSignaturesForTalk exception:", e); return []; }
};

// Fetch workers currently clocked-in today across given sites — returns map {site: [{id,name}]}.
// Uses clock_events with event_type='in' and clocked_at >= today; ignores workers who also have an 'out' since.
const fetchOnSiteTodayForSites = async (siteList) => {
  if (!siteList || siteList.length === 0) return {};
  try {
    const todayStart = new Date(); todayStart.setHours(0,0,0,0);
    const { data, error } = await sb.from("clock_events")
      .select("worker_id,worker_name,site_name,event_type,clocked_at")
      .eq("company_id", COMPANY_ID)
      .in("site_name", siteList)
      .gte("clocked_at", todayStart.toISOString())
      .order("clocked_at", { ascending: true });
    if (error) { console.error("fetchOnSiteTodayForSites failed:", error.message); return {}; }
    // For each (worker,site), the latest event tells us current status
    const latest = {};
    (data || []).forEach(ev => {
      const k = ev.worker_id + "||" + ev.site_name;
      latest[k] = ev; // since ordered asc, last write wins
    });
    const bySite = {};
    siteList.forEach(s => bySite[s] = []);
    Object.values(latest).forEach(ev => {
      if (ev.event_type === "in" && bySite[ev.site_name]) {
        bySite[ev.site_name].push({ id: ev.worker_id, name: ev.worker_name });
      }
    });
    return bySite;
  } catch (e) { console.error("fetchOnSiteTodayForSites exception:", e); return {}; }
};

// v17.66 — Fetch toolbox messages for a supervisor. Worker messages embed "Re: {posted_by}" in the note,
// so a supervisor sees messages whose note mentions their name. Admin sees all toolbox messages.
const fetchSupervisorMessages = async (supervisorName, isAdmin) => {
  try {
    let q = sb.from("requests")
      .select("id,worker_id,worker_name,note,status,gps_lat,gps_lng,created_at,target_supervisor_name")
      .eq("company_id", COMPANY_ID)
      .eq("request_type", "toolbox_message")
      .order("created_at", { ascending: false });
    if (!isAdmin && supervisorName) q = q.eq("target_supervisor_name", supervisorName);
    const { data, error } = await q;
    if (error) { console.error("fetchSupervisorMessages failed:", error.message); return []; }
    return data || [];
  } catch (e) { console.error("fetchSupervisorMessages exception:", e); return []; }
};

// Mark a toolbox message as read (status -> 'read').
const markMessageRead = async (id) => {
  try {
    const { error } = await sb.from("requests").update({ status: "read" }).eq("id", id);
    if (error) { console.error("markMessageRead failed:", error.message); return false; }
    return true;
  } catch (e) { console.error("markMessageRead exception:", e); return false; }
};

// ── v17.18 — Fetch plot entries for a specific shift ──
// Used when worker opens a signed sheet to view full detail.
const fetchShiftEntries = async (shiftId) => {
  try {
    const { data, error } = await sb.from("plot_entries")
      .select("plot_number,description,time_spent_mins,section")
      .eq("shift_id", shiftId)
      .order("section", { ascending: true })
      .order("plot_number", { ascending: true });
    if (error) { console.error("fetchShiftEntries failed:", error.message); return []; }
    return data || [];
  } catch (e) { console.error("fetchShiftEntries exception:", e); return []; }
};

let LOGO_LIGHT = "data:image/png;base64,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";
let LOGO_DARK = "data:image/png;base64,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";


// ═══════════════════════════════════════════════════════════════════════
// CONFIG — swap this block to white-label for a different company/trade
// ═══════════════════════════════════════════════════════════════════════

let COMPANY_NAME = "SiteVouch";
// Changelog (display shows version only):
// v18.53 - 23 Jul: dead-code sweep (lint-clean) — removed the unused voice
//   quick-entry subsystem (parseFullSpeech/applyQuickEntry/extractTime, its
//   quickEntry/showQuick state) plus orphaned states (pwPickedSupId,
//   showPwSupPicker, dayLogOpen, pwSheetInnerRef), stray locals, and a
//   duplicate borderRadius key. No behaviour change intended; live voice
//   dictation (fixWords/whisper) untouched.
// v18.52 - 23 Jul: tidy pass — worker header loses the theme toggle + cog
//   (duplicates of the Profile page; supervisor header untouched), every
//   screen change scrolls to the top, and the price-work summary is renamed
//   from "My Week" to "Price Work" (home row, sheet button, page title,
//   bottom nav).
// v18.51 - 22 Jul: toolbox_signatures insert now stamps company_id (both the
//   direct insert and the offline-queue fallback). Prerequisite for locking
//   the last open table — without it, signing a toolbox talk would be
//   rejected by the RLS policy.
// v18.50 - 22 Jul: main Supabase client now sends BAC's company key on every
//   request, not just branding. Nothing changes on screen — this is the
//   prerequisite for putting per-company RLS on the legacy tables (workers,
//   shifts, clock_events...). Without it those policies would blank the app.
// v18.46 - 19 Jul: My Cards front + BACK photos (needs worker_cards.image_back
//   column — alter table before deploying). Bottom nav for workers:
//   Home / My Week / Profile (Profile opens the settings sheet with My Cards).
// v18.45 - 19 Jul: My Cards — workers upload CSCS/SSSTS/SMSTS card photo +
//   expiry from Settings; stored in worker_cards (company-key isolated, upsert
//   per worker+type, no deletes). Office Compliance tabs show them with
//   expiry warnings.
// v18.44 - 19 Jul: RAMS revisions — archived docs no longer prompt workers.
//   Office re-send = new row at version+1 with the old row archived, so this
//   one filter is the whole worker-side change. "All sites" is now snapshotted
//   to concrete names at send time (office side), so new sites don't inherit.
// v18.43 - 18 Jul: on-screen build shows just the version number
// v18.42 - 17 Jul: white-label — company name, logo & sites load from Supabase via per-company key; sheet logo comes from the company's own branding
const BUILD = "v18.53";
const AUTO_CLOCKOUT_TIME = "23:55"; // CONFIG: time to auto-clock-out forgotten shifts

// BRAND COLOURS — change these to match the company
// BL (primary blue), GR (green/confirm), RD (red/alert) are used throughout
// These are defined later in the component as inline constants.
// To rebrand: search BL/GR/RD below and replace hex values.
// Current: BL="#2F80ED", GR="#27AE60", RD="#EB5757"

// SITES — name, postcode, contractor name, short location label
// v18.41 — this default list is now only the FALLBACK. Real branding
// (company name, logos, sites, contractors) is fetched from Supabase by
// COMPANY_ID below and overwrites these values when available.
let SD=[
  {name:"Bellway Bexhill",pc:"TN39 4TW",ctr:"Bellway",loc:"Bexhill"},
  {name:"Bellway Burgess Hill",pc:"RH15 9QU",ctr:"Bellway",loc:"Burgess Hill"},
  {name:"Bellway Crawley",pc:"RH10 3FF",ctr:"Bellway",loc:"Crawley"},
  {name:"Bellway Durrington",pc:"BN13 2QG",ctr:"Bellway",loc:"Durrington"},
  {name:"Bellway Midhurst",pc:"GU29 9PY",ctr:"Bellway",loc:"Midhurst"},
  {name:"Bellway Raynes Park",pc:"SW20 9AB",ctr:"Bellway",loc:"Raynes Park"},
  {name:"Brookworth Maresfield",pc:"TN22 2HA",ctr:"Brookworth",loc:"Maresfield"},
  {name:"Brookworth Rottingdean",pc:"BN2 7HB",ctr:"Brookworth",loc:"Rottingdean"},
  {name:"Crest Hailsham",pc:"BN27 3PH",ctr:"Crest",loc:"Hailsham"},
  {name:"Croudace Ringmer",pc:"BN8 5LB",ctr:"Croudace",loc:"Ringmer"},
  {name:"Customer Care",pc:"",ctr:"SiteVouch",loc:"Customer Care"},
  {name:"Hills Tunbridge Wells",pc:"TN2 3UY",ctr:"Hills",loc:"Tunbridge Wells"},
  {name:"JVB Chalfont St Giles",pc:"HP8 4QF",ctr:"JVB",loc:"Chalfont St Giles"},
  {name:"JVB Esher",pc:"KT10 8AJ",ctr:"JVB",loc:"Esher"},
  {name:"JVB Monks Gate",pc:"RH13 6JN",ctr:"JVB",loc:"Monks Gate"},
  {name:"JVB Radlett",pc:"WD7 8NW",ctr:"JVB",loc:"Radlett"},
  {name:"JVP Felbridge",pc:"RH19 2PP",ctr:"JVP",loc:"Felbridge"},
  {name:"Office",pc:"",ctr:"SiteVouch",loc:"Office"},
  {name:"SDP Copyhold Lane",pc:"RH17 5EB",ctr:"SDP",loc:"Copyhold Lane"},
  {name:"SDP Naldretts Farm",pc:"RH16 1XR",ctr:"SDP",loc:"Naldretts Farm"},
  {name:"SDP Newick",pc:"BN8 4LE",ctr:"SDP",loc:"Newick"},
  {name:"SDP Redhill Farm",pc:"RH1 4AH",ctr:"SDP",loc:"Redhill Farm"},
  {name:"SDP Wineham",pc:"BN5 9AY",ctr:"SDP",loc:"Wineham"},
  {name:"Taylor Wimpey Friars Oak",pc:"BN6 8EH",ctr:"Taylor Wimpey",loc:"Friars Oak"},
  {name:"Taylor Wimpey Hellingly",pc:"BN27 4EP",ctr:"Taylor Wimpey",loc:"Hellingly"},
  {name:"Taylor Wimpey Horley",pc:"RH6 8EZ",ctr:"Taylor Wimpey",loc:"Horley"},
  {name:"Vistry Kirdford",pc:"RH14 0NQ",ctr:"Vistry",loc:"Kirdford"},
  {name:"Vistry Summer Hill",pc:"RH13 6QA",ctr:"Vistry",loc:"Summer Hill"},
];
let SITES=SD.map(s=>s.name);
let PC=Object.fromEntries(SD.map(s=>[s.name,s.pc]));
let CT=Object.fromEntries(SD.map(s=>[s.name,s.ctr]));
let LC=Object.fromEntries(SD.map(s=>[s.name,s.loc]));

// ═══════════════════════════════════════════════════════════════════════
// v18.41 — COMPANY BRANDING (name, logo, sites, contractors) from Supabase.
// Everything above (COMPANY_NAME, LOGO_LIGHT/DARK, SD) is the fallback: a
// company that hasn't set up its own branding yet — or a device that's
// offline — keeps working exactly as it does today, unbranded shield and
// all. When Supabase has this company's row, it overwrites those values.
//
// Pattern matches the rest of the file: localStorage holds the last-known
// branding so a repeat launch applies it instantly (no blank flash while
// waiting on the network); Supabase is fetched in the background and
// refreshes the cache for next time. On the very first-ever launch (no
// cache yet) the fetch result is applied live and the screen updates as
// soon as it lands, via notifyBrandUpdate().
// ═══════════════════════════════════════════════════════════════════════
const BRAND_CACHE_KEY = "bac_brand_" + COMPANY_ID;
const _brandListeners = [];
const onBrandUpdate = (fn) => { _brandListeners.push(fn); };
const notifyBrandUpdate = () => { _brandListeners.forEach(fn => { try { fn(); } catch (e) {} }); };

const recomputeSiteIndexes = () => {
  SITES = SD.map(s => s.name);
  PC = Object.fromEntries(SD.map(s => [s.name, s.pc]));
  CT = Object.fromEntries(SD.map(s => [s.name, s.ctr]));
  LC = Object.fromEntries(SD.map(s => [s.name, s.loc]));
};

const applyBranding = (b) => {
  if (!b) return;
  if (b.name) COMPANY_NAME = b.name;
  if (b.logo_light) LOGO_LIGHT = b.logo_light;
  if (b.logo_dark) LOGO_DARK = b.logo_dark;
  if (b.sites && b.sites.length) {
    SD = b.sites.map(s => ({ name: s.name, pc: s.postcode || "", ctr: s.contractor || "", loc: s.location_label || "" }));
    recomputeSiteIndexes();
  }
};

// Instant hydrate from last-known branding — no network wait.
try { applyBranding(JSON.parse(localStorage.getItem(BRAND_CACHE_KEY) || "null")); } catch (e) {}

// Background refresh (fire-and-forget). Also what applies branding at all
// on a brand-new device that has no cache yet.
const fetchBranding = async () => {
  try {
    const [{ data: co }, { data: sites }] = await Promise.all([
      sbBrand.from("companies").select("name,logo_light,logo_dark").eq("id", COMPANY_ID).maybeSingle(),
      sbBrand.from("company_sites").select("name,postcode,contractor,location_label").eq("company_id", COMPANY_ID).order("sort_order", { ascending: true }),
    ]);
    if (!co && !sites) return; // offline / table not set up yet — keep fallback or cache
    const brand = { name: co && co.name, logo_light: co && co.logo_light, logo_dark: co && co.logo_dark, sites };
    applyBranding(brand);
    try { localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(brand)); } catch (e) {}
    notifyBrandUpdate();
  } catch (e) { console.error("fetchBranding failed:", e); }
};
fetchBranding();

const DOTS=["#2196F3","#4CAF50","#FF5722","#9C27B0","#F44336","#FF9800","#795548","#009688","#3F51B5","#E91E63","#00BCD4","#8BC34A","#FFC107","#607D8B","#673AB7","#F06292","#4DB6AC","#90A4AE","#A5D6A7","#EF9A9A","#80DEEA","#CE93D8","#FFCC02","#66BB6A","#42A5F5","#EC407A","#26A69A","#FFA726"];

// DAYWORK SHEET SECTIONS — two work-type tabs and sheet labels
// Drylining: "Work Sheet" + "Site Instruction Works"
// To adapt: change tab/sheetLabel text for the new trade
const SHEET_SECTION_A={key:"mg",tab:"📝 Work Sheet",sheetLabel:"Work Sheet - Please Include Plot Numbers",hoursLabel:"Hours"};
const SHEET_SECTION_B={key:"si",tab:"📋 Site Instruction",sheetLabel:"Site Instruction Works - Please Include Plot Numbers",hoursLabel:"Hours"};

// ═══════════════════════════════════════════════════════════════════════
// v18.13 — PRICE WORK operations (BAC drylining, hardcoded for now).
// Worker enters HIS OWN metres + HIS OWN rate on each line, so no rate
// values are baked in — only name, unit, and formula type.
//   formula "qty"  → line value = qty × rate
//   formula "flat" → tick only, fixed price, no metres/rate entry (SVP)
// Later this list moves to per-company Supabase config (office builds it
// per house type). Custom items are added by the worker at line-entry time.
// ═══════════════════════════════════════════════════════════════════════
const PRICE_WORK_OPS=[
  {id:"coving",name:"Coving",unit:"LM",formula:"qty"},
  {id:"wallins",name:"Insulation to partitions",unit:"m²",formula:"qty"},
  {id:"tack",name:"Tack / Wallboard / FireLine / Ceiling",unit:"m²",formula:"qty"},
  {id:"loft",name:"Loft insulation / roll",unit:"m²",formula:"qty"},
  {id:"resbar",name:"Res bar",unit:"m²",formula:"qty"},
  {id:"vcl",name:"VCL (polythene)",unit:"m²",formula:"qty"},
  {id:"dotdab",name:"Dot & dab",unit:"m²",formula:"qty"},
  {id:"tapejoint",name:"Tape & joint",unit:"m²",formula:"qty"},
  {id:"svp",name:"SVP",unit:"house",formula:"flat",flatPrice:35},
];
// v18.14 — Price Work invoice numbering. Kept SEPARATE from timesheet numbering
// (SHEET_NUMBER_PREFIX) and interchangeable per company. Format PW-YYYY-0001.
const PRICE_WORK_SHEET_PREFIX="PW";
const PW_SHEET_SEQ_KEY="bac_pw_sheet_seq";
const nextPwSheetNumber=()=>{
  try{
    const yr=new Date().getFullYear();
    const raw=JSON.parse(localStorage.getItem(PW_SHEET_SEQ_KEY)||"{}");
    const n=(raw.year===yr?(raw.n||0):0)+1;
    localStorage.setItem(PW_SHEET_SEQ_KEY,JSON.stringify({year:yr,n}));
    return PRICE_WORK_SHEET_PREFIX+"-"+yr+"-"+String(n).padStart(4,"0");
  }catch(e){return PRICE_WORK_SHEET_PREFIX+"-"+new Date().getFullYear()+"-0001";}
};
const PW_RECENT_KEY="bac_pw_recent";
const getPwRecent=()=>{try{return JSON.parse(localStorage.getItem(PW_RECENT_KEY)||"[]");}catch(e){return[];}};
const pushPwRecent=(opId)=>{try{const r=getPwRecent().filter(x=>x!==opId);r.unshift(opId);localStorage.setItem(PW_RECENT_KEY,JSON.stringify(r.slice(0,8)));}catch(e){}};
const gbp=n=>"£"+(Math.round((Number(n)||0)*100)/100).toFixed(2);
// v18.30 — Mon–Sun week window in LOCAL calendar dates (BST-safe; no toISOString).
const pwWeekWindow=(offset)=>{
  const now=new Date();
  const dow=(now.getDay()+6)%7; // 0 = Monday
  const mon=new Date(now.getFullYear(),now.getMonth(),now.getDate()-dow-(offset*7));
  const sun=new Date(mon.getFullYear(),mon.getMonth(),mon.getDate()+6);
  const iso=d=>d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0");
  const lbl=d=>d.toLocaleDateString("en-GB",{day:"numeric",month:"short"});
  return {from:iso(mon),to:iso(sun),label:lbl(mon)+" – "+lbl(sun)};
};

// TRADE VOICE CORRECTIONS — see fixWords() function below
// All drylining mishear corrections live there. To adapt for another trade,
// replace the fixes array with trade-specific patterns.
// ═══════════════════════════════════════════════════════════════════════
// END CONFIG
// ═══════════════════════════════════════════════════════════════════════

// Parse time from any format - walks through string finding hours and minutes
// Handles: "2", "2h", "2hr", "2hrs", "2HRS", "30m", "30min", "30mins", 
//          "2h30m", "1hr 30min", "1 hour 30 minutes", "1.5", "1.5h", "90"
const pMin=t=>{
  if(!t)return 0;
  let s=String(t).toLowerCase().trim();
  // Colon format: "2:30" = 2h 30min
  const colon=s.match(/^(\d+):(\d{2})$/);
  if(colon)return parseInt(colon[1])*60+parseInt(colon[2]);
  // Decimal hours: "1.5" or "1.5h" or "1,5" or "2.5hrs"
  const dec=s.match(/^(\d+)[.,](\d+)\s*(?:h|hr|hrs|hour|hours)?$/);
  if(dec){const whole=parseInt(dec[1]);const frac=parseInt(dec[2])/(10**dec[2].length);return Math.round((whole+frac)*60);}
  let mins=0,found=false;
  // Find hours component
  const hMatch=s.match(/(\d+)\s*(?:hours?|hrs?|h)(?![a-z])/);
  if(hMatch){mins+=parseInt(hMatch[1])*60;found=true;s=s.replace(hMatch[0],"");}
  // Find minutes component - also catches plain number after hours e.g. "2hrs 30"
  // v17.48: accept 'mn' and 'mns' as well (common typed shorthand on mobile)
  const mMatch=s.match(/(\d+)\s*(?:minutes?|mins?|mns?|m)(?![a-z])/);
  if(mMatch){mins+=parseInt(mMatch[1]);found=true;}
  else if(found){
    // Check for trailing number with no unit e.g. "2hrs 30" -> 30 mins
    const trailing=s.match(/^\s*(\d+)\s*$/);
    if(trailing){const n=parseInt(trailing[1]);if(n<60)mins+=n;}
  }
  if(found)return mins;
  // Half/quarter hour
  if(/half\s*(?:an?\s*)?hour/.test(s))return 30;
  if(/quarter\s*(?:of\s*an?\s*)?hour/.test(s))return 15;
  // Plain number - if <=12 assume hours, else minutes
  const plain=s.match(/^(\d+)$/);
  if(plain){const n=parseInt(plain[1]);return n<=12?n*60:n;}
  return 0;
};
const fMin=m=>{if(!m)return"";const h=Math.floor(m/60),mm=m%60;return h&&mm?h+"hr "+mm+"min":h?h+"hr":mm+"min";};
const round5=m=>Math.round(m/5)*5;
const fSec=s=>String(Math.floor(s/3600)).padStart(2,"0")+":"+String(Math.floor((s%3600)/60)).padStart(2,"0")+":"+String(s%60).padStart(2,"0");
const gPlot=es=>{const map={};es.forEach(e=>{const k=e.plot.padStart(8,"0");if(!map[k])map[k]={plot:e.plot,items:[],mins:0};map[k].items.push(e);map[k].mins+=e.mins;});return Object.keys(map).sort().map(k=>map[k]);};

// ── v17.18 — Build supervisor display labels with first-name-only fallback ──
// If two supervisors share a first name, both show "First Surname". Otherwise just first name.
const buildSupervisorOptions = (rawList) => {
  const supervisors = (rawList || []).map(w => {
    const parts = (w.name || "").trim().split(/\s+/);
    const firstName = parts[0] || w.name || "";
    const surname = parts.slice(1).join(" ");
    return { id: w.id, full_name: w.name, first_name: firstName, surname };
  });
  const counts = supervisors.reduce((a, s) => { a[s.first_name] = (a[s.first_name] || 0) + 1; return a; }, {});
  return supervisors
    .map(s => ({ ...s, label: counts[s.first_name] > 1 ? s.full_name : s.first_name }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// ── v17.18 — Build signed daywork sheet HTML (for "Save for Invoicing" download) ──
// Same A4 layout as buildPDF but with signature embedded. This is the deliverable.
const buildSignedSheetHTML = (shift, entries, workerName, logoSrc) => {
  const mgEntries = (entries||[]).filter(e => e.section === "mg");
  const siEntries = (entries||[]).filter(e => e.section === "si");
  const groupForHtml = (rows) => {
    const map = {};
    rows.forEach(e => {
      const k = String(e.plot_number).padStart(8,"0");
      if (!map[k]) map[k] = { plot: e.plot_number, items: [], mins: 0 };
      map[k].items.push({ desc: e.description, mins: e.time_spent_mins });
      map[k].mins += e.time_spent_mins || 0;
    });
    return Object.keys(map).sort().map(k => map[k]);
  };
  const mgGroups = groupForHtml(mgEntries);
  const siGroups = groupForHtml(siEntries);
  const mgTxt = mgGroups.map(g => "PLT "+g.plot+" - "+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
  const siTxt = siGroups.map(g => "PLT "+g.plot+" - "+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
  const mgMins = mgEntries.reduce((a,e)=>a+(e.time_spent_mins||0),0);
  const siMins = siEntries.reduce((a,e)=>a+(e.time_spent_mins||0),0);
  const mhStr = mgMins ? fMin(mgMins) : "";
  const shStr = siMins ? fMin(siMins) : "";
  const dateStr = shift.shift_date ? new Date(shift.shift_date).toLocaleDateString("en-GB") : "";
  const signedDateStr = shift.signed_at ? new Date(shift.signed_at).toLocaleDateString("en-GB") : "";
  const td = "border:1px solid #444;padding:3px 6px;font-family:Arial,sans-serif;font-size:11px;color:#111;vertical-align:middle;";
  const th = td + "background:#BDD6F1;font-weight:700;";
  const sigCell = shift.signature_data
    ? "<td style=\""+td+"width:20%;padding:4px;text-align:center;\"><img src=\""+shift.signature_data+"\" alt=\"signature\" style=\"max-height:46px;max-width:100%;object-fit:contain;\"/></td>"
    : "<td style=\""+td+"width:20%;padding:10px;\">&nbsp;</td>";
  const html = "<!DOCTYPE html><html><head><meta charset=\"utf-8\"><title>SiteVouch Daywork Sheet - Signed</title>"
    + "<style>body{margin:20px;font-family:Arial,sans-serif;}table{border-collapse:collapse;width:100%;}"
    + "@media print{body{margin:0;}@page{margin:8mm;}.noprint{display:none;}}</style>"
    + "</head><body>"
    + "<div style=\"width:680px;border:1.5px solid #333;padding:4px 10px 6px;box-sizing:border-box;\">"
    + "<div style=\"display:flex;align-items:center;margin-bottom:4px;gap:12px;\">"
    + "<img src=\""+logoSrc+"\" style=\"width:56px;height:56px;object-fit:contain;\" alt=\"SiteVouch\"/>"
    + "<div style=\"flex:1;text-align:center;font-weight:900;font-size:16px;letter-spacing:2px;text-decoration:underline;\">DAYWORK SHEET</div>"
    + "</div>"
    + "<table style=\"margin-bottom:2px\"><tbody>"
    + "<tr><td rowspan=\"3\" style=\""+th+"width:18%;text-align:center;font-size:9px;\">SUB-CONTRACTOR NAME</td>"
    + "<td rowspan=\"3\" style=\""+td+"width:25%;font-size:13px;font-weight:700;text-align:center;\">"+workerName+"</td>"
    + "<td style=\""+th+"width:19%;font-size:9px;\">DATE</td><td style=\""+td+"font-size:11px;\">"+dateStr+"</td></tr>"
    + "<tr><td style=\""+th+"font-size:9px;\">SITE LOCATION</td><td style=\""+td+"font-size:11px;\">"+(LC[shift.site_name]||shift.site_name||"")+"</td></tr>"
    + "<tr><td style=\""+th+"font-size:9px;\">CONTRACTOR</td><td style=\""+td+"font-size:11px;\">"+(CT[shift.site_name]||"")+"</td></tr>"
    + "</tbody></table>"
    + "<table style=\"margin-bottom:2px\"><tbody>"
    + "<tr><td colspan=\"2\" style=\""+th+"font-size:10px;\">Work Sheet - Please Include Plot Numbers</td></tr>"
    + "<tr><td style=\""+td+"font-size:9px;color:#CC0000;font-weight:700;font-style:italic;\">Please confirm how long you spent in each plot next to your plot number</td>"
    + "<td style=\""+td+"width:140px;text-align:right;font-weight:700;font-size:14px;\">Hours: "+mhStr+"</td></tr>"
    + "<tr><td colspan=\"2\" style=\""+td+"height:380px;vertical-align:top;padding-top:5px;white-space:pre-wrap;\">"+mgTxt+"</td></tr>"
    + "</tbody></table>"
    + "<table style=\"margin-bottom:2px\"><tbody>"
    + "<tr><td style=\""+th+"font-size:10px;\">Site Instruction Works - Please Include Plot Numbers</td>"
    + "<td style=\""+th+"width:140px;text-align:right;font-size:14px;\">Hours: "+shStr+"</td></tr>"
    + "<tr><td colspan=\"2\" style=\""+td+"height:140px;vertical-align:top;padding-top:5px;white-space:pre-wrap;\">"+siTxt+"</td></tr>"
    + "</tbody></table>"
    + "<table style=\"width:45%;margin-bottom:2px\"><tbody>"
    + "<tr><td style=\""+th+"text-align:center;width:50%;font-size:10px;\">TIME IN</td><td style=\""+th+"text-align:center;width:50%;font-size:10px;\">TIME OUT</td></tr>"
    + "<tr><td style=\""+td+"text-align:center;font-size:14px;font-weight:700;\">"+(shift.time_in||"")+"</td><td style=\""+td+"text-align:center;font-size:14px;font-weight:700;\">"+(shift.time_out||"")+"</td></tr>"
    + "</tbody></table>"
    + "<table><tbody><tr>"
    + "<td style=\""+th+"width:28%;font-size:9px;\">SiteVouch Supervisor Signature</td>"
    + sigCell
    + "<td style=\""+th+"width:10%;font-size:9px;\">Date</td>"
    + "<td style=\""+td+"width:42%;padding:4px;font-size:11px;text-align:center;\">"+signedDateStr+"</td>"
    + "</tr></tbody></table>"
    + (shift.signed_by_name ? "<div style=\"margin-top:4px;padding:3px 6px;background:#F0FAF4;border:1px solid #A8D5B5;font-size:10px;color:#15803D;text-align:center;font-weight:600;\">&#10003; Signed by "+shift.signed_by_name+"</div>" : "")
    + (shift.supervisor_notes ? "<div style=\"margin-top:4px;padding:6px 8px;background:#EBF3FD;border:1px solid #BDD6F1;font-size:11px;color:#1E40AF;\"><strong>Supervisor Notes:</strong> "+shift.supervisor_notes+"</div>" : "")
    + "</div>"
    + "<br><p class=\"noprint\" style=\"color:#666;font-size:13px;\">To save as PDF: tap the share/menu button in your browser &rarr; Print &rarr; Save as PDF</p>"
    + "</body></html>";
  return html;
};
const saveShift=r=>{try{const ex=JSON.parse(localStorage.getItem("bac_shifts")||"[]");ex.push(r);localStorage.setItem("bac_shifts",JSON.stringify(ex));return true;}catch(e){return false;}};
const getShifts=()=>{try{return JSON.parse(localStorage.getItem("bac_shifts")||"[]").sort((a,b)=>b.id-a.id);}catch(e){return[];}};

// ── v17.47 — ACTIVE SHIFT PERSISTENCE ──
// Restores clocked-in state if the worker swipes the app away mid-shift.
// Computed once at module load; only restores if the saved shift's workerId
// matches the currently signed-in worker.
const _activeShiftOnBoot = (() => {
  try {
    const raw = localStorage.getItem("bac_active_shift");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const wid = localStorage.getItem("bac_worker_id") || "";
    if (parsed && parsed.workerId && parsed.workerId === wid) return parsed;
    return null;
  } catch (e) { return null; }
})();

// ── v17.47 — WHISPER HALLUCINATION FILTER ──
// Whisper invents common phrases when handed silent/unclear audio
// (YouTube training-data echoes). Filter these out before they hit the form.
const HALLUCINATION_PATTERNS = [
  /thank you for watching/i,
  /thanks for watching/i,
  /thank you so much/i,
  /thank you,? everyone/i,
  /thanks for joining/i,
  /thank you for joining/i,
  /please subscribe/i,
  /don'?t forget to subscribe/i,
  /for more information/i,
  /\bvisit\b.*(?:www\.|http|\.com|\.org|\.net|\.gov)/i,
  /\bwww\./i,
  /https?:\/\//i,
  /\.(?:com|org|net|gov|edu|io|me|tv|info)\b/i,
  /\.(?:co\.uk|co\.nz|com\.au)\b/i,
  /subtitle/i,
  /captions provided/i,
  /subscribe to.*channel/i,
  /\bbye[- ]?bye\b/i,                  // covers "bye-bye" / "bye bye" loop (very common silence hallucination)
  /have a great day/i,
  /have a nice day/i,
  /sharing my screen/i,
  /going to (?:stop|turn it off|go ahead)/i,
  /^\s*bye[.! ]*$/i,
];
const isHallucination = (t) => {
  if (!t) return true;
  const s = t.trim();
  if (s.length < 2) return true;
  return HALLUCINATION_PATTERNS.some(p => p.test(s));
};



export default function App(){
  // v17.47 — restore screen + shift state if app was swiped away mid-shift
  const _A = _activeShiftOnBoot;
  // v18.04 — boot to home EXCEPT when the saved shift is mid clock-out flow
  // (clocked out: tIn+tOut set, not yet submitted). That state has no card on
  // home, so a refresh after clock-out would land on a dead screen. Restoring
  // "summary" keeps the worker on the daywork summary where they can submit.
  // v18.14 — price-work screens ("pricework","pwsummary") restore the same way
  // so a refresh mid price-work never dumps the worker on the daywork sheet.
  // Any other saved screen still boots to home (v18.02 safety rule).
  const _PW_BOOT_OK = _A && _A.tIn && !_A.submitted && (_A.sc==="pricework" || _A.sc==="pwsummary");
  const _bootSc = _PW_BOOT_OK ? _A.sc : ((_A && _A.tIn && !_A.submitted && _A.sc) ? _A.sc : "home");
  const [sc,setSc]=useState(_bootSc);
  // v18.52 — every screen opens at the top (was keeping the previous screen's scroll position)
  useEffect(()=>{ window.scrollTo(0,0); },[sc]);
  // v18.41 — re-render once real branding lands (first-ever launch, no
  // cache yet). Repeat launches already have the cache applied above
  // before this component even mounts, so this is a no-op then.
  const [,_brandTick]=useState(0);
  useEffect(()=>{ onBrandUpdate(()=>_brandTick(v=>v+1)); },[]);
  // ── v17.19 — THEME (light default, persisted) ──
  const [theme,setTheme]=useState(()=>{try{return localStorage.getItem("bac_theme")||"light";}catch(e){return"light";}});
  useEffect(()=>{try{localStorage.setItem("bac_theme",theme);}catch(e){}}, [theme]);
  const isDark=theme==="dark";
  const LOGO=isDark?LOGO_DARK:LOGO_LIGHT; // v18.37 — theme-aware app logo
  const BG=isDark?"#0f1117":"#EEF2F7";
  const W=isDark?"#1a1d26":"#fff";
  const BL=isDark?"#4f8ef7":"#2F80ED";
  const GR=isDark?"#34d399":"#27AE60";
  const RD=isDark?"#f87171":"#EB5757";
  const BD=isDark?"rgba(255,255,255,0.12)":"#D1D5DB";
  const MT=isDark?"#8a8fa8":"#6B7280";
  const DK=isDark?"#f0f2ff":"#111827";
  const LBL=isDark?"#1a2744":"#EBF3FD";
  const SBL=isDark?"#2a4a7f":"#BDD6F1";
  const F2=isDark?"#22263a":"#F2F2F7";
  const FA=isDark?"#1e2235":"#FAFAFA";
  const [showS,setShowS]=useState(false);
  const [srch,setSrch]=useState("");
  const [recent,setRecent]=useState(["SDP Redhill Farm","Taylor Wimpey Friars Oak","Bellway Burgess Hill"]);
  const [site,setSite]=useState(_A?.site || "");
  const [ctr,setCtr]=useState(_A?.ctr || "");
  const [tIn,setTIn]=useState(_A?.tIn || "");
  const [tOut,setTOut]=useState(_A?.tOut || "");
  const [name,setName]=useState(()=>{try{return localStorage.getItem("bac_name")||"";}catch(e){return"";}});
  const [workerId,setWorkerId]=useState(()=>{try{return localStorage.getItem("bac_worker_id")||"";}catch(e){return"";}});
  const [workerRole,setWorkerRole]=useState(()=>{try{return localStorage.getItem("bac_worker_role")||"";}catch(e){return"";}});
  const [pinInput,setPinInput]=useState("");
  const [pinError,setPinError]=useState("");
  const [pinChecking,setPinChecking]=useState(false);
  const [showPinPrompt,setShowPinPrompt]=useState(false);
  const [showPinChange,setShowPinChange]=useState(false);
  const [newPin1,setNewPin1]=useState("");
  const [newPin2,setNewPin2]=useState("");
  const [pinChangeError,setPinChangeError]=useState("");
  const [pinStep,setPinStep]=useState(1);  // 1 = entering new, 2 = confirming
  const [showSettings,setShowSettings]=useState(false);
  // v18.45 — My Cards (CSCS/SSSTS/SMSTS)
  const [showMyCards,setShowMyCards]=useState(false);
  const [myCards,setMyCards]=useState([]);       // saved rows from worker_cards
  const [cardDraft,setCardDraft]=useState({});   // unsaved per-type edits {image,expiry,number,busy}
  // v18.48 — Notifications: the worker's own request outcomes + card expiry warnings.
  // Read state is per-device localStorage (no schema change, works on a clean DB).
  const [myRequests,setMyRequests]=useState([]);
  const [notifReadIds,setNotifReadIds]=useState([]);
  const [entries,setEntries]=useState(_A?.entries || []);
  const [plot,setPlot]=useState("");
  const [desc,setDesc]=useState("");
  const [tSpent,setTSpent]=useState("");
  const [sec,setSec]=useState(_A?.sec || "mg");
  const [mats,setMats]=useState(_A?.mats || "");
  // ── v18.13 — PRICE WORK sheet state (self-contained; worker enters own metres+rate) ──
  const [pwLines,setPwLines]=useState(_A?.pwLines || []); // each: {id,plot,opId,name,unit,formula,qty,rate,flatPrice,custom}
  const [pwPlot,setPwPlot]=useState("");
  const [pwOpId,setPwOpId]=useState("");
  const [pwQty,setPwQty]=useState("");
  const [pwRate,setPwRate]=useState("");
  const [pwCustomOpen,setPwCustomOpen]=useState(false);
  const [pwOpMenuOpen,setPwOpMenuOpen]=useState(false);
  const [pwCustomName,setPwCustomName]=useState("");
  const [pwCustomUnit,setPwCustomUnit]=useState("unit");
  const [pwRecent,setPwRecent]=useState(getPwRecent());
  // v18.26 — office-booked meterage pull. When the worker's plot matches a plot the
  // office has tagged (price_work_office), we fetch those booked lines so he can
  // confirm or override each one. Pull-only; nothing is pushed back from here.
  const [pwOfficeBooked,setPwOfficeBooked]=useState([]); // office rows for the current site+plot
  const [pwOfficeFor,setPwOfficeFor]=useState("");       // "site||plot" the rows belong to
  const [pwOfficeLoading,setPwOfficeLoading]=useState(false);
  // v18.14 — Price Work submit/routing state (mirrors timesheet clock-out→review→submit)
  const [pwMode,setPwMode]=useState(_A?.pwMode || false); // true = this shift is a price-work shift; clock-out routes to pwsummary
  const [,setPwActivePlot]=useState(""); // which plot's sheet is being submitted (value itself unused)
  const [pwSubmittedPlots,setPwSubmittedPlots]=useState(_A?.pwSubmittedPlots || {}); // {plot: {sheetNumber, supervisorName, at}}
  const [pwSubmitting,setPwSubmitting]=useState(false);
  const [pwEditId,setPwEditId]=useState(null); // line id being edited on review screen
  const [pwInvoiceNos,setPwInvoiceNos]=useState(_A?.pwInvoiceNos || {}); // v18.21 — worker's own invoice number per plot {plot: "INV-123"}
  // v18.30 — "My Week" read-only price work summary. Pulls this worker's submitted
  // claims + lines from Supabase for a Mon–Sun window and staples them into one
  // priced sheet. NOT a tax invoice — no CIS/VAT is calculated anywhere here.
  const [pwWeekClaims,setPwWeekClaims]=useState([]);   // parent claims for the window
  const [pwWeekLines,setPwWeekLines]=useState([]);     // all child lines for those claims
  const [pwWeekLoading,setPwWeekLoading]=useState(false);
  const [pwWeekErr,setPwWeekErr]=useState("");
  const [pwWeekOffset,setPwWeekOffset]=useState(0);    // 0 = this week, 1 = last week
  const [pwWeekInvNo,setPwWeekInvNo]=useState("");     // v18.31 — worker's editable invoice number for the week sheet
  const [pwWeekPdf,setPwWeekPdf]=useState(null);       // v18.31 — generated week-sheet HTML data-url
  // v18.32 — editable, remembered header details for the My Week sheet (per-device
  // self-service branding; full Supabase company branding is a later build).
  const [pwTradeName,setPwTradeName]=useState(()=>{try{return localStorage.getItem("bac_pw_trade_name")||"";}catch(e){return"";}});
  const [pwContractor,setPwContractor]=useState(()=>{try{return localStorage.getItem("bac_pw_contractor")||"";}catch(e){return"";}});
  const [pwLogo,setPwLogo]=useState(()=>{try{return localStorage.getItem("bac_pw_logo")||"";}catch(e){return"";}}); // base64 data-url
  // v18.33 — CIS/VAT + invoice header fields (all per-device localStorage, set once)
  const [pwCisOn,setPwCisOn]=useState(()=>{try{const v=localStorage.getItem("bac_pw_cis_on");return v===null?true:v==="1";}catch(e){return true;}}); // default ON (sole traders)
  const [pwCisRate,setPwCisRate]=useState(()=>{try{return localStorage.getItem("bac_pw_cis_rate")||"20";}catch(e){return"20";}});
  const [pwVatOn,setPwVatOn]=useState(()=>{try{return localStorage.getItem("bac_pw_vat_on")==="1";}catch(e){return false;}}); // default OFF (rare)
  const [pwVatRate,setPwVatRate]=useState(()=>{try{return localStorage.getItem("bac_pw_vat_rate")||"20";}catch(e){return"20";}});
  const [pwUtr,setPwUtr]=useState(()=>{try{return localStorage.getItem("bac_pw_utr")||"";}catch(e){return"";}});
  const [pwVatNo,setPwVatNo]=useState(()=>{try{return localStorage.getItem("bac_pw_vat_no")||"";}catch(e){return"";}});
  const [pwCoNo,setPwCoNo]=useState(()=>{try{return localStorage.getItem("bac_pw_co_no")||"";}catch(e){return"";}});
  const [pwPayInfo,setPwPayInfo]=useState(()=>{try{return localStorage.getItem("bac_pw_pay_info")||"";}catch(e){return"";}}); // free-text bank/sort/account block
  // v18.34 — full editable invoice header: sender contact, Bill To block, dates
  const [pwAddress,setPwAddress]=useState(()=>{try{return localStorage.getItem("bac_pw_address")||"";}catch(e){return"";}}); // multi-line sender address
  const [pwPhone,setPwPhone]=useState(()=>{try{return localStorage.getItem("bac_pw_phone")||"";}catch(e){return"";}});
  const [pwEmail,setPwEmail]=useState(()=>{try{return localStorage.getItem("bac_pw_email")||"";}catch(e){return"";}});
  const [pwBillContact,setPwBillContact]=useState(()=>{try{return localStorage.getItem("bac_pw_bill_contact")||"";}catch(e){return"";}}); // contractor contact name
  const [pwBillEmail,setPwBillEmail]=useState(()=>{try{return localStorage.getItem("bac_pw_bill_email")||"";}catch(e){return"";}});
  const [pwInvDate,setPwInvDate]=useState("");   // per-week, not persisted
  const [pwDueDate,setPwDueDate]=useState("");   // per-week, not persisted
  const [pwEditHeader,setPwEditHeader]=useState(false);
  // v17.49 — restore elapsed counter from saved clock-in epoch (so refresh doesn't reset it)
  const [tInTs,setTInTs]=useState(_A?.tInTs || 0);
  const [elapsed,setElapsed]=useState(() => {
    if (_A?.tInTs) {
      const diff = Math.floor((Date.now() - _A.tInTs) / 1000);
      return diff > 0 ? diff : 0;
    }
    return 0;
  });
  const [nowStr,setNowStr]=useState("");
  const [gpsIn,setGpsIn]=useState(_A?.gpsIn || null);
  const [gpsOut,setGpsOut]=useState(_A?.gpsOut || null);
  const [gpsErr,setGpsErr]=useState("");
  const [scale,setScale]=useState(1);
  const supSheetInnerRef=useRef(null); // v17.93 — measure real supervisor sheet height
  const [supSheetH,setSupSheetH]=useState(700); // v17.93
  const [submitted,setSubmitted]=useState(!!_A?.submitted);
  const [listen,setListen]=useState(false);
  const [lisF,setLisF]=useState(null);
  const [voiceStatus,setVoiceStatus]=useState("");
  const [activeField,setActiveField]=useState(null);
  const [voiceStep,setVoiceStep]=useState("plot"); // v17.4: cycles plot→time→desc→plot for hands-free voice flow
  const [fromSummary,setFromSummary]=useState(false);
  const [sheetDate,setSheetDate]=useState(_A?.sheetDate||"");
  const [sheetSite,setSheetSite]=useState(_A?.sheetSite||"");
  const [sheetSiteCustom,setSheetSiteCustom]=useState(false); // v18.27 — worker typing a site not in the list
  const [pastShifts,setPastShifts]=useState([]);
  const [showEdit,setShowEdit]=useState(false);
  const [editShift,setEditShift]=useState(null);
  const [editIn,setEditIn]=useState("");
  const [editOut,setEditOut]=useState("");
  const [editNote,setEditNote]=useState("");
  const [editDone,setEditDone]=useState(false);
  const [showAdd,setShowAdd]=useState(false);
  const [addSite,setAddSite]=useState("");
  const [addDate,setAddDate]=useState("");
  const [addIn,setAddIn]=useState("");
  const [addOut,setAddOut]=useState("");
  const [addNote,setAddNote]=useState("");
  const [addDone,setAddDone]=useState(false);
  const [showAddSite,setShowAddSite]=useState(false);
  const [addSrch,setAddSrch]=useState("");
  const [offlineQueue,setOfflineQueue]=useState(()=>{try{return JSON.parse(localStorage.getItem("bac_offline")||"[]");}catch(e){return[];}});
  const [isOnline,setIsOnline]=useState(navigator.onLine);
  const [weekSummary,setWeekSummary]=useState(null);
  const [pdfUrl,setPdfUrl]=useState(null);
  const [showPreview,setShowPreview]=useState(false);
  const [showSiteRequest,setShowSiteRequest]=useState(false);
  const [siteRequestNote,setSiteRequestNote]=useState("");
  const [siteRequestSent,setSiteRequestSent]=useState(false);
  const [autoClockOutBanner,setAutoClockOutBanner]=useState(null); // {date, tIn, site} when auto-clocked-out

  // ── v17.77 — SWITCH JOB / MY DAY LOG ──
  // dayStints: today's clock_events paired into {site,inTs,outTs|null}. Read-only, derived from Supabase.
  const [dayStints,setDayStints]=useState([]);

  // ── v17.28 — TOOLBOX TALKS state ──
  const [toolboxPending,setToolboxPending]=useState([]); // [{talk row}] pending for this worker
  const [toolboxOpenTalk,setToolboxOpenTalk]=useState(null); // talk currently being watched/signed
  const [tbVideoWatched,setTbVideoWatched]=useState(false);
  const [tbQuestionAnswered,setTbQuestionAnswered]=useState(false);
  const [tbAnswerGiven,setTbAnswerGiven]=useState("");
  const [tbWrongTry,setTbWrongTry]=useState(false);
  const [tbWrongLetter,setTbWrongLetter]=useState("");
  const [tbGps,setTbGps]=useState(null);
  const [tbGpsErr,setTbGpsErr]=useState("");
  const [tbSupPhone,setTbSupPhone]=useState(null);
  const [tbMessage,setTbMessage]=useState("");
  const [tbMessageStatus,setTbMessageStatus]=useState(""); // "ok" | "err" | ""
  const [tbMessageSending,setTbMessageSending]=useState(false);
  const [tbSubmitting,setTbSubmitting]=useState(false);
  const [tbSignedDone,setTbSignedDone]=useState(false);
  const tbSigRef = useRef(null); // signature canvas ref
  const tbSigHasInkRef = useRef(false);
  const tbRecentlySignedRef = useRef(new Set()); // replica-lag guard
  const tbMaxWatchedRef = useRef(0); // max video time watched for scrub blocker

  // ── v17.18 — SUPERVISOR PICKER + SIGNED SHEETS STATE ──
  const [supervisors,setSupervisors]=useState([]); // list of supervisors (workers w/ role=supervisor)
  const [showSupPicker,setShowSupPicker]=useState(false); // bottom sheet visible
  const [pickedSupId,setPickedSupId]=useState(""); // selection inside picker before confirm
  const [submitting,setSubmitting]=useState(false); // submit in progress
  const [signedSheets,setSignedSheets]=useState([]); // signed shifts for this worker
  const [signedLoading,setSignedLoading]=useState(false);
  const [openSignedSheet,setOpenSignedSheet]=useState(null); // currently viewed signed sheet
  const [signedSheetEntries,setSignedSheetEntries]=useState([]);
  const [signedSheetLoading,setSignedSheetLoading]=useState(false);
  const [signedSheetUrl,setSignedSheetUrl]=useState(null); // download URL for current signed sheet

  // ── v17.57 — Worker feedback loop (per-device localStorage tracking) ──
  const [signedOpenedIds,setSignedOpenedIds]=useState(()=>{try{return JSON.parse(localStorage.getItem("bac_signed_opened")||"[]");}catch(e){return[];}});
  const [signedDownloadedIds,setSignedDownloadedIds]=useState(()=>{try{return JSON.parse(localStorage.getItem("bac_signed_downloaded")||"[]");}catch(e){return[];}});
  const [signedDismissedIds,setSignedDismissedIds]=useState(()=>{try{return JSON.parse(localStorage.getItem("bac_signed_dismissed")||"[]");}catch(e){return[];}});
  const [showDismissConfirm,setShowDismissConfirm]=useState(false); // confirmation overlay when dismissing un-downloaded sheet
  // ── v17.60 — debug snapshot from last signed-sheets fetch (for on-screen strip when 0 results) ──
  const [signedDiag,setSignedDiag]=useState(null);

  // ── v17.75 — Filter state for My Signed Sheets page ──
  const [signedDateFilter,setSignedDateFilter]=useState("week"); // today | week | month | year | all
  const [signedSiteFilter,setSignedSiteFilter]=useState("all"); // all | exact site_name

  // ── SUPERVISOR STATE ──
  const [supSites,setSupSites]=useState([]);
  const [supLoading,setSupLoading]=useState(false);
  const [supSelSite,setSupSelSite]=useState(null);
  const [supSc,setSupSc]=useState("sites"); // sites | dashboard
  const [supWorkers,setSupWorkers]=useState([]);
  const [supSheets,setSupSheets]=useState([]);
  const [supNotices,setSupNotices]=useState([]);
  const [supNoticeInput,setSupNoticeInput]=useState("");
  const [supAllSheets,setSupAllSheets]=useState([]);
  const [supSheetSearch,setSupSheetSearch]=useState("");
  const [supSelSheet,setSupSelSheet]=useState(null);
  const [supSheetEntries,setSupSheetEntries]=useState([]);
  const [supSheetNotes,setSupSheetNotes]=useState("");
  const [supSigData,setSupSigData]=useState("");
  const [supSigDrawing,setSupSigDrawing]=useState(false);
  const [supSigning,setSupSigning]=useState(false);
  const [supSiteStats,setSupSiteStats]=useState({}); // {siteName:{onSite:n}}

  // ── v17.84 — SNAGS STATE ──
  const [snagList,setSnagList]=useState([]);
  const [snagLoading,setSnagLoading]=useState(false);
  const [snagStatusFilter,setSnagStatusFilter]=useState("open"); // open | all
  const [snagPhotoView,setSnagPhotoView]=useState(null); // url string when viewing full-size
  const [snagForm,setSnagForm]=useState({site:"",plot:"",area:"",areaOther:"",desc:"",priority:"",due:"",photoBlob:null,annotatedBlob:null,photoPreview:""});
  const [snagSubmitting,setSnagSubmitting]=useState(false);
  const [snagMsg,setSnagMsg]=useState("");

  // v17.85 — annotator state
  const [annoOpen,setAnnoOpen]=useState(false);
  const [annoTool,setAnnoTool]=useState("arrow"); // arrow|line|circle|rect|free|text
  const [annoColor,setAnnoColor]=useState("#FF0000");
  const [annoSize,setAnnoSize]=useState(6);
  const annoCanvasRef=useRef(null);
  const annoImgRef=useRef(null);
  const annoStrokesRef=useRef([]);
  const annoDrawingRef=useRef(false);
  const annoCurrentRef=useRef(null);
  const [annoTextPos,setAnnoTextPos]=useState(null);
  const [annoTextValue,setAnnoTextValue]=useState("");

  // v17.85 — RAMS state (restored from v17.28)
  const [ramsToSign,setRamsToSign]=useState(null); // current unsigned RAMS doc being shown
  const [ramsPendingSite,setRamsPendingSite]=useState(null); // site to clock into after sign
  const [ramsRead,setRamsRead]=useState(false);
  const [ramsSigData,setRamsSigData]=useState("");
  const [ramsSigning,setRamsSigning]=useState(false);
  const ramsSigCanvasRef=useRef(null);
  const ramsSigDrawingRef=useRef(false);
  const sessionSignedRamsIds=useRef(new Set()); // v18.11 — every doc signed this session; replica lag can't re-surface them

  // ── v17.65 — SUPERVISOR TOOLBOX TALK STATE ──
  // Create Talk form
  const [ctTitle,setCtTitle]=useState("");
  const [ctDesc,setCtDesc]=useState("");
  const [ctVideoMode,setCtVideoMode]=useState("url"); // "url" | "upload"
  const [ctVideoUrl,setCtVideoUrl]=useState("");
  const [ctVideoFile,setCtVideoFile]=useState(null);
  const [ctQuestion,setCtQuestion]=useState("");
  const [ctOptA,setCtOptA]=useState("");
  const [ctOptB,setCtOptB]=useState("");
  const [ctOptC,setCtOptC]=useState("");
  const [ctCorrect,setCtCorrect]=useState(""); // "a" | "b" | "c"
  const [ctSelSites,setCtSelSites]=useState([]); // array of site names
  const [ctSubmitting,setCtSubmitting]=useState(false);
  const [ctError,setCtError]=useState("");
  const [ctSuccess,setCtSuccess]=useState(false);
  // Sign-off Tracker
  const [soTalks,setSoTalks]=useState([]);
  const [soLoading,setSoLoading]=useState(false);
  const [soOpenTalkId,setSoOpenTalkId]=useState(null);
  const [soSigsByTalk,setSoSigsByTalk]=useState({}); // {talkId: [signatures]}
  const [soOnSiteByTalk,setSoOnSiteByTalk]=useState({}); // {talkId: {site:[workers]}}
  const [soDetailLoading,setSoDetailLoading]=useState(false);
  const [soDateFilter,setSoDateFilter]=useState("all"); // today | week | all
  // v17.66 — Supervisor messages inbox
  const [supMsgs,setSupMsgs]=useState([]);
  const [supMsgLoading,setSupMsgLoading]=useState(false);
  const [supMsgShowRead,setSupMsgShowRead]=useState(false); // v17.67: default unread only
  const [soNewSigs,setSoNewSigs]=useState(0); // v17.81 — new signatures since Tracker last opened

  // ── v17.21 — NOTICE BOARD STATE ──
  const [notices,setNotices]=useState([]);
  const [expandedNotices,setExpandedNotices]=useState({}); // {id:true} when expanded

  // ── v17.22 — SUPERVISOR SITES COLLAPSED STATE ──
  const [sitesExpanded,setSitesExpanded]=useState(false);

  const wrapRef=useRef(null);
  const previewWrapRef=useRef(null); // v17.82 — for Daywork Sheet Preview modal scaling
  const [previewScale,setPreviewScale]=useState(1); // v17.82
  const previewInnerRef=useRef(null); // v17.88 — measure true sheet height
  const [previewH,setPreviewH]=useState(1600); // v17.88
  const summaryInnerRef=useRef(null); // v17.91 — measure true summary sheet height
  const [summarySheetH,setSummarySheetH]=useState(1400); // v17.91
  const [pwSheetHs,setPwSheetHs]=useState({}); // v18.20 — measured NATURAL height per plot sheet (unscaled)
  const tmRef=useRef(null);
  const supSigRef=useRef(null);
  const supSigLastPos=useRef(null);
  const SHEET_W=680;
  const SHEET_H=1400; // Full sheet height including all sections + signature row

  const today=new Date().toLocaleDateString("en-GB");
  const todayISO=new Date().toISOString().split("T")[0];
  const hr=new Date().getHours();
  const greet=hr<12?"Good morning":hr<17?"Good afternoon":"Good evening";

  const card={background:W,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 1px 3px rgba(0,0,0,.08)"};
  const inp={width:"100%",padding:"11px 13px",border:"1.5px solid "+BD,borderRadius:10,fontSize:15,boxSizing:"border-box",background:FA,fontFamily:"inherit",outline:"none",color:DK};

  // ── v18.45 — My Cards: open, save, status + shared modal (used by both the
  // worker and supervisor settings menus; state lives on this one component) ──
  const openMyCards=async()=>{
    setShowSettings(false);setShowMyCards(true);setCardDraft({});
    const rows=await fetchWorkerCards(workerId);
    if(rows)setMyCards(rows);
  };
  const saveCard=async(t)=>{
    const d=cardDraft[t]||{};
    const existing=myCards.find(c=>c.card_type===t)||{};
    const imageData=d.image!==undefined?d.image:(existing.image_data||null);
    const expiry=d.expiry!==undefined?d.expiry:(existing.expiry_date||"");
    const num=d.number!==undefined?d.number:(existing.card_number||"");
    if(!imageData&&!expiry){alert("Add a photo or an expiry date first");return;}
    setCardDraft(p=>({...p,[t]:{...d,busy:true}}));
    // back photo only sent when changed this session — an unchanged back
    // stays as it is in the DB (upsert merges only supplied columns)
    const ok=await pushWorkerCard({workerIdArg:workerId,workerName:name,cardType:t,cardNumber:num,expiryDate:expiry||null,imageData,imageBack:d.imageBack});
    const rows=ok?await fetchWorkerCards(workerId):null;
    if(rows)setMyCards(rows);
    setCardDraft(p=>({...p,[t]:ok?{}:{...d,busy:false}}));
    if(!ok)alert("Save failed — check signal and try again");
  };
  const cardStatus=(row)=>{
    if(!row||!row.expiry_date)return{txt:row&&row.image_data?"No expiry set":"Not uploaded",col:MT};
    const days=Math.floor((new Date(row.expiry_date+"T00:00:00")-new Date())/86400000);
    if(days<0)return{txt:"EXPIRED "+new Date(row.expiry_date+"T00:00:00").toLocaleDateString("en-GB"),col:RD};
    if(days<=30)return{txt:"Expires in "+days+" day"+(days===1?"":"s"),col:"#D97706"};
    return{txt:"Valid until "+new Date(row.expiry_date+"T00:00:00").toLocaleDateString("en-GB"),col:GR};
  };
  // ── v18.48 — NOTIFICATIONS ──
  // Derived, not stored: request outcomes + card expiry warnings, newest first.
  // Ids are stable strings so the per-device read state survives refetches.
  const REQ_TYPE_LABEL={site_missing:"Missing site",time_edit:"Time edit",manual_add:"Manual shift",other:"Request"};
  const notifItems=(()=>{
    const out=[];
    myRequests.forEach(r=>{
      const label=REQ_TYPE_LABEL[r.request_type]||"Request";
      const when=r.created_at?new Date(r.created_at):null;
      if(r.status==="approved")      out.push({id:"req-"+r.id,ic:"✅",tone:GR,   title:label+" approved",body:r.note||"",when});
      else if(r.status==="rejected") out.push({id:"req-"+r.id,ic:"❌",tone:RD,   title:label+" rejected",body:r.note||"",when});
      else                           out.push({id:"req-"+r.id,ic:"⏳",tone:"#D97706",title:label+" — waiting on the office",body:r.note||"",when,quiet:true});
    });
    CARD_TYPES.forEach(({t,label})=>{
      const row=myCards.find(c=>c.card_type===t);
      if(!row||!row.expiry_date)return;
      const days=Math.floor((new Date(row.expiry_date+"T00:00:00")-new Date())/86400000);
      const dateStr=new Date(row.expiry_date+"T00:00:00").toLocaleDateString("en-GB");
      if(days<0)        out.push({id:"card-"+t+"-exp",  ic:"🪪",tone:RD,        title:label+" has expired",body:"Expired "+dateStr+". Upload the renewed card in My Cards.",when:null});
      // Id deliberately excludes the day count: a countdown in the id would make
      // the same warning go unread again every single day for a month. Two nags
      // per card is enough — one when it enters the 30-day window, one when it
      // actually expires (different id, so it resurfaces then).
      else if(days<=30) out.push({id:"card-"+t+"-soon",ic:"🪪",tone:"#D97706",title:label+" expires in "+days+" day"+(days===1?"":"s"),body:"Valid until "+dateStr+". Get the renewal booked.",when:null});
    });
    // Newest first; undated items (card warnings) float to the top as they're live status
    return out.sort((a,b)=>(b.when?b.when.getTime():Infinity)-(a.when?a.when.getTime():Infinity));
  })();
  // "Quiet" items (still pending) don't count as unread — nothing has happened yet.
  const notifUnread=notifItems.filter(n=>!n.quiet&&!notifReadIds.includes(n.id)).length;
  const markNotifsRead=()=>{
    const ids=notifItems.map(n=>n.id);
    setNotifReadIds(ids);
    try{localStorage.setItem("bac_notifs_read_"+workerId,JSON.stringify(ids));}catch(e){}
  };
  const notifWhen=(d)=>{
    if(!d)return"";
    const mins=Math.floor((Date.now()-d.getTime())/60000);
    if(mins<1)return"just now";
    if(mins<60)return mins+"m ago";
    if(mins<1440)return Math.floor(mins/60)+"h ago";
    if(mins<10080)return Math.floor(mins/1440)+"d ago";
    return d.toLocaleDateString("en-GB");
  };

  const myCardsModal=showMyCards&&(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:600,display:"flex",alignItems:"flex-end"}}>
      <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",padding:"20px 20px 40px",maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
          <div style={{fontWeight:900,fontSize:20,color:DK}}>🪪 My Cards</div>
          <button onClick={()=>setShowMyCards(false)} style={{background:F2,border:"none",borderRadius:8,padding:"6px 12px",fontSize:13,cursor:"pointer",fontFamily:"inherit",color:DK}}>Close</button>
        </div>
        <div style={{fontSize:13,color:MT,marginBottom:14,lineHeight:1.5}}>A photo of the front of each card plus its expiry date. The office can see these, so there's no digging cards out on site.</div>
        {CARD_TYPES.map(({t,label})=>{
          const row=myCards.find(c=>c.card_type===t);
          const d=cardDraft[t]||{};
          const img=d.image!==undefined?d.image:(row&&row.image_data);
          const imgBack=d.imageBack!==undefined?d.imageBack:(row&&row.image_back);
          const st=cardStatus(row);
          const dirty=d.image!==undefined||d.imageBack!==undefined||d.expiry!==undefined||d.number!==undefined;
          const pickPhoto=(side)=>async e=>{
            const f=e.target.files&&e.target.files[0];e.target.value="";
            if(!f)return;
            const data=await resizeCardPhoto(f);
            if(!data){alert("Couldn't read that photo — try another");return;}
            setCardDraft(p=>({...p,[t]:{...(p[t]||{}),[side]:data}}));
          };
          return(
            <div key={t} style={card}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontWeight:800,fontSize:15,color:DK}}>{label}</span>
                <span style={{fontSize:12,fontWeight:800,color:st.col}}>{st.txt}</span>
              </div>
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,color:MT,fontWeight:700,marginBottom:3}}>FRONT</div>
                  {img&&<img src={img} alt={label+" front"} style={{width:"100%",maxHeight:140,objectFit:"contain",borderRadius:10,background:F2,marginBottom:6}}/>}
                  <label style={{display:"block"}}>
                    <span style={{...inp,display:"flex",alignItems:"center",justifyContent:"center",gap:6,cursor:"pointer",fontWeight:700,fontSize:13}}>📷 {img?"Replace":"Add front"}</span>
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={pickPhoto("image")}/>
                  </label>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,color:MT,fontWeight:700,marginBottom:3}}>BACK</div>
                  {imgBack&&<img src={imgBack} alt={label+" back"} style={{width:"100%",maxHeight:140,objectFit:"contain",borderRadius:10,background:F2,marginBottom:6}}/>}
                  <label style={{display:"block"}}>
                    <span style={{...inp,display:"flex",alignItems:"center",justifyContent:"center",gap:6,cursor:"pointer",fontWeight:700,fontSize:13}}>📷 {imgBack?"Replace":"Add back"}</span>
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={pickPhoto("imageBack")}/>
                  </label>
                </div>
              </div>
              <div style={{display:"flex",gap:8,marginBottom:4}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,color:MT,fontWeight:700,marginBottom:3}}>EXPIRY DATE</div>
                  <input type="date" value={d.expiry!==undefined?d.expiry:(row&&row.expiry_date)||""} onChange={e=>setCardDraft(p=>({...p,[t]:{...(p[t]||{}),expiry:e.target.value}}))} style={inp}/>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,color:MT,fontWeight:700,marginBottom:3}}>CARD NUMBER <span style={{fontWeight:400}}>(optional)</span></div>
                  <input placeholder="—" value={d.number!==undefined?d.number:(row&&row.card_number)||""} onChange={e=>setCardDraft(p=>({...p,[t]:{...(p[t]||{}),number:e.target.value}}))} style={inp}/>
                </div>
              </div>
              {dirty&&(
                <button disabled={!!d.busy} onClick={()=>saveCard(t)} style={{width:"100%",marginTop:6,padding:12,background:d.busy?MT:GR,color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{d.busy?"Saving…":"Save "+label}</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
  const lbl={fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:5,display:"block"};
  const C={border:"1px solid #444",padding:"3px 6px",fontFamily:"Arial,sans-serif",fontSize:11,color:"#111",verticalAlign:"middle"};
  const CB={...C,background:SBL,fontWeight:700};

  useEffect(()=>{
    const goOnline=()=>{
      setIsOnline(true);
      // Sync any queued offline submissions
      try{
        const q=JSON.parse(localStorage.getItem("bac_offline")||"[]");
        if(q.length>0){
          const ex=JSON.parse(localStorage.getItem("bac_shifts")||"[]");
          q.forEach(r=>{
            ex.push({...r,synced:true});
            // Push each queued shift to Supabase
            pushShift(r, r._entries||[]);
          });
          localStorage.setItem("bac_shifts",JSON.stringify(ex));
          localStorage.setItem("bac_offline","[]");
          setOfflineQueue([]);
        }
      }catch(e){}
      // v17.94 — drain supervisor/worker op queue (talks, notices, messages, snags)
      flushSupQueue().then(n=>{ if(n>0){ try{ if(typeof loadSnags==="function") loadSnags(); }catch(e){} } });
    };
    const goOffline=()=>setIsOnline(false);
    const onVisible=()=>{ if(document.visibilityState==="visible" && navigator.onLine){ flushSupQueue().then(n=>{ if(n>0){ try{ if(typeof loadSnags==="function") loadSnags(); }catch(e){} } }); } };
    window.addEventListener("online",goOnline);
    window.addEventListener("offline",goOffline);
    document.addEventListener("visibilitychange",onVisible);
    // Attempt one flush on mount in case ops were left from a previous session
    if(navigator.onLine) flushSupQueue();
    return()=>{window.removeEventListener("online",goOnline);window.removeEventListener("offline",goOffline);document.removeEventListener("visibilitychange",onVisible);};
  },[]);

  useEffect(()=>{
    try{if(name)localStorage.setItem("bac_name",name);}catch(e){}
  },[name]);

  useEffect(()=>{
    try{if(workerId)localStorage.setItem("bac_worker_id",workerId);}catch(e){}
  },[workerId]);

  useEffect(()=>{
    try{if(workerRole)localStorage.setItem("bac_worker_role",workerRole);}catch(e){}
  },[workerRole]);

  // ── v17.47 — Auto-persist active shift state on every change ──
  // Survives the worker swiping the app away mid-shift. Cleared when there's
  // no active shift (no tIn, or submitted, or no workerId).
  useEffect(() => {
    try {
      if (!workerId || !tIn || submitted) {
        localStorage.removeItem("bac_active_shift");
        return;
      }
      localStorage.setItem("bac_active_shift", JSON.stringify({
        workerId, site, ctr, tIn, tOut, tInTs, entries, mats, gpsIn, gpsOut, sec, sc, sheetDate, sheetSite, pwLines, pwMode, pwSubmittedPlots, pwInvoiceNos, savedAt: Date.now()
      }));
    } catch (e) {}
  }, [workerId, site, ctr, tIn, tOut, tInTs, entries, mats, gpsIn, gpsOut, sec, sc, sheetDate, sheetSite, pwLines, pwMode, pwSubmittedPlots, pwInvoiceNos, submitted]);

  useEffect(()=>{
    const iv=setInterval(()=>setNowStr(new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})),1000);
    return()=>clearInterval(iv);
  },[]);

  useEffect(()=>{
    if((sc==="sheet"&&!fromSummary)||(sc==="pricework"&&!tOut)||(sc==="home"&&tIn&&!tOut)) tmRef.current=setInterval(()=>setElapsed(s=>s+1),1000);
    else clearInterval(tmRef.current);
    return()=>clearInterval(tmRef.current);
  },[sc,fromSummary,tIn,tOut]);

  useEffect(()=>{
    const upd=()=>{if(wrapRef.current){const w=wrapRef.current.clientWidth-32;setScale(Math.min(1,w/SHEET_W));}};
    upd();window.addEventListener("resize",upd);return()=>window.removeEventListener("resize",upd);
  },[]);

  // v17.55/56 — recompute scale when supervisor opens a sheet. Subtracts 64 (wrapper padding 32 + card padding 32)
  useEffect(()=>{
    if(workerRole!=="supervisor"||supSc!=="sheet"||!supSelSheet)return;
    const upd=()=>{if(wrapRef.current){const w=wrapRef.current.clientWidth-64;const s=Math.min(1,w/SHEET_W);setScale(s);if(supSheetInnerRef.current)setSupSheetH(supSheetInnerRef.current.offsetHeight*s);}};
    setTimeout(upd,50);
    setTimeout(upd,250);
    window.addEventListener("resize",upd);
    return()=>window.removeEventListener("resize",upd);
  },[workerRole,supSc,supSelSheet,supSheetEntries]);

  // v17.82 — recompute scale when worker opens the Daywork Sheet Preview modal.
  useEffect(()=>{
    if(!showPreview) return;
    const upd=()=>{
      if(previewWrapRef.current){const w=previewWrapRef.current.clientWidth-24;const s=Math.min(1,w/SHEET_W);setPreviewScale(s);if(previewInnerRef.current)setPreviewH(previewInnerRef.current.offsetHeight*s);}
    };
    setTimeout(upd,50);
    setTimeout(upd,200);
    window.addEventListener("resize",upd);
    return()=>window.removeEventListener("resize",upd);
  },[showPreview]);

  // v17.91 — recompute summary screen sheet height to eliminate gap below sheet
  useEffect(()=>{
    if(sc!=="summary") return;
    const upd=()=>{if(summaryInnerRef.current) setSummarySheetH(summaryInnerRef.current.offsetHeight*scale);};
    setTimeout(upd,50);
    setTimeout(upd,200);
  },[sc,scale]);

  // v18.28 — if clocked in on an unlisted site (e.g. "Site Not Listed"), open the type-a-site box automatically
  useEffect(()=>{
    if(sc==="summary" && !sheetSite && !SITES.includes(site)) setSheetSiteCustom(true);
  },[sc]);

  // v18.20 — recompute scale for the Price Work review sheet so the full A4 fits the screen width
  useEffect(()=>{
    if(sc!=="pwsummary") return;
    const upd=()=>{if(wrapRef.current){const w=wrapRef.current.clientWidth-64;setScale(Math.min(1,w/SHEET_W));}};
    setTimeout(upd,50);
    setTimeout(upd,250);
    window.addEventListener("resize",upd);
    return()=>window.removeEventListener("resize",upd);
  },[sc,pwLines,pwEditId]);

  // v18.26 — pull the office's booked meterage for the plot the worker is on.
  // Debounced 400ms after he stops typing the plot number. Read-only fetch from
  // price_work_office; if the office hasn't tagged the plot, we just get [].
  useEffect(()=>{
    if(sc!=="pricework"){return;}
    const plotStr=(pwPlot||"").trim();
    const siteStr=(site||"").trim();
    if(!plotStr||!siteStr){setPwOfficeBooked([]);setPwOfficeFor("");return;}
    const key=siteStr+"||"+plotStr;
    if(key===pwOfficeFor){return;} // already have these rows
    let cancelled=false;
    setPwOfficeLoading(true);
    const t=setTimeout(async()=>{
      try{
        const {data,error}=await sb.from("price_work_office")
          .select("operation,unit,formula_type,office_qty,rate,flat_price")
          .eq("company_id",COMPANY_ID)
          .eq("site_name",siteStr)
          .eq("plot_number",plotStr);
        if(cancelled)return;
        if(error){setPwOfficeBooked([]);setPwOfficeFor(key);setPwOfficeLoading(false);return;}
        setPwOfficeBooked(Array.isArray(data)?data:[]);
        setPwOfficeFor(key);
        setPwOfficeLoading(false);
      }catch(e){ if(!cancelled){setPwOfficeBooked([]);setPwOfficeFor(key);setPwOfficeLoading(false);} }
    },400);
    return()=>{cancelled=true;clearTimeout(t);};
  },[sc,pwPlot,site,pwOfficeFor]);

  useEffect(()=>{
    if(sc==="home") setPastShifts(getShifts().slice(0,5));
  },[sc]);

  // v18.30 — "My Week": load this worker's submitted price work claims + lines for
  // the selected Mon–Sun window. Read-only; nothing is written back.
  useEffect(()=>{
    if(sc!=="pwweek"||!workerId)return;
    let dead=false;
    setPwWeekPdf(null);
    (async()=>{
      setPwWeekLoading(true);setPwWeekErr("");
      try{
        const {from,to}=pwWeekWindow(pwWeekOffset);
        const {data:claims,error:cErr}=await sb.from("price_work_claims")
          .select("id,site_name,plot_number,claim_date,sheet_number,sheet_total,submitted_at,status")
          .eq("company_id",COMPANY_ID).eq("worker_id",workerId)
          .gte("claim_date",from).lte("claim_date",to)
          .order("claim_date",{ascending:true});
        if(cErr)throw new Error(cErr.message);
        let lines=[];
        if(claims&&claims.length){
          const ids=claims.map(c=>c.id);
          const {data:ls,error:lErr}=await sb.from("price_work_claim_lines")
            .select("claim_id,plot_number,operation,unit,formula_type,qty,rate,flat_price,value")
            .in("claim_id",ids);
          if(lErr)throw new Error(lErr.message);
          lines=ls||[];
        }
        if(!dead){setPwWeekClaims(claims||[]);setPwWeekLines(lines);}
      }catch(e){if(!dead)setPwWeekErr(e.message||"Could not load — check connection");}
      if(!dead)setPwWeekLoading(false);
    })();
    return()=>{dead=true;};
  },[sc,pwWeekOffset,workerId]);

  // ── AUTO CLOCK-OUT: fires when worker logs in or app opens ──
  // Checks Supabase for an open clock-in (no matching clock-out) from before today's AUTO_CLOCKOUT_TIME.
  // If found: creates a clock-out at 23:55 of that day, flags it auto_clocked_out=true,
  // and shows a banner so the worker can submit a correction.
  useEffect(()=>{
    if(!workerId)return;
    // Only run once per session (banner already set means we already checked)
    if(autoClockOutBanner!==null)return;
    (async()=>{
      try{
        // Fetch this worker's clock-in events from the last 7 days (covers weekend gaps)
        const since=new Date();
        since.setDate(since.getDate()-7);
        const {data:events,error}=await sb.from("clock_events")
          .select("id,event_type,clocked_at,site_name")
          .eq("company_id",COMPANY_ID)
          .eq("worker_id",workerId)
          .gte("clocked_at",since.toISOString())
          .order("clocked_at",{ascending:true});
        if(error||!events||events.length===0)return;

        // ── v17.78 — Robust detection: walk events chronologically and pair in→out.
        // The LAST clock-in with no following clock-out is the open shift. This correctly
        // handles multiple clock-ins per day and prior (auto or manual) clock-outs — the
        // old per-day "ins>outs + already-auto-clocked-this-day" logic could block a real
        // open shift if any earlier out existed on the same day (caused overnight runs).
        let openIn=null;
        events.forEach(ev=>{
          if(ev.event_type==="in")openIn=ev;       // new clock-in becomes the open shift
          else if(ev.event_type==="out")openIn=null; // any clock-out closes it
        });
        if(!openIn)return; // everything is paired — nothing to auto-close

        const foundIn=openIn;
        // v17.87 — work in LOCAL time throughout. Old code sliced the UTC date and
        // string-concatenated "23:55" (local), which on BST built the target for the
        // wrong day — auto clock-out fired sometimes, skipped other times.
        const inDate=new Date(foundIn.clocked_at);            // clock-in moment
        const [acH,acM]=AUTO_CLOCKOUT_TIME.split(":").map(Number);
        // 23:55 LOCAL on the clock-in's LOCAL calendar day
        const autoOutDate=new Date(inDate.getFullYear(),inDate.getMonth(),inDate.getDate(),acH,acM,0,0);
        const now=new Date();
        // Live shift guard: only skip if the cutoff for the clock-in's day hasn't passed yet.
        if(now<autoOutDate)return;
        // foundDay = LOCAL YYYY-MM-DD of the clock-in (for banner correction prefill)
        const foundDay=`${inDate.getFullYear()}-${String(inDate.getMonth()+1).padStart(2,"0")}-${String(inDate.getDate()).padStart(2,"0")}`;
        const clockInTime=inDate.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
        const displayDate=inDate.toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"});
        // No "existingAuto" query needed: the chronological pairing above already proves
        // there is no clock-out (auto or manual) after this clock-in.

        // Push the auto clock-out to Supabase
        const {error:autoErr}=await sb.from("clock_events").insert({
          company_id:COMPANY_ID,
          worker_id:workerId,
          worker_name:name,
          site_name:foundIn.site_name,
          event_type:"out",
          auto_clocked_out:true,
          clocked_at:autoOutDate.toISOString()
        });
        if(autoErr){console.error("auto clock-out insert failed:",autoErr.message);return;}

        // v18.07 — keep entries intact so worker can submit next morning
        // v18.10 — also persist sc to localStorage so _bootSc doesn't override back to "sheet"
        setTOut("23:55");setSc("summary");
        try{const _aLS=JSON.parse(localStorage.getItem("_A")||"{}");_aLS.sc="summary";localStorage.setItem("_A",JSON.stringify(_aLS));}catch(_){}

        // Show the banner
        setAutoClockOutBanner({
          date:displayDate,
          tIn:clockInTime,
          site:foundIn.site_name,
          rawDate:foundDay
        });

      }catch(e){console.error("auto clock-out check failed:",e);}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[workerId]);

  // ── v17.77 — Fetch today's stints for My Day Log (read-only, derived from clock_events) ──
  // Pairs in/out events chronologically. Last "in" with no following "out" is the active stint.
  // Refreshes on home screen and whenever tIn/tOut change (i.e. after a switch or clock-out).
  useEffect(()=>{
    if(!workerId)return;
    if(workerRole==="supervisor")return;
    if(sc!=="home"&&sc!=="daylog")return;
    let cancelled=false;
    (async()=>{
      try{
        const todayStart=new Date();todayStart.setHours(0,0,0,0);
        const {data:evts,error}=await sb.from("clock_events")
          .select("event_type,clocked_at,site_name")
          .eq("company_id",COMPANY_ID)
          .eq("worker_id",workerId)
          .gte("clocked_at",todayStart.toISOString())
          .order("clocked_at",{ascending:true});
        if(error||!evts){if(!cancelled)setDayStints([]);return;}
        // Walk events, pairing each "in" with the next "out"
        const stints=[];let open=null;
        evts.forEach(ev=>{
          if(ev.event_type==="in"){
            if(open)open.outTs=new Date(ev.clocked_at).getTime(); // safety: close any dangling in
            open={site:ev.site_name,inTs:new Date(ev.clocked_at).getTime(),outTs:null};
            stints.push(open);
          }else if(ev.event_type==="out"&&open){
            open.outTs=new Date(ev.clocked_at).getTime();open=null;
          }
        });
        if(!cancelled)setDayStints(stints);
      }catch(e){if(!cancelled)setDayStints([]);}
    })();
    return ()=>{cancelled=true;};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[workerId,workerRole,sc,tIn,tOut]);

  // ── v17.70 — Real-time 23:55 auto clock-out: fires while app is open ──
  // Complements the on-load check: if the worker falls asleep with the app open,
  // this setTimeout fires at exactly 23:55 and clocks them out automatically.
  useEffect(()=>{
    if(!tIn||!workerId)return; // only when clocked in
    const now=new Date();
    const target=new Date();
    target.setHours(23,55,0,0);
    const msUntil=target-now;
    if(msUntil<=0)return; // already past 23:55 today — on-load check handles it
    const t=setTimeout(async()=>{
      try{
        const _ao=new Date();_ao.setHours(23,55,0,0); // pin to exactly 23:55 local
        const autoOutISO=_ao.toISOString();
        const todayKey=`${_ao.getFullYear()}-${String(_ao.getMonth()+1).padStart(2,"0")}-${String(_ao.getDate()).padStart(2,"0")}`;
        await sb.from("clock_events").insert({
          company_id:COMPANY_ID,
          worker_id:workerId,
          worker_name:name,
          site_name:site,
          event_type:"out",
          auto_clocked_out:true,
          clocked_at:autoOutISO
        });
        const clockInDisplay=tIn; // already formatted HH:MM
        const displayDate=new Date().toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"});
        setAutoClockOutBanner({date:displayDate,tIn:clockInDisplay,site:site,rawDate:todayKey});
        // v18.07 — keep entries intact so worker can submit next morning
        // v18.10 — persist sc so _bootSc doesn't override on refresh
        setTOut("23:55");setSc("summary");
        try{const _aLS=JSON.parse(localStorage.getItem("_A")||"{}");_aLS.sc="summary";localStorage.setItem("_A",JSON.stringify(_aLS));}catch(_){}
      }catch(e){console.error("live auto clock-out failed:",e);}
    },msUntil);
    return ()=>clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tIn,workerId]);

  // ── v17.18 — Fetch supervisor list on login (for any worker, used in submit picker) ──
  useEffect(()=>{
    if(!workerId)return;
    if(workerRole==="admin")return; // admin doesn't submit sheets
    (async()=>{
      const list=await fetchSupervisors();
      setSupervisors(buildSupervisorOptions(list));
    })();
  },[workerId,workerRole]);

  // ── v17.28 — Fetch pending toolbox talks for this worker (refresh on home + when site changes) ──
  // Soft push: shows amber banner on home screen but doesn't block clock-in.
  useEffect(()=>{
    if(!workerId||!site)return;
    if(workerRole==="admin")return; // admin doesn't sign toolbox talks
    let cancelled=false;
    (async()=>{
      const pending=await fetchPendingToolboxTalks(workerId,site);
      if(cancelled)return;
      // Apply replica-lag guard
      const filtered=pending.filter(t=>!tbRecentlySignedRef.current.has(t.id));
      setToolboxPending(filtered);
    })();
    return ()=>{cancelled=true;};
  },[workerId,workerRole,site,sc]);

  // ── v17.18 — Fetch signed sheets for this worker (refresh on home screen) ──
  // ── v17.57 — added 20s polling while on home so newly-signed sheets land live ──
  // ── v17.58 — dropped admin from the gate (admin/owner submits their own daywork sheets too) ──
  // ── v17.64 — also polls while on the dedicated "signed" screen ──
  useEffect(()=>{
    if(!workerId)return;
    if(workerRole==="supervisor")return; // supervisors have their own UI; everyone else (worker + admin) sees My Signed Sheets
    if(sc!=="home" && sc!=="signed")return;
    let cancelled=false;
    const load=async(showSpinner)=>{
      if(showSpinner)setSignedLoading(true);
      const { sheets, diag } = await fetchSignedShifts(workerId,name);
      if(cancelled)return;
      setSignedSheets(sheets);
      setSignedDiag(diag);
      if(showSpinner)setSignedLoading(false);
    };
    load(true); // initial load with spinner
    const id=setInterval(()=>load(false),20000); // poll every 20s, silent
    return ()=>{cancelled=true;clearInterval(id);};
  },[workerId,workerRole,sc]);

  // ── v17.57 — Load opened/downloaded/dismissed lists from localStorage when workerId resolves ──
  useEffect(()=>{
    if(!workerId)return;
    try{
      const o=JSON.parse(localStorage.getItem("bac_signed_opened_"+workerId)||"[]");
      const d=JSON.parse(localStorage.getItem("bac_signed_downloaded_"+workerId)||"[]");
      const x=JSON.parse(localStorage.getItem("bac_signed_dismissed_"+workerId)||"[]");
      setSignedOpenedIds(Array.isArray(o)?o:[]);
      setSignedDownloadedIds(Array.isArray(d)?d:[]);
      setSignedDismissedIds(Array.isArray(x)?x:[]);
    }catch(e){console.error("load signed-state failed:",e);}
  },[workerId]);

  // ── v17.21 — Fetch notices on login (worker + supervisor) ──
  useEffect(()=>{
    if(!workerId)return;
    (async()=>{
      const data=await fetchNotices();
      setNotices(data);
    })();
  },[workerId]);

  // ── v18.48 — Notifications source data ──
  // Requests + cards are re-fetched whenever the worker lands on Home or opens
  // the notifications screen, so an approval that happened while the app was
  // open still shows up without a full restart.
  useEffect(()=>{
    if(!workerId)return;
    if(sc!=="home"&&sc!=="notifs"&&sc!=="profile")return;
    (async()=>{
      const reqs=await fetchMyRequests(workerId);
      setMyRequests(reqs);
      const cards=await fetchWorkerCards(workerId);
      if(cards)setMyCards(cards);
    })();
  },[workerId,sc]);

  // Load per-device read state for notifications
  useEffect(()=>{
    if(!workerId){setNotifReadIds([]);return;}
    try{
      const r=JSON.parse(localStorage.getItem("bac_notifs_read_"+workerId)||"[]");
      setNotifReadIds(Array.isArray(r)?r:[]);
    }catch(e){setNotifReadIds([]);}
  },[workerId]);

  // ── SUPERVISOR: load assigned sites on login ──
  useEffect(()=>{
    if(!workerId||workerRole!=="supervisor")return;
    (async()=>{
      setSupLoading(true);
      const {data}=await sb.from("supervisor_sites").select("site_name").eq("supervisor_id",workerId);
      const sites=data?data.map(r=>r.site_name):[];
      setSupSites(sites);
      // Load all pending sheets across all assigned sites — strict filter by assigned_supervisor_id (v17.18)
      if(sites.length>0){
        const {data:sheets}=await sb.from("shifts")
          .select("id,worker_name,shift_date,total_mins,status,submitted_at,site_name,time_in,time_out,sheet_number")
          .eq("company_id",COMPANY_ID)
          .in("site_name",sites)
          .eq("status","submitted")
          .eq("assigned_supervisor_id",workerId)
          .order("submitted_at",{ascending:false});
        setSupAllSheets(sheets||[]);
        // Load today's clock events across all sites for on-site counts
        const todayStart=new Date();todayStart.setHours(0,0,0,0);
        const {data:evts}=await sb.from("clock_events")
          .select("worker_name,event_type,site_name,clocked_at")
          .eq("company_id",COMPANY_ID)
          .in("site_name",sites)
          .gte("clocked_at",todayStart.toISOString())
          .order("clocked_at",{ascending:true});
        // v18.05 — a worker can only be on ONE site at a time. Walk all events in
        // chronological order and track each worker's SINGLE latest event globally.
        // If their most recent event anywhere is "in", they're on that site; an "out"
        // (or a later "in" elsewhere via Switch Job) removes them from the old site.
        // This self-heals a missed Switch Job clock-out instead of double-counting.
        const latestByWorker={}; // worker_name -> {site, status}
        (evts||[]).forEach(ev=>{
          latestByWorker[ev.worker_name]={site:ev.site_name,status:ev.event_type};
        });
        const stats={};
        sites.forEach(s=>{stats[s]={onSite:0,workers:[]};});
        Object.entries(latestByWorker).forEach(([name,info])=>{
          if(info.status==="in" && stats[info.site]){
            stats[info.site].workers.push(name);
            stats[info.site].onSite=stats[info.site].workers.length;
          }
        });
        setSupSiteStats(stats);
      }
      setSupLoading(false);
      // v17.66 — load toolbox messages for the badge
      const msgs = await fetchSupervisorMessages(name, workerRole==="admin");
      setSupMsgs(msgs);
      // v17.81 — Tracker badge: count signatures since this supervisor last opened Tracker
      const lo = localStorage.getItem("tracker_last_open_"+(workerId||name)) || "";
      const nSig = await fetchNewSignatureCount(name, workerRole==="admin", lo);
      setSoNewSigs(nSig);
    })();
  },[workerId,workerRole]);

  // v17.66 — open the supervisor messages inbox
  const loadSupMessages = async () => {
    setSupSc("messages");
    setSupMsgLoading(true);
    const msgs = await fetchSupervisorMessages(name, workerRole==="admin");
    if (msgs && msgs.length) { setSupMsgs(msgs); cacheSet("supmsgs_"+(workerId||name), msgs); }
    else if (!navigator.onLine) { setSupMsgs(cacheGet("supmsgs_"+(workerId||name)) || []); }
    else { setSupMsgs(msgs || []); }
    setSupMsgLoading(false);
  };

  const handleMarkMessageRead = async (id) => {
    const ok = await markMessageRead(id);
    if (ok) setSupMsgs(prev => prev.map(m => m.id===id ? {...m, status:"read"} : m));
  };

  // ── v17.84 — SNAG HANDLERS ──
  const loadSnags = async () => {
    setSupSc("snags");
    setSnagLoading(true);
    try {
      let q = sb.from("snags").select("*").eq("company_id", COMPANY_ID).order("raised_at",{ascending:false});
      // Supervisor sees only their assigned sites; admin sees all
      if (workerRole !== "admin" && supSites.length > 0) {
        q = q.in("site_name", supSites);
      }
      const { data, error } = await q;
      if (error) throw error;
      setSnagList(data || []);
      cacheSet("snags_"+(workerId||name), data || []);
    } catch (e) {
      console.error("snags load", e);
      const cached = cacheGet("snags_"+(workerId||name));
      setSnagList(cached || []);
    }
    setSnagLoading(false);
  };

  const openSnagForm = () => {
    setSnagForm({site:supSites[0]||"",plot:"",area:"",areaOther:"",desc:"",priority:"",due:"",photoBlob:null,annotatedBlob:null,photoPreview:""});
    setSnagMsg("");
    setSupSc("snag-new");
  };

  const handleSnagPhoto = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setSnagForm(p=>({...p, photoBlob:file, annotatedBlob:null, photoPreview:URL.createObjectURL(file)}));
  };

  // v17.85 — annotator
  const openAnnotator = () => {
    if (!snagForm.photoBlob) return;
    annoStrokesRef.current = [];
    setAnnoOpen(true);
    setAnnoTool("arrow"); setAnnoColor("#FF0000"); setAnnoSize(6);
    setTimeout(()=>{
      const img = new Image();
      img.onload = () => {
        annoImgRef.current = img;
        const cv = annoCanvasRef.current;
        if (!cv) return;
        cv.width = img.width; cv.height = img.height;
        redrawAnno();
      };
      img.src = URL.createObjectURL(snagForm.annotatedBlob || snagForm.photoBlob);
    }, 50);
  };

  const getAnnoPos = (e) => {
    const cv = annoCanvasRef.current;
    const r = cv.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (cv.width / r.width),
      y: (e.clientY - r.top) * (cv.height / r.height)
    };
  };

  const drawAnnoStroke = (ctx, s) => {
    ctx.strokeStyle = s.color; ctx.fillStyle = s.color;
    ctx.lineWidth = s.size; ctx.lineCap = "round"; ctx.lineJoin = "round";
    if (s.tool === "free") {
      ctx.beginPath();
      s.points.forEach((p,i)=> i===0 ? ctx.moveTo(p.x,p.y) : ctx.lineTo(p.x,p.y));
      ctx.stroke();
    } else if (s.tool === "line") {
      ctx.beginPath(); ctx.moveTo(s.x1,s.y1); ctx.lineTo(s.x2,s.y2); ctx.stroke();
    } else if (s.tool === "arrow") {
      ctx.beginPath(); ctx.moveTo(s.x1,s.y1); ctx.lineTo(s.x2,s.y2); ctx.stroke();
      const ang = Math.atan2(s.y2-s.y1, s.x2-s.x1);
      const head = s.size * 4;
      ctx.beginPath();
      ctx.moveTo(s.x2,s.y2);
      ctx.lineTo(s.x2 - head*Math.cos(ang-Math.PI/6), s.y2 - head*Math.sin(ang-Math.PI/6));
      ctx.lineTo(s.x2 - head*Math.cos(ang+Math.PI/6), s.y2 - head*Math.sin(ang+Math.PI/6));
      ctx.closePath(); ctx.fill();
    } else if (s.tool === "rect") {
      ctx.strokeRect(s.x1, s.y1, s.x2-s.x1, s.y2-s.y1);
    } else if (s.tool === "circle") {
      const r = Math.hypot(s.x2-s.x1, s.y2-s.y1);
      ctx.beginPath(); ctx.arc(s.x1, s.y1, r, 0, Math.PI*2); ctx.stroke();
    } else if (s.tool === "text") {
      ctx.font = (60 + s.size*7) + "px sans-serif";
      ctx.fillText(s.text, s.x1, s.y1);
    }
  };

  const redrawAnno = (preview) => {
    const cv = annoCanvasRef.current;
    const img = annoImgRef.current;
    if (!cv || !img) return;
    const ctx = cv.getContext("2d");
    ctx.drawImage(img, 0, 0);
    annoStrokesRef.current.forEach(s => drawAnnoStroke(ctx, s));
    if (preview) drawAnnoStroke(ctx, preview);
  };

  const onAnnoStart = (e) => {
    e.preventDefault();
    const p = getAnnoPos(e);
    if (annoTool === "text") {
      setAnnoTextPos(p); setAnnoTextValue("");
      return;
    }
    annoDrawingRef.current = true;
    annoCurrentRef.current = { tool: annoTool, color: annoColor, size: annoSize, x1:p.x, y1:p.y, x2:p.x, y2:p.y, points:[{x:p.x,y:p.y}] };
  };

  const onAnnoMove = (e) => {
    if (!annoDrawingRef.current) return;
    e.preventDefault();
    const p = getAnnoPos(e);
    const c = annoCurrentRef.current;
    c.x2 = p.x; c.y2 = p.y;
    if (c.tool === "free") c.points.push({x:p.x,y:p.y});
    redrawAnno(c);
  };

  const onAnnoEnd = () => {
    if (!annoDrawingRef.current) return;
    annoDrawingRef.current = false;
    if (annoCurrentRef.current) annoStrokesRef.current.push(annoCurrentRef.current);
    annoCurrentRef.current = null;
    redrawAnno();
  };

  const confirmAnnoText = () => {
    if (annoTextValue.trim() && annoTextPos) {
      annoStrokesRef.current.push({ tool:"text", color:annoColor, size:annoSize, x1:annoTextPos.x, y1:annoTextPos.y, text:annoTextValue.trim() });
      redrawAnno();
    }
    setAnnoTextPos(null); setAnnoTextValue("");
  };

  const undoAnno = () => { annoStrokesRef.current.pop(); redrawAnno(); };
  const clearAnno = () => { annoStrokesRef.current = []; redrawAnno(); };

  const saveAnno = () => {
    const cv = annoCanvasRef.current;
    cv.toBlob(blob => {
      setSnagForm(p => ({...p, annotatedBlob: blob, photoPreview: URL.createObjectURL(blob)}));
      setAnnoOpen(false);
    }, "image/jpeg", 0.85);
  };

  const submitSnag = async () => {
    setSnagMsg("");
    const f = snagForm;
    let area = f.area;
    if (area === "Other") area = f.areaOther.trim() || "Other";
    if (!f.site || !f.plot.trim() || !area || !f.desc.trim() || !f.priority) {
      setSnagMsg("Fill site, plot, area, description, priority");
      return;
    }
    if (!f.photoBlob) {
      setSnagMsg("Photo required — tap PHOTO to take one");
      return;
    }
    setSnagSubmitting(true);
    const snagPayload = {
      company_id: COMPANY_ID,
      site_name: f.site,
      plot_number: f.plot.trim(),
      area: area,
      description: f.desc.trim(),
      priority: f.priority,
      due_date: f.due || null,
      status: "open",
      raised_by_id: workerId,
      raised_by_name: name,
      raised_at: new Date().toISOString()
    };
    if (!navigator.onLine) {
      try {
        const ts = Date.now();
        const original = await blobToDataUrl(f.photoBlob);
        const annotated = f.annotatedBlob ? await blobToDataUrl(f.annotatedBlob) : null;
        enqueueSupOp({ table: "snags", action: "insert", payload: snagPayload, photo: { ts, original, annotated } });
        setSnagMsg("Offline — snag queued, will upload when back online.");
        setSnagSubmitting(false);
        await loadSnags();
        return;
      } catch (e) {
        setSnagMsg("Could not queue snag — storage full?");
        setSnagSubmitting(false);
        return;
      }
    }
    try {
      let photo_url = null, annotated_url = null;
      const ts = Date.now();
      if (f.photoBlob) {
        const path = COMPANY_ID + "/" + ts + "-original.jpg";
        const { error: upErr } = await sb.storage.from("snag-photos").upload(path, f.photoBlob, { contentType: "image/jpeg" });
        if (upErr) throw new Error("Photo upload: " + (upErr.message || JSON.stringify(upErr)));
        photo_url = sb.storage.from("snag-photos").getPublicUrl(path).data.publicUrl;
      }
      if (f.annotatedBlob) {
        const path = COMPANY_ID + "/" + ts + "-annotated.jpg";
        const { error: upErr } = await sb.storage.from("snag-photos").upload(path, f.annotatedBlob, { contentType: "image/jpeg" });
        if (upErr) throw new Error("Annotated upload: " + (upErr.message || JSON.stringify(upErr)));
        annotated_url = sb.storage.from("snag-photos").getPublicUrl(path).data.publicUrl;
      }
      const { error: insErr } = await sb.from("snags").insert({ ...snagPayload, photo_url, annotated_photo_url: annotated_url });
      if (insErr) throw new Error("Insert: " + (insErr.message || JSON.stringify(insErr)));
      await loadSnags();
      setSupSc("snags");
    } catch (e) {
      // Genuinely offline → queue. Otherwise show the real error so we can see it.
      if (!navigator.onLine) {
        try {
          const ts = Date.now();
          const original = await blobToDataUrl(f.photoBlob);
          const annotated = f.annotatedBlob ? await blobToDataUrl(f.annotatedBlob) : null;
          enqueueSupOp({ table: "snags", action: "insert", payload: snagPayload, photo: { ts, original, annotated } });
          setSnagMsg("Offline — snag queued, will upload when back online.");
        } catch (e2) { setSnagMsg("Could not queue snag — storage full?"); }
      } else {
        setSnagMsg(e.message || "Failed");
      }
      setSnagSubmitting(false);
      return;
    }
    setSnagSubmitting(false);
  };

  const updateSnagStatus = async (id, newStatus) => {
    const patch = { status: newStatus };
    if (newStatus === "fixed" || newStatus === "closed") {
      patch.fixed_by_id = workerId;
      patch.fixed_by_name = name;
      patch.fixed_at = new Date().toISOString();
    }
    if (!navigator.onLine) {
      enqueueSupOp({ table: "snags", action: "update", id, payload: patch });
      setSnagList(prev => prev.map(s => s.id===id ? {...s, ...patch} : s));
      return;
    }
    const { error } = await sb.from("snags").update(patch).eq("id", id);
    if (error) { enqueueSupOp({ table: "snags", action: "update", id, payload: patch }); }
    setSnagList(prev => prev.map(s => s.id===id ? {...s, ...patch} : s));
  };

  // ── v17.65 — SUPERVISOR TOOLBOX TALK HANDLERS ──
  const openSupCreateTalk = () => {
    // Reset form
    setCtTitle(""); setCtDesc(""); setCtVideoMode("url"); setCtVideoUrl("");
    setCtVideoFile(null); setCtQuestion(""); setCtOptA(""); setCtOptB(""); setCtOptC("");
    setCtCorrect(""); setCtSelSites([]); setCtError(""); setCtSuccess(false); setCtSubmitting(false);
    setSupSc("create-talk");
  };

  const toggleCtSite = (siteName) => {
    setCtSelSites(prev => prev.includes(siteName) ? prev.filter(s=>s!==siteName) : [...prev, siteName]);
  };

  // v17.80 — fill the Create Talk form from a preset. Leaves sites untouched
  // and video blank (supervisor adds their own clip / mp4 after).
  const applyToolboxPreset = (preset) => {
    if(!preset)return;
    setCtTitle(preset.title||"");
    setCtDesc(preset.description||"");
    setCtQuestion(preset.question||"");
    setCtOptA(preset.optionA||"");
    setCtOptB(preset.optionB||"");
    setCtOptC(preset.optionC||"");
    setCtCorrect(preset.correctAnswer||"");
    if(preset.videoUrl){ setCtVideoMode("url"); setCtVideoUrl(preset.videoUrl); }
    setCtError("");
  };

  const submitCreateTalk = async () => {
    setCtError("");
    // Validate
    if (!ctTitle.trim()) { setCtError("Title is required"); return; }
    if (!ctQuestion.trim()) { setCtError("Question is required"); return; }
    if (!ctOptA.trim() || !ctOptB.trim() || !ctOptC.trim()) { setCtError("All three options (A, B, C) are required"); return; }
    if (!ctCorrect) { setCtError("Pick which option is correct"); return; }
    if (ctSelSites.length === 0) { setCtError("Pick at least one site"); return; }
    if (ctVideoMode === "url" && !ctVideoUrl.trim()) { setCtError("Add a video URL or switch to Upload"); return; }
    if (ctVideoMode === "upload" && !ctVideoFile) { setCtError("Pick a video file or switch to URL"); return; }

    setCtSubmitting(true);

    // Upload video if needed
    let finalUrl = ctVideoUrl.trim();
    if (ctVideoMode === "upload") {
      const uploadedUrl = await uploadToolboxVideo(ctVideoFile);
      if (!uploadedUrl) { setCtSubmitting(false); setCtError("Video upload failed — check connection and try again"); return; }
      finalUrl = uploadedUrl;
    }

    const inserted = await pushToolboxTalk({
      title: ctTitle.trim(),
      description: ctDesc.trim(),
      videoUrl: finalUrl,
      question: ctQuestion.trim(),
      optionA: ctOptA.trim(),
      optionB: ctOptB.trim(),
      optionC: ctOptC.trim(),
      correctAnswer: ctCorrect,
      assignedSites: ctSelSites,
      postedBy: name || ""
    });

    setCtSubmitting(false);
    if (!inserted) { setCtError("Save failed — check connection and try again"); return; }
    setCtSuccess(true);
  };

  // Load talks for the sign-off tracker
  const loadSignoffTracker = async () => {
    setSupSc("signoff");
    localStorage.setItem("tracker_last_open_"+(workerId||name), new Date().toISOString()); // v17.81
    setSoNewSigs(0); // v17.81 — clear badge on open
    setSoLoading(true);
    setSoOpenTalkId(null);
    const isAdmin = (workerRole === "admin");
    const talks = await fetchToolboxTalksForSupervisor(name, isAdmin);
    setSoTalks(talks);
    setSoLoading(false);
  };

  // Expand a talk in the tracker — fetch signatures + today's on-site workers per assigned site
  const openSignoffTalk = async (talk) => {
    if (soOpenTalkId === talk.id) { setSoOpenTalkId(null); return; } // toggle close
    setSoOpenTalkId(talk.id);
    setSoDetailLoading(true);
    const [sigs, onSite] = await Promise.all([
      fetchSignaturesForTalk(talk.id),
      fetchOnSiteTodayForSites(talk.assigned_sites || [])
    ]);
    setSoSigsByTalk(prev => ({...prev, [talk.id]: sigs}));
    setSoOnSiteByTalk(prev => ({...prev, [talk.id]: onSite}));
    setSoDetailLoading(false);
  };

  // ── SUPERVISOR: load site dashboard data ──
  const loadSiteDashboard=async(siteName)=>{
    setSupSelSite(siteName);setSupSc("dashboard");setSupLoading(true);
    // Who clocked in today?
    const todayStart=new Date();todayStart.setHours(0,0,0,0);
    const {data:events}=await sb.from("clock_events")
      .select("worker_name,event_type,clocked_at")
      .eq("company_id",COMPANY_ID)
      .eq("site_name",siteName)
      .gte("clocked_at",todayStart.toISOString())
      .order("clocked_at",{ascending:true});
    const wmap={};
    (events||[]).forEach(ev=>{
      if(!wmap[ev.worker_name])wmap[ev.worker_name]={name:ev.worker_name,status:"out",lastIn:null,lastOut:null};
      if(ev.event_type==="in"){wmap[ev.worker_name].status="in";wmap[ev.worker_name].lastIn=ev.clocked_at;}
      else if(ev.event_type==="out"){wmap[ev.worker_name].status="out";wmap[ev.worker_name].lastOut=ev.clocked_at;}
    });
    setSupWorkers(Object.values(wmap));
    if(!events && !navigator.onLine){ const c=cacheGet("supdash_"+siteName); if(c){ setSupWorkers(c.workers||[]); setSupSheets(c.sheets||[]); setSupNotices(c.notices||[]); setSupLoading(false); return; } }
    // Pending daywork sheets — strict filter by assigned_supervisor_id (v17.18)
    const {data:sheets}=await sb.from("shifts")
      .select("id,worker_name,shift_date,total_mins,status,submitted_at,site_name,time_in,time_out,sheet_number")
      .eq("company_id",COMPANY_ID)
      .eq("site_name",siteName)
      .eq("status","submitted")
      .eq("assigned_supervisor_id",workerId)
      .order("submitted_at",{ascending:false});
    setSupSheets(sheets||[]);
    // Notices — fetch both supervisor-posted (this site) AND company-wide office notices
    const {data:allNoticeRows}=await sb.from("notices")
      .select("*")
      .order("created_at",{ascending:false})
      .limit(30);
    const combinedNotices=(allNoticeRows||[]).filter(n=>{
      if(n.active===false)return false;
      if(n.expires_at&&new Date(n.expires_at)<new Date())return false;
      // Company-wide notice (office posted)
      if(n.company_id===COMPANY_ID)return true;
      // Supervisor-posted notice for this site
      if(n.supervisor_id===workerId&&n.site_name===siteName)return true;
      return false;
    });
    setSupNotices(combinedNotices);
    cacheSet("supdash_"+siteName, {workers:Object.values(wmap), sheets:sheets||[], notices:combinedNotices});
    setSupLoading(false);
  };

  // ── v17.22 — Supervisor post a company-wide notice (shows in worker feed) ──
  const [supHomeNoticeInput,setSupHomeNoticeInput]=useState("");
  const [supHomeNoticePosting,setSupHomeNoticePosting]=useState(false);
  const postNoticeFromSupervisor=async()=>{
    if(!supHomeNoticeInput.trim())return;
    setSupHomeNoticePosting(true);
    const payload={
      company_id:COMPANY_ID,
      message:supHomeNoticeInput.trim(),
      posted_by:name,
      active:true,
      expires_at:null
    };
    if(!navigator.onLine){
      enqueueSupOp({table:"notices",action:"insert",payload});
      setSupHomeNoticePosting(false);
      setSupHomeNoticeInput("");
      alert("Offline — notice queued, will post when back online.");
      return;
    }
    const{error}=await sb.from("notices").insert(payload);
    if(error){enqueueSupOp({table:"notices",action:"insert",payload});setSupHomeNoticePosting(false);setSupHomeNoticeInput("");alert("Saved offline — will post when back online.");return;}
    setSupHomeNoticePosting(false);
    setSupHomeNoticeInput("");
    // Refresh notices
    const data=await fetchNotices();
    setNotices(data);
  };

  // ── SUPERVISOR: post a site notice (from site dashboard) ──
  const postNotice=async()=>{
    if(!supNoticeInput.trim())return;
    const payload={supervisor_id:workerId,site_name:supSelSite,message:supNoticeInput.trim()};
    if(!navigator.onLine){enqueueSupOp({table:"notices",action:"insert",payload});setSupNoticeInput("");setSupNotices(p=>[{id:"local-"+Date.now(),message:payload.message,created_at:new Date().toISOString()},...(p||[])]);return;}
    const{error}=await sb.from("notices").insert(payload);
    if(error){enqueueSupOp({table:"notices",action:"insert",payload});setSupNoticeInput("");return;}
    setSupNoticeInput("");
    // Reload notices
    const {data:notices}=await sb.from("notices")
      .select("id,message,created_at")
      .eq("supervisor_id",workerId)
      .eq("site_name",supSelSite)
      .order("created_at",{ascending:false});
    setSupNotices(notices||[]);
  };

  // ── SUPERVISOR: delete a notice ──
  const deleteNotice=async(id)=>{
    if(typeof id==="string"&&id.startsWith("local-")){setSupNotices(p=>p.filter(n=>n.id!==id));return;}
    if(!navigator.onLine){enqueueSupOp({table:"notices",action:"delete",id});setSupNotices(p=>p.filter(n=>n.id!==id));return;}
    const{error}=await sb.from("notices").delete().eq("id",id);
    if(error){enqueueSupOp({table:"notices",action:"delete",id});}
    setSupNotices(p=>p.filter(n=>n.id!==id));
  };

  // ── SUPERVISOR: open a sheet for review ──
  const loadSheetDetail=async(sh)=>{
    setSupSelSheet(sh);setSupSheetEntries([]);setSupSheetNotes("");setSupSigData("");setSupSc("sheet");
    const {data}=await sb.from("plot_entries")
      .select("plot_number,description,time_spent_mins,section")
      .eq("shift_id",sh.id)
      .order("section",{ascending:true})
      .order("plot_number",{ascending:true});
    setSupSheetEntries(data||[]);
  };

  // ── SUPERVISOR: sign off a sheet ──
  const signOffSheet=async()=>{
    if(!supSigData){alert("Please draw your signature first");return;}
    setSupSigning(true);
    const {error}=await sb.from("shifts").update({
      status:"signed",
      signed_at:new Date().toISOString(),
      signed_by_id:workerId,
      signed_by_name:name,
      supervisor_notes:supSheetNotes.trim()||null,
      signature_data:supSigData
    }).eq("id",supSelSheet.id);
    setSupSigning(false);
    if(error){alert("Sign-off failed: "+error.message);return;}
    setSupSheets(p=>p.filter(s=>s.id!==supSelSheet.id));
    setSupAllSheets(p=>p.filter(s=>s.id!==supSelSheet.id));
    setSupSc("dashboard");
  };

  // ── SUPERVISOR: canvas signature helpers ──
  const getSigPos=(e)=>{
    const canvas=supSigRef.current;if(!canvas)return{x:0,y:0};
    const rect=canvas.getBoundingClientRect();
    const scaleX=canvas.width/rect.width,scaleY=canvas.height/rect.height;
    if(e.touches)return{x:(e.touches[0].clientX-rect.left)*scaleX,y:(e.touches[0].clientY-rect.top)*scaleY};
    return{x:(e.clientX-rect.left)*scaleX,y:(e.clientY-rect.top)*scaleY};
  };
  const sigStart=(e)=>{
    e.preventDefault();setSupSigDrawing(true);
    const pos=getSigPos(e);supSigLastPos.current=pos;
    const ctx=supSigRef.current.getContext("2d");
    ctx.beginPath();ctx.arc(pos.x,pos.y,1.5,0,Math.PI*2);ctx.fillStyle="#1a1a1a";ctx.fill();
  };
  const sigMove=(e)=>{
    e.preventDefault();if(!supSigDrawing)return;
    const pos=getSigPos(e);
    const ctx=supSigRef.current.getContext("2d");
    ctx.beginPath();ctx.moveTo(supSigLastPos.current.x,supSigLastPos.current.y);ctx.lineTo(pos.x,pos.y);
    ctx.strokeStyle="#1a1a1a";ctx.lineWidth=2.5;ctx.lineCap="round";ctx.stroke();
    supSigLastPos.current=pos;
  };
  const sigEnd=(e)=>{
    e.preventDefault();setSupSigDrawing(false);
    if(supSigRef.current)setSupSigData(supSigRef.current.toDataURL());
  };
  const clearSig=()=>{
    if(!supSigRef.current)return;
    const ctx=supSigRef.current.getContext("2d");
    ctx.clearRect(0,0,supSigRef.current.width,supSigRef.current.height);
    setSupSigData("");
  };

  const getGPS=cb=>{
    if(!navigator.geolocation){cb(null,"GPS not available");return;}
    navigator.geolocation.getCurrentPosition(
      p=>cb({lat:p.coords.latitude.toFixed(5),lng:p.coords.longitude.toFixed(5),acc:Math.round(p.coords.accuracy)}),
      ()=>cb(null,"GPS unavailable - will work on live site"),
      {enableHighAccuracy:true,timeout:10000,maximumAge:0}
    );
  };

  // v17.85 — Actual clock-in logic, called either directly or after RAMS sign
  const doClockIn=s=>{
    // If already clocked in somewhere else, push a silent clock-out for that site first
    if(tIn&&!tOut&&site&&site!==s){
      pushClockEvent("out",workerId,name,site,null);
    }
    setSite(s);setCtr(CT[s]||"");
    setRecent(p=>[s,...p.filter(x=>x!==s)].slice(0,3));
    setTIn(new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}));
    setTInTs(Date.now()); // v17.49 — used to restore elapsed counter on refresh
    setTOut(""); // v18.17 — clear previous shift's clock-out so Home doesn't loop to site select
    setPwMode(false);setPwLines([]);setPwSubmittedPlots({});setPwEditId(null);setEntries([]);setMats(""); // v18.17 — fresh sheet
    setElapsed(0);setShowS(false);setSrch("");
    setGpsIn(null);setGpsOut(null);setGpsErr("");
    setSubmitted(false);setFromSummary(false);setSheetDate("");setSheetSite("");setSc("home");
    getGPS((pos,err)=>{
      if(pos)setGpsIn(pos);else setGpsErr(err||"");
      pushClockEvent("in",workerId,name,s,pos);
    });
  };

  const selSite=async s=>{
    // v17.85 — check unsigned RAMS for this site BEFORE clocking in
    // Skip only for pure supervisor role; workers and admin both go through RAMS check
    if (workerRole !== "supervisor" && workerId && navigator.onLine) {
      const unsigned = await fetchUnsignedRamsForSite(workerId, s, sessionSignedRamsIds.current);
      if (unsigned) {
        setRamsToSign(unsigned);
        setRamsPendingSite(s);
        setRamsRead(false);
        setRamsSigData("");
        return;
      }
    }
    doClockIn(s);
  };

  const clockOut=()=>{
    setTOut(new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}));
    getGPS((pos,err)=>{
      if(pos)setGpsOut(pos);else setGpsErr(err||"");
      // Push clock-out to Supabase (fire and forget)
      pushClockEvent("out", workerId, name, site, pos);
    });
    setFromSummary(false);
    // v18.14 — a price-work shift clocks out to its OWN review screen, not the
    // daywork summary. Mirrors the timesheet clock-out→review→submit flow.
    setSc(pwMode ? "pwsummary" : "summary");
  };

  // Go back to edit entries - keeps tOut, GPS, submitted state intact
  const goEdit=()=>{
    setFromSummary(true);
    setSc("sheet");
  };

  // Return to summary after editing entries
  const backToSummary=()=>{
    setFromSummary(false);
    setSc("summary");
  };

  // v17.85 — RAMS signature canvas handlers + submit
  const onRamsSigStart = (e) => {
    e.preventDefault();
    const cv = ramsSigCanvasRef.current;
    if (!cv) return;
    ramsSigDrawingRef.current = true;
    const r = cv.getBoundingClientRect();
    const x = (e.clientX - r.left) * (cv.width / r.width);
    const y = (e.clientY - r.top) * (cv.height / r.height);
    const ctx = cv.getContext("2d");
    ctx.beginPath(); ctx.moveTo(x,y);
    ctx.strokeStyle = "#000"; ctx.lineWidth = 2; ctx.lineCap = "round";
  };
  const onRamsSigMove = (e) => {
    if (!ramsSigDrawingRef.current) return;
    e.preventDefault();
    const cv = ramsSigCanvasRef.current;
    const r = cv.getBoundingClientRect();
    const x = (e.clientX - r.left) * (cv.width / r.width);
    const y = (e.clientY - r.top) * (cv.height / r.height);
    const ctx = cv.getContext("2d");
    ctx.lineTo(x,y); ctx.stroke();
  };
  const onRamsSigEnd = () => {
    if (!ramsSigDrawingRef.current) return;
    ramsSigDrawingRef.current = false;
    const cv = ramsSigCanvasRef.current;
    if (cv) setRamsSigData(cv.toDataURL("image/png"));
  };
  const clearRamsSig = () => {
    const cv = ramsSigCanvasRef.current;
    if (!cv) return;
    cv.getContext("2d").clearRect(0,0,cv.width,cv.height);
    setRamsSigData("");
  };

  const submitRamsSig = async () => {
    if (!ramsRead || !ramsSigData || !ramsToSign || !ramsPendingSite) return;
    setRamsSigning(true);
    try {
      // Capture GPS for the signature
      const pos = await new Promise(res => {
        if (!navigator.geolocation) return res(null);
        navigator.geolocation.getCurrentPosition(
          p => res({lat: p.coords.latitude, lng: p.coords.longitude}),
          () => res(null),
          { timeout: 6000, maximumAge: 60000 }
        );
      });
      const { error } = await sb.from("rams_signatures").insert({
        rams_id: ramsToSign.id,
        worker_id: workerId,
        worker_name: name,
        signed_at: new Date().toISOString(),
        signature_data: ramsSigData,
        gps_lat: pos ? pos.lat : null,
        gps_lng: pos ? pos.lng : null,
        site_name: ramsPendingSite,
        company_id: COMPANY_ID
      });
      if (error) {
        // v18.11 — a duplicate-key (23505) means the signature is already recorded for
        // this rams+worker+site. Treat as success, not a failure that re-loops the modal.
        const isDup = error.code === "23505" || /duplicate key/i.test(error.message || "");
        if (!isDup) {
          console.error("RAMS sign insert", error);
          // v18.12 DIAGNOSTIC — show the real reason on screen so we can see why it loops
          alert("RAMS sign failed:\n"+(error.code||"")+" "+(error.message||"")+"\n"+(error.details||"")+"\n"+(error.hint||""));
          setRamsSigning(false); return;
        }
      }
      const siteToClock = ramsPendingSite;
      const justSignedId = ramsToSign.id;
      // v18.11 — record in the session Set BEFORE the follow-up fetch so replica lag
      // can never re-surface this doc.
      sessionSignedRamsIds.current.add(justSignedId);
      setRamsToSign(null); setRamsPendingSite(null);
      setRamsRead(false); setRamsSigData("");
      setRamsSigning(false);
      // Check if there's ANOTHER unsigned RAMS for this site, excluding everything
      // signed this session (covers read-replica lag for all docs, not just the last).
      const next = await fetchUnsignedRamsForSite(workerId, siteToClock, sessionSignedRamsIds.current);
      if (next) {
        setRamsToSign(next); setRamsPendingSite(siteToClock);
        return;
      }
      // All signed — proceed to clock in
      doClockIn(siteToClock);
    } catch (e) {
      console.error("submitRamsSig", e);
      setRamsSigning(false);
    }
  };

  const cancelRamsSign = () => {
    setRamsToSign(null); setRamsPendingSite(null);
    setRamsRead(false); setRamsSigData("");
  };

  const wordsToNum=t=>{
    const W={"zero":0,"one":1,"two":2,"to":2,"too":2,"three":3,"four":4,"for":4,"five":5,"six":6,"seven":7,"eight":8,"nine":9,"ten":10,"eleven":11,"twelve":12,"thirteen":13,"fourteen":14,"fifteen":15,"sixteen":16,"seventeen":17,"eighteen":18,"nineteen":19,"twenty":20,"thirty":30,"forty":40,"fifty":50,"sixty":60,"seventy":70,"eighty":80,"ninety":90,"hundred":100};
    // Replace spoken digit sequences like "one two zero" -> "120"
    let s=t;
    // v17.43 — Chrome mishear pre-pass for small numbers (audio mistranscription corrections)
    s=s.replace(/\bfree\b/gi,"three");
    s=s.replace(/\btree\b/gi,"three");
    s=s.replace(/\bwon\b/gi,"one");
    s=s.replace(/\bate\b/gi,"eight");
    // Handle "one hundred and twenty" style
    s=s.replace(/(\w+)\s+hundred(?:\s+and\s+(\w+))?/gi,(m,h,r)=>{
      const hv=(W[h.toLowerCase()]||0)*100;
      const rv=r?(W[r.toLowerCase()]||0):0;
      return hv+rv;
    });
    // Replace individual spoken numbers
    s=s.replace(/(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)\s+(one|two|to|too|three|four|for|five|six|seven|eight|nine)/gi,(m,t,u)=>(W[t.toLowerCase()]||0)+(W[u.toLowerCase()]||0));
    s=s.replace(/(zero|one|two|to|too|three|four|for|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)/gi,m=>W[m.toLowerCase()]!==undefined?W[m.toLowerCase()]:m);
    return s;
  };

  const fixWords=t=>{
    const fixes=[
      // Rub down mishears (very comprehensive - browser mishears often)
      [/\brope down\b/gi,"rub down"],[/\block down\b/gi,"rub down"],[/\bbreak down\b/gi,"rub down"],
      [/\brub-down\b/gi,"rub down"],[/\brubb?ing down\b/gi,"rub down"],[/\bruff down\b/gi,"rub down"],
      [/\brupt down\b/gi,"rub down"],[/\brun down\b/gi,"rub down"],[/\brut down\b/gi,"rub down"],
      [/\bbroke down\b/gi,"rub down"],[/\blook down\b/gi,"rub down"],[/\bhook down\b/gi,"rub down"],
      [/\brob down\b/gi,"rub down"],[/\brop down\b/gi,"rub down"],[/\brubdown\b/gi,"rub down"],
      [/\bgroup down\b/gi,"rub down"],[/\bgrub down\b/gi,"rub down"],[/\bgroup-down\b/gi,"rub down"],
      // v17.43 — single-word mishears (no space, Chrome's new behaviour)
      [/\blopedown\b/gi,"rub down"],[/\bropedown\b/gi,"rub down"],[/\blope down\b/gi,"rub down"],
      [/\bloopdown\b/gi,"rub down"],[/\bloop down\b/gi,"rub down"],
      // Make good / making good mishears
      [/\bmake good\b/gi,"make good"],[/\bmake-good\b/gi,"make good"],[/\bmaking good\b/gi,"making good"],
      [/\bmgg\b/gi,"make good"],[/\b\bmg\b\b/gi,"make good"],[/\bm\.g\.\b/gi,"make good"],
      [/\bfinish mg\b/gi,"finish make good"],[/\bfinish mgg\b/gi,"finish make good"],
      [/\bmake goods\b/gi,"make good"],[/\bmate good\b/gi,"make good"],[/\bmace good\b/gi,"make good"],
      [/\bmaking go to\b/gi,"making good"],[/\bmake go to\b/gi,"make good"],[/\bmaking go\b/gi,"making good"],
      [/\bmaking road\b/gi,"making good"],[/\bmake road\b/gi,"make good"],[/\bmaking goods\b/gi,"making good"],
      // General mishears
      [/\bjanu\b/gi,"general"],[/\bjenna\b/gi,"general"],[/\bjenu\b/gi,"general"],
      // Around pipes / sockets / windows mishears
      [/\brampied\b/gi,"around pipes"],[/\brampip\b/gi,"around pipes"],[/\brampi\b/gi,"around pipes"],
      [/\bram pies?\b/gi,"around pipes"],[/\bramp pipes?\b/gi,"around pipes"],
      [/\bshowcase\b/gi,"sockets"],[/\bshow case\b/gi,"sockets"],[/\bshore case\b/gi,"sockets"],
      [/\bsoccer\b/gi,"socket"],[/\bsockets?s\b/gi,"sockets"],
      [/\bfight for\b/gi,"and doors"],[/\bfight four\b/gi,"and doors"],[/\bfights for\b/gi,"and doors"],
      [/\blikes? showcase\b/gi,"pipes, sockets"],
      [/\bmaking to ram fight\b/gi,"making good around pipes"],[/\bram fight\b/gi,"around pipes"],
      [/\bmaking code around\b/gi,"making good around boiler"],[/\bmaking code\b/gi,"making good"],[/\bmake code\b/gi,"make good"],
      [/\bcode around\b/gi,"around boiler"],
      // TJ / tape and joint
      [/\btj\b/gi,"TJ"],[/\btape and joint\b/gi,"TJ"],[/\btape joint\b/gi,"TJ"],
      [/\bteejay\b/gi,"TJ"],[/\btee jay\b/gi,"TJ"],
      // Nail off mishears
      [/\bnailer\b/gi,"nail off"],[/\bnail of\b/gi,"nail off"],[/\bnailed off\b/gi,"nail off"],
      [/\bnailing off\b/gi,"nail off"],[/\bnale off\b/gi,"nail off"],
      // Channel mishears
      [/\bchannel down\b/gi,"channel down"],[/\bchannels\b/gi,"channels"],[/\bchannelling\b/gi,"channelling"],
      [/\bcannel\b/gi,"channel"],[/\bchanel\b/gi,"channel"],
      // Skim mishears
      [/\bskim coat\b/gi,"skim coat"],[/\bskim over\b/gi,"skim over"],[/\bskimming\b/gi,"skimming"],
      [/\bskim off\b/gi,"skim off"],[/\bscheme\b/gi,"skim"],[/\bscam\b/gi,"skim"],
      // Board out mishears
      [/\bbored out\b/gi,"board out"],[/\bbolt out\b/gi,"board out"],[/\bboarded out\b/gi,"board out"],
      [/\bboarding out\b/gi,"board out"],
      // Dot and dab mishears
      [/\bdot in dab\b/gi,"dot and dab"],[/\bdot n dab\b/gi,"dot and dab"],[/\bdot dab\b/gi,"dot and dab"],
      // Patch mishears
      [/\bpatching\b/gi,"patching"],[/\bpatch up\b/gi,"patch up"],[/\bpatched\b/gi,"patched"],
      [/\bpash\b/gi,"patch"],[/\bpatch's\b/gi,"patches"],
      // Clean up
      [/\bclean up\b/gi,"clean up"],[/\bcleaned up\b/gi,"clean up"],[/\bcleaning up\b/gi,"clean up"],
      [/\bclean-up\b/gi,"clean up"],
      // Cupboard mishears
      [/\bcupboad\b/gi,"cupboard"],[/\bcupbord\b/gi,"cupboard"],[/\bcupbored\b/gi,"cupboard"],
      // Crack/cracks mishears
      [/\bcracks?\b/gi,"cracks"],[/\bcrack in\b/gi,"crack in"],[/\bcracking\b/gi,"cracking"],
      // Number corrections (when used as counts not times)
      [/\bfree patches\b/gi,"3 patches"],[/\bfree coats?\b/gi,"3 coats"],
      [/\bto patches\b/gi,"2 patches"],[/\bto coats?\b/gi,"2 coats"],
      [/\bfor patches\b/gi,"4 patches"],[/\bfor coats?\b/gi,"4 coats"],
      // Plasterboard
      [/\bplaster board\b/gi,"plasterboard"],[/\bplasterboarding\b/gi,"plasterboarding"],
      // Areas / rooms
      [/\bup stair[s]?\b/gi,"upstairs"],[/\bdown stair[s]?\b/gi,"downstairs"],
      [/\blounge\b/gi,"lounge"],[/\bbedroom[s]?\b/gi,"bedrooms"],[/\bceil+ing\b/gi,"ceiling"],
    ];
    let s=t;
    fixes.forEach(([from,to])=>{s=s.replace(from,to);});
    return s;
  };


  // ── v17.45 WHISPER RECORDER with auto-stop on silence ──
  // Records audio via MediaRecorder, sends to Cloudflare Worker /transcribe (OpenAI Whisper),
  // returns clean transcript text. Tap mic to start; auto-stops after silence (or tap again to force stop).
  // Bypasses Chrome's broken on-device speech recognition entirely.
  const WHISPER_URL = "https://sitevouch-voice.gavinscottmitchellmccall.workers.dev/transcribe";

  // CONFIG — silence detection tunables (adjust if too sensitive / not sensitive enough)
  const VOICE_SILENCE_THRESHOLD = 0.020; // RMS below this = quiet
  const VOICE_SILENCE_DURATION  = 800;   // ms of silence after speech before auto-stop (v17.51: was 1500)
  const VOICE_MAX_DURATION      = 15000; // ms hard cap regardless of activity

  const whisperRecord = async (onPhase) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const opts = ["audio/webm;codecs=opus","audio/webm","audio/mp4"];
    const mime = opts.find(m => MediaRecorder.isTypeSupported(m)) || "";
    const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    const chunks = [];
    window._bacMR = mr;
    mr.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };

    // v17.45: Web Audio analyser for silence detection. If it fails, fall back to manual stop only.
    let audioCtx = null, analyser = null, rafId = null;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      audioCtx = new AC();
      const src = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.2;
      src.connect(analyser);
    } catch (e) { audioCtx = null; analyser = null; }

    return new Promise((resolve, reject) => {
      mr.onstop = async () => {
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        if (audioCtx && audioCtx.state !== "closed") { try { audioCtx.close(); } catch(e){} }
        stream.getTracks().forEach(t => t.stop());
        window._bacMR = null;
        // v17.51 — user pressed Cancel: drop audio, no upload to Whisper
        if (window._bacCancelled) {
          window._bacCancelled = false;
          resolve("");
          return;
        }
        try {
          const ext = mime.includes("mp4") ? "mp4" : "webm";
          const blob = new Blob(chunks, { type: mime || "audio/webm" });
          if (blob.size < 200) { resolve(""); return; }
          const form = new FormData();
          form.append("audio", blob, "audio." + ext);
          const resp = await fetch(WHISPER_URL, { method: "POST", body: form });
          const data = await resp.json();
          if (data.transcript) resolve(data.transcript);
          else reject(new Error(data.error || "No transcript"));
        } catch (e) { reject(e); }
      };
      mr.onerror = () => reject(new Error("Recorder error"));
      mr.start();

      // v17.45 silence monitor — only runs if Web Audio came up
      if (analyser) {
        const buf = new Uint8Array(analyser.fftSize);
        let hasSpoken = false;
        let silenceStartTs = null;
        const startTs = Date.now();
        const tick = () => {
          if (!window._bacMR || window._bacMR.state !== "recording") return;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / buf.length);
          const now = Date.now();

          // hard cap
          if (now - startTs > VOICE_MAX_DURATION) {
            try { mr.stop(); } catch(e) {}
            return;
          }

          if (rms > VOICE_SILENCE_THRESHOLD) {
            if (!hasSpoken) { hasSpoken = true; if (onPhase) onPhase("heard"); }
            silenceStartTs = null;
          } else if (hasSpoken) {
            if (silenceStartTs === null) {
              silenceStartTs = now;
              if (onPhase) onPhase("silence");
            } else if (now - silenceStartTs >= VOICE_SILENCE_DURATION) {
              try { mr.stop(); } catch(e) {}
              return;
            }
          }
          rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
      }
    });
  };

  // v17.51 — drop in-flight voice recording without uploading anything to Whisper
  const cancelVoice = () => {
    if (window._bacMR && window._bacMR.state === "recording") {
      window._bacCancelled = true;
      try { window._bacMR.stop(); } catch(e) {}
    }
    setListen(false); setLisF(null); setVoiceStatus("");
  };

  const voiceEntry = async () => {
    // v17.52 — If currently recording, the user pressing the red Stop button means CANCEL.
    // (Auto-silence-stop is the only path that uploads to Whisper.)
    if (window._bacMR && window._bacMR.state === "recording") {
      cancelVoice();
      return;
    }

    // If user has tapped into a specific field, route voice directly there (manual override)
    if (activeField) { voice(activeField); return; }

      // v17.73: Smart cycle — skip already-filled fields
      let target = voiceStep;
      if (target === "plot" && plot) target = "time";
      if (target === "time" && tSpent) target = "desc";
      const labelMap = { plot:"Plot No.", time:"Time", desc:"Description" };

    try {
      setListen(true); setLisF("entry");
      setVoiceStatus("🎤 Listening — " + labelMap[target]);
      const t = (await whisperRecord(phase => {
        if (phase === "heard")   setVoiceStatus("🟢 Heard you — " + labelMap[target]);
        if (phase === "silence") setVoiceStatus("⏸ Silence — stopping…");
      }) || "").trim();
      setListen(false); setLisF(null);
      if (!t) { setVoiceStatus(""); return; }
      // v17.47: Whisper hallucinates YouTube-style endings on silent/garbled audio — reject
      if (isHallucination(t)) {
        setVoiceStatus("❓ Didn't catch that — try again");
        setTimeout(()=>setVoiceStatus(""), 2500);
        return;
      }

      // Apply to the target field based on current cycle step
      if (target === "plot") {
        let cleaned = t.replace(/\b(?:plot|plt|block|blot|plop|plod|plug|lot|slot|blood)\b/gi, "");
        let p = wordsToNum(cleaned.toLowerCase()).replace(/[^0-9]/g, "");
        if (p) {
          setPlot(p);
        } else {
          setVoiceStatus("❓ Need a plot number — try again");
          setTimeout(()=>setVoiceStatus(""),2500);
          return; // don't advance cycle
        }
      } else if (target === "time") {
        const m = round5(pMin(wordsToNum(t.toLowerCase())));
        if (m > 0) setTSpent(fMin(m)); else setTSpent(t.trim());
      } else if (target === "desc") {
        setDesc(p => p ? p + ", " + fixWords(t) : fixWords(t));
      }
      // Advance the cycle: plot → time → desc → plot
      setVoiceStep(target === "plot" ? "time" : target === "time" ? "desc" : "plot");
      setVoiceStatus("✅ " + t);
    } catch (e) {
      setListen(false); setLisF(null);
      setVoiceStatus("");
      if (e.name === "NotAllowedError" || /denied|permission/i.test(e.message)) {
        alert("Microphone blocked — allow microphone in browser settings");
      } else {
        alert("Voice error: " + e.message);
      }
    }
  };

  const voice = async (field) => {
    // v17.52 — tap-while-recording = CANCEL (drop audio, no upload). Auto-silence is the upload path.
    if (window._bacMR && window._bacMR.state === "recording") {
      cancelVoice();
      return;
    }
    const target = activeField || field;
    try {
      setListen(true); setLisF(target);
      setVoiceStatus("🎤 Listening — " + target);
      const t = (await whisperRecord(phase => {
        if (phase === "heard")   setVoiceStatus("🟢 Heard you — " + target);
        if (phase === "silence") setVoiceStatus("⏸ Silence — stopping…");
      }) || "").trim();
      setListen(false); setLisF(null);
      if (!t) { setVoiceStatus(""); return; }
      // v17.47: Whisper hallucinates YouTube-style endings on silent/garbled audio — reject
      if (isHallucination(t)) {
        setVoiceStatus("❓ Didn't catch that — try again");
        setTimeout(()=>setVoiceStatus(""), 2500);
        return;
      }
      if (target === "plot") {
        let p = wordsToNum(t.toLowerCase()).replace(/[^0-9]/g, "");
        if (p) setPlot(p);
        else { setVoiceStatus("❓ Need a plot number — try again"); setTimeout(()=>setVoiceStatus(""),2500); return; }
      }
      else if (target === "time") {
        const m = round5(pMin(wordsToNum(t.toLowerCase())));
        if (m > 0) setTSpent(fMin(m)); else setTSpent(t.trim());
      }
      else if (target === "desc") setDesc(p => p ? p + ", " + fixWords(t) : fixWords(t));
      else if (target === "mat") setMats(p => p ? p + " " + t : t);
      setVoiceStatus("✅ " + t);
    } catch (e) {
      setListen(false); setLisF(null);
      setVoiceStatus("");
      if (e.name === "NotAllowedError" || /denied|permission/i.test(e.message)) {
        alert("Microphone blocked — allow microphone in browser settings");
      } else {
        alert("Voice error: " + e.message);
      }
    }
  };

  const addE=()=>{if(!desc)return;const rm=round5(pMin(tSpent));setEntries(p=>[...p,{id:Date.now(),plot:plot.trim(),desc,tt:fMin(rm),mins:rm,sec}]);setPlot("");setDesc("");setTSpent("");setVoiceStep("plot");setActiveField(null);};;
  const newDay=()=>{setTIn("");setTOut("");setEntries([]);setMats("");setSite("");setCtr("");setElapsed(0);setTInTs(0);setGpsIn(null);setGpsOut(null);setGpsErr("");setSubmitted(false);setFromSummary(false);setSheetDate("");setSheetSite("");setPdfUrl(null);setVoiceStep("plot");setPlot("");setDesc("");setTSpent("");setActiveField(null);setPwLines([]);setPwMode(false);setPwSubmittedPlots({});setPwActivePlot("");setPwEditId(null);setPwInvoiceNos({});setSc("home");};

  const buildWeekSummary=()=>{
    const all=getShifts();
    const now=new Date();
    const day=now.getDay();
    const diffToMon=day===0?-6:1-day;
    const mon=new Date(now);mon.setDate(now.getDate()+diffToMon);mon.setHours(0,0,0,0);
    const sun=new Date(mon);sun.setDate(mon.getDate()+6);sun.setHours(23,59,59,999);
    const weekShifts=all.filter(s=>{
      const parts=s.date.split("/");
      const sd=new Date(parts[2],parts[1]-1,parts[0]);
      return sd>=mon&&sd<=sun;
    });
    if(weekShifts.length===0){alert("No shifts this week yet");return;}
    const days={};
    weekShifts.forEach(s=>{if(!days[s.date])days[s.date]={date:s.date,shifts:[]};days[s.date].shifts.push(s);});
    const totalMins=weekShifts.reduce((a,s)=>a+(s.totalMins||0),0);
    const weekLabel=mon.toLocaleDateString("en-GB",{day:"numeric",month:"short"})+" - "+sun.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
    setWeekSummary({weekLabel,days,totalMins,shifts:weekShifts});
  };

  // ── v17.18 — Submit flow now goes through supervisor picker ──
  // Step 1: tap Submit → openSubmitPicker opens the bottom sheet
  // Step 2: pick a supervisor in the modal → confirmSubmit fires the actual save
  const openSubmitPicker=()=>{
    if(submitted)return;
    if(supervisors.length===0){
      // Defensive: no supervisors loaded — fall back to direct submit so worker isn't blocked
      console.warn("No supervisors available — submitting without assignment");
      doSubmitShift(null);
      return;
    }
    // Pre-select last-used supervisor if still valid
    let preselect="";
    try{
      const last=localStorage.getItem("bac_last_supervisor_id");
      if(supervisors.find(s=>s.id===last))preselect=last;
    }catch(e){}
    setPickedSupId(preselect);
    setShowSupPicker(true);
  };

  const confirmSubmitToSupervisor=()=>{
    const sup=supervisors.find(s=>s.id===pickedSupId);
    if(!sup){alert("Please pick a supervisor first");return;}
    try{localStorage.setItem("bac_last_supervisor_id",sup.id);}catch(e){}
    setShowSupPicker(false);
    doSubmitShift(sup);
  };

  // ── v18.14 — PRICE WORK submit (one sheet PER PLOT → supervisor) ──
  // Mirrors the timesheet supervisor-picker flow. For demo this saves locally +
  // queues; the real Supabase push (price_work_claims table) is the PC/Claude
  // Code job. A plot may span several days, so each plot submits as its own sheet.
  // v18.21 — build a true A4 printable / save-as-PDF price work sheet for one plot
  const printPwSheet=(plot)=>{
    const lineTot=(ln)=> ln.formula==="flat" ? (ln.flatPrice||0) : (Number(ln.qty)||0)*(Number(ln.rate)||0);
    const plotLines=pwLines.filter(ln=>String(ln.plot)===String(plot));
    const plotTotal=plotLines.reduce((a,ln)=>a+lineTot(ln),0);
    const _site=sheetSite||site;
    const sent=pwSubmittedPlots[plot];
    const sheetNum=(sent&&sent.sheetNumber)?sent.sheetNumber:"PW-2026-XXXX";
    const inv=pwInvoiceNos[plot]||"";
    const dateStr=sheetDate?new Date(sheetDate+"T00:00:00").toLocaleDateString("en-GB"):new Date().toLocaleDateString("en-GB");
    const esc=s=>String(s==null?"":s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    const cB="border:1px solid #444;padding:6px 8px;font-size:12px;background:#BDD6F1;font-weight:700;vertical-align:middle;";
    const cC="border:1px solid #444;padding:6px 8px;font-size:13px;background:#fff;vertical-align:middle;";
    let rows="";
    plotLines.forEach(ln=>{
      rows+="<tr><td style=\""+cC+"\">"+esc(ln.name)+(ln.custom?" (CUSTOM)":"")+"</td>"
        +"<td style=\""+cC+"text-align:center;\">"+(ln.formula==="flat"?"—":esc(ln.qty)+" "+esc(ln.unit))+"</td>"
        +"<td style=\""+cC+"text-align:center;\">"+(ln.formula==="flat"?"flat":gbp(ln.rate).replace("£",""))+"</td>"
        +"<td style=\""+cC+"text-align:right;font-weight:700;\">"+gbp(lineTot(ln)).replace("£","")+"</td></tr>";
    });
    for(let i=plotLines.length;i<8;i++){rows+="<tr><td style=\""+cC+"height:24px;\">&nbsp;</td><td style=\""+cC+"\">&nbsp;</td><td style=\""+cC+"\">&nbsp;</td><td style=\""+cC+"\">&nbsp;</td></tr>";}
    const html="<!doctype html><html><head><meta charset=\"utf-8\"><title>Price Work Sheet - Plot "+esc(plot)+"</title>"
      +"<style>@page{size:A4;margin:14mm;}*{box-sizing:border-box;}body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0;}"
      +".sheet{width:100%;border:1.5px solid #333;padding:14px 16px;}table{width:100%;border-collapse:collapse;margin-bottom:8px;}"
      +".hd{display:flex;align-items:center;gap:16px;margin-bottom:12px;}.hd img{width:70px;height:70px;object-fit:contain;}"
      +".ttl{flex:1;text-align:center;font-weight:900;font-size:24px;letter-spacing:2px;text-decoration:underline;}"
      +".sn{text-align:right;}.sn .n{font-size:18px;font-weight:900;color:#1a3f88;}.sn .l{font-size:10px;color:#888;}</style></head><body>"
      +"<div class=\"sheet\">"
      +"<div class=\"hd\"><img src=\""+LOGO_LIGHT+"\"><div class=\"ttl\">PRICE WORK SHEET</div><div class=\"sn\"><div class=\"n\">"+esc(sheetNum)+"</div><div class=\"l\">Sheet Number</div></div></div>"
      +"<table><tr>"
      +"<td rowspan=\"2\" style=\""+cB+"width:20%;text-align:center;\">SUB-CONTRACTOR NAME</td>"
      +"<td rowspan=\"2\" style=\""+cC+"width:30%;text-align:center;font-weight:700;font-size:15px;\">"+esc(name)+"</td>"
      +"<td style=\""+cB+"width:16%;\">DATE</td><td style=\""+cC+"\">"+esc(dateStr)+"</td></tr>"
      +"<tr><td style=\""+cB+"\">PLOT</td><td style=\""+cC+"font-weight:700;\">"+esc(plot)+"</td></tr>"
      +"<tr><td style=\""+cB+"\">SITE LOCATION</td><td style=\""+cC+"\">"+esc(LC[_site]||_site)+"</td><td style=\""+cB+"\">CONTRACTOR</td><td style=\""+cC+"\">"+esc(CT[_site]||ctr||"")+"</td></tr>"
      +"<tr><td style=\""+cB+"\">YOUR INVOICE No.</td><td colspan=\"3\" style=\""+cC+"font-weight:700;\">"+esc(inv||"—")+"</td></tr>"
      +"</table>"
      +"<table><tr><td style=\""+cB+"text-align:left;\">OPERATION</td><td style=\""+cB+"width:18%;text-align:center;\">QTY</td><td style=\""+cB+"width:18%;text-align:center;\">RATE £</td><td style=\""+cB+"width:20%;text-align:right;\">TOTAL £</td></tr>"
      +rows
      +"<tr><td colspan=\"3\" style=\""+cB+"text-align:right;font-size:14px;\">PLOT TOTAL</td><td style=\""+cB+"text-align:right;font-size:15px;font-weight:900;\">"+gbp(plotTotal)+"</td></tr></table>"
      +"<table><tr><td style=\""+cB+"width:30%;\">SiteVouch Supervisor Signature</td><td style=\""+cC+"width:30%;padding:20px;\">&nbsp;</td><td style=\""+cB+"width:10%;\">Date</td><td style=\""+cC+"width:30%;padding:20px;\">&nbsp;</td></tr></table>"
      +"</div>"
      +"<script>window.onload=function(){setTimeout(function(){window.print();},350);};<\/script>"
      +"</body></html>";
    const w=window.open("","_blank");
    if(!w){alert("Please allow pop-ups to print or save the sheet as a PDF.");return;}
    w.document.open();w.document.write(html);w.document.close();
  };

  // v18.25 — Price Work is an invoice → goes straight to Office (no supervisor sign-off).
  const submitPwToOffice=(plot)=>{
    if(pwSubmitting)return;
    setPwActivePlot(plot);
    doPwSubmit(plot,null);
  };
  const doPwSubmit=async(plot,sup)=>{
    setPwSubmitting(true);
    const lineTot=(ln)=> ln.formula==="flat" ? (ln.flatPrice||0) : (Number(ln.qty)||0)*(Number(ln.rate)||0);
    const plotLines=pwLines.filter(ln=>String(ln.plot)===String(plot));
    const sheetTotal=plotLines.reduce((a,ln)=>a+lineTot(ln),0);
    const sheetNumber=nextPwSheetNumber();
    const record={
      id:Date.now(), type:"pricework", sheetNumber,
      workerId, name, site:sheetSite||site, plot,
      date:(sheetDate||todayISO)?new Date((sheetDate||todayISO)+"T00:00:00").toLocaleDateString("en-GB"):today,
      tIn,tOut,
      gpsIn:gpsIn||null,gpsOut:gpsOut||null,
      sheetTotal,
      lines:plotLines,
      submitted:new Date().toISOString(),
      assignedSupervisorId:sup?sup.id:null,
      assignedSupervisorName:sup?sup.full_name:null,
      worker_invoice_number:pwInvoiceNos[plot]||null,
      company_id:COMPANY_ID
      // Phase 2: worker_invoice_file slot on here.
    };
    // v18.24 — queue locally if offline OR if the Supabase push fails. Office is source of truth.
    const queueLocally=()=>{
      try{
        const q=JSON.parse(localStorage.getItem("bac_pw_offline")||"[]");
        q.push(record);
        localStorage.setItem("bac_pw_offline",JSON.stringify(q));
        return true;
      }catch(e){return false;}
    };
    if(isOnline){
      const ok=await pushPwClaim(record);
      if(!ok){
        // Network/Supabase failed — drop into offline queue so it isn't lost.
        if(queueLocally()){
          alert("Saved — will send when connection is back");
        } else {
          setPwSubmitting(false);
          alert("Could not save. Try again.");
          return;
        }
      }
    } else {
      if(!queueLocally()){
        setPwSubmitting(false);
        alert("Could not save offline");
        return;
      }
    }
    // Mark this plot submitted so the review screen shows it as sent to Office.
    setPwSubmittedPlots(p=>({...p,[plot]:{sheetNumber,supervisorName:"Office",at:Date.now()}}));
    setPwSubmitting(false);
  };

  const doSubmitShift=async(sup)=>{
    setSubmitting(true);
    const record={
      id:Date.now(),workerId,name,
      site:sheetSite||site,
      date:(sheetDate||todayISO)?new Date((sheetDate||todayISO)+"T00:00:00").toLocaleDateString("en-GB"):today,
      tIn,tOut,
      gpsIn:gpsIn||null,gpsOut:gpsOut||null,
      mgH,siH,totalMins:mgM+siM,
      submitted:new Date().toISOString(),
      assignedSupervisorId:sup?sup.id:null,
      assignedSupervisorName:sup?sup.full_name:null
    };
    // v18.29 — sheet carries a site that's not in the official list: quietly raise a
    // "new site" request so the Office can approve it and add it to everyone's dropdown.
    // Fire-and-forget (never blocks or fails the sheet), deduped per site name per device.
    const _submitSite=(sheetSite||site||"").trim();
    if(_submitSite && !SITES.includes(_submitSite) && _submitSite!=="Site Not Listed"){
      try{
        const seenKey="bac_newsite_req_"+_submitSite.toLowerCase();
        if(!localStorage.getItem(seenKey)){
          pushRequest({workerId,workerName:name,type:"new_site",note:"New site typed on daywork sheet: \""+_submitSite+"\" ("+record.date+"). Approve to add it to the site list.",gps:gpsIn||null});
          localStorage.setItem(seenKey,"1");
        }
      }catch(e){}
    }
    // v17.90 (C2) — queue locally if offline, OR if the Supabase push fails. Office is the source of truth.
    const queueLocally=()=>{
      try{
        const q=JSON.parse(localStorage.getItem("bac_offline")||"[]");
        q.push({...record,_entries:entries});
        localStorage.setItem("bac_offline",JSON.stringify(q));
        setOfflineQueue(q);
        return true;
      }catch(e){return false;}
    };
    if(isOnline){
      if(saveShift(record)){
        // Await the Supabase push and only mark "sent" if it actually landed.
        const ok=await pushShift(record, entries);
        if(ok){
          setSubmitted(true);
        } else {
          // Network/Supabase failed — drop into offline queue so it auto-retries.
          if(queueLocally()){
            setSubmitted(true);
            alert("Saved — will send when connection is back");
          } else {
            setSubmitting(false);
            alert("Could not save. Try again.");
            return;
          }
        }
      }
      else { setSubmitting(false); alert("Could not save"); return; }
    } else {
      if(queueLocally()) setSubmitted(true);
      else { setSubmitting(false); alert("Could not save offline"); return; }
    }
    setSubmitting(false);
  };


  // ── v17.18 — Open a signed sheet for viewing ──
  // ── v17.57 — also marks as "opened" (clears NEW pill) and persists per device ──
  const openSignedSheetDetail=async(sheet)=>{
    setOpenSignedSheet(sheet);
    setSignedSheetEntries([]);
    setSignedSheetUrl(null);
    setSignedSheetLoading(true);
    if(workerId && sheet.id && !signedOpenedIds.includes(sheet.id)){
      const next=[...signedOpenedIds,sheet.id];
      setSignedOpenedIds(next);
      try{localStorage.setItem("bac_signed_opened_"+workerId,JSON.stringify(next));}catch(e){}
    }
    const ents=await fetchShiftEntries(sheet.id);
    setSignedSheetEntries(ents);
    setSignedSheetLoading(false);
  };

  // ── v17.18 — Build download URL for the open signed sheet ──
  const buildSignedDownload=()=>{
    if(!openSignedSheet)return;
    const html=buildSignedSheetHTML(openSignedSheet, signedSheetEntries, openSignedSheet.worker_name||name, LOGO_LIGHT);
    const encoded="data:text/html;charset=utf-8,"+encodeURIComponent(html);
    setSignedSheetUrl(encoded);
  };

  const closeSignedSheet=()=>{
    setOpenSignedSheet(null);
    setSignedSheetEntries([]);
    setSignedSheetUrl(null);
    setShowDismissConfirm(false);
  };

  // ── v17.57 — Mark sheet as downloaded (fires when worker clicks the Download link) ──
  const markSignedDownloaded=(shiftId)=>{
    if(!workerId||!shiftId)return;
    if(signedDownloadedIds.includes(shiftId))return;
    const next=[...signedDownloadedIds,shiftId];
    setSignedDownloadedIds(next);
    try{localStorage.setItem("bac_signed_downloaded_"+workerId,JSON.stringify(next));}catch(e){}
  };

  // ── v17.57 — Dismiss a signed sheet (hides from worker's list; Supabase record untouched). ──
  // If sheet hasn't been downloaded yet, prompt for confirmation first.
  const requestDismissSignedSheet=()=>{
    if(!openSignedSheet)return;
    if(!signedDownloadedIds.includes(openSignedSheet.id)){
      setShowDismissConfirm(true);
      return;
    }
    confirmDismissSignedSheet();
  };
  const confirmDismissSignedSheet=()=>{
    if(!openSignedSheet||!workerId)return;
    const id=openSignedSheet.id;
    if(!signedDismissedIds.includes(id)){
      const next=[...signedDismissedIds,id];
      setSignedDismissedIds(next);
      try{localStorage.setItem("bac_signed_dismissed_"+workerId,JSON.stringify(next));}catch(e){}
    }
    closeSignedSheet();
  };

  const openEdit=shift=>{
    setEditShift(shift);setEditIn(shift.tIn);setEditOut(shift.tOut);setEditNote("");setEditDone(false);setShowEdit(true);
  };

  const submitEdit=()=>{
    try{
      const all=getShifts();
      const idx=all.findIndex(s=>s.id===editShift.id);
      if(idx>=0){all[idx]={...all[idx],tIn:editIn,tOut:editOut,editNote,pending:true,edited:new Date().toISOString()};}
      localStorage.setItem("bac_shifts",JSON.stringify(all));
      // Push edit request to Supabase
      pushRequest({
        workerId,
        workerName:name,
        type:"time_edit",
        note:"Edit shift "+editShift.date+" at "+(LC[editShift.site]||editShift.site)+(editNote?" — "+editNote:""),
        tIn:editIn,
        tOut:editOut
      });
      setEditDone(true);setPastShifts(getShifts().slice(0,5));
    }catch(e){alert("Could not save edit");}
  };

  const submitAdd=()=>{
    if(!addSite||!addDate||!addIn||!addOut){alert("Please fill in all fields");return;}
    const record={id:Date.now(),workerId,name,site:addSite,date:addDate,tIn:addIn,tOut:addOut,gpsIn:null,gpsOut:null,mgH:"",siH:"",totalMins:0,addNote,pending:true,manualEntry:true,submitted:new Date().toISOString()};
    if(saveShift(record)){
      // Push manual add request to Supabase
      pushRequest({
        workerId,
        workerName:name,
        type:"missed_clock",
        note:"Add shift "+addDate+" at "+addSite+" ("+addIn+"–"+addOut+")"+(addNote?" — "+addNote:""),
        tIn:addIn,
        tOut:addOut
      });
      setAddDone(true);setPastShifts(getShifts().slice(0,5));
    }
    else alert("Could not save shift");
  };

  // ── v17.21 — Notice card helpers ──
  const NOTICE_COLOURS=["#2F80ED","#7C3AED","#059669","#DC2626","#D97706","#0284C7"];
  const noticeAvatarColour=name=>{let h=0;for(let c of(name||""))h+=c.charCodeAt(0);return NOTICE_COLOURS[h%NOTICE_COLOURS.length];};
  const noticeInitials=name=>(!name?"OF":name.split(" ").map(w=>w[0]||"").join("").toUpperCase().slice(0,2));
  const noticeDate=iso=>{if(!iso)return"";const d=new Date(iso),m=Math.floor((Date.now()-d)/6e4);if(m<1)return"Just now";if(m<60)return m+"m ago";const h=Math.floor(m/60);if(h<24)return h+"h ago";const dy=Math.floor(h/24);if(dy<7)return dy+"d ago";return d.toLocaleDateString("en-GB");};

  const mgE=entries.filter(e=>e.sec==="mg"),siE=entries.filter(e=>e.sec==="si");
  const mgG=gPlot(mgE),siG=gPlot(siE);
  const mgM=mgE.reduce((a,e)=>a+e.mins,0),siM=siE.reduce((a,e)=>a+e.mins,0);
  const mgH=mgM?fMin(mgM):"";
  const siH=siM?fMin(siM):"";
  const mgTxt=mgG.map(g=>"PLT "+g.plot+" - "+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
  const siTxt=siG.map(g=>"PLT "+g.plot+" - "+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
  const filt=SITES.filter(s=>s.toLowerCase().includes(srch.toLowerCase()));
  const addFilt=SITES.filter(s=>s.toLowerCase().includes(addSrch.toLowerCase()));

  const buildPDF=()=>{
    const rg=gPlot(entries.filter(e=>e.sec==="mg"));
    const sg=gPlot(entries.filter(e=>e.sec==="si"));
    const rt=rg.map(g=>"PLT "+g.plot+" - "+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
    const st=sg.map(g=>"PLT "+g.plot+" - "+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
    const mh=entries.filter(e=>e.sec==="mg").reduce((a,e)=>a+e.mins,0);
    const sh=entries.filter(e=>e.sec==="si").reduce((a,e)=>a+e.mins,0);
    const mhStr=mh?fMin(mh):"";
    const shStr=sh?fMin(sh):"";
    const td="border:1px solid #444;padding:3px 6px;font-family:Arial,sans-serif;font-size:11px;color:#111;vertical-align:middle;";
    const th=td+"background:#BDD6F1;font-weight:700;";
    const html="<!DOCTYPE html><html><head><meta charset=\"utf-8\"><title>SiteVouch Daywork Sheet</title>"
      +"<style>body{margin:20px;font-family:Arial,sans-serif;}table{border-collapse:collapse;width:100%;}"
      +"@media print{body{margin:0;}@page{margin:8mm;}.noprint{display:none;}}</style>"
      +"</head><body>"
      +"<div style=\"width:680px;border:1.5px solid #333;padding:4px 10px 6px;box-sizing:border-box;\">"
      +"<div style=\"display:flex;align-items:center;margin-bottom:4px;gap:12px;\">"
      +"<img src=\""+LOGO_LIGHT+"\" style=\"width:56px;height:56px;object-fit:contain;\" alt=\"SiteVouch\"/>"
      +"<div style=\"flex:1;text-align:center;font-weight:900;font-size:16px;letter-spacing:2px;text-decoration:underline;\">DAYWORK SHEET</div>"
      +"</div>"
      +"<table style=\"margin-bottom:2px\"><tbody>"
      +"<tr><td rowspan=\"3\" style=\""+th+"width:18%;text-align:center;font-size:9px;\">SUB-CONTRACTOR NAME</td>"
      +"<td rowspan=\"3\" style=\""+td+"width:25%;font-size:13px;font-weight:700;text-align:center;\">"+name+"</td>"
      +"<td style=\""+th+"width:19%;font-size:9px;\">DATE</td><td style=\""+td+"font-size:11px;\">"+today+"</td></tr>"
      +"<tr><td style=\""+th+"font-size:9px;\">SITE LOCATION</td><td style=\""+td+"font-size:11px;\">"+(LC[site]||site)+"</td></tr>"
      +"<tr><td style=\""+th+"font-size:9px;\">CONTRACTOR</td><td style=\""+td+"font-size:11px;\">"+ctr+"</td></tr>"
      +"</tbody></table>"
      +"<table style=\"margin-bottom:2px\"><tbody>"
      +"<tr><td colspan=\"2\" style=\""+th+"font-size:10px;\">Work Sheet - Please Include Plot Numbers</td></tr>"
      +"<tr><td style=\""+td+"font-size:9px;color:#CC0000;font-weight:700;font-style:italic;\">Please confirm how long you spent in each plot next to your plot number</td>"
      +"<td style=\""+td+"width:140px;text-align:right;font-weight:700;font-size:14px;\">Hours: "+mhStr+"</td></tr>"
      +"<tr><td colspan=\"2\" style=\""+td+"height:380px;vertical-align:top;padding-top:5px;white-space:pre-wrap;\">"+rt+"</td></tr>"
      +"</tbody></table>"
      +"<table style=\"margin-bottom:2px\"><tbody>"
      +"<tr><td style=\""+th+"font-size:10px;\">Site Instruction Works - Please Include Plot Numbers</td>"
      +"<td style=\""+th+"width:140px;text-align:right;font-size:14px;\">Hours: "+shStr+"</td></tr>"
      +"<tr><td colspan=\"2\" style=\""+td+"height:140px;vertical-align:top;padding-top:5px;white-space:pre-wrap;\">"+st+"</td></tr>"
      +"</tbody></table>"
      +"<table style=\"margin-bottom:2px\"><tbody>"
      +"<tr><td style=\""+th+"font-size:10px;\">Materials/Plant/Access Equipment Used</td></tr>"
      +"<tr><td style=\""+td+"height:26px;vertical-align:top;font-size:11px;\">"+mats+"</td></tr>"
      +"</tbody></table>"
      +"<table style=\"width:45%;margin-bottom:2px\"><tbody>"
      +"<tr><td style=\""+th+"text-align:center;width:50%;font-size:10px;\">TIME IN</td><td style=\""+th+"text-align:center;width:50%;font-size:10px;\">TIME OUT</td></tr>"
      +"<tr><td style=\""+td+"text-align:center;font-size:14px;font-weight:700;\">"+tIn+"</td><td style=\""+td+"text-align:center;font-size:14px;font-weight:700;\">"+tOut+"</td></tr>"
      +"</tbody></table>"
      +"<table><tbody><tr>"
      +"<td style=\""+th+"width:28%;font-size:9px;\">SiteVouch Supervisor Signature</td>"
      +"<td style=\""+td+"width:20%;padding:10px;\">&nbsp;</td>"
      +"<td style=\""+th+"width:10%;font-size:9px;\">Date</td>"
      +"<td style=\""+td+"width:42%;padding:10px;\">&nbsp;</td>"
      +"</tr></tbody></table>"
      +"</div>"
      +"<br><p class=\"noprint\" style=\"color:#666;font-size:13px;\">To save as PDF: tap the share/menu button in your browser → Print → Save as PDF</p>"
      +"</body></html>";
    const encoded="data:text/html;charset=utf-8,"+encodeURIComponent(html);
    setPdfUrl(encoded);
  };

  // v18.32 — read a chosen logo file, downscale to keep localStorage small, remember it.
  const onPwLogoPick=(file)=>{
    if(!file)return;
    const rd=new FileReader();
    rd.onload=()=>{
      const img=new Image();
      img.onload=()=>{
        try{
          const maxW=400,scale=Math.min(1,maxW/img.width);
          const cv=document.createElement("canvas");
          cv.width=Math.round(img.width*scale);cv.height=Math.round(img.height*scale);
          cv.getContext("2d").drawImage(img,0,0,cv.width,cv.height);
          const out=cv.toDataURL("image/png");
          setPwLogo(out);setPwWeekPdf(null);
          try{localStorage.setItem("bac_pw_logo",out);}catch(e){alert("Logo too large to save on this device.");}
        }catch(e){alert("Could not read that image.");}
      };
      img.onerror=()=>alert("Could not read that image.");
      img.src=rd.result;
    };
    rd.readAsDataURL(file);
  };

  // v18.31 — My Week price-work invoice sheet (NO logo). Invoice-style A4 built from
  // the worker's already-submitted claims. Summary only — no CIS/VAT lines.
  const buildWeekPDF=()=>{
    const win=pwWeekWindow(pwWeekOffset);
    const linesByClaim={};
    pwWeekLines.forEach(ln=>{(linesByClaim[ln.claim_id]=linesByClaim[ln.claim_id]||[]).push(ln);});
    const claimTot=(c)=>{const ls=linesByClaim[c.id];if(ls&&ls.length)return ls.reduce((a,ln)=>a+(Number(ln.value)||0),0);return Number(c.sheet_total)||0;};
    const gross=pwWeekClaims.reduce((a,c)=>a+claimTot(c),0);
    const td="border:1px solid #444;padding:5px 8px;font-family:Arial,sans-serif;font-size:12px;color:#111;vertical-align:middle;";
    const th=td+"background:#D9E5D3;font-weight:700;";
    const money="text-align:right;white-space:nowrap;";
    let rows="";
    pwWeekClaims.forEach(c=>{
      const ls=linesByClaim[c.id]||[];
      const dLbl=c.claim_date?new Date(c.claim_date+"T00:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"}):"";
      rows+="<tr><td style=\""+td+"font-weight:700;background:#F3F7F1;\" colspan=\"4\">Plot "+c.plot_number+" &middot; "+(LC[c.site_name]||c.site_name)+"  <span style=\"font-weight:400;color:#555;\">&mdash; "+dLbl+(c.sheet_number?" &middot; "+c.sheet_number:"")+"</span></td></tr>";
      if(ls.length){
        ls.forEach(ln=>{
          const desc=ln.operation||"";
          const detail=ln.formula_type==="flat"?"flat rate":((Number(ln.qty)||0)+" "+(ln.unit||"")+" &times; "+gbp(ln.rate));
          rows+="<tr><td style=\""+td+"\">"+desc+"</td><td style=\""+td+"\">"+detail+"</td><td style=\""+td+"\"></td><td style=\""+td+money+"\">"+gbp(ln.value)+"</td></tr>";
        });
      } else {
        rows+="<tr><td style=\""+td+"\" colspan=\"3\">(sheet total)</td><td style=\""+td+money+"\">"+gbp(claimTot(c))+"</td></tr>";
      }
    });
    const subName=(pwTradeName||name||"").trim();
    const contractorName=(pwContractor||"").trim();
    const logoHtml=pwLogo?"<img src=\""+pwLogo+"\" style=\"max-height:60px;max-width:200px;object-fit:contain;display:block;margin:0 auto 8px;\" alt=\"logo\"/>":"";
    // v18.33 — CIS deducted on labour (subtotal) BEFORE VAT; VAT added on subtotal. Both optional.
    const cisRateN=pwCisOn?(Number(pwCisRate)||0):0;
    const vatRateN=pwVatOn?(Number(pwVatRate)||0):0;
    const vatAmt=gross*(vatRateN/100);
    const cisAmt=gross*(cisRateN/100);
    const balanceDue=gross+vatAmt-cisAmt;
    const tdR=td+money;
    let totalsRows="<tr><td style=\""+td+"font-weight:700;\">Subtotal (labour)</td><td style=\""+tdR+"\">"+gbp(gross)+"</td></tr>";
    if(pwVatOn)totalsRows+="<tr><td style=\""+td+"\">VAT @ "+vatRateN+"%</td><td style=\""+tdR+"\">"+gbp(vatAmt)+"</td></tr>";
    if(pwCisOn)totalsRows+="<tr><td style=\""+td+"\">Less CIS @ "+cisRateN+"% (on labour)</td><td style=\""+tdR+"color:#B00;\">-"+gbp(cisAmt)+"</td></tr>";
    totalsRows+="<tr><td style=\""+td+"font-weight:900;background:#D9E5D3;\">BALANCE DUE</td><td style=\""+tdR+"font-weight:900;background:#D9E5D3;\">"+gbp(balanceDue)+"</td></tr>";
    const utrBits=[];
    if((pwUtr||"").trim())utrBits.push("UTR: "+pwUtr.trim());
    if(pwVatOn&&(pwVatNo||"").trim())utrBits.push("VAT No: "+pwVatNo.trim());
    if((pwCoNo||"").trim())utrBits.push("Company No: "+pwCoNo.trim());
    const payHtml=(pwPayInfo||"").trim()?"<div style=\"margin-top:14px;border:1px solid #ccc;padding:8px 10px;font-size:11px;color:#333;line-height:1.6;\"><b>Payment details</b><br>"+pwPayInfo.trim().replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/\n/g,"<br>")+"</div>":"";
    // v18.34 — full editable header fragments
    const esc=(s)=>String(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;");
    const senderAddr=(pwAddress||"").trim()?esc(pwAddress).replace(/\n/g,"<br>")+"<br>":"";
    const senderPhone=(pwPhone||"").trim()?esc(pwPhone)+"<br>":"";
    const senderEmail=(pwEmail||"").trim()?esc(pwEmail)+"<br>":"";
    const senderBlock="<div style=\"font-size:12px;color:#333;line-height:1.55;\">"
      +"<div style=\"font-weight:900;font-size:15px;color:#111;margin-bottom:3px;\">"+esc(subName||"&mdash;")+"</div>"
      +senderAddr+senderPhone+senderEmail
      +(utrBits.length?"<div style=\"margin-top:3px;color:#555;\">"+utrBits.map(esc).join("<br>")+"</div>":"")
      +"</div>";
    const invMeta=[];
    invMeta.push("<div><span style=\"color:#888;font-size:10px;letter-spacing:1px;\">INVOICE</span><br><b>"+esc(pwWeekInvNo||"&mdash;")+"</b></div>");
    if((pwInvDate||"").trim())invMeta.push("<div style=\"margin-top:6px;\"><span style=\"color:#888;font-size:10px;letter-spacing:1px;\">DATE</span><br>"+esc(pwInvDate)+"</div>");
    if((pwDueDate||"").trim())invMeta.push("<div style=\"margin-top:6px;\"><span style=\"color:#888;font-size:10px;letter-spacing:1px;\">DUE DATE</span><br>"+esc(pwDueDate)+"</div>");
    const invMetaBlock="<div style=\"text-align:right;font-size:12px;color:#333;line-height:1.4;\">"+invMeta.join("")+"</div>";
    const billLines=[];
    if(contractorName)billLines.push("<div style=\"font-weight:800;font-size:14px;color:#111;\">"+esc(contractorName)+"</div>");
    if((pwBillContact||"").trim())billLines.push("<div>"+esc(pwBillContact)+"</div>");
    if((pwBillEmail||"").trim())billLines.push("<div>"+esc(pwBillEmail)+"</div>");
    const billBlock=billLines.length?"<div style=\"margin:14px 0;\"><div style=\"color:#888;font-size:10px;letter-spacing:1px;margin-bottom:3px;\">BILL TO</div><div style=\"font-size:12px;color:#333;line-height:1.5;\">"+billLines.join("")+"</div></div>":"";
    const html="<!DOCTYPE html><html><head><meta charset=\"utf-8\"><title>Price Work Summary</title>"
      +"<style>html,body{margin:0;padding:0;font-family:Arial,sans-serif;background:#eee;}"
      +".page{width:210mm;min-height:297mm;margin:0 auto;background:#fff;padding:16mm 14mm;box-sizing:border-box;}"
      +"table{border-collapse:collapse;width:100%;}"
      +"@media screen{body{padding:12px;}.page{box-shadow:0 2px 10px rgba(0,0,0,.2);}}"
      +"@media print{body{background:#fff;}.page{width:auto;min-height:auto;margin:0;padding:0;box-shadow:none;}@page{size:A4;margin:14mm;}.noprint{display:none;}}</style>"
      +"</head><body>"
      +"<div class=\"page\">"
      +logoHtml
      +"<div style=\"display:flex;justify-content:space-between;align-items:flex-start;gap:20px;margin-bottom:6px;\">"+senderBlock+invMetaBlock+"</div>"
      +"<div style=\"text-align:center;font-weight:900;font-size:18px;letter-spacing:1px;margin:6px 0 2px;\">PRICE WORK INVOICE</div>"
      +"<div style=\"text-align:center;font-size:12px;color:#555;margin-bottom:6px;\">Week ending "+win.label.split("–").pop().trim()+"</div>"
      +billBlock
      +"<table style=\"margin-bottom:6px;\"><tbody>"
      +"<tr><td style=\""+th+"width:22%;\">Week</td><td style=\""+td+"\">"+win.label+"</td></tr>"
      +"</tbody></table>"
      +"<table><thead>"
      +"<tr><th style=\""+th+"text-align:left;\">Operation</th><th style=\""+th+"text-align:left;\">Detail</th><th style=\""+th+"\"></th><th style=\""+th+money+"\">Amount</th></tr>"
      +"</thead><tbody>"+rows
      +"</tbody></table>"
      +"<table style=\"margin-top:14px;width:60%;margin-left:auto;\"><tbody>"+totalsRows+"</tbody></table>"
      +payHtml
      +"<div style=\"margin-top:14px;border:1px solid #E0A800;background:#FFF8E1;padding:8px 10px;font-size:10px;color:#7A5900;line-height:1.5;\">"
      +"<b>Please check figures before submitting.</b> CIS and VAT shown are self-declared and set by you on this device. Confirm your CIS/VAT status and final pay with the contractor."
      +"</div>"
      +"</div>"
      +"<br><p class=\"noprint\" style=\"color:#666;font-size:13px;text-align:center;\">To save as PDF: open this file &rarr; Print &rarr; Save as PDF</p>"
      +"</body></html>";
    setPwWeekPdf("data:text/html;charset=utf-8,"+encodeURIComponent(html));
  };
  const signOut=()=>{
    if(tIn&&!tOut){
      // v18.02 — never trap the worker. Offer to clock out then sign out
      // in one step instead of refusing (old behaviour deadlocked them).
      if(!window.confirm("You're still clocked in. Clock out now and sign out?"))return;
      try{pushClockEvent("out", workerId, name, site, gpsOut||gpsIn||null);}catch(e){}
      try{localStorage.removeItem("bac_active_shift");}catch(e){}
    }
    try{
      localStorage.removeItem("bac_name");
      localStorage.removeItem("bac_worker_id");
      localStorage.removeItem("bac_worker_role");
    }catch(e){}
    setName("");setWorkerId("");setWorkerRole("");setShowSettings(false);
    setSc("home");setSite("");setCtr("");setTIn("");setTOut("");setEntries([]);setMats("");
  };

  // ═══════════════════════════════════════════════════════════════════════
  // v17.28 — TOOLBOX TALK HANDLERS
  // ═══════════════════════════════════════════════════════════════════════
  const openToolboxTalk=(talk)=>{
    setToolboxOpenTalk(talk);
    setTbVideoWatched(false);
    setTbQuestionAnswered(false);
    setTbAnswerGiven("");
    setTbWrongTry(false);
    setTbWrongLetter("");
    setTbGps(null);setTbGpsErr("");
    setTbSupPhone(null);
    setTbMessage("");setTbMessageStatus("");setTbMessageSending(false);
    setTbSubmitting(false);
    setTbSignedDone(false);
    tbSigHasInkRef.current=false;
    tbMaxWatchedRef.current=0;
    setSc("toolbox");
    // Look up supervisor phone (silent)
    (async()=>{
      const ph=await fetchSupervisorPhone(talk.posted_by);
      setTbSupPhone(ph);
    })();
    // Ask for GPS straight away while user is actively engaged — the prompt
    // is most likely to be granted at this moment, not 60 seconds later after
    // they've watched a video and answered a question.
    tbFetchGps();
  };

  const closeToolboxTalk=()=>{
    setToolboxOpenTalk(null);
    setSc("home");
  };

  const tbMarkWatched=()=>{
    setTbVideoWatched(true);
  };

  const tbPickAnswer=(letter)=>{
    if(tbQuestionAnswered||!toolboxOpenTalk)return;
    const correct=(toolboxOpenTalk.correct_answer||"").toLowerCase();
    if(letter===correct){
      setTbQuestionAnswered(true);
      setTbAnswerGiven(letter);
      setTbWrongTry(false);setTbWrongLetter("");
      // If GPS hasn't been captured yet (or was denied earlier), retry now.
      if(!tbGps)tbFetchGps();
    } else {
      setTbWrongTry(true);
      setTbWrongLetter(letter);
      setTimeout(()=>{setTbWrongLetter("");},500);
    }
  };

  const tbFetchGps=()=>{
    if(!navigator.geolocation){setTbGpsErr("Location not available on this device");return;}
    setTbGpsErr(""); // clear before retrying
    navigator.geolocation.getCurrentPosition(
      p=>{setTbGps({lat:p.coords.latitude,lng:p.coords.longitude,acc:Math.round(p.coords.accuracy)});setTbGpsErr("");},
      e=>{
        // Map specific error codes for clarity
        if(e.code===1){setTbGpsErr("blocked");}      // PERMISSION_DENIED
        else if(e.code===2){setTbGpsErr("unavailable");} // POSITION_UNAVAILABLE
        else if(e.code===3){setTbGpsErr("timeout");}     // TIMEOUT
        else {setTbGpsErr("unknown");}
      },
      {enableHighAccuracy:true,timeout:8000,maximumAge:60000}
    );
  };

  const tbCallSupervisor=()=>{
    if(tbSupPhone){
      window.location.href="tel:"+tbSupPhone;
    } else {
      // No phone — focus the message field
      const f=document.getElementById("tbMsgField");
      if(f){f.focus();f.scrollIntoView({behavior:"smooth",block:"center"});}
    }
  };

  const tbSendMessage=async()=>{
    const txt=(tbMessage||"").trim();
    if(!txt){setTbMessageStatus("err");setTbMessage("");return;}
    setTbMessageSending(true);setTbMessageStatus("");
    const ok=await pushToolboxMessage({
      talk:toolboxOpenTalk,
      workerId:workerId,
      workerName:name,
      message:txt,
      gps:tbGps
    });
    setTbMessageSending(false);
    if(ok){setTbMessageStatus("ok");setTbMessage("");}
    else {setTbMessageStatus("err");}
  };

  // Signature canvas setup — call from ref callback
  const tbInitSigCanvas=(canvas)=>{
    if(!canvas)return;
    tbSigRef.current=canvas;
    const rect=canvas.getBoundingClientRect();
    canvas.width=rect.width;
    canvas.height=rect.height;
    const ctx=canvas.getContext("2d");
    ctx.lineWidth=2.5;ctx.lineCap="round";ctx.lineJoin="round";
    ctx.strokeStyle=isDark?"#f0f2ff":"#111827";
    let drawing=false;
    const getPt=(e)=>{
      const r=canvas.getBoundingClientRect();
      const t=e.touches?e.touches[0]:e;
      return {x:t.clientX-r.left,y:t.clientY-r.top};
    };
    const start=(e)=>{drawing=true;const p=getPt(e);ctx.beginPath();ctx.moveTo(p.x,p.y);};
    const move=(e)=>{
      if(!drawing)return;
      if(e.touches){e.preventDefault();}
      const p=getPt(e);
      ctx.lineTo(p.x,p.y);ctx.stroke();
      tbSigHasInkRef.current=true;
    };
    const end=()=>{drawing=false;};
    canvas.addEventListener("mousedown",start);
    canvas.addEventListener("mousemove",move);
    canvas.addEventListener("mouseup",end);
    canvas.addEventListener("mouseleave",end);
    canvas.addEventListener("touchstart",start,{passive:true});
    canvas.addEventListener("touchmove",move,{passive:false});
    canvas.addEventListener("touchend",end);
  };

  const tbClearSig=()=>{
    const c=tbSigRef.current;if(!c)return;
    const ctx=c.getContext("2d");
    ctx.clearRect(0,0,c.width,c.height);
    tbSigHasInkRef.current=false;
    // Force re-render to update submit button disabled state
    setTbSubmitting(false);
  };

  const tbSubmitSignoff=async()=>{
    if(!tbSigRef.current||!tbSigHasInkRef.current){
      alert("Please sign first");return;
    }
    if(!toolboxOpenTalk)return;
    // v17.30 — warn if submitting without GPS captured
    if(!tbGps){
      const proceed=window.confirm("Sign without location?\n\nYour supervisor will see this was signed without GPS proof of being on site.");
      if(!proceed)return;
    }
    setTbSubmitting(true);
    const sigData=tbSigRef.current.toDataURL("image/png");
    const ok=await pushToolboxSignature({
      talkId:toolboxOpenTalk.id,
      workerId:workerId,
      workerName:name,
      siteName:site,
      signatureData:sigData,
      gps:tbGps,
      answerGiven:tbAnswerGiven
    });
    if(!ok){
      setTbSubmitting(false);
      alert("Submit failed — check connection and try again");
      return;
    }
    // Local cache for replica-lag guard
    tbRecentlySignedRef.current.add(toolboxOpenTalk.id);
    // Update pending list immediately
    setToolboxPending(prev=>prev.filter(t=>t.id!==toolboxOpenTalk.id));
    setTbSignedDone(true);
    // After 3s, go home
    setTimeout(()=>{closeToolboxTalk();},3000);
  };

  const savePinChange=async()=>{
    if(newPin1.length<4){setPinChangeError("PIN must be at least 4 digits");return;}
    if(newPin1!==newPin2){setPinChangeError("PINs don't match — try again");return;}
    const ok=await updateWorkerPin(workerId,newPin1);
    if(!ok){setPinChangeError("Could not save — check connection and try again");return;}
    setNewPin1("");setNewPin2("");setPinChangeError("");setPinStep(1);
    setShowPinChange(false);setShowPinPrompt(false);
    alert("PIN changed. Use your new PIN next time you sign in.");
  };

  const keepTempPin=()=>{
    // They chose to keep the temp PIN. Just dismiss the prompt - it'll show again next sign-in.
    setShowPinPrompt(false);
  };

  // PIN keypad button — used in login + change screens
  const PinPad=({onPress,onClear,onBack})=>{
    const [pressed,setPressed]=useState(null); // tracks which key is held down
    // base styles per key type
    const numBase={padding:"18px 0",background:"#E9EDF2",border:"1.5px solid #DBE0E7",borderRadius:14,fontSize:24,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK,boxSizing:"border-box",outline:"none",WebkitTapHighlightColor:"transparent",WebkitAppearance:"none",appearance:"none",transition:"transform .08s ease, background .08s ease, border-color .08s ease"};
    const actBase={padding:"18px 0",background:"#F1F3F6",border:"1.5px solid #E4E8EE",borderRadius:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK,boxSizing:"border-box",outline:"none",WebkitTapHighlightColor:"transparent",WebkitAppearance:"none",appearance:"none",transition:"transform .08s ease, background .08s ease, border-color .08s ease"};
    const pressStyle={background:"#DCEBFF",border:"2px solid #3B82F6",transform:"scale(0.97)"};
    // press handlers: highlight on down, clear on up/leave
    const down=k=>setPressed(k);
    const up=()=>setPressed(null);
    const keyStyle=(k,base)=>({...base,...(pressed===k?pressStyle:{})});
    return (
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,maxWidth:280,margin:"0 auto"}}>
        {[1,2,3,4,5,6,7,8,9].map(n=>(
          <button key={n} style={keyStyle(String(n),numBase)}
            onMouseDown={()=>down(String(n))} onMouseUp={up} onMouseLeave={up}
            onTouchStart={()=>down(String(n))} onTouchEnd={up}
            onClick={()=>onPress(String(n))}>{n}</button>
        ))}
        <button style={{...keyStyle("clear",actBase),fontSize:14}}
          onMouseDown={()=>down("clear")} onMouseUp={up} onMouseLeave={up}
          onTouchStart={()=>down("clear")} onTouchEnd={up}
          onClick={onClear}>Clear</button>
        <button style={keyStyle("0",numBase)}
          onMouseDown={()=>down("0")} onMouseUp={up} onMouseLeave={up}
          onTouchStart={()=>down("0")} onTouchEnd={up}
          onClick={()=>onPress("0")}>0</button>
        <button style={{...keyStyle("back",actBase),fontSize:18}}
          onMouseDown={()=>down("back")} onMouseUp={up} onMouseLeave={up}
          onTouchStart={()=>down("back")} onTouchEnd={up}
          onClick={onBack}>⌫</button>
      </div>
    );
  };

  // Render PIN dots
  const pinDots=(val,len=6)=>{
    const dots=[];
    for(let i=0;i<len;i++){
      const filled=i<val.length;
      dots.push(<div key={i} style={{width:14,height:14,borderRadius:7,background:filled?"#3B82F6":"transparent",border:"2px solid "+(filled?"#3B82F6":BD),margin:"0 6px",transition:"background .12s ease, border-color .12s ease"}}/>);
    }
    return <div style={{display:"flex",justifyContent:"center",alignItems:"center",margin:"24px 0"}}>{dots}</div>;
  };

  // ── If not logged in, show login screen and bail out before main UI ──
  if(!workerId){
    return (
      <div style={{minHeight:"100vh",background:isDark?BG:"#ffffff",fontFamily:"-apple-system,BlinkMacSystemFont,sans-serif",color:DK,display:"flex",flexDirection:"column"}}>
        <div style={{padding:"32px 16px 0",textAlign:"center"}}>
          <img src={LOGO} style={{width:80,height:80,objectFit:"contain",marginBottom:8}} alt="SiteVouch"/>
          <div style={{fontWeight:800,fontSize:22,marginBottom:4}}>{COMPANY_NAME}</div>
          <div style={{fontSize:13,color:MT,marginBottom:24}}>Enter your PIN to sign in</div>
        </div>
        <div style={{flex:1,padding:"0 24px",maxWidth:380,margin:"0 auto",width:"100%",boxSizing:"border-box"}}>
          {pinDots(pinInput,4)}
          {pinError && <div style={{textAlign:"center",color:RD,fontSize:13,fontWeight:600,marginBottom:14}}>{pinError}</div>}
          {pinChecking && <div style={{textAlign:"center",color:MT,fontSize:13,marginBottom:14}}>Checking…</div>}
          <PinPad
            value={pinInput}
            onPress={d=>{
              if(pinInput.length>=4)return;
              const next=pinInput+d;
              setPinInput(next);setPinError("");
              if(next.length===4){
                setTimeout(()=>{
                  (async()=>{
                    setPinChecking(true);setPinError("");
                    const w=await loginByPin(next);
                    setPinChecking(false);
                    if(!w){setPinError(navigator.onLine?"PIN not recognised. Try again or ask the office.":"Offline — this PIN hasn't been used on this phone yet. Connect once to sign in.");setPinInput("");return;}
                    setWorkerId(w.id);setName(w.name);setWorkerRole(w.role||"worker");
                    setPinInput("");
                    if(w.pin_temporary)setShowPinPrompt(true);
                  })();
                },200);
              }
            }}
            onClear={()=>{setPinInput("");setPinError("");}}
            onBack={()=>{setPinInput(p=>p.slice(0,-1));setPinError("");}}
          />
          <div style={{textAlign:"center",fontSize:12,color:MT,marginTop:32,padding:"0 20px"}}>
            Forgot your PIN? Ask the office to reset it for you.
          </div>
        </div>
        <div style={{padding:"12px 16px",textAlign:"center",fontSize:11,color:MT}}>{BUILD}</div>
      </div>
    );
  }

  // ── SUPERVISOR UI ──
  if(workerRole==="supervisor"){
    const supCard={background:W,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 1px 3px rgba(0,0,0,.08)"};
    const onSite=supWorkers.filter(w=>w.status==="in");
    const offSite=supWorkers.filter(w=>w.status==="out");
    return(
      <div style={{minHeight:"100vh",background:BG,fontFamily:"-apple-system,BlinkMacSystemFont,sans-serif",color:DK}}>
        {/* Header */}
        <div style={{background:W,padding:"6px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid "+BD,position:"sticky",top:0,zIndex:100}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <img src={LOGO} style={{width:44,height:44,objectFit:"contain"}} alt="SiteVouch"/>
            <span style={{fontWeight:800,fontSize:18}}>{COMPANY_NAME}</span>
            <span style={{fontSize:11,background:"#7C3AED",color:W,padding:"2px 8px",borderRadius:8,fontWeight:700}}>SUPERVISOR</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:16,fontWeight:800,color:BL}}>{nowStr}</span>
            <button onClick={()=>setShowSettings(true)} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,padding:"4px 6px"}}>⚙️</button>
          </div>
        </div>

        <div ref={wrapRef} style={{maxWidth:540,margin:"0 auto",padding:"16px 16px 80px"}}>

          {(supSc==="dashboard"||supSc==="sheet"||supSc==="allsheets"||supSc==="onsite"||supSc==="create-talk"||supSc==="signoff"||supSc==="messages"||supSc==="snags"||supSc==="snag-new")&&(
            <button onClick={()=>{
              if(supSc==="sheet")setSupSc(supSelSite?"dashboard":"allsheets");
              else if(supSc==="dashboard")setSupSc("sites");
              else if(supSc==="snag-new")setSupSc("snags");
              else setSupSc("sites");
            }} style={{background:"none",border:"none",cursor:"pointer",color:BL,fontSize:17,fontWeight:800,padding:"0 0 14px",display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:26,lineHeight:1}}>‹</span>
              {supSc==="sheet"?(supSelSite?"Back to Dashboard":"All Pending"):supSc==="dashboard"?supSelSite||"My Sites":supSc==="snag-new"?"Back to Snags":(supSc==="create-talk"||supSc==="signoff"||supSc==="messages"||supSc==="snags")?"Back":"My Sites"}
            </button>
          )}

          {/* ── SITES LIST ── */}
          {supSc==="sites"&&(
            <div>
              <div style={{fontSize:22,fontWeight:800,marginBottom:16}}>{greet}{name?", "+name.split(" ")[0]:""} 👋</div>

              {/* Stat strip — tap Sites to expand/collapse */}
              {!supLoading&&(
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
                  {[
                    {label:"Sites",value:supSites.length,icon:"📍",alert:false,action:()=>setSitesExpanded(p=>!p),active:sitesExpanded},
                    {label:"On Site",value:Object.values(supSiteStats).reduce((a,s)=>a+(s.onSite||0),0),icon:"👷",alert:false,action:()=>setSupSc("onsite"),active:false},
                    {label:"Pending",value:supAllSheets.length,icon:"📋",alert:supAllSheets.length>0,action:()=>supAllSheets.length>0&&setSupSc("allsheets"),active:false},
                  ].map(stat=>(
                    <button key={stat.label} onClick={stat.action} style={{background:W,borderRadius:14,padding:"14px 8px",textAlign:"center",boxShadow:"0 1px 4px rgba(0,0,0,.09)",border:stat.alert?"2px solid "+RD:stat.active?"2px solid "+BL:"2px solid transparent",cursor:"pointer",fontFamily:"inherit",width:"100%"}}>
                      <div style={{fontSize:20,marginBottom:2}}>{stat.icon}</div>
                      <div style={{fontWeight:900,fontSize:26,color:stat.alert?RD:stat.active?BL:DK,lineHeight:1}}>{stat.value}</div>
                      <div style={{fontSize:11,color:MT,marginTop:3,fontWeight:600,textTransform:"uppercase",letterSpacing:".5px"}}>{stat.label}</div>
                    </button>
                  ))}
                </div>
              )}

              {/* Pending sign-off action card */}
              {supAllSheets.length>0&&(
                <button onClick={()=>setSupSc("allsheets")} style={{width:"100%",background:RD,border:"none",borderRadius:14,padding:"16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",fontFamily:"inherit",boxSizing:"border-box"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{background:"rgba(255,255,255,.2)",borderRadius:10,width:42,height:42,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>📋</div>
                    <div style={{textAlign:"left"}}>
                      <div style={{fontWeight:800,fontSize:16,color:W}}>Pending Sign-Off</div>
                      <div style={{fontSize:13,color:"rgba(255,255,255,.8)"}}>{supAllSheets.length} sheet{supAllSheets.length!==1?"s":""} waiting across your sites</div>
                    </div>
                  </div>
                  <span style={{fontSize:22,color:W}}>›</span>
                </button>
              )}
              {supAllSheets.length===0&&!supLoading&&(
                <div style={{background:"#D1FAE5",borderRadius:14,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:18}}>✅</span>
                  <span style={{fontSize:14,fontWeight:700,color:"#065F46"}}>All clear — no sheets pending</span>
                </div>
              )}

              {/* ── v17.65 — TOOLBOX TALKS tile row · v17.66 added Messages ── */}
              {(() => {
                const unreadMsgs = supMsgs.filter(m => m.status!=="read").length;
                return (
                  <>
                    <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:10,padding:"0 4px"}}>Toolbox Talks</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:18}}>
                      <button onClick={openSupCreateTalk} style={{background:W,borderRadius:14,padding:"14px 10px",textAlign:"left",boxShadow:"0 1px 4px rgba(0,0,0,.09)",border:"1.5px solid "+BD,cursor:"pointer",fontFamily:"inherit"}}>
                        <div style={{fontSize:22,marginBottom:6}}>🎬</div>
                        <div style={{fontWeight:800,fontSize:13,color:DK,marginBottom:2}}>Create</div>
                        <div style={{fontSize:10,color:MT,lineHeight:1.35}}>Post a talk</div>
                      </button>
                      <button onClick={loadSignoffTracker} style={{background:W,borderRadius:14,padding:"14px 10px",textAlign:"left",boxShadow:"0 1px 4px rgba(0,0,0,.09)",border:"1.5px solid "+(soNewSigs>0?"#FCA5A5":BD),cursor:"pointer",fontFamily:"inherit",position:"relative"}}>
                        {soNewSigs>0 && <div style={{position:"absolute",top:8,right:8,background:RD,color:W,fontSize:11,fontWeight:800,minWidth:20,height:20,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 5px"}}>{soNewSigs}</div>}
                        <div style={{fontSize:22,marginBottom:6}}>📋</div>
                        <div style={{fontWeight:800,fontSize:13,color:DK,marginBottom:2}}>Tracker</div>
                        <div style={{fontSize:10,color:MT,lineHeight:1.35}}>{soNewSigs>0?soNewSigs+" new signed":"Who's signed"}</div>
                      </button>
                      <button onClick={loadSupMessages} style={{background:W,borderRadius:14,padding:"14px 10px",textAlign:"left",boxShadow:"0 1px 4px rgba(0,0,0,.09)",border:"1.5px solid "+(unreadMsgs>0?"#FCA5A5":BD),cursor:"pointer",fontFamily:"inherit",position:"relative"}}>
                        {unreadMsgs>0 && <div style={{position:"absolute",top:8,right:8,background:RD,color:W,fontSize:11,fontWeight:800,minWidth:20,height:20,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 5px"}}>{unreadMsgs}</div>}
                        <div style={{fontSize:22,marginBottom:6}}>💬</div>
                        <div style={{fontWeight:800,fontSize:13,color:DK,marginBottom:2}}>Messages</div>
                        <div style={{fontSize:10,color:MT,lineHeight:1.35}}>{unreadMsgs>0?unreadMsgs+" new":"From workers"}</div>
                      </button>
                    </div>
                  </>
                );
              })()}

              {/* ── v17.84 — SNAGS tile row ── */}
              <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:10,padding:"0 4px"}}>Snags</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:18}}>
                <button onClick={openSnagForm} style={{background:W,borderRadius:14,padding:"14px 10px",textAlign:"left",boxShadow:"0 1px 4px rgba(0,0,0,.09)",border:"1.5px solid "+BD,cursor:"pointer",fontFamily:"inherit"}}>
                  <div style={{fontSize:22,marginBottom:6}}>📷</div>
                  <div style={{fontWeight:800,fontSize:13,color:DK,marginBottom:2}}>Raise Snag</div>
                  <div style={{fontSize:10,color:MT,lineHeight:1.35}}>Photo + plot</div>
                </button>
                <button onClick={loadSnags} style={{background:W,borderRadius:14,padding:"14px 10px",textAlign:"left",boxShadow:"0 1px 4px rgba(0,0,0,.09)",border:"1.5px solid "+BD,cursor:"pointer",fontFamily:"inherit"}}>
                  <div style={{fontSize:22,marginBottom:6}}>🔧</div>
                  <div style={{fontWeight:800,fontSize:13,color:DK,marginBottom:2}}>View Snags</div>
                  <div style={{fontSize:10,color:MT,lineHeight:1.35}}>Open / fixed</div>
                </button>
              </div>

              {/* ── COLLAPSIBLE SITES LIST ── */}
              {sitesExpanded&&(
                <div style={{marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span>Your Sites</span>
                    <button onClick={()=>setSitesExpanded(false)} style={{background:"none",border:"none",color:MT,fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Hide ▲</button>
                  </div>
                  {supLoading&&<div style={{textAlign:"center",color:MT,padding:20}}>Loading…</div>}
                  {!supLoading&&supSites.length===0&&(
                    <div style={{...supCard,textAlign:"center",color:MT,padding:24}}>No sites assigned yet. Ask the office.</div>
                  )}
                  {supSites.map(s=>{
                    const sitePending=supAllSheets.filter(sh=>sh.site_name===s).length;
                    const siteOnSite=supSiteStats[s]?.onSite||0;
                    return(
                      <button key={s} onClick={()=>loadSiteDashboard(s)} style={{...supCard,width:"100%",textAlign:"left",border:"none",cursor:"pointer",boxSizing:"border-box",padding:"14px 16px"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontWeight:800,fontSize:17}}>{s}</div>
                            {PC[s]&&<div style={{fontSize:13,color:MT,marginTop:2}}>{PC[s]}</div>}
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0,marginLeft:8}}>
                            {siteOnSite>0&&<span style={{background:"#DCFCE7",color:"#166534",fontSize:12,fontWeight:700,padding:"3px 9px",borderRadius:8}}>👷 {siteOnSite}</span>}
                            {sitePending>0&&<span style={{background:RD,color:W,fontSize:12,fontWeight:700,padding:"3px 9px",borderRadius:8}}>{sitePending} pending</span>}
                            <span style={{fontSize:22,color:MT}}>›</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* ── NOTICE BOARD ── */}
              <div style={{marginBottom:12}}>
                <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:10}}>📢 Notice Board</div>

                {/* Compose bar */}
                <div style={{...supCard,padding:12,marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:10}}>
                    <div style={{width:38,height:38,borderRadius:19,background:noticeAvatarColour(name),display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:900,color:"#fff",flexShrink:0,marginTop:2}}>
                      {noticeInitials(name)}
                    </div>
                    <textarea
                      value={supHomeNoticeInput}
                      onChange={e=>setSupHomeNoticeInput(e.target.value)}
                      placeholder="Write a notice to all workers…"
                      rows={4}
                      style={{flex:1,border:"1.5px solid "+BD,borderRadius:10,padding:"10px 12px",fontSize:15,fontFamily:"inherit",background:FA,color:DK,resize:"vertical",outline:"none",minHeight:100,lineHeight:1.5}}
                    />
                  </div>
                  <button
                    onClick={postNoticeFromSupervisor}
                    disabled={!supHomeNoticeInput.trim()||supHomeNoticePosting}
                    style={{width:"100%",padding:"11px",background:supHomeNoticeInput.trim()?BL:"#ccc",color:W,border:"none",borderRadius:10,fontSize:15,fontWeight:700,cursor:supHomeNoticeInput.trim()?"pointer":"default",fontFamily:"inherit"}}>
                    {supHomeNoticePosting?"Posting…":"Post Notice"}
                  </button>
                </div>

                {/* Notice feed */}
                {notices.length===0&&(
                  <div style={{...supCard,textAlign:"center",color:MT,padding:24,fontSize:14}}>No notices posted yet</div>
                )}
                {notices.map(n=>{
                  const poster=n.posted_by||n.author||n.name||"SiteVouch Office";
                  const postedAt=n.posted_at||n.created_at||null;
                  return(
                    <div key={n.id} style={{background:W,marginBottom:10,borderRadius:16,boxShadow:"0 1px 4px rgba(0,0,0,.09)",overflow:"hidden",borderLeft:"4px solid "+BL}}>
                      <div style={{padding:"12px 14px 0",display:"flex",alignItems:"flex-start",gap:10}}>
                        <div style={{width:44,height:44,borderRadius:22,background:noticeAvatarColour(poster),display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:900,color:"#fff",flexShrink:0}}>
                          {noticeInitials(poster)}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap",marginBottom:1}}>
                            <span style={{fontWeight:800,fontSize:15,color:DK}}>{poster}</span>
                            <span style={{color:MT,fontSize:11}}>▶</span>
                            <span style={{fontSize:13,color:MT}}>All Workers</span>
                            <span style={{fontSize:11,fontWeight:700,color:MT,border:"1px solid "+BD,borderRadius:20,padding:"1px 8px",whiteSpace:"nowrap"}}>✓ Sent</span>
                          </div>
                          <div style={{fontSize:12,color:MT}}>{noticeDate(postedAt)}</div>
                        </div>
                      </div>
                      <div style={{padding:"10px 14px 12px"}}>
                        {n.title&&<div style={{fontSize:17,fontWeight:800,color:DK,marginBottom:7}}>{n.title}</div>}
                        <div style={{fontSize:15,color:isDark?"#c8cfe8":"#374151",lineHeight:1.65,whiteSpace:"pre-wrap",
                          ...((n.message||"").length>280&&!expandedNotices[n.id]?{display:"-webkit-box",WebkitLineClamp:8,WebkitBoxOrient:"vertical",overflow:"hidden",whiteSpace:"normal"}:{})}}>
                          {n.message}
                        </div>
                        {(n.message||"").length>280&&(
                          <div style={{textAlign:"center",paddingTop:8}}>
                            <button onClick={()=>setExpandedNotices(p=>({...p,[n.id]:!p[n.id]}))}
                              style={{background:W,border:"1.5px solid "+BD,borderRadius:22,padding:"7px 24px",fontSize:14,fontWeight:700,color:DK,cursor:"pointer",fontFamily:"inherit"}}>
                              {expandedNotices[n.id]?"- Read less":"+ Read more"}
                            </button>
                          </div>
                        )}
                      </div>
                      <div style={{display:"flex",borderTop:"1px solid "+BD}}>
                        <button style={{flex:1,background:"none",border:"none",padding:"11px 8px",fontSize:14,fontWeight:700,color:MT,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>👍 Like</button>
                        <button style={{flex:1,background:"none",borderLeft:"1px solid "+BD,borderTop:"none",borderRight:"none",borderBottom:"none",padding:"11px 8px",fontSize:14,fontWeight:700,color:MT,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>💬 Comment</button>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          )}

          {/* ── ON SITE NOW ── */}
          {supSc==="onsite"&&(
            <div>
              <div style={{fontSize:20,fontWeight:900,marginBottom:4}}>On Site Now</div>
              <div style={{fontSize:13,color:MT,marginBottom:16}}>Workers currently clocked in across your sites</div>
              {(()=>{
                const activeSites=Object.entries(supSiteStats).filter(([,s])=>s.onSite>0);
                if(activeSites.length===0)return(
                  <div style={{...supCard,textAlign:"center",padding:32}}>
                    <div style={{fontSize:36,marginBottom:8}}>👷</div>
                    <div style={{fontWeight:700,color:DK}}>Nobody on site right now</div>
                    <div style={{fontSize:13,color:MT,marginTop:4}}>Check back once the lads clock in</div>
                  </div>
                );
                return activeSites.map(([site,s])=>(
                  <div key={site} style={{...supCard,marginBottom:12}}>
                    <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:10}}>{site}</div>
                    {s.workers.map((w,i)=>(
                      <div key={w} style={{display:"flex",alignItems:"center",gap:10,paddingTop:i>0?10:0,marginTop:i>0?10:0,borderTop:i>0?"1px solid "+BD:"none"}}>
                        <div style={{background:"#DCFCE7",borderRadius:"50%",width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>👷</div>
                        <div style={{fontWeight:700,fontSize:15,flex:1}}>{w}</div>
                        <span style={{background:"#DCFCE7",color:"#166534",fontSize:12,fontWeight:700,padding:"3px 9px",borderRadius:8}}>On Site</span>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          )}

          {/* ── ALL PENDING SHEETS ── */}
          {supSc==="allsheets"&&(
            <div>
              <div style={{fontSize:20,fontWeight:900,marginBottom:4}}>Pending Sign-Off</div>
              <div style={{fontSize:13,color:MT,marginBottom:12}}>{supAllSheets.length} sheet{supAllSheets.length!==1?"s":""} across your sites</div>

              {/* Search */}
              <div style={{position:"relative",marginBottom:16}}>
                <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:16,color:MT}}>🔍</span>
                <input
                  value={supSheetSearch}
                  onChange={e=>setSupSheetSearch(e.target.value)}
                  placeholder="Search by worker or site…"
                  style={{width:"100%",padding:"11px 13px 11px 38px",border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontFamily:"inherit",outline:"none",color:DK,background:W,boxSizing:"border-box"}}
                />
                {supSheetSearch&&<button onClick={()=>setSupSheetSearch("")} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:MT,fontSize:18,cursor:"pointer",padding:0}}>✕</button>}
              </div>

              {(()=>{
                const q=supSheetSearch.toLowerCase().trim();
                const filtered=q?supAllSheets.filter(sh=>sh.worker_name.toLowerCase().includes(q)||sh.site_name.toLowerCase().includes(q)):supAllSheets;
                if(filtered.length===0)return(
                  <div style={{...supCard,textAlign:"center",color:MT,padding:32}}>
                    <div style={{fontSize:32,marginBottom:8}}>{q?"🔍":"✅"}</div>
                    <div style={{fontWeight:700}}>{q?"No results":"All clear"}</div>
                    <div style={{fontSize:13,marginTop:4}}>{q?"Try a different name or site":"No sheets waiting for sign-off"}</div>
                  </div>
                );
                // Group by site
                const bySite={};
                filtered.forEach(sh=>{if(!bySite[sh.site_name])bySite[sh.site_name]=[];bySite[sh.site_name].push(sh);});
                return Object.entries(bySite).map(([site,sheets])=>(
                  <div key={site} style={supCard}>
                    <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:10}}>{site}</div>
                    {sheets.map((sh,i)=>(
                      <button key={sh.id} onClick={()=>{setSupSelSite(sh.site_name);loadSheetDetail(sh);}} style={{width:"100%",textAlign:"left",background:"none",border:"none",cursor:"pointer",padding:0,paddingBottom:i<sheets.length-1?10:0,marginBottom:i<sheets.length-1?10:0,borderBottom:i<sheets.length-1?"1px solid "+BD:"none",fontFamily:"inherit"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <div>
                            <div style={{fontWeight:700,fontSize:15,color:DK}}>{sh.worker_name}</div>
                            <div style={{fontSize:13,color:MT}}>{sh.shift_date?new Date(sh.shift_date).toLocaleDateString("en-GB"):""}{sh.total_mins?" · "+fMin(sh.total_mins):""}</div>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{background:"#FEF3C7",color:"#92400E",fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:8}}>Pending</span>
                            <span style={{color:MT,fontSize:18}}>›</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ));
              })()}
            </div>
          )}

          {/* ── SITE DASHBOARD ── */}
          {supSc==="dashboard"&&supSelSite&&(
            <div>
              <div style={{fontSize:20,fontWeight:900,marginBottom:2}}>{supSelSite}</div>
              <div style={{fontSize:13,color:MT,marginBottom:16}}>{today}</div>
              {supLoading&&<div style={{textAlign:"center",color:MT,padding:20}}>Loading…</div>}
              {!supLoading&&(<>

                {/* Stat strip */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                  <div style={{...supCard,textAlign:"center",marginBottom:0,padding:"12px 8px"}}>
                    <div style={{fontWeight:900,fontSize:24,color:GR}}>{onSite.length}</div>
                    <div style={{fontSize:11,color:MT,fontWeight:600}}>On Site</div>
                  </div>
                  <div style={{...supCard,textAlign:"center",marginBottom:0,padding:"12px 8px"}}>
                    <div style={{fontWeight:900,fontSize:24,color:MT}}>{offSite.length}</div>
                    <div style={{fontSize:11,color:MT,fontWeight:600}}>Left</div>
                  </div>
                  <div style={{...supCard,textAlign:"center",marginBottom:0,padding:"12px 8px"}}>
                    <div style={{fontWeight:900,fontSize:24,color:supSheets.length>0?RD:MT}}>{supSheets.length}</div>
                    <div style={{fontSize:11,color:MT,fontWeight:600}}>Sheets</div>
                  </div>
                </div>

                {/* On site now */}
                <div style={supCard}>
                  <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:10}}>On Site Now</div>
                  {onSite.length===0&&<div style={{color:MT,fontSize:14}}>No one clocked in yet today</div>}
                  {onSite.map((w,i)=>(
                    <div key={w.name} style={{display:"flex",alignItems:"center",justifyContent:"space-between",paddingBottom:i<onSite.length-1?10:0,marginBottom:i<onSite.length-1?10:0,borderBottom:i<onSite.length-1?"1px solid "+BD:"none"}}>
                      <div style={{fontWeight:700,fontSize:15}}>{w.name}</div>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        {w.lastIn&&<span style={{fontSize:12,color:MT}}>{new Date(w.lastIn).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</span>}
                        <span style={{background:"#D1FAE5",color:"#065F46",fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:8}}>● On Site</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Left today */}
                {offSite.length>0&&(
                  <div style={supCard}>
                    <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:10}}>Left Today</div>
                    {offSite.map((w,i)=>(
                      <div key={w.name} style={{display:"flex",alignItems:"center",justifyContent:"space-between",paddingBottom:i<offSite.length-1?10:0,marginBottom:i<offSite.length-1?10:0,borderBottom:i<offSite.length-1?"1px solid "+BD:"none"}}>
                        <div style={{fontWeight:700,fontSize:15,color:MT}}>{w.name}</div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          {w.lastOut&&<span style={{fontSize:12,color:MT}}>{new Date(w.lastOut).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</span>}
                          <span style={{background:"#F3F4F6",color:MT,fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:8}}>Left</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Daywork sheets */}
                <div style={supCard}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                    <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px"}}>Daywork Sheets</div>
                    {supSheets.length>0&&<span style={{background:RD,color:W,fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:8}}>{supSheets.length} pending</span>}
                  </div>
                  {supSheets.length===0&&<div style={{color:MT,fontSize:14}}>No sheets awaiting sign-off</div>}
                  {supSheets.map((sh,i)=>(
                    <button key={sh.id} onClick={()=>loadSheetDetail(sh)} style={{width:"100%",textAlign:"left",background:"none",border:"none",cursor:"pointer",padding:0,paddingBottom:i<supSheets.length-1?10:0,marginBottom:i<supSheets.length-1?10:0,borderBottom:i<supSheets.length-1?"1px solid "+BD:"none",fontFamily:"inherit"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:15,color:DK}}>{sh.worker_name}</div>
                          <div style={{fontSize:13,color:MT}}>{sh.shift_date?new Date(sh.shift_date).toLocaleDateString("en-GB"):""}{sh.total_mins?" · "+fMin(sh.total_mins):""}</div>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{background:"#FEF3C7",color:"#92400E",fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:8}}>Pending</span>
                          <span style={{color:MT,fontSize:18}}>›</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Notice board */}
                <div style={supCard}>
                  <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:10}}>Notice Board</div>
                  <div style={{display:"flex",gap:8,marginBottom:12}}>
                    <input value={supNoticeInput} onChange={e=>setSupNoticeInput(e.target.value)} placeholder="Post a notice to the lads…"
                      style={{flex:1,padding:"10px 12px",border:"1.5px solid "+BD,borderRadius:10,fontSize:14,fontFamily:"inherit",outline:"none",color:DK,background:FA}}/>
                    <button onClick={postNotice} style={{padding:"10px 16px",background:BL,color:W,border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Post</button>
                  </div>
                  {supNotices.length===0&&<div style={{color:MT,fontSize:14}}>No notices posted yet</div>}
                  {supNotices.map(n=>(
                    <div key={n.id} style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,paddingBottom:10,marginBottom:10,borderBottom:"1px solid "+BD}}>
                      <div>
                        <div style={{fontSize:14,color:DK,lineHeight:1.5}}>{n.message}</div>
                        <div style={{fontSize:11,color:MT,marginTop:2}}>{new Date(n.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})+" "+new Date(n.created_at).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</div>
                      </div>
                      <button onClick={()=>deleteNotice(n.id)} style={{background:"none",border:"none",color:MT,cursor:"pointer",fontSize:16,padding:"0 2px",flexShrink:0}}>✕</button>
                    </div>
                  ))}
                </div>

              </>)}
            </div>
          )}

          {/* ── SHEET DETAIL + SIGN-OFF ── */}
          {supSc==="sheet"&&supSelSheet&&(
            <div>
              <div style={{...supCard,marginBottom:12}}>
                <div style={{fontWeight:900,fontSize:17,marginBottom:2}}>{supSelSheet.worker_name}</div>
                <div style={{fontSize:13,color:MT}}>
                  {supSelSheet.shift_date?new Date(supSelSheet.shift_date).toLocaleDateString("en-GB",""):""}&nbsp;·&nbsp;{supSelSite}{supSelSheet.total_mins?" · "+fMin(supSelSheet.total_mins):""}
                </div>
              </div>

              {/* v17.55 — A4 Daywork Sheet preview (replaces card layout). Matches worker preview + office signed-sheet view. */}
              {(()=>{
                if(supSheetEntries.length===0)return <div style={{...supCard,textAlign:"center",color:MT,fontSize:14}}>Loading entries…</div>;
                const mgE=supSheetEntries.filter(e=>e.section==="mg");
                const siE=supSheetEntries.filter(e=>e.section==="si");
                const grp=arr=>{
                  const map={};
                  arr.forEach(e=>{const k=String(e.plot_number).padStart(8,"0");if(!map[k])map[k]={plot:e.plot_number,items:[],mins:0};map[k].items.push(e);map[k].mins+=e.time_spent_mins||0;});
                  return Object.keys(map).sort().map(k=>map[k]);
                };
                const fmt=arr=>grp(arr).map(g=>"PLT "+g.plot+" - "+g.items.map(i=>i.description).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
                const mgTxt=fmt(mgE);
                const siTxt=fmt(siE);
                const mgMins=mgE.reduce((a,e)=>a+(e.time_spent_mins||0),0);
                const siMins=siE.reduce((a,e)=>a+(e.time_spent_mins||0),0);
                const mgH=fMin(mgMins);
                const siH=fMin(siMins);
                const C={border:"1px solid #444",padding:"3px 6px",fontFamily:"Arial,sans-serif",fontSize:11,color:"#111",verticalAlign:"middle",background:"#fff"};
                const CB={...C,background:"#BDD6F1",fontWeight:700};
                const wname=supSelSheet.worker_name||"";
                const sname=supSelSheet.site_name||supSelSite||"";
                const dStr=supSelSheet.shift_date?new Date(supSelSheet.shift_date+"T00:00:00").toLocaleDateString("en-GB"):"";
                const tIn=supSelSheet.time_in||"";
                const tOut=supSelSheet.time_out||"";
                const sNum=supSelSheet.sheet_number||"";
                return (
                  <div style={supCard}>
                    <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:10}}>Daywork Sheet</div>
                    <div style={{width:"100%",overflow:"hidden",position:"relative",height:Math.round(supSheetH)+"px"}}>
                      <div ref={supSheetInnerRef} style={{transformOrigin:"top left",transform:"scale("+scale+")",width:SHEET_W,position:"absolute",top:0,left:0}}>
                        <div style={{width:SHEET_W,background:"#fff",border:"1.5px solid #333",padding:"4px 10px 6px",fontFamily:"Arial,sans-serif",boxSizing:"border-box"}}>
                          <div style={{display:"flex",alignItems:"center",marginBottom:3,gap:12}}>
                            <div style={{flexShrink:0,width:56,height:56}}><img src={LOGO} alt="SiteVouch" style={{width:"100%",height:"100%",objectFit:"contain",display:"block"}}/></div>
                            <div style={{flex:1,textAlign:"center"}}><span style={{fontWeight:900,fontSize:16,letterSpacing:2,textDecoration:"underline",color:"#111"}}>DAYWORK SHEET</span></div>
                            {sNum&&<div style={{textAlign:"right"}}><div style={{fontSize:16,fontWeight:900,color:"#1a3f88"}}>{sNum}</div><div style={{fontSize:9,color:"#888"}}>Sheet Number</div></div>}
                          </div>
                          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                            <tr>
                              <td rowSpan={3} style={{...CB,width:"18%",textAlign:"center",fontSize:9,padding:"1px 3px"}}>SUB-CONTRACTOR NAME</td>
                              <td rowSpan={3} style={{...C,width:"25%",fontSize:13,fontWeight:700,textAlign:"center",padding:"1px 6px"}}>{wname}</td>
                              <td style={{...CB,width:"19%",fontSize:9,padding:"1px 4px"}}>DATE</td>
                              <td style={{...C,width:"38%",fontSize:11,padding:"1px 4px"}}>{dStr}</td>
                            </tr>
                            <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>SITE LOCATION</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{LC[sname]||sname}</td></tr>
                            <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>CONTRACTOR</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{CT[sname]||""}</td></tr>
                          </tbody></table>
                          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                            <tr><td colSpan={2} style={{...CB,fontSize:11,padding:"2px 4px"}}>Work Sheet - Please Include Plot Numbers</td></tr>
                            <tr>
                              <td style={{...C,fontSize:9,color:"#CC0000",fontWeight:700,fontStyle:"italic",padding:"1px 4px"}}>Please confirm how long you spent in each plot next to your plot number</td>
                              <td style={{...C,width:140,textAlign:"right",fontWeight:700,fontSize:14,padding:"1px 4px"}}>Hours: {mgH}</td>
                            </tr>
                            <tr><td colSpan={2} style={{...C,height:380,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{mgTxt}</td></tr>
                          </tbody></table>
                          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                            <tr>
                              <td style={{...CB,fontSize:11,padding:"2px 4px"}}>Site Instruction Works - Please Include Plot Numbers</td>
                              <td style={{...CB,width:140,textAlign:"right",fontSize:14,padding:"2px 4px"}}>Hours: {siH}</td>
                            </tr>
                            <tr><td colSpan={2} style={{...C,height:140,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{siTxt}</td></tr>
                          </tbody></table>
                          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                            <tr><td style={{...CB,fontSize:10,padding:"1px 4px"}}>Materials/Plant/Access Equipment Used</td></tr>
                            <tr><td style={{...C,height:30,verticalAlign:"top",paddingTop:2,fontSize:11}}>&nbsp;</td></tr>
                          </tbody></table>
                          <table style={{width:"45%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                            <tr>
                              <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME IN</td>
                              <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME OUT</td>
                            </tr>
                            <tr>
                              <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{tIn}</td>
                              <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{tOut}</td>
                            </tr>
                          </tbody></table>
                          <table style={{width:"100%",borderCollapse:"collapse"}}><tbody>
                            <tr>
                              <td style={{...CB,width:"28%",fontSize:9,padding:"2px 4px"}}>SiteVouch Supervisor Signature</td>
                              <td style={{...C,width:"20%",padding:"2px 4px"}}>&nbsp;</td>
                              <td style={{...CB,width:"10%",fontSize:9,padding:"2px 4px"}}>Date</td>
                              <td style={{...C,width:"42%",padding:"2px 4px"}}>&nbsp;</td>
                            </tr>
                          </tbody></table>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Supervisor notes */}
              <div style={supCard}>
                <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:8}}>Supervisor Notes (optional)</div>
                <textarea
                  value={supSheetNotes}
                  onChange={e=>setSupSheetNotes(e.target.value)}
                  placeholder="Add any notes before signing off…"
                  rows={3}
                  style={{width:"100%",padding:"10px 12px",border:"1.5px solid "+BD,borderRadius:10,fontSize:14,fontFamily:"inherit",resize:"none",boxSizing:"border-box",outline:"none",color:DK,background:FA}}
                />
              </div>

              {/* Signature canvas */}
              <div style={supCard}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{fontWeight:700,fontSize:12,color:MT,textTransform:"uppercase",letterSpacing:".8px"}}>Your Signature</div>
                  {supSigData&&<button onClick={clearSig} style={{background:"none",border:"none",color:MT,fontSize:13,cursor:"pointer",fontFamily:"inherit",padding:0}}>Clear</button>}
                </div>
                <div style={{border:"1.5px solid "+(supSigData?GR:BD),borderRadius:10,background:FA,overflow:"hidden",position:"relative"}}>
                  {!supSigData&&(
                    <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",color:BD,fontSize:14,fontWeight:600,pointerEvents:"none",userSelect:"none"}}>Sign here</div>
                  )}
                  <canvas
                    ref={supSigRef}
                    width={600}
                    height={160}
                    style={{width:"100%",height:160,display:"block",touchAction:"none",cursor:"crosshair"}}
                    onMouseDown={sigStart}
                    onMouseMove={sigMove}
                    onMouseUp={sigEnd}
                    onMouseLeave={sigEnd}
                    onTouchStart={sigStart}
                    onTouchMove={sigMove}
                    onTouchEnd={sigEnd}
                  />
                </div>
                {!supSigData&&<div style={{fontSize:12,color:MT,marginTop:6,textAlign:"center"}}>Draw your signature above to enable sign-off</div>}
              </div>

              {/* Sign off button */}
              <button
                onClick={signOffSheet}
                disabled={!supSigData||supSigning}
                style={{width:"100%",padding:16,background:supSigData&&!supSigning?GR:"#D1D5DB",color:W,border:"none",borderRadius:14,fontSize:17,fontWeight:800,cursor:supSigData&&!supSigning?"pointer":"not-allowed",fontFamily:"inherit",marginBottom:8,transition:"background .2s"}}
              >{supSigning?"Signing off…":"✅ Sign Off Sheet"}</button>
              <button onClick={()=>setSupSc("dashboard")} style={{width:"100%",padding:14,background:W,color:DK,border:"1.5px solid "+BD,borderRadius:14,fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          )}

          {/* ── v17.65 — CREATE TOOLBOX TALK SCREEN ── */}
          {supSc==="create-talk" && (
            <div>
              {ctSuccess ? (
                <div style={{...supCard,textAlign:"center",padding:28}}>
                  <div style={{fontSize:48,marginBottom:8}}>✅</div>
                  <div style={{fontSize:20,fontWeight:800,color:DK,marginBottom:6}}>Toolbox Talk Posted</div>
                  <div style={{fontSize:13,color:MT,marginBottom:20,lineHeight:1.5}}>Workers clocked in at the selected site{ctSelSites.length>1?"s":""} will see an alert on their home screen.</div>
                  <button onClick={openSupCreateTalk} style={{width:"100%",padding:14,background:BL,color:W,border:"none",borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:10}}>Post another</button>
                  <button onClick={()=>setSupSc("sites")} style={{width:"100%",padding:14,background:W,color:DK,border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Back to home</button>
                </div>
              ) : (
                <>
                  <div style={{fontSize:22,fontWeight:800,marginBottom:4,color:DK}}>🎬 Create Toolbox Talk</div>
                  <div style={{fontSize:13,color:MT,marginBottom:16}}>Post a video + question. Workers must watch, answer, and sign off.</div>

                  {/* v17.80 — Preset quick-fill */}
                  <div style={{...supCard,marginBottom:16}}>
                    <label style={{display:"block",fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".7px",marginBottom:8}}>⚡ Quick-fill from preset</label>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {TOOLBOX_PRESETS.map(p=>(
                        <button key={p.key} onClick={()=>applyToolboxPreset(p)} style={{padding:"9px 14px",background:W,color:DK,border:"1.5px solid "+BD,borderRadius:999,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{p.label}</button>
                      ))}
                    </div>
                    <div style={{fontSize:11,color:MT,marginTop:10,lineHeight:1.5}}>Fills the title, question and answers. Add your own video below, then pick sites and post.</div>
                  </div>

                  {/* Title */}
                  <div style={supCard}>
                    <label style={{display:"block",fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".7px",marginBottom:6}}>Title *</label>
                    <input value={ctTitle} onChange={e=>setCtTitle(e.target.value)} placeholder="e.g. Working at Height — Ladder Safety" style={{width:"100%",padding:12,fontSize:15,border:"1px solid "+BD,borderRadius:10,fontFamily:"inherit",boxSizing:"border-box",background:F2,color:DK,outline:"none"}}/>
                    <label style={{display:"block",fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".7px",marginBottom:6,marginTop:14}}>Description</label>
                    <textarea value={ctDesc} onChange={e=>setCtDesc(e.target.value)} placeholder="Short summary workers see before the video" rows={2} style={{width:"100%",padding:12,fontSize:14,border:"1px solid "+BD,borderRadius:10,fontFamily:"inherit",boxSizing:"border-box",background:F2,color:DK,resize:"vertical",outline:"none"}}/>
                  </div>

                  {/* Video */}
                  <div style={supCard}>
                    <div style={{fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".7px",marginBottom:10}}>Video *</div>
                    <div style={{display:"flex",gap:6,marginBottom:12}}>
                      <button onClick={()=>setCtVideoMode("url")} style={{flex:1,padding:"10px",background:ctVideoMode==="url"?BL:W,color:ctVideoMode==="url"?W:DK,border:"1.5px solid "+(ctVideoMode==="url"?BL:BD),borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Paste URL</button>
                      <button onClick={()=>setCtVideoMode("upload")} style={{flex:1,padding:"10px",background:ctVideoMode==="upload"?BL:W,color:ctVideoMode==="upload"?W:DK,border:"1.5px solid "+(ctVideoMode==="upload"?BL:BD),borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Upload mp4</button>
                    </div>
                    {ctVideoMode==="url" ? (
                      <>
                        <input value={ctVideoUrl} onChange={e=>setCtVideoUrl(e.target.value)} placeholder="YouTube or direct mp4 URL" style={{width:"100%",padding:12,fontSize:14,border:"1px solid "+BD,borderRadius:10,fontFamily:"inherit",boxSizing:"border-box",background:F2,color:DK,outline:"none"}}/>
                        <div style={{fontSize:11,color:MT,marginTop:6,lineHeight:1.4}}>YouTube embeds work but skip the scrub blocker. Direct mp4 enforces watch-to-end.</div>
                      </>
                    ) : (
                      <>
                        <label style={{display:"block",padding:"14px 12px",background:F2,border:"1.5px dashed "+BD,borderRadius:10,textAlign:"center",cursor:"pointer",fontSize:13,fontWeight:700,color:ctVideoFile?DK:MT}}>
                          {ctVideoFile ? "📹 "+ctVideoFile.name+" ("+Math.round(ctVideoFile.size/1024)+" KB)" : "Tap to pick mp4 from phone"}
                          <input type="file" accept="video/mp4,video/*" onChange={e=>setCtVideoFile(e.target.files&&e.target.files[0]||null)} style={{display:"none"}}/>
                        </label>
                        <div style={{fontSize:11,color:MT,marginTop:6,lineHeight:1.4}}>Uploaded to Supabase. Workers can't skip ahead.</div>
                      </>
                    )}
                  </div>

                  {/* Question */}
                  <div style={supCard}>
                    <label style={{display:"block",fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".7px",marginBottom:6}}>Question *</label>
                    <input value={ctQuestion} onChange={e=>setCtQuestion(e.target.value)} placeholder="What workers must answer after the video" style={{width:"100%",padding:12,fontSize:15,border:"1px solid "+BD,borderRadius:10,fontFamily:"inherit",boxSizing:"border-box",background:F2,color:DK,outline:"none",marginBottom:14}}/>
                    {[["a",ctOptA,setCtOptA],["b",ctOptB,setCtOptB],["c",ctOptC,setCtOptC]].map(([letter,val,setter])=>(
                      <div key={letter} style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                        <button onClick={()=>setCtCorrect(letter)} title="Mark as correct" style={{width:34,height:34,borderRadius:"50%",border:"2px solid "+(ctCorrect===letter?GR:BD),background:ctCorrect===letter?GR:W,color:ctCorrect===letter?W:DK,fontWeight:800,fontSize:14,cursor:"pointer",fontFamily:"inherit",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>{ctCorrect===letter?"✓":letter.toUpperCase()}</button>
                        <input value={val} onChange={e=>setter(e.target.value)} placeholder={"Option "+letter.toUpperCase()} style={{flex:1,padding:12,fontSize:14,border:"1px solid "+BD,borderRadius:10,fontFamily:"inherit",boxSizing:"border-box",background:F2,color:DK,outline:"none"}}/>
                      </div>
                    ))}
                    <div style={{fontSize:11,color:MT,marginTop:6,lineHeight:1.4}}>Tap the circle beside the correct option (it turns green ✓).</div>
                  </div>

                  {/* Sites */}
                  <div style={supCard}>
                    <div style={{fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".7px",marginBottom:10}}>Assign to sites *</div>
                    {supSites.length===0 ? (
                      <div style={{fontSize:13,color:MT,padding:"8px 0"}}>No sites assigned to you. Ask the office.</div>
                    ) : (
                      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                        {supSites.map(s=>{
                          const on = ctSelSites.includes(s);
                          return (
                            <button key={s} onClick={()=>toggleCtSite(s)} style={{padding:"8px 12px",borderRadius:999,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",background:on?GR:W,color:on?W:DK,border:"1.5px solid "+(on?GR:BD)}}>{on?"✓ ":""}{s}</button>
                          );
                        })}
                      </div>
                    )}
                    {ctSelSites.length>0 && <div style={{fontSize:11,color:MT,marginTop:10}}>{ctSelSites.length} site{ctSelSites.length===1?"":"s"} selected</div>}
                  </div>

                  {ctError && (
                    <div style={{background:"#FEF2F2",border:"1.5px solid #FCA5A5",borderRadius:12,padding:"12px 14px",marginBottom:14,fontSize:13,fontWeight:600,color:"#991B1B"}}>⚠ {ctError}</div>
                  )}

                  <button onClick={submitCreateTalk} disabled={ctSubmitting} style={{width:"100%",padding:16,background:ctSubmitting?"#94A3B8":BL,color:W,border:"none",borderRadius:14,fontSize:16,fontWeight:800,cursor:ctSubmitting?"not-allowed":"pointer",fontFamily:"inherit",marginBottom:10}}>
                    {ctSubmitting ? (ctVideoMode==="upload" ? "Uploading video…" : "Posting…") : "📤 Post Toolbox Talk"}
                  </button>
                  <button onClick={()=>setSupSc("sites")} style={{width:"100%",padding:14,background:W,color:DK,border:"1.5px solid "+BD,borderRadius:14,fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                </>
              )}
            </div>
          )}

          {/* ── v17.65 — SIGN-OFF TRACKER SCREEN · v17.66 date filters ── */}
          {supSc==="signoff" && (
            <div>
              <div style={{fontSize:22,fontWeight:800,marginBottom:4,color:DK}}>📋 Sign-off Tracker</div>
              <div style={{fontSize:13,color:MT,marginBottom:14}}>{workerRole==="admin"?"All talks across all supervisors.":"Talks you posted. Tap one to see signed vs pending per site."}</div>

              {/* Date filter pills */}
              {!soLoading && soTalks.length>0 && (() => {
                const pillBase={padding:"7px 14px",borderRadius:999,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"};
                const on={...pillBase,background:BL,color:W,border:"1.5px solid "+BL};
                const off={...pillBase,background:W,color:MT,border:"1.5px solid "+BD};
                return (
                  <div style={{display:"flex",gap:6,marginBottom:14}}>
                    <button onClick={()=>setSoDateFilter("today")} style={soDateFilter==="today"?on:off}>Today</button>
                    <button onClick={()=>setSoDateFilter("week")} style={soDateFilter==="week"?on:off}>Last 7 days</button>
                    <button onClick={()=>setSoDateFilter("all")} style={soDateFilter==="all"?on:off}>All</button>
                  </div>
                );
              })()}

              {soLoading && <div style={{textAlign:"center",color:MT,padding:30}}>Loading…</div>}
              {!soLoading && soTalks.length===0 && (
                <div style={{...supCard,textAlign:"center",padding:24}}>
                  <div style={{fontSize:32,marginBottom:8}}>📭</div>
                  <div style={{fontWeight:800,fontSize:15,color:DK,marginBottom:4}}>No talks posted yet</div>
                  <div style={{fontSize:12,color:MT,lineHeight:1.5}}>Use Create Talk on the home screen to post your first one.</div>
                </div>
              )}

              {!soLoading && (() => {
                // Apply date filter
                const now=new Date();
                const todayStart=new Date(now); todayStart.setHours(0,0,0,0);
                const weekStart=new Date(now); weekStart.setDate(weekStart.getDate()-7);
                const dateFiltered = soTalks.filter(t=>{
                  if(soDateFilter==="all") return true;
                  if(!t.posted_at) return false;
                  const p=new Date(t.posted_at);
                  if(soDateFilter==="today") return p>=todayStart;
                  if(soDateFilter==="week") return p>=weekStart;
                  return true;
                });
                if(soTalks.length>0 && dateFiltered.length===0){
                  return (
                    <div style={{...supCard,textAlign:"center",padding:20,color:MT,fontSize:13}}>
                      <div style={{fontSize:24,marginBottom:6}}>🔍</div>
                      <div style={{fontWeight:700,color:DK,marginBottom:2}}>No talks in this range</div>
                      <div style={{fontSize:12}}>Try a wider date filter</div>
                    </div>
                  );
                }
                return dateFiltered.map(talk => {
                const isOpen = soOpenTalkId === talk.id;
                const sigs = soSigsByTalk[talk.id] || [];
                const onSite = soOnSiteByTalk[talk.id] || {};
                return (
                  <div key={talk.id} style={{...supCard,padding:0,overflow:"hidden"}}>
                    <button onClick={()=>openSignoffTalk(talk)} style={{width:"100%",textAlign:"left",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",padding:"14px 16px"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:15,fontWeight:800,color:DK,marginBottom:4}}>{talk.title}</div>
                          <div style={{fontSize:11,color:MT}}>
                            {(talk.assigned_sites||[]).length} site{(talk.assigned_sites||[]).length===1?"":"s"} · posted by {talk.posted_by||"—"}
                            {talk.posted_at && " · "+new Date(talk.posted_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}
                          </div>
                        </div>
                        <span style={{fontSize:18,color:MT,transform:isOpen?"rotate(180deg)":"none",transition:"transform .15s"}}>▾</span>
                      </div>
                    </button>

                    {isOpen && (
                      <div style={{padding:"0 16px 16px",borderTop:"1px solid "+BD}}>
                        {soDetailLoading ? (
                          <div style={{textAlign:"center",color:MT,padding:20,fontSize:13}}>Loading…</div>
                        ) : (talk.assigned_sites||[]).length===0 ? (
                          <div style={{fontSize:13,color:MT,padding:14,textAlign:"center"}}>No sites assigned to this talk.</div>
                        ) : (talk.assigned_sites||[]).map(site=>{
                          // v17.79 — tolerant site match (case/space-insensitive) so signatures
                          // always surface even if the stored site string differs slightly.
                          const norm=(x)=>(x||"").trim().toLowerCase();
                          const workersOnSite = onSite[site] || [];
                          // All signatures recorded for THIS site on this talk — independent of clock-in.
                          const siteSigs = sigs.filter(s=>norm(s.site_name)===norm(site));
                          const signedIds = new Set(siteSigs.map(s=>s.worker_id));
                          // Pending = clocked in today on this site but no signature yet.
                          const pendingOnSite = workersOnSite.filter(w=>!signedIds.has(w.id));
                          return (
                            <div key={site} style={{paddingTop:14}}>
                              <div style={{fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".7px",marginBottom:8}}>{site}</div>
                              {siteSigs.length===0 && pendingOnSite.length===0 && (
                                <div style={{fontSize:12,color:MT,padding:"6px 0"}}>No signatures yet, no workers clocked in today.</div>
                              )}
                              {/* Signed — every signature for this site, clocked in or not */}
                              {siteSigs.map(s=>(
                                <div key={"s"+s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",fontSize:13,color:DK}}>
                                  <span style={{fontSize:14,color:GR}}>✓</span>
                                  <span style={{fontWeight:600}}>{s.worker_name}</span>
                                  <span style={{fontSize:11,color:MT,marginLeft:"auto"}}>signed {s.signed_at?new Date(s.signed_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"}):""}</span>
                                </div>
                              ))}
                              {/* Pending — clocked in today but not yet signed */}
                              {pendingOnSite.map(w=>(
                                <div key={"p"+w.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",fontSize:13,color:DK}}>
                                  <span style={{fontSize:14,color:"#F59E0B"}}>⏳</span>
                                  <span style={{fontWeight:600}}>{w.name}</span>
                                  <span style={{fontSize:11,color:"#92400E",marginLeft:"auto"}}>pending — on site</span>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              });
              })()}
            </div>
          )}

          {/* ── v17.84 — SNAGS LIST ── */}
          {supSc==="snags" && (
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                <div>
                  <div style={{fontSize:22,fontWeight:800,color:DK}}>🔧 Snags</div>
                  <div style={{fontSize:13,color:MT}}>{snagList.length} snag{snagList.length!==1?"s":""}{workerRole==="admin"?" · all sites":""}</div>
                </div>
                <button onClick={openSnagForm} style={{padding:"10px 14px",background:BL,color:W,border:"none",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ Raise</button>
              </div>

              {/* Filter pills */}
              <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
                {[["open","Open"],["in_progress","In progress"],["fixed","Fixed"],["all","All"]].map(([k,lbl])=>(
                  <button key={k} onClick={()=>setSnagStatusFilter(k)} style={{padding:"6px 12px",borderRadius:999,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",border:"1.5px solid "+BD,background:snagStatusFilter===k?BL:W,color:snagStatusFilter===k?W:MT}}>{lbl}</button>
                ))}
              </div>

              {snagLoading && <div style={{textAlign:"center",color:MT,padding:30}}>Loading…</div>}
              {!snagLoading && (()=>{
                const visible = snagStatusFilter==="all" ? snagList : snagList.filter(s=>s.status===snagStatusFilter);
                if (visible.length===0) {
                  return (
                    <div style={{...supCard,textAlign:"center",padding:24}}>
                      <div style={{fontSize:32,marginBottom:8}}>✅</div>
                      <div style={{fontWeight:800,fontSize:15,color:DK,marginBottom:4}}>No {snagStatusFilter==="all"?"":snagStatusFilter.replace("_"," ")} snags</div>
                      <div style={{fontSize:12,color:MT,lineHeight:1.5}}>Tap + Raise to log one.</div>
                    </div>
                  );
                }
                return visible.map(s=>{
                  const priColour = s.priority==="high"?"#DC2626":s.priority==="med"?"#F59E0B":"#16A34A";
                  const statColour = s.status==="open"?"#DC2626":s.status==="in_progress"?"#F59E0B":s.status==="fixed"?"#16A34A":"#6B7280";
                  const photo = s.annotated_photo_url || s.photo_url;
                  return (
                    <div key={s.id} style={{...supCard,padding:"12px 14px",marginBottom:10}}>
                      <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                        {photo && (
                          <img src={photo} alt="" onClick={()=>setSnagPhotoView(photo)} style={{width:64,height:64,objectFit:"cover",borderRadius:8,cursor:"pointer",flexShrink:0,background:F2}}/>
                        )}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:4}}>
                            <span style={{background:statColour,color:W,fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:6,textTransform:"uppercase",letterSpacing:".5px"}}>{s.status.replace("_"," ")}</span>
                            <span style={{background:priColour,color:W,fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:6,textTransform:"uppercase",letterSpacing:".5px"}}>{s.priority}</span>
                          </div>
                          <div style={{fontWeight:800,fontSize:14,color:DK,marginBottom:2}}>{s.site_name} · Plot {s.plot_number}</div>
                          <div style={{fontSize:12,color:MT,marginBottom:4}}>{s.area}</div>
                          <div style={{fontSize:13,color:DK,lineHeight:1.4,marginBottom:6,whiteSpace:"pre-wrap"}}>{s.description}</div>
                          <div style={{fontSize:11,color:MT,marginBottom:8}}>
                            {s.raised_by_name} · {s.raised_at?new Date(s.raised_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"}):""}
                            {s.due_date && <span> · due {new Date(s.due_date).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</span>}
                          </div>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            {s.status==="open" && <button onClick={()=>updateSnagStatus(s.id,"in_progress")} style={{padding:"6px 10px",background:W,color:"#F59E0B",border:"1.5px solid #F59E0B",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>In progress</button>}
                            {(s.status==="open"||s.status==="in_progress") && <button onClick={()=>updateSnagStatus(s.id,"fixed")} style={{padding:"6px 10px",background:W,color:"#16A34A",border:"1.5px solid #16A34A",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Mark fixed</button>}
                            {s.status==="fixed" && <button onClick={()=>updateSnagStatus(s.id,"closed")} style={{padding:"6px 10px",background:W,color:"#6B7280",border:"1.5px solid #6B7280",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Close</button>}
                            {s.status==="fixed" && <button onClick={()=>updateSnagStatus(s.id,"open")} style={{padding:"6px 10px",background:W,color:MT,border:"1.5px solid "+BD,borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Reopen</button>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}

              {/* Full-size photo viewer */}
              {snagPhotoView && (
                <div onClick={()=>setSnagPhotoView(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.92)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:20,cursor:"pointer"}}>
                  <img src={snagPhotoView} alt="" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
                  <button onClick={(e)=>{e.stopPropagation();setSnagPhotoView(null);}} style={{position:"absolute",top:20,right:20,background:W,border:"none",borderRadius:"50%",width:40,height:40,fontSize:20,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                </div>
              )}
            </div>
          )}

          {/* ── v17.84 — RAISE SNAG FORM ── */}
          {supSc==="snag-new" && (
            <div>
              <div style={{fontSize:22,fontWeight:800,marginBottom:4,color:DK}}>📷 Raise a Snag</div>
              <div style={{fontSize:13,color:MT,marginBottom:14}}>Log an issue with photo and plot details.</div>

              <div style={supCard}>
                <div style={{fontSize:12,color:MT,fontWeight:700,marginBottom:4}}>SITE</div>
                <select value={snagForm.site} onChange={e=>setSnagForm(p=>({...p,site:e.target.value}))} style={{width:"100%",padding:12,fontSize:15,borderRadius:10,border:"1.5px solid "+BD,background:W,color:DK,marginBottom:12,fontFamily:"inherit"}}>
                  <option value="">— select site —</option>
                  {supSites.map(s=><option key={s} value={s}>{s}</option>)}
                </select>

                <div style={{fontSize:12,color:MT,fontWeight:700,marginBottom:4}}>PLOT NUMBER</div>
                <input value={snagForm.plot} onChange={e=>setSnagForm(p=>({...p,plot:e.target.value}))} placeholder="e.g. 47" style={{width:"100%",padding:12,fontSize:15,borderRadius:10,border:"1.5px solid "+BD,background:W,color:DK,marginBottom:12,fontFamily:"inherit",boxSizing:"border-box"}}/>

                <div style={{fontSize:12,color:MT,fontWeight:700,marginBottom:4}}>AREA</div>
                <select value={snagForm.area} onChange={e=>setSnagForm(p=>({...p,area:e.target.value}))} style={{width:"100%",padding:12,fontSize:15,borderRadius:10,border:"1.5px solid "+BD,background:W,color:DK,marginBottom:12,fontFamily:"inherit"}}>
                  <option value="">— select area —</option>
                  {["Kitchen","Bathroom","Living Room","Bedroom","Hall","Stairs","Garage","External","Other"].map(a=><option key={a}>{a}</option>)}
                </select>

                {snagForm.area==="Other" && (
                  <input value={snagForm.areaOther} onChange={e=>setSnagForm(p=>({...p,areaOther:e.target.value}))} placeholder="Specify area" style={{width:"100%",padding:12,fontSize:15,borderRadius:10,border:"1.5px solid "+BD,background:W,color:DK,marginBottom:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                )}

                <div style={{fontSize:12,color:MT,fontWeight:700,marginBottom:4}}>DESCRIPTION</div>
                <textarea value={snagForm.desc} onChange={e=>setSnagForm(p=>({...p,desc:e.target.value}))} placeholder="What's the snag?" style={{width:"100%",padding:12,fontSize:15,borderRadius:10,border:"1.5px solid "+BD,background:W,color:DK,marginBottom:12,fontFamily:"inherit",boxSizing:"border-box",minHeight:80,resize:"vertical"}}/>

                <div style={{fontSize:12,color:MT,fontWeight:700,marginBottom:6}}>PRIORITY</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:12}}>
                  {[["low","Low","#16A34A"],["med","Medium","#F59E0B"],["high","High","#DC2626"]].map(([k,lbl,col])=>(
                    <button key={k} onClick={()=>setSnagForm(p=>({...p,priority:k}))} style={{padding:10,fontSize:13,fontWeight:700,borderRadius:8,cursor:"pointer",fontFamily:"inherit",border:"1.5px solid "+col,background:snagForm.priority===k?col:W,color:snagForm.priority===k?W:col}}>{lbl}</button>
                  ))}
                </div>

                <div style={{fontSize:12,color:MT,fontWeight:700,marginBottom:4}}>DUE DATE (optional)</div>
                <input type="date" value={snagForm.due} onChange={e=>setSnagForm(p=>({...p,due:e.target.value}))} style={{width:"100%",padding:12,fontSize:15,borderRadius:10,border:"1.5px solid "+BD,background:W,color:DK,marginBottom:12,fontFamily:"inherit",boxSizing:"border-box"}}/>

                <div style={{fontSize:12,color:MT,fontWeight:700,marginBottom:4}}>PHOTO <span style={{color:RD}}>*</span></div>
                <input type="file" accept="image/*" capture="environment" onChange={handleSnagPhoto} style={{width:"100%",padding:10,fontSize:13,borderRadius:10,border:"1.5px solid "+BD,background:W,color:DK,marginBottom:8,fontFamily:"inherit",boxSizing:"border-box"}}/>
                {snagForm.photoPreview && (
                  <>
                    <img src={snagForm.photoPreview} alt="" style={{width:"100%",maxHeight:200,objectFit:"contain",borderRadius:8,background:F2,marginBottom:8}}/>
                    <button onClick={openAnnotator} style={{width:"100%",padding:12,background:W,color:BL,border:"1.5px solid "+BL,borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:12}}>✏️ Annotate Photo</button>
                  </>
                )}

                {snagMsg && <div style={{padding:10,background:"#FEE2E2",color:"#991B1B",borderRadius:8,fontSize:13,marginBottom:12,fontWeight:600}}>{snagMsg}</div>}

                <button onClick={submitSnag} disabled={snagSubmitting} style={{width:"100%",padding:14,background:snagSubmitting?MT:BL,color:W,border:"none",borderRadius:12,fontSize:15,fontWeight:700,cursor:snagSubmitting?"default":"pointer",fontFamily:"inherit",marginBottom:8}}>
                  {snagSubmitting?"Submitting…":"Submit Snag"}
                </button>
                <button onClick={()=>setSupSc("snags")} style={{width:"100%",padding:12,background:W,color:DK,border:"1.5px solid "+BD,borderRadius:12,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              </div>
            </div>
          )}

          {/* ── v17.85 — ANNOTATOR MODAL ── */}
          {annoOpen && (
            <div style={{position:"fixed",inset:0,background:"#000",zIndex:500,display:"flex",flexDirection:"column"}}>
              <div style={{flex:1,overflow:"auto",display:"flex",alignItems:"center",justifyContent:"center",background:"#000"}}>
                <canvas
                  ref={annoCanvasRef}
                  onPointerDown={onAnnoStart}
                  onPointerMove={onAnnoMove}
                  onPointerUp={onAnnoEnd}
                  onPointerCancel={onAnnoEnd}
                  style={{maxWidth:"100%",maxHeight:"100%",touchAction:"none",display:"block"}}
                />
              </div>
              <div style={{background:"#1e293b",padding:8,display:"flex",flexDirection:"column",gap:8}}>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"center"}}>
                  {[["arrow","↗"],["line","—"],["circle","○"],["rect","▭"],["free","✎"],["text","T"]].map(([t,lbl])=>(
                    <button key={t} onClick={()=>setAnnoTool(t)} style={{flex:1,minWidth:48,padding:"10px 4px",fontSize:13,background:annoTool===t?"#3b82f6":"#334155",border:"none",color:"#fff",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>{lbl}</button>
                  ))}
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"center"}}>
                  {["#FF0000","#FF8800","#FFEE00","#00CC00","#0088FF","#FFFFFF","#000000"].map(c=>(
                    <button key={c} onClick={()=>setAnnoColor(c)} style={{width:32,height:32,borderRadius:"50%",background:c,border:annoColor===c?"3px solid #fff":"2px solid #475569",cursor:"pointer",padding:0}}/>
                  ))}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"0 8px",color:"#94a3b8",fontSize:12}}>
                  <span>Size</span>
                  <input type="range" min="2" max="20" value={annoSize} onChange={e=>setAnnoSize(parseInt(e.target.value))} style={{flex:1}}/>
                  <span>{annoSize}</span>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"center"}}>
                  <button onClick={undoAnno} style={{flex:1,padding:10,fontSize:13,background:"#334155",border:"none",color:"#fff",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>↶ Undo</button>
                  <button onClick={clearAnno} style={{flex:1,padding:10,fontSize:13,background:"#dc2626",border:"none",color:"#fff",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Clear</button>
                  <button onClick={()=>setAnnoOpen(false)} style={{flex:1,padding:10,fontSize:13,background:"#475569",border:"none",color:"#fff",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Cancel</button>
                  <button onClick={saveAnno} style={{flex:1,padding:10,fontSize:13,background:"#16a34a",border:"none",color:"#fff",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>✓ Save</button>
                </div>
              </div>
              {annoTextPos && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.8)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                  <div style={{background:W,borderRadius:14,padding:16,width:"100%",maxWidth:380}}>
                    <div style={{fontSize:12,color:MT,fontWeight:700,marginBottom:6}}>TEXT</div>
                    <input value={annoTextValue} onChange={e=>setAnnoTextValue(e.target.value)} autoFocus style={{width:"100%",padding:12,fontSize:15,borderRadius:8,border:"1.5px solid "+BD,background:W,color:DK,marginBottom:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>{setAnnoTextPos(null);setAnnoTextValue("");}} style={{flex:1,padding:12,background:W,color:DK,border:"1.5px solid "+BD,borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                      <button onClick={confirmAnnoText} style={{flex:1,padding:12,background:BL,color:W,border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Add</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── v17.66 — SUPERVISOR MESSAGES INBOX (toolbox replies from workers) ── */}
          {supSc==="messages" && (
            <div>
              <div style={{fontSize:22,fontWeight:800,marginBottom:4,color:DK}}>💬 Messages</div>
              <div style={{fontSize:13,color:MT,marginBottom:12}}>{workerRole==="admin"?"All worker messages from toolbox talks.":"Workers who messaged you from a toolbox talk."}</div>

              {/* v17.67 — filter: unread only + 30-day cutoff */}
              {(()=>{
                const cutoff=new Date(Date.now()-30*24*60*60*1000);
                const within30=supMsgs.filter(m=>m.created_at&&new Date(m.created_at)>=cutoff);
                const visible=supMsgShowRead?within30:within30.filter(m=>m.status!=="read");
                const readCount=within30.filter(m=>m.status==="read").length;
                return (
                  <>
                    {/* Show read toggle — only if there are read messages */}
                    {readCount>0&&(
                      <button onClick={()=>setSupMsgShowRead(p=>!p)} style={{marginBottom:14,padding:"7px 14px",borderRadius:999,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",border:"1.5px solid "+BD,background:supMsgShowRead?BL:W,color:supMsgShowRead?W:MT}}>
                        {supMsgShowRead?"Hide read ("+readCount+")":"Show read ("+readCount+")"}
                      </button>
                    )}

                    {supMsgLoading && <div style={{textAlign:"center",color:MT,padding:30}}>Loading…</div>}
                    {!supMsgLoading && visible.length===0 && (
                      <div style={{...supCard,textAlign:"center",padding:24}}>
                        <div style={{fontSize:32,marginBottom:8}}>📭</div>
                        <div style={{fontWeight:800,fontSize:15,color:DK,marginBottom:4}}>{within30.length===0?"No messages":"All caught up"}</div>
                        <div style={{fontSize:12,color:MT,lineHeight:1.5}}>{within30.length===0?'When a worker taps "contact supervisor" inside a talk, it lands here.':"No unread messages. Tap \"Show read\" to see older ones."}</div>
                      </div>
                    )}

                    {!supMsgLoading && visible.map(m=>{
                      const isUnread = m.status!=="read";
                      let talkTitle="", body=m.note||"";
                      const tm=(m.note||"").match(/^\[Toolbox Talk:\s*(.*?)\]/);
                      if(tm) talkTitle=tm[1];
                      const nl=(m.note||"").indexOf("\n");
                      if(nl>-1) body=(m.note||"").slice(nl+1).trim();
                      return (
                        <div key={m.id} style={{...supCard,padding:"14px 16px",border:isUnread?"1.5px solid #FCA5A5":"1px solid "+BD,background:isUnread?"#FFF7F7":W}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                            <span style={{fontWeight:800,fontSize:14,color:DK}}>{m.worker_name||"Worker"}</span>
                            {isUnread && <span style={{background:RD,color:W,fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:8,letterSpacing:".5px"}}>NEW</span>}
                            <span style={{fontSize:11,color:MT,marginLeft:"auto"}}>{m.created_at?new Date(m.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}):""}</span>
                          </div>
                          {talkTitle && <div style={{fontSize:11,color:MT,marginBottom:6}}>Re: <span style={{fontWeight:700}}>{talkTitle}</span></div>}
                          <div style={{fontSize:14,color:DK,lineHeight:1.5,whiteSpace:"pre-wrap",marginBottom:isUnread?12:0}}>{body}</div>
                          {isUnread && (
                            <button onClick={()=>handleMarkMessageRead(m.id)} style={{padding:"8px 14px",background:W,color:BL,border:"1.5px solid "+BL,borderRadius:10,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Mark as read</button>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          )}

        </div>

        {/* Settings modal */}
        {showSettings&&(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
            <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",paddingBottom:40}}>
              <div style={{padding:"20px 20px 0"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
                  <div style={{fontWeight:900,fontSize:20}}>Settings</div>
                  <button onClick={()=>setShowSettings(false)} style={{background:F2,border:"none",borderRadius:8,padding:"6px 12px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
                </div>
                <div style={supCard}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                    <span style={{color:MT,fontSize:14}}>Signed in as</span>
                    <span style={{fontWeight:700,fontSize:14}}>{name}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{color:MT,fontSize:14}}>Role</span>
                    <span style={{fontWeight:700,fontSize:14,background:"#EDE9FE",color:"#5B21B6",padding:"3px 10px",borderRadius:12}}>Supervisor</span>
                  </div>
                </div>
                <button onClick={openMyCards} style={{width:"100%",padding:14,background:W,border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between",color:DK}}>
                  <span>🪪 My Cards — CSCS / SSSTS</span><span style={{color:MT,fontSize:18}}>›</span>
                </button>
                <button onClick={()=>{
                  try{localStorage.removeItem("bac_name");localStorage.removeItem("bac_worker_id");localStorage.removeItem("bac_worker_role");}catch(e){}
                  setName("");setWorkerId("");setWorkerRole("");setShowSettings(false);
                  setSupSites([]);setSupSelSite(null);setSupSc("sites");setSupWorkers([]);setSupSheets([]);setSupNotices([]);
                }} style={{width:"100%",padding:14,background:W,border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between",color:RD}}>
                  <span>🚪 Sign out</span><span style={{color:MT,fontSize:18}}>›</span>
                </button>
                <div style={{textAlign:"center",fontSize:11,color:MT,marginTop:14}}>{BUILD}</div>
              </div>
            </div>
          </div>
        )}
        {myCardsModal}
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:BG,fontFamily:"-apple-system,BlinkMacSystemFont,sans-serif",color:DK}}>
      <style>{"@media print{#ui{display:none!important;}#ps{display:block!important;position:absolute;top:0;left:0;width:100%;}}"}</style>
      <div id="ps" style={{display:"none"}}>
        <div style={{width:SHEET_W,background:"#fff",border:"1.5px solid #333",padding:"4px 10px 6px",fontFamily:"Arial,sans-serif",boxSizing:"border-box"}}>
          <div style={{display:"flex",alignItems:"center",marginBottom:3,gap:12}}>
            <div style={{flexShrink:0,width:56,height:56}}><img src={LOGO} alt="SiteVouch" style={{width:"100%",height:"100%",objectFit:"contain",objectPosition:"left center",display:"block"}}/></div>
            <div style={{flex:1,textAlign:"center"}}><span style={{fontWeight:900,fontSize:16,letterSpacing:2,textDecoration:"underline",color:"#111"}}>DAYWORK SHEET</span></div>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
            <tr>
              <td rowSpan={3} style={{...CB,width:"18%",textAlign:"center",fontSize:9,padding:"1px 3px"}}>SUB-CONTRACTOR NAME</td>
              <td rowSpan={3} style={{...C,width:"25%",fontSize:13,fontWeight:700,textAlign:"center",padding:"1px 6px"}}>{name}</td>
              <td style={{...CB,width:"19%",fontSize:9,padding:"1px 4px"}}>DATE</td>
              <td style={{...C,width:"38%",fontSize:11,padding:"1px 4px"}}>{(sheetDate||todayISO)?new Date((sheetDate||todayISO)+"T00:00:00").toLocaleDateString("en-GB"):today}</td>
            </tr>
            <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>SITE LOCATION</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{LC[sheetSite||site]||(sheetSite||site)}</td></tr>
            <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>CONTRACTOR</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{CT[sheetSite||site]||ctr}</td></tr>
          </tbody></table>
          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
            <tr><td colSpan={2} style={{...CB,fontSize:10,padding:"1px 4px"}}>Work Sheet - Please Include Plot Numbers</td></tr>
            <tr>
              <td style={{...C,fontSize:9,color:"#CC0000",fontWeight:700,fontStyle:"italic",padding:"1px 4px"}}>Please confirm how long you spent in each plot next to your plot number</td>
              <td style={{...C,width:140,textAlign:"right",fontWeight:700,fontSize:14,whiteSpace:"nowrap",padding:"1px 4px"}}>Hours: {mgH}</td>
            </tr>
            <tr><td colSpan={2} style={{...C,height:380,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{mgTxt}</td></tr>
          </tbody></table>
          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
            <tr>
              <td style={{...CB,fontSize:10,padding:"1px 4px"}}>Site Instruction Works - Please Include Plot Numbers</td>
              <td style={{...CB,width:140,textAlign:"right",fontSize:14,whiteSpace:"nowrap",padding:"1px 4px"}}>Hours: {siH}</td>
            </tr>
            <tr><td colSpan={2} style={{...C,height:140,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{siTxt}</td></tr>
          </tbody></table>
          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
            <tr><td style={{...CB,fontSize:10,padding:"1px 4px"}}>Materials/Plant/Access Equipment Used</td></tr>
            <tr><td style={{...C,height:26,verticalAlign:"top",paddingTop:2,fontSize:11}}>{mats}</td></tr>
          </tbody></table>
          <table style={{width:"45%",borderCollapse:"collapse",marginBottom:2}}><tbody>
            <tr>
              <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME IN</td>
              <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME OUT</td>
            </tr>
            <tr>
              <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{tIn}</td>
              <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{tOut}</td>
            </tr>
          </tbody></table>
          <table style={{width:"100%",borderCollapse:"collapse"}}><tbody>
            <tr>
              <td style={{...CB,width:"28%",fontSize:9,padding:"2px 4px"}}>SiteVouch Supervisor Signature</td>
              <td style={{...C,width:"20%",padding:"2px 4px"}}>&nbsp;</td>
              <td style={{...CB,width:"10%",fontSize:9,padding:"2px 4px"}}>Date</td>
              <td style={{...C,width:"42%",padding:"2px 4px"}}>&nbsp;</td>
            </tr>
          </tbody></table>
        </div>
      </div>
      <div id="ui" style={{paddingBottom:64}}>
        <div style={{background:W,padding:"6px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid "+BD,position:"sticky",top:0,zIndex:100}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <img src={LOGO} style={{width:44,height:44,objectFit:"contain"}} alt="SiteVouch"/>
            <span style={{fontWeight:800,fontSize:18}}>{COMPANY_NAME}</span>
            <span style={{fontSize:11,color:MT,fontWeight:600,marginLeft:2}}>{BUILD}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {!isOnline&&<span style={{background:RD,color:W,fontSize:11,fontWeight:700,padding:"3px 7px",borderRadius:8}}>📵 Offline</span>}
            {offlineQueue.length>0&&isOnline&&<span style={{background:"#F59E0B",color:W,fontSize:11,fontWeight:700,padding:"3px 7px",borderRadius:8}}>⟳ {offlineQueue.length} syncing</span>}
            <span style={{fontSize:16,fontWeight:800,color:BL}}>{nowStr}</span>
            {/* v18.48 — notifications bell with unread count */}
            {workerRole!=="supervisor"&&(
              <button onClick={()=>setSc("notifs")} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,padding:"4px 6px",position:"relative"}} aria-label="Notifications">🔔
                {notifUnread>0&&<span style={{position:"absolute",top:0,right:0,background:RD,color:"#fff",fontSize:10,fontWeight:800,minWidth:16,height:16,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 4px"}}>{notifUnread}</span>}
              </button>
            )}
            {/* v18.52 — theme toggle + cog removed from worker header: both live on the Profile page (bottom nav). Supervisor header (line ~4032) keeps its cog — only entry to the supervisor settings sheet. */}
          </div>
        </div>

        <div ref={wrapRef} style={{maxWidth:540,margin:"0 auto",padding:"16px 16px 80px"}}>

          {sc==="home" && (
            <div>
              <div style={{marginBottom:16,fontSize:22,fontWeight:800}}>{greet}{name?", "+name.split(" ")[0]:""} 👋</div>

              {/* v17.28 — TOOLBOX TALK BANNER (soft push, doesn't block clock-in) */}
              {toolboxPending.length>0 && site && (
                <div
                  onClick={()=>openToolboxTalk(toolboxPending[0])}
                  style={{
                    background:"linear-gradient(135deg,#fbbf24 0%,#f59e0b 100%)",
                    color:"#1a1408",
                    borderRadius:16,
                    padding:"14px 16px",
                    marginBottom:14,
                    cursor:"pointer",
                    display:"flex",
                    alignItems:"center",
                    gap:12,
                    boxShadow:"0 6px 16px rgba(245,158,11,0.35)"
                  }}
                >
                  <span style={{fontSize:28,flexShrink:0}}>⚠️</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:11,fontWeight:800,textTransform:"uppercase",letterSpacing:0.7,opacity:0.75}}>
                      {toolboxPending.length>1 ? toolboxPending.length+" Toolbox Talks Required" : "Toolbox Talk Required"}
                    </div>
                    <div style={{fontSize:15,fontWeight:800,marginTop:2,lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis"}}>
                      {toolboxPending[0].title}
                    </div>
                    <div style={{fontSize:12,fontWeight:500,opacity:0.75,marginTop:3}}>
                      {toolboxPending.length>1 ? "Tap to start — more queued after this" : "Tap to watch and sign off"}
                    </div>
                  </div>
                  {toolboxPending.length>1 && (
                    <div style={{
                      background:"rgba(26,20,8,0.85)",color:"#fff",
                      width:36,height:36,borderRadius:"50%",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:16,fontWeight:900,flexShrink:0
                    }}>{toolboxPending.length}</div>
                  )}
                  <div style={{fontSize:22,fontWeight:800,flexShrink:0}}>›</div>
                </div>
              )}

              {/* AUTO CLOCK-OUT BANNER */}
              {autoClockOutBanner && (
                <div style={{...card,background:"#FFF7ED",border:"1.5px solid #FB923C",padding:14,marginBottom:14}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                    <span style={{fontSize:22,flexShrink:0}}>⏰</span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:800,fontSize:14,color:"#92400E",marginBottom:3}}>You forgot to clock out on {autoClockOutBanner.date}</div>
                      <div style={{fontSize:13,color:"#92400E",marginBottom:10,lineHeight:1.4}}>We've clocked you out at {AUTO_CLOCKOUT_TIME}. If that's wrong, tap below to request a correction.</div>
                      <button
                        onClick={()=>{
                          // Pre-fill the request modal with original tIn and 23:55 tOut
                          setEditNote("Auto clock-out correction: I clocked in at "+autoClockOutBanner.tIn+" on "+autoClockOutBanner.date+" but forgot to clock out. Please correct my clock-out time.");
                          setEditIn(autoClockOutBanner.tIn);
                          setEditOut(AUTO_CLOCKOUT_TIME);
                          setShowAdd(true);
                          setAddSite(autoClockOutBanner.site||"");
                          setAddDate(autoClockOutBanner.rawDate||todayISO);
                          setAddIn(autoClockOutBanner.tIn);
                          setAddOut(AUTO_CLOCKOUT_TIME);
                          setAddNote("Auto clock-out correction: I clocked in at "+autoClockOutBanner.tIn+" on "+autoClockOutBanner.date+" — please correct my clock-out time.");
                          setAddDone(false);
                          setAutoClockOutBanner(null);
                        }}
                        style={{width:"100%",padding:"10px 12px",background:"#EA580C",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}
                      >Request time correction →</button>
                      <button onClick={()=>setAutoClockOutBanner(null)} style={{marginTop:6,width:"100%",padding:"8px 12px",background:"none",color:"#92400E",border:"1px solid #FB923C",borderRadius:10,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Dismiss</button>
                    </div>
                  </div>
                </div>
              )}
              {tIn && !tOut && (
                <div>
                  <div style={{...card,background:LBL,border:"1px solid #BDD6F5",padding:16,marginBottom:12}}>
                    <div style={{fontSize:40,fontWeight:800,color:BL,letterSpacing:2}}>{fSec(elapsed)}</div>
                    <div style={{fontSize:14,color:BL,marginTop:4,fontWeight:600}}>{site} · Started {tIn}</div>
                    {gpsIn ? <div style={{fontSize:11,color:GR,marginTop:6}}>📍 GPS locked · ±{gpsIn.acc}m</div> : <div style={{fontSize:11,color:"#999",marginTop:6}}>{gpsErr?"📍 GPS will work on live site":"📍 Getting GPS..."}</div>}
                    <button onClick={clockOut} style={{marginTop:14,width:"100%",padding:13,background:W,color:RD,border:"1.5px solid "+RD,borderRadius:10,fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Clock Out</button>
                  </div>
                  <div style={{display:"flex",gap:10,marginBottom:12}}>
                    <button onClick={()=>{setPwMode(false);setSc("sheet");}} style={{flex:1,background:W,border:"2px solid "+BL,borderRadius:14,padding:"12px 10px",display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 1px 3px rgba(0,0,0,.08)"}}>
                      <span style={{fontSize:20}}>📋</span>
                      <div><div style={{fontWeight:800,fontSize:13,color:BL}}>Timesheet</div><div style={{fontSize:11,color:MT}}>{entries.length>0?entries.length+" entries":"Day work"}</div></div>
                    </button>
                    <button onClick={()=>{setPwMode(true);setSc("pricework");}} style={{flex:1,background:W,border:"2px solid "+GR,borderRadius:14,padding:"12px 10px",display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 1px 3px rgba(0,0,0,.08)"}}>
                      <span style={{fontSize:20}}>💷</span>
                      <div><div style={{fontWeight:800,fontSize:13,color:GR}}>Price Work</div><div style={{fontSize:11,color:MT}}>{pwLines.length>0?pwLines.length+" lines":"Priced by metre"}</div></div>
                    </button>
                  </div>
                </div>
              )}
              {/* ── v18.47 — Clock In card shows whenever NOT on the clock (was hidden after a
                     normal day-work clock-out: old condition needed pwMode, so the button vanished).
                     Unsubmitted sheets get a "finish" button for BOTH modes, not just price work. ── */}
              {(!tIn || tOut) && (
                <div style={{...card,background:LBL,border:"1px solid #BDD6F5",padding:14,marginBottom:12}}>
                  {tOut && !submitted && pwMode && pwLines.length>0 && (
                    <button onClick={()=>setSc("pwsummary")} style={{width:"100%",marginBottom:12,padding:11,background:W,color:GR,border:"1.5px solid "+GR,borderRadius:10,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>💷 Finish price work sheet →</button>
                  )}
                  {tOut && !submitted && !pwMode && (
                    <button onClick={()=>setSc("summary")} style={{width:"100%",marginBottom:12,padding:11,background:W,color:BL,border:"1.5px solid "+BL,borderRadius:10,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>📋 Finish & submit today's timesheet →</button>
                  )}
                  <div style={{marginBottom:12,fontSize:13,color:BL,fontWeight:600}}>Signed in as <span style={{fontWeight:800}}>{name}</span></div>
                  <button onClick={()=>setShowS(true)} style={{width:"100%",padding:16,background:BL,color:W,border:"none",borderRadius:12,fontSize:18,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 12px rgba(47,128,237,.35)",display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
                    <span style={{fontSize:20}}>⏱</span> Clock In
                  </button>
                </div>
              )}
              {/* ── v18.47 — Quick links: flat grouped rows (Connecteam-style), replaces the old
                     stack of bordered buttons. Switch Job appears only while on the clock;
                     My Requests + My Week now visible in BOTH clock states. ── */}
              {(() => {
                const visibleSheets = workerRole!=="supervisor" ? signedSheets.filter(sh => !signedDismissedIds.includes(sh.id)) : [];
                const newCount = visibleSheets.filter(sh => !signedOpenedIds.includes(sh.id)).length;
                const tintG = isDark?"#14301f":"#E8F7EE";
                const tintO = isDark?"#33250f":"#FFF4E5";
                const rows = [];
                if(tIn && !tOut) rows.push({ic:"⇄",tint:LBL,icCol:BL,label:"Switch Job",sub:dayStints.length>0?dayStints.length+" "+(dayStints.length===1?"stint":"stints")+" today":"Change site mid-day",go:()=>setSc("daylog")});
                rows.push({ic:"📩",tint:LBL,label:"My Requests",sub:"Time edits & issues",go:()=>{setAddSite("");setAddDate(todayISO);setAddIn("");setAddOut("");setAddNote("");setAddDone(false);setShowAdd(true);}});
                rows.push({ic:"📅",tint:tintG,label:"Price Work",sub:"Weekly summary",go:()=>{setPwWeekOffset(0);setSc("pwweek");}});
                rows.push({ic:"📋",tint:tintO,label:"This Week's Summary",sub:"Hours & entries for the week",go:buildWeekSummary});
                if(visibleSheets.length>0) rows.push({ic:"✓",tint:tintG,icCol:GR,label:"Signed Sheets",sub:visibleSheets.length+" sheet"+(visibleSheets.length===1?"":"s")+" · tap to view & download",badge:newCount>0?newCount+" NEW":null,go:()=>setSc("signed")});
                return (
                  <div style={{background:W,borderRadius:16,boxShadow:"0 1px 3px rgba(0,0,0,.06)",overflow:"hidden",marginBottom:12}}>
                    {rows.map((r,i)=>(
                      <button key={r.label} onClick={r.go} style={{width:"100%",background:"none",border:"none",borderTop:i>0?"1px solid "+BD:"none",padding:"13px 16px",display:"flex",alignItems:"center",gap:12,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                        <div style={{width:38,height:38,borderRadius:"50%",background:r.tint,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0,color:r.icCol||DK,fontWeight:800}}>{r.ic}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:15,color:DK,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                            {r.label}
                            {r.badge && <span style={{background:"#15803D",color:"#fff",fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:8,letterSpacing:".5px"}}>{r.badge}</span>}
                          </div>
                          {r.sub && <div style={{fontSize:12,color:MT,marginTop:1}}>{r.sub}</div>}
                        </div>
                        <span style={{color:MT,fontSize:18,flexShrink:0}}>›</span>
                      </button>
                    ))}
                  </div>
                );
              })()}

              {/* ── v17.21 NOTICE BOARD — below clock-in, above signed sheets ── */}
              {notices.length>0&&(
                <div style={{marginBottom:4}}>
                  {notices.map(n=>{
                    const poster=n.posted_by||n.author||n.name||"SiteVouch Office";
                    const postedAt=n.posted_at||n.created_at||n.timestamp||null;
                    return(
                      <div key={n.id} style={{background:W,marginBottom:10,borderRadius:16,boxShadow:"0 1px 4px rgba(0,0,0,.09)",overflow:"hidden",borderLeft:"4px solid "+BL}}>
                        <div style={{padding:"12px 14px 0",display:"flex",alignItems:"flex-start",gap:10}}>
                          <div style={{width:44,height:44,borderRadius:22,background:noticeAvatarColour(poster),display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:900,color:"#fff",flexShrink:0}}>
                            {noticeInitials(poster)}
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap",marginBottom:1}}>
                              <span style={{fontWeight:800,fontSize:15,color:DK}}>{poster}</span>
                              <span style={{color:MT,fontSize:11}}>▶</span>
                              <span style={{fontSize:13,color:MT}}>All Workers</span>
                              <span style={{fontSize:11,fontWeight:700,color:MT,border:"1px solid "+BD,borderRadius:20,padding:"1px 8px",whiteSpace:"nowrap"}}>✓ Sent</span>
                            </div>
                            <div style={{fontSize:12,color:MT}}>{noticeDate(postedAt)}</div>
                          </div>
                        </div>
                        <div style={{padding:"10px 14px 12px"}}>
                          {n.title&&<div style={{fontSize:17,fontWeight:800,color:DK,marginBottom:7}}>{n.title}</div>}
                          <div style={{fontSize:15,color:isDark?"#c8cfe8":"#374151",lineHeight:1.65,whiteSpace:"pre-wrap",
                            ...((n.message||"").length>280&&!expandedNotices[n.id]?{display:"-webkit-box",WebkitLineClamp:8,WebkitBoxOrient:"vertical",overflow:"hidden",whiteSpace:"normal"}:{})}}>
                            {n.message}
                          </div>
                          {(n.message||"").length>280&&(
                            <div style={{textAlign:"center",paddingTop:8}}>
                              <button onClick={()=>setExpandedNotices(p=>({...p,[n.id]:!p[n.id]}))}
                                style={{background:W,border:"1.5px solid "+BD,borderRadius:22,padding:"7px 24px",fontSize:14,fontWeight:700,color:DK,cursor:"pointer",fontFamily:"inherit"}}>
                                {expandedNotices[n.id]?"- Read less":"+ Read more"}
                              </button>
                            </div>
                          )}
                        </div>
                        <div style={{display:"flex",borderTop:"1px solid "+BD}}>
                          <button style={{flex:1,background:"none",border:"none",padding:"11px 8px",fontSize:14,fontWeight:700,color:MT,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>👍 Like</button>
                          <button style={{flex:1,background:"none",borderLeft:"1px solid "+BD,borderTop:"none",borderRight:"none",borderBottom:"none",padding:"11px 8px",fontSize:14,fontWeight:700,color:MT,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>💬 Comment</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={card}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{fontWeight:800,fontSize:15}}>Recent Shifts</div>
                  <button onClick={()=>{setAddSite("");setAddDate(todayISO);setAddIn("");setAddOut("");setAddNote("");setAddDone(false);setShowAdd(true);}} style={{background:LBL,border:"none",borderRadius:8,padding:"6px 12px",fontSize:13,fontWeight:700,color:BL,cursor:"pointer",fontFamily:"inherit"}}>+ Add Shift</button>
                </div>
                {pastShifts.length===0 && <div style={{textAlign:"center",color:MT,fontSize:13,padding:"10px 0"}}>No shifts yet</div>}
                {pastShifts.map((s,i)=>(
                  <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",paddingBottom:i<pastShifts.length-1?12:0,marginBottom:i<pastShifts.length-1?12:0,borderBottom:i<pastShifts.length-1?"1px solid "+BD:"none"}}>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2,flexWrap:"wrap"}}>
                        {s.pending && <span style={{background:"#FFF3CD",color:"#856404",fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:6}}>Pending</span>}
                        {s.manualEntry && <span style={{background:"#E8F4FD",color:"#0c5460",fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:6}}>Manual</span>}
                        <span style={{fontWeight:700,fontSize:13}}>{LC[s.site]||s.site}</span>
                      </div>
                      <div style={{fontSize:12,color:MT}}>{s.date} · {s.tIn}–{s.tOut}</div>
                    </div>
                    <button onClick={()=>openEdit(s)} style={{background:F2,border:"none",borderRadius:8,padding:"6px 10px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK,flexShrink:0,marginLeft:8}}>✏️ Edit</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── v18.48 — NOTIFICATIONS PAGE ── */}
          {sc==="notifs" && (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                <button onClick={()=>setSc("home")} style={{background:"none",border:"none",fontSize:24,color:BL,cursor:"pointer",fontFamily:"inherit",padding:0,lineHeight:1}}>‹</button>
                <div style={{fontSize:22,fontWeight:800,flex:1}}>Notifications</div>
                {notifItems.some(n=>!n.quiet&&!notifReadIds.includes(n.id))&&(
                  <button onClick={markNotifsRead} style={{background:F2,border:"none",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:700,color:DK,cursor:"pointer",fontFamily:"inherit"}}>Mark all read</button>
                )}
              </div>
              {notifItems.length===0 ? (
                <div style={{...card,textAlign:"center",padding:"36px 20px"}}>
                  <div style={{fontSize:38,marginBottom:10}}>🔔</div>
                  <div style={{fontWeight:800,fontSize:16,color:DK,marginBottom:6}}>Nothing to report</div>
                  <div style={{fontSize:13,color:MT,lineHeight:1.5}}>When the office approves or rejects one of your requests, it'll show up here. So will a card that's coming up for renewal.</div>
                </div>
              ) : (
                <div style={{background:W,borderRadius:16,boxShadow:"0 1px 3px rgba(0,0,0,.06)",overflow:"hidden"}}>
                  {notifItems.map((n,i)=>{
                    const unread=!n.quiet&&!notifReadIds.includes(n.id);
                    return (
                      <div key={n.id} style={{display:"flex",gap:12,padding:"14px 16px",borderTop:i>0?"1px solid "+BD:"none",background:unread?(isDark?"#16233a":"#F5F9FF"):"transparent",borderLeft:unread?"3px solid "+BL:"3px solid transparent"}}>
                        <div style={{width:38,height:38,borderRadius:"50%",background:F2,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{n.ic}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:15,color:n.tone,marginBottom:2}}>{n.title}</div>
                          {n.body&&<div style={{fontSize:13,color:MT,lineHeight:1.45,whiteSpace:"pre-wrap"}}>{n.body}</div>}
                          {n.when&&<div style={{fontSize:11,color:MT,marginTop:4}}>{notifWhen(n.when)}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{fontSize:12,color:MT,textAlign:"center",marginTop:14,lineHeight:1.5}}>Requests you've sent stay listed here until the office decides.</div>
            </div>
          )}

          {/* ── v18.49 — PROFILE PAGE ──
               Replaces the worker settings sheet: same contents, but a real page
               with the cards surfaced instead of buried one tap deeper. */}
          {sc==="profile" && (
            <div>
              {/* Header card — company branding + who you are */}
              <div style={{background:BL,borderRadius:18,padding:"18px 18px 16px",marginBottom:14,position:"relative",overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
                  <img src={LOGO} style={{width:26,height:26,objectFit:"contain"}} alt=""/>
                  <span style={{color:"#fff",fontWeight:800,fontSize:14,opacity:.95}}>{COMPANY_NAME}</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:14}}>
                  <div style={{width:60,height:60,borderRadius:14,background:"rgba(255,255,255,.22)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:900,color:"#fff",flexShrink:0}}>
                    {noticeInitials(name)}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{color:"#fff",fontWeight:900,fontSize:21,lineHeight:1.2,marginBottom:5}}>{name||"Signed in"}</div>
                    <span style={{background:"rgba(255,255,255,.22)",color:"#fff",fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:12,textTransform:"capitalize"}}>{workerRole||"worker"}</span>
                  </div>
                </div>
              </div>

              {/* My Cards — status visible without opening anything */}
              <div style={{fontSize:12,fontWeight:800,color:MT,textTransform:"uppercase",letterSpacing:.7,margin:"0 4px 8px"}}>My Cards</div>
              <div style={{background:W,borderRadius:16,boxShadow:"0 1px 3px rgba(0,0,0,.06)",overflow:"hidden",marginBottom:18}}>
                {CARD_TYPES.map(({t,label},i)=>{
                  const row=myCards.find(c=>c.card_type===t);
                  const st=cardStatus(row);
                  return (
                    <button key={t} onClick={openMyCards} style={{width:"100%",background:"none",border:"none",borderTop:i>0?"1px solid "+BD:"none",padding:"13px 16px",display:"flex",alignItems:"center",gap:12,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                      <div style={{width:38,height:38,borderRadius:"50%",background:F2,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>🪪</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:15,color:DK}}>{label}</div>
                        <div style={{fontSize:12,color:st.col,fontWeight:600,marginTop:1}}>{st.txt}</div>
                      </div>
                      <span style={{color:MT,fontSize:18,flexShrink:0}}>›</span>
                    </button>
                  );
                })}
              </div>

              {/* Account */}
              <div style={{fontSize:12,fontWeight:800,color:MT,textTransform:"uppercase",letterSpacing:.7,margin:"0 4px 8px"}}>Account</div>
              <div style={{background:W,borderRadius:16,boxShadow:"0 1px 3px rgba(0,0,0,.06)",overflow:"hidden",marginBottom:18}}>
                {[
                  {ic:"🔔",label:"Notifications",sub:notifUnread>0?notifUnread+" unread":"Approvals & card reminders",go:()=>setSc("notifs")},
                  {ic:isDark?"☀️":"🌙",label:"Appearance",sub:isDark?"Dark mode":"Light mode",go:()=>setTheme(x=>x==="dark"?"light":"dark")},
                  {ic:"🔐",label:"Change my PIN",sub:"Keep your account yours",go:()=>{setNewPin1("");setNewPin2("");setPinChangeError("");setPinStep(1);setShowPinChange(true);}},
                ].map((r,i)=>(
                  <button key={r.label} onClick={r.go} style={{width:"100%",background:"none",border:"none",borderTop:i>0?"1px solid "+BD:"none",padding:"13px 16px",display:"flex",alignItems:"center",gap:12,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                    <div style={{width:38,height:38,borderRadius:"50%",background:F2,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>{r.ic}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:15,color:DK}}>{r.label}</div>
                      <div style={{fontSize:12,color:MT,marginTop:1}}>{r.sub}</div>
                    </div>
                    <span style={{color:MT,fontSize:18,flexShrink:0}}>›</span>
                  </button>
                ))}
              </div>

              <button onClick={signOut} style={{width:"100%",padding:14,background:W,border:"1.5px solid "+BD,borderRadius:14,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:RD,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                🚪 Sign out
              </button>
              <div style={{textAlign:"center",fontSize:11,color:MT,marginTop:16}}>{COMPANY_NAME} · {BUILD}</div>
            </div>
          )}

          {showS && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:W,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"85vh",display:"flex",flexDirection:"column"}}>
                <div style={{padding:"16px 20px 12px",borderBottom:"1px solid "+BD}}>
                  <div style={{textAlign:"center",fontSize:18,fontWeight:700,marginBottom:12}}>Select Job Site</div>
                  <input style={{...inp,background:F2}} placeholder="Search sites..." value={srch} onChange={e=>setSrch(e.target.value)}/>
                </div>
                <div style={{overflowY:"auto",flex:1}}>
                  {!srch && recent.length>0 && (
                    <div>
                      <div style={{padding:"10px 20px 4px",fontSize:11,color:MT,fontWeight:700,textTransform:"uppercase",letterSpacing:".8px"}}>Most Used</div>
                      {recent.map(r=>(
                        <button key={r} onClick={()=>selSite(r)} style={{width:"100%",padding:"14px 20px",background:W,border:"none",borderBottom:"1px solid "+BD,cursor:"pointer",display:"flex",alignItems:"center",gap:14,fontSize:16,color:DK,fontFamily:"inherit",textAlign:"left"}}>
                          <div style={{width:48,height:48,borderRadius:6,background:W,border:"2px solid "+DOTS[SITES.indexOf(r)%DOTS.length],flexShrink:0,overflow:"hidden",padding:2,boxSizing:"border-box"}}>
                            <img src={LOGO} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/>
                          </div>
                          <div style={{flex:1}}>{r}</div>
                          {PC[r] && <span style={{fontSize:12,color:MT,fontWeight:600}}>{PC[r]}</span>}
                        </button>
                      ))}
                      <div style={{padding:"10px 20px 4px",fontSize:11,color:MT,fontWeight:700,textTransform:"uppercase",letterSpacing:".8px"}}>All Sites</div>
                    </div>
                  )}
                  {filt.map(r=>(
                    <button key={r} onClick={()=>selSite(r)} style={{width:"100%",padding:"14px 20px",background:W,border:"none",borderBottom:"1px solid "+BD,cursor:"pointer",display:"flex",alignItems:"center",gap:14,fontSize:16,color:DK,fontFamily:"inherit",textAlign:"left"}}>
                      <div style={{width:48,height:48,borderRadius:6,background:W,border:"2px solid "+DOTS[SITES.indexOf(r)%DOTS.length],flexShrink:0,overflow:"hidden",padding:2,boxSizing:"border-box"}}>
                        <img src={LOGO} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/>
                      </div>
                      <div style={{flex:1}}>{r}</div>
                      {PC[r] && <span style={{fontSize:12,color:MT,fontWeight:600}}>{PC[r]}</span>}
                    </button>
                  ))}
                </div>
                <div style={{padding:"12px 20px 24px",borderTop:"1px solid "+BD}}>
                  <button onClick={()=>{doClockIn("Site Not Listed");setCtr("SiteVouch");setSiteRequestNote("");setSiteRequestSent(false);setShowSiteRequest(true);}} style={{width:"100%",padding:12,background:"#FFF8E1",border:"1.5px solid #F59E0B",borderRadius:12,fontSize:15,color:"#92400E",cursor:"pointer",fontFamily:"inherit",fontWeight:700,marginBottom:8}}>⚠️ My site isn't listed</button>
                  <button onClick={()=>{setShowS(false);setSrch("");}} style={{width:"100%",padding:14,background:F2,border:"none",borderRadius:12,fontSize:17,color:DK,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* ── v17.78 — MY DAY / SWITCH JOB PAGE (own screen: day log + big Switch Job button) ── */}
          {sc==="daylog" && (() => {
            const fClock=ts=>new Date(ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
            const fDur=ms=>{let s=Math.floor(ms/1000);const h=Math.floor(s/3600);const m=Math.floor((s%3600)/60);return h+":"+String(m).padStart(2,"0");};
            const totalMs=dayStints.reduce((a,st)=>a+((st.outTs||Date.now())-st.inTs),0);
            return (
              <div style={{padding:"16px"}}>
                <button onClick={()=>setSc("home")} style={{background:"none",border:"none",color:BL,fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"inherit",padding:0,marginBottom:14}}>‹ Back</button>
                <div style={{fontSize:24,fontWeight:800,color:DK,marginBottom:4}}>My day log</div>
                <div style={{fontSize:14,color:MT,marginBottom:18}}>{dayStints.length} {dayStints.length===1?"stint":"stints"} · {fDur(totalMs)} total today</div>

                {/* Big Switch Job button → site picker (selSite does the clock out/in) */}
                <button onClick={()=>{setSrch("");setShowS(true);}} style={{width:"100%",padding:17,background:BL,color:W,border:"none",borderRadius:14,fontSize:18,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 12px rgba(47,128,237,.35)",display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:20}}>⇄ Switch Job</button>

                {/* Stint timeline */}
                {dayStints.length===0 ? (
                  <div style={{...card,textAlign:"center",color:MT,padding:24}}>No stints logged yet today.</div>
                ) : (
                  <div style={{...card}}>
                    {dayStints.map((st,i)=>{
                      const active=st.outTs===null;
                      const dur=(active?Date.now():st.outTs)-st.inTs;
                      const col=DOTS[SITES.indexOf(st.site)%DOTS.length]||MT;
                      return (
                        <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderTop:i>0?"1px solid "+BD:"none"}}>
                          <span style={{width:13,height:13,borderRadius:"50%",background:col,flexShrink:0}}></span>
                          <span style={{borderRadius:999,padding:"5px 12px",fontWeight:700,fontSize:14,background:col+"22",color:col,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:130}}>{st.site}</span>
                          <span style={{fontSize:14,color:DK}}>{fClock(st.inTs)}{active?" – ":" – "+fClock(st.outTs)}{active&&<span style={{color:GR,fontWeight:700}}>active</span>}</span>
                          {!active && <span style={{marginLeft:"auto",fontWeight:800,fontSize:14,color:DK,whiteSpace:"nowrap"}}>{fDur(dur)}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── v17.75 — SIGNED SHEETS PAGE (date range + site dropdowns, NEW floats to top) ── */}
          {sc==="signed" && workerRole!=="supervisor" && (() => {
            const visibleSheets = signedSheets.filter(sh => !signedDismissedIds.includes(sh.id));
            const newCount = visibleSheets.filter(sh => !signedOpenedIds.includes(sh.id)).length;
            // Date-range filter
            const now = new Date();
            const startOfToday = new Date(now.getFullYear(),now.getMonth(),now.getDate()).getTime();
            const rangeMs = {today:0,week:7*864e5,month:30*864e5,year:365*864e5};
            const inRange = (sh) => {
              if (signedDateFilter==="all") return true;
              const d = sh.shift_date ? new Date(sh.shift_date).getTime() : 0;
              if (signedDateFilter==="today") return d>=startOfToday;
              return d >= (now.getTime() - rangeMs[signedDateFilter]);
            };
            let filtered = visibleSheets.filter(inRange);
            // Site dropdown options derived from the date-filtered set
            const siteList = Array.from(new Set(filtered.map(s=>s.site_name).filter(Boolean)));
            const activeSiteFilter = (signedSiteFilter!=="all" && !siteList.includes(signedSiteFilter)) ? "all" : signedSiteFilter;
            if (activeSiteFilter!=="all") filtered = filtered.filter(s=>s.site_name===activeSiteFilter);
            // Sort: NEW (unopened) first, then newest shift_date first
            filtered = [...filtered].sort((a,b)=>{
              const aNew = !signedOpenedIds.includes(a.id) ? 0 : 1;
              const bNew = !signedOpenedIds.includes(b.id) ? 0 : 1;
              if (aNew!==bNew) return aNew-bNew;
              const ad = a.shift_date?new Date(a.shift_date).getTime():0;
              const bd = b.shift_date?new Date(b.shift_date).getTime():0;
              return bd-ad;
            });
            const filteredNew = filtered.filter(s=>!signedOpenedIds.includes(s.id)).length;
            const dateLabels = {today:"Today",week:"Last 7 days",month:"Last 30 days",year:"Last 365 days",all:"All time"};
            const ddStyle = {flex:1,padding:"10px 12px",borderRadius:12,fontSize:13,fontWeight:700,fontFamily:"inherit",color:DK,background:W,border:"1.5px solid "+BD,cursor:"pointer",outline:"none",WebkitAppearance:"none",appearance:"none"};
            return (
              <div>
                {/* Header row: back link + title */}
                <button onClick={()=>setSc("home")} style={{background:"none",border:0,color:MT,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",padding:"6px 0",marginBottom:8}}>‹ Home</button>
                <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:14,gap:10}}>
                  <div style={{fontSize:22,fontWeight:800,color:DK,display:"flex",alignItems:"center",gap:8}}>
                    <span style={{color:GR}}>✓</span> Signed Sheets
                  </div>
                  <div style={{fontSize:12,color:MT}}>{visibleSheets.length} total{newCount>0?" · "+newCount+" new":""}</div>
                </div>

                {visibleSheets.length===0 ? (
                  <>
                    <div style={{...card,padding:24,textAlign:"center"}}>
                      <div style={{fontSize:32,marginBottom:8}}>📋</div>
                      <div style={{fontWeight:800,fontSize:16,color:DK,marginBottom:4}}>No signed sheets yet</div>
                      <div style={{fontSize:13,color:MT,lineHeight:1.5}}>When a supervisor signs off a daywork sheet you submitted, it will appear here.</div>
                      {signedLoading && <div style={{fontSize:11,color:MT,marginTop:12}}>Checking…</div>}
                    </div>
                    {/* Debug strip — only when 0 results, for diagnosing missing sign-offs */}
                    <div style={{background:"#FFFBEB",border:"1.5px solid #FCD34D",borderRadius:12,padding:"10px 12px",marginTop:12,fontSize:11,color:"#92400E",fontFamily:"monospace",lineHeight:1.5,wordBreak:"break-all"}}>
                      <div style={{fontWeight:800,marginBottom:4,fontFamily:"inherit"}}>🔍 Debug</div>
                      <div>workerId: {workerId||"(empty)"}</div>
                      <div>name: {name||"(empty)"}</div>
                      <div>workerRole: {workerRole||"(empty)"}</div>
                      <div>signedLoading: {String(signedLoading)}</div>
                      {signedDiag ? (
                        <>
                          <div>matched by_id: {signedDiag.byId} · by_name: {signedDiag.byName}</div>
                          <div>latest signed in company: {signedDiag.latestInCompany ? (signedDiag.latestInCompany.worker_name+" / "+(signedDiag.latestInCompany.worker_id||"(null id)")+" / "+(signedDiag.latestInCompany.signed_at||"").slice(0,10)) : "(none)"}</div>
                          {signedDiag.errMsg && <div style={{color:"#B91C1C"}}>err: {signedDiag.errMsg}</div>}
                        </>
                      ) : (
                        <div style={{color:"#B91C1C",marginTop:4}}>signedDiag: null — fetch didn't run</div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Date range + site dropdowns — one tidy row */}
                    <div style={{display:"flex",gap:8,marginBottom:12}}>
                      <select value={signedDateFilter} onChange={e=>setSignedDateFilter(e.target.value)} style={ddStyle}>
                        <option value="today">Today</option>
                        <option value="week">Last 7 days</option>
                        <option value="month">Last 30 days</option>
                        <option value="year">Last 365 days</option>
                        <option value="all">All time</option>
                      </select>
                      {siteList.length>=2 && (
                        <select value={activeSiteFilter} onChange={e=>setSignedSiteFilter(e.target.value)} style={ddStyle}>
                          <option value="all">All sites</option>
                          {siteList.map(site=>(
                            <option key={site} value={site}>{LC[site]||site}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    {/* Count line */}
                    <div style={{fontSize:12,color:MT,marginBottom:10,paddingLeft:2}}>
                      {dateLabels[signedDateFilter]}{activeSiteFilter!=="all"?" · "+(LC[activeSiteFilter]||activeSiteFilter):""} — {filtered.length} sheet{filtered.length===1?"":"s"}{filteredNew>0?" · "+filteredNew+" new":""}
                    </div>
                    {/* Filtered list */}
                    {filtered.length===0 ? (
                      <div style={{...card,padding:24,textAlign:"center",color:MT,fontSize:13}}>
                        <div style={{fontSize:32,marginBottom:8}}>🔍</div>
                        <div style={{fontWeight:700,marginBottom:4,color:DK}}>Nothing in this range</div>
                        <div style={{fontSize:12}}>Try a wider date range.</div>
                      </div>
                    ) : filtered.map(sh=>{
                      const isNew = !signedOpenedIds.includes(sh.id);
                      const isDownloaded = signedDownloadedIds.includes(sh.id);
                      return (
                        <button key={sh.id} onClick={()=>openSignedSheetDetail(sh)} style={{width:"100%",display:"block",textAlign:"left",padding:0,background:"transparent",border:"none",cursor:"pointer",fontFamily:"inherit",marginBottom:8}}>
                          <div style={{...card,padding:14,marginBottom:0,border:isNew?"1.5px solid #A8D5B5":"1px solid "+BD,background:isNew?"#F0FAF4":W}}>
                            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                                  <div style={{fontSize:14,fontWeight:800,color:DK}}>{sh.shift_date?new Date(sh.shift_date).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"}):""}</div>
                                  {isNew
                                    ? <span style={{background:"#15803D",color:W,fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:8,letterSpacing:".5px"}}>NEW</span>
                                    : <span style={{background:"#F0FAF4",color:GR,fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,border:"1px solid #A8D5B5"}}>✓ Signed</span>}
                                  {isDownloaded && <span style={{background:"#EBF3FD",color:"#1E40AF",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,border:"1px solid #BDD6F1"}}>⬇️ Downloaded</span>}
                                </div>
                                <div style={{fontSize:13,color:DK,marginBottom:2}}>{LC[sh.site_name]||sh.site_name}</div>
                                <div style={{fontSize:11,color:MT}}>{sh.total_mins?fMin(sh.total_mins):""}{sh.signed_by_name?" · Signed by "+sh.signed_by_name:""}</div>
                              </div>
                              <div style={{color:MT,fontSize:18,flexShrink:0}}>›</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            );
          })()}

          {/* v17.28 — TOOLBOX TALK SCREEN */}
          {sc==="toolbox" && toolboxOpenTalk && (
            <div>
              {/* Back link */}
              <button
                onClick={closeToolboxTalk}
                style={{background:"none",border:0,color:MT,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",padding:"6px 0",marginBottom:8}}
              >‹ Back</button>

              {/* Header card */}
              <div style={{...card,padding:16,marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.7,color:BL}}>Toolbox Talk</div>
                <div style={{fontSize:20,fontWeight:800,marginTop:4,lineHeight:1.25,color:DK}}>{toolboxOpenTalk.title}</div>
                {toolboxOpenTalk.description && (
                  <div style={{fontSize:13,color:MT,marginTop:8,lineHeight:1.5}}>{toolboxOpenTalk.description}</div>
                )}
                <div style={{fontSize:11,color:MT,marginTop:10}}>
                  Posted by {toolboxOpenTalk.posted_by||"Supervisor"}
                  {toolboxOpenTalk.posted_at && " · "+new Date(toolboxOpenTalk.posted_at).toLocaleString("en-GB",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}
                </div>
              </div>

              {/* Video — YouTube uses iframe + manual confirm; mp4 uses native + scrub blocker */}
              {(() => {
                const url = toolboxOpenTalk.video_url || "";
                const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
                if (ytMatch) {
                  return (
                    <div style={{background:"#000",borderRadius:14,overflow:"hidden",marginBottom:14}}>
                      <iframe
                        src={"https://www.youtube.com/embed/"+ytMatch[1]+"?modestbranding=1&rel=0"}
                        style={{width:"100%",aspectRatio:"16/9",border:0,display:"block"}}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  );
                }
                return (
                  <div style={{background:"#000",borderRadius:14,overflow:"hidden",marginBottom:14}}>
                    <video
                      src={url}
                      controls
                      controlsList="nodownload noplaybackrate noremoteplayback"
                      disablePictureInPicture
                      playsInline
                      style={{width:"100%",aspectRatio:"16/9",display:"block"}}
                      onTimeUpdate={(e)=>{
                        const v=e.target;
                        if(!tbVideoWatched && v.currentTime>tbMaxWatchedRef.current){
                          tbMaxWatchedRef.current=v.currentTime;
                        }
                        if(!tbVideoWatched && v.duration && v.currentTime>=v.duration-0.5){
                          tbMarkWatched();
                        }
                      }}
                      onSeeking={(e)=>{
                        if(tbVideoWatched)return;
                        const v=e.target;
                        if(v.currentTime>tbMaxWatchedRef.current+1.0){
                          v.currentTime=tbMaxWatchedRef.current;
                        }
                      }}
                      onEnded={tbMarkWatched}
                    />
                  </div>
                );
              })()}

              {/* Video watched status */}
              <div style={{
                ...card,
                padding:"10px 14px",
                marginBottom:14,
                background:tbVideoWatched ? (isDark?"rgba(52,211,153,0.12)":"#ECFDF5") : W,
                border:"1px solid "+(tbVideoWatched ? (isDark?"rgba(52,211,153,0.3)":"#A7F3D0") : BD),
                display:"flex",alignItems:"center",gap:10,fontSize:12,fontWeight:600,
                color:tbVideoWatched ? (isDark?"#34d399":"#065F46") : MT
              }}>
                {tbVideoWatched ? (
                  <span>✓ Video watched</span>
                ) : (() => {
                  const url=toolboxOpenTalk.video_url||"";
                  const isYt=/(?:youtube\.com|youtu\.be)/.test(url);
                  return isYt ? (
                    <button
                      onClick={tbMarkWatched}
                      style={{background:BL,color:"#fff",border:0,padding:"8px 14px",borderRadius:8,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}
                    >I've watched it</button>
                  ) : (
                    <span>▶ Watch video to the end to unlock the question</span>
                  );
                })()}
              </div>

              {/* Question card */}
              <div style={{
                ...card,
                padding:18,marginBottom:14,
                opacity:tbVideoWatched?1:0.45,
                pointerEvents:tbVideoWatched?"auto":"none",
                transition:"opacity 0.3s"
              }}>
                <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.7,color:BL}}>Question</div>
                <div style={{fontSize:16,fontWeight:700,marginTop:6,lineHeight:1.4,color:DK}}>{toolboxOpenTalk.question}</div>
                <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:10}}>
                  {["a","b","c"].map(letter=>{
                    const text=toolboxOpenTalk["option_"+letter];
                    if(!text)return null;
                    const isCorrect=tbQuestionAnswered && tbAnswerGiven===letter;
                    const isWrongFlash=tbWrongLetter===letter;
                    const dim=tbQuestionAnswered && tbAnswerGiven!==letter;
                    return (
                      <button
                        key={letter}
                        onClick={()=>tbPickAnswer(letter)}
                        disabled={tbQuestionAnswered}
                        style={{
                          background:isCorrect ? (isDark?"rgba(52,211,153,0.15)":"#ECFDF5")
                                  : isWrongFlash ? (isDark?"rgba(248,113,113,0.15)":"#FEF2F2")
                                  : F2,
                          border:"2px solid "+(isCorrect ? GR : isWrongFlash ? RD : BD),
                          borderRadius:12,
                          padding:"14px 16px",
                          textAlign:"left",
                          fontSize:14,fontWeight:600,
                          color:isCorrect ? (isDark?"#34d399":"#065F46") : isWrongFlash ? (isDark?"#fca5a5":"#991B1B") : DK,
                          cursor:tbQuestionAnswered?"default":"pointer",
                          opacity:dim?0.4:1,
                          fontFamily:"inherit",
                          display:"flex",alignItems:"center",gap:12,
                          transition:"all 0.2s"
                        }}
                      >
                        <span style={{background:F2,width:28,height:28,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0,color:DK}}>{letter.toUpperCase()}</span>
                        <span>{text}</span>
                      </button>
                    );
                  })}
                </div>
                {tbWrongTry && !tbQuestionAnswered && (
                  <div style={{
                    marginTop:12,padding:"10px 14px",
                    background:isDark?"rgba(248,113,113,0.12)":"#FEF2F2",
                    border:"1px solid "+(isDark?"rgba(248,113,113,0.3)":"#FECACA"),
                    borderRadius:10,fontSize:13,fontWeight:600,
                    color:isDark?"#fca5a5":"#991B1B",textAlign:"center"
                  }}>✕ Not quite — try again</div>
                )}
              </div>

              {/* SIGN-OFF section — appears after correct answer, hidden once submitted */}
              {tbQuestionAnswered && !tbSignedDone && (
                <div style={{...card,padding:18,marginBottom:14}}>
                  <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.7,color:GR}}>Sign Off</div>
                  <div style={{fontSize:17,fontWeight:800,marginTop:4,color:DK}}>Confirm you watched the talk</div>
                  <div style={{fontSize:12,color:MT,marginTop:6}}>Sign below to record this on your training log.</div>

                  {/* GPS pill */}
                  <div style={{
                    marginTop:12,padding:"10px 12px",
                    background:F2,borderRadius:10,
                    fontSize:12,fontWeight:600,
                    display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",
                    color:tbGps ? (isDark?"#34d399":"#059669") : tbGpsErr ? RD : MT
                  }}>
                    <span>📍</span>
                    {tbGps ? (
                      <span>Location captured (±{tbGps.acc}m)</span>
                    ) : tbGpsErr==="blocked" ? (
                      <>
                        <span style={{flex:1,minWidth:0}}>Location blocked — tap the address bar to enable</span>
                        <button onClick={tbFetchGps} style={{background:BL,color:"#fff",border:0,padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Try again</button>
                      </>
                    ) : tbGpsErr==="unavailable" ? (
                      <>
                        <span style={{flex:1,minWidth:0}}>GPS unavailable — check phone settings</span>
                        <button onClick={tbFetchGps} style={{background:BL,color:"#fff",border:0,padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Try again</button>
                      </>
                    ) : tbGpsErr==="timeout" ? (
                      <>
                        <span style={{flex:1,minWidth:0}}>Location timed out</span>
                        <button onClick={tbFetchGps} style={{background:BL,color:"#fff",border:0,padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Try again</button>
                      </>
                    ) : tbGpsErr ? (
                      <>
                        <span style={{flex:1,minWidth:0}}>Couldn't get location</span>
                        <button onClick={tbFetchGps} style={{background:BL,color:"#fff",border:0,padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Try again</button>
                      </>
                    ) : (
                      <span>Getting location…</span>
                    )}
                  </div>

                  {/* Contact supervisor button */}
                  <button
                    onClick={tbCallSupervisor}
                    style={{
                      marginTop:14,width:"100%",padding:"12px 14px",
                      background:F2,border:"1px solid "+BD,borderRadius:10,
                      fontSize:13,fontWeight:700,color:DK,cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:10,
                      fontFamily:"inherit"
                    }}
                  >
                    <span>📞</span>
                    <span>{tbSupPhone ? "Call "+(toolboxOpenTalk.posted_by||"supervisor") : "Message "+(toolboxOpenTalk.posted_by||"supervisor")}</span>
                  </button>

                  {/* Message field */}
                  <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.7,color:MT,marginTop:18,marginBottom:8}}>
                    Message supervisor (optional)
                  </div>
                  <textarea
                    id="tbMsgField"
                    value={tbMessage}
                    onChange={e=>setTbMessage(e.target.value)}
                    placeholder="Type a question or report a problem — sent straight to the supervisor"
                    rows={3}
                    style={{
                      width:"100%",background:F2,border:"1px solid "+BD,borderRadius:10,
                      padding:12,fontSize:13,fontFamily:"inherit",color:DK,
                      resize:"vertical",minHeight:60,boxSizing:"border-box",outline:"none"
                    }}
                  />
                  <button
                    onClick={tbSendMessage}
                    disabled={tbMessageSending}
                    style={{
                      marginTop:10,width:"100%",padding:"12px 14px",
                      background:tbMessageSending?"#94a3b8":BL,
                      border:0,borderRadius:10,
                      fontSize:13,fontWeight:700,color:"#fff",cursor:tbMessageSending?"not-allowed":"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:10,
                      fontFamily:"inherit"
                    }}
                  >
                    <span>✉</span>
                    <span>{tbMessageSending?"Sending…":"Send message"}</span>
                  </button>
                  {tbMessageStatus==="ok" && (
                    <div style={{marginTop:8,fontSize:12,fontWeight:600,textAlign:"center",color:isDark?"#34d399":"#059669"}}>
                      ✓ Message sent to {toolboxOpenTalk.posted_by||"supervisor"}
                    </div>
                  )}
                  {tbMessageStatus==="err" && (
                    <div style={{marginTop:8,fontSize:12,fontWeight:600,textAlign:"center",color:RD}}>
                      Failed to send — check connection
                    </div>
                  )}

                  {/* Signature canvas */}
                  <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.7,color:MT,marginTop:18,marginBottom:8}}>
                    Signature
                  </div>
                  <div style={{position:"relative",background:F2,border:"2px dashed "+BD,borderRadius:12,overflow:"hidden"}}>
                    <canvas
                      ref={tbInitSigCanvas}
                      style={{display:"block",width:"100%",height:140,touchAction:"none",background:F2}}
                    />
                    <button
                      onClick={tbClearSig}
                      style={{
                        position:"absolute",top:8,right:8,
                        background:W,border:"1px solid "+BD,borderRadius:8,
                        padding:"5px 10px",fontSize:11,fontWeight:700,color:MT,cursor:"pointer",fontFamily:"inherit"
                      }}
                    >Clear</button>
                  </div>

                  {/* Submit */}
                  <button
                    onClick={tbSubmitSignoff}
                    disabled={tbSubmitting}
                    style={{
                      marginTop:16,width:"100%",padding:16,
                      background:tbSubmitting ? "#94a3b8" : "linear-gradient(135deg,#10b981 0%,#059669 100%)",
                      border:0,borderRadius:12,color:"#fff",
                      fontSize:15,fontWeight:800,cursor:tbSubmitting?"not-allowed":"pointer",
                      fontFamily:"inherit",
                      boxShadow:tbSubmitting?"none":"0 6px 14px rgba(16,185,129,0.35)"
                    }}
                  >{tbSubmitting?"Submitting…":"Submit sign-off"}</button>
                </div>
              )}

              {/* Done state */}
              {tbSignedDone && (
                <div style={{
                  background:"linear-gradient(135deg,#10b981 0%,#059669 100%)",
                  color:"#fff",borderRadius:16,padding:"24px 18px",textAlign:"center",
                  boxShadow:"0 8px 20px rgba(16,185,129,0.4)",marginBottom:14
                }}>
                  <div style={{fontSize:48,marginBottom:8,lineHeight:1}}>✓</div>
                  <div style={{fontSize:18,fontWeight:800}}>Signed off</div>
                  <div style={{fontSize:13,opacity:0.85,marginTop:6}}>Logged with GPS and signature. Back to work.</div>
                </div>
              )}
            </div>
          )}

          {sc==="sheet" && (
            <div>
              <div style={{background:LBL,border:"1px solid #BDD6F5",borderRadius:fromSummary?16:0,padding:"18px 16px",marginBottom:16,...(!fromSummary?{position:"sticky",top:50,zIndex:50,borderRadius:"0 0 16px 16px"}:{})}}>
                {fromSummary ? (
                  <div>
                    <div style={{fontSize:16,fontWeight:800,color:BL,marginBottom:4}}>Editing entries for</div>
                    <div style={{fontSize:14,color:BL,fontWeight:600,marginBottom:14}}>{site} · {tIn}–{tOut}</div>
                    <button onClick={backToSummary} style={{width:"100%",padding:13,background:GR,color:W,border:"none",borderRadius:10,fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✅ Done — Back to Summary</button>
                  </div>
                ) : (
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:BL}}>{site}</div>
                      <div style={{fontSize:12,color:MT,marginTop:1}}>{fSec(elapsed)} · Started {tIn}</div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>setSc("home")} style={{background:W,border:"1.5px solid "+BD,borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK}}>‹ Home</button>
                      <button onClick={clockOut} style={{background:RD,border:"none",borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:W}}>Clock Out</button>
                    </div>
                  </div>
                )}
              </div>
              <div style={{display:"flex",gap:8,marginBottom:14}}>
                {[SHEET_SECTION_A,SHEET_SECTION_B].map(s=>(
                  <button key={s.key} onClick={()=>setSec(s.key)} style={{flex:1,padding:"10px 8px",border:"2px solid "+(sec===s.key?BL:BD),borderRadius:10,background:sec===s.key?LBL:W,color:sec===s.key?BL:MT,fontFamily:"inherit",fontWeight:700,fontSize:13,cursor:"pointer"}}>{s.tab}</button>
                ))}
              </div>
              <div style={{...card,border:"2px solid "+BL}}>
                <div style={{fontWeight:800,fontSize:15,color:BL,marginBottom:8}}>+ Add Plot Entry</div>
                {/* Plot number - full width */}
                <div style={{marginBottom:8}}>
                  <label style={lbl}>Plot No.</label>
                  <input style={{...inp,padding:"9px 8px"}} value={plot} onChange={e=>setPlot(e.target.value)} onFocus={()=>setActiveField("plot")} onBlur={()=>setTimeout(()=>setActiveField(null),200)} placeholder="120" inputMode="numeric"/>
                </div>
                {/* Speak + Add buttons — above all other inputs so keyboard NEVER covers them */}
                <div style={{display:"flex",gap:6,marginBottom:8}}>
                  <button onClick={voiceEntry} style={{flex:1,padding:12,background:listen&&lisF==="entry"?RD:GR,color:W,border:"none",borderRadius:10,cursor:"pointer",fontWeight:800,fontSize:15,fontFamily:"inherit"}}>
                    {listen&&lisF==="entry"?"🔴 Stop":activeField==="plot"?"🎤 Plot No.":activeField==="time"?"🎤 Time":activeField==="desc"?"🎤 Description":voiceStep==="plot"?"🎤 Plot No.":voiceStep==="time"?"🎤 Time":"🎤 Description"}
                  </button>
                  <button onClick={addE} style={{flex:1,padding:12,background:BL,color:W,border:"none",borderRadius:10,fontWeight:800,fontSize:15,cursor:"pointer",fontFamily:"inherit"}}>+ Add Entry</button>
                </div>
                {/* Live voice status */}
                {voiceStatus?<div style={{fontSize:13,color:listen&&lisF==="entry"?"#C0392B":GR,marginBottom:8,padding:"8px 12px",background:listen&&lisF==="entry"?"#FFF5F5":"#F0FAF4",borderRadius:10,border:"1px solid "+(listen&&lisF==="entry"?"#FBBCBC":"#A8D5B5"),lineHeight:1.4,wordBreak:"break-word"}}>{voiceStatus}</div>:null}
                {/* Time + Description — at bottom so keyboard can open safely */}
                <div style={{marginBottom:8}}>
                  <label style={lbl}>Time Spent</label>
                  <input style={{...inp,padding:"9px 8px"}} value={tSpent} onChange={e=>setTSpent(e.target.value)} onFocus={()=>setActiveField("time")} onBlur={()=>setTimeout(()=>setActiveField(null),200)} placeholder="2h, 30m"/>
                </div>
                <label style={lbl}>What Was Done</label>
                <textarea style={{...inp,minHeight:56,resize:"vertical"}} value={desc} onChange={e=>setDesc(e.target.value)} onFocus={()=>setActiveField("desc")} onBlur={()=>setTimeout(()=>setActiveField(null),200)} placeholder="e.g. making good, rub down"/>
              </div>
              {entries.length>0 && (
                <div style={card}>
                  <div style={{fontWeight:800,fontSize:14,marginBottom:10}}>Entries ({entries.length})</div>
                  {entries.map(e=>(
                    <div key={e.id} style={{background:e.sec==="mg"?"#EBF3FD":"#F0FFF4",borderRadius:10,padding:"9px 12px",marginBottom:7}}>
                      {e.editing ? (
                        <div>
                          <div style={{display:"flex",gap:8,marginBottom:6}}>
                            <input style={{...inp,flex:"0 0 80px",fontSize:13,padding:"6px 8px"}} defaultValue={e.plot} onChange={ev=>setEntries(p=>p.map(x=>x.id===e.id?{...x,plot:ev.target.value}:x))} placeholder="Plot"/>
                            <input style={{...inp,flex:1,fontSize:13,padding:"6px 8px"}} defaultValue={e.tt} onChange={ev=>setEntries(p=>p.map(x=>x.id===e.id?{...x,tt:ev.target.value,mins:round5(pMin(ev.target.value))}:x))} placeholder="Time"/>
                          </div>
                          <textarea style={{...inp,fontSize:13,padding:"6px 8px",minHeight:50,resize:"vertical",marginBottom:6}} defaultValue={e.desc} onChange={ev=>setEntries(p=>p.map(x=>x.id===e.id?{...x,desc:ev.target.value}:x))}/>
                          <button onClick={()=>setEntries(p=>p.map(x=>x.id===e.id?{...x,editing:false}:x))} style={{width:"100%",padding:"7px",background:GR,color:W,border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>✅ Done</button>
                        </div>
                      ) : (
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <div style={{flex:1}} onClick={()=>setShowPreview(true)}>
                            <div style={{fontWeight:800,fontSize:13,color:e.sec==="mg"?BL:GR}}>{e.plot?"PLT "+e.plot:e.sec==="mg"?"Worksheet":"Site Instruction"}{e.tt?" · "+e.tt:""}</div>
                            <div style={{fontSize:13,marginTop:2}}>{e.desc}</div>
                          </div>
                          <div style={{display:"flex",gap:4,paddingLeft:8}}>
                            <button onClick={()=>setEntries(p=>p.map(x=>x.id===e.id?{...x,editing:true}:x))} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,padding:2}}>✏️</button>
                            <button onClick={()=>setEntries(p=>p.filter(x=>x.id!==e.id))} style={{background:"none",border:"none",cursor:"pointer",color:MT,fontSize:20,padding:2}}>×</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={card}>
                <label style={lbl}>Materials / Plant / Access Equipment</label>
                <textarea style={{...inp,minHeight:60,resize:"vertical"}} value={mats} onChange={e=>setMats(e.target.value)} placeholder="List any materials or equipment used"/>
                <button onClick={()=>voice("mat")} style={{marginTop:8,padding:"8px 14px",background:listen&&lisF==="mat"?RD:F2,border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13,fontFamily:"inherit",color:listen&&lisF==="mat"?W:DK}}>{listen&&lisF==="mat"?"🔴 Listening...":"🎤 Speak"}</button>
              </div>
              {fromSummary && (
                <button onClick={backToSummary} style={{width:"100%",padding:14,background:GR,color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"inherit",marginTop:4}}>✅ Done — Back to Summary</button>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* v18.13 — PRICE WORK SHEET (worker earnings = qty × rate)         */}
          {/* Self-contained: worker picks plot + operation, enters HIS metres */}
          {/* and HIS rate. SVP = flat £35 tick. Custom items added inline.    */}
          {/* ══════════════════════════════════════════════════════════════ */}
          {sc==="pricework" && (() => {
            const selOp = pwCustomOpen ? null : PRICE_WORK_OPS.find(o=>o.id===pwOpId);
            const isFlat = selOp && selOp.formula==="flat";
            const lineTotal = (ln)=> ln.formula==="flat" ? (ln.flatPrice||0) : (Number(ln.qty)||0)*(Number(ln.rate)||0);
            const sheetTotal = pwLines.reduce((a,ln)=>a+lineTotal(ln),0);
            // Ordered op list: recently-used floats to top, rest follow.
            const orderedOps = [
              ...pwRecent.map(id=>PRICE_WORK_OPS.find(o=>o.id===id)).filter(Boolean),
              ...PRICE_WORK_OPS.filter(o=>!pwRecent.includes(o.id)),
            ];
            const addLine = () => {
              if(!pwPlot.trim()){alert("Enter a plot number first");return;}
              if(pwCustomOpen){
                if(!pwCustomName.trim()){alert("Give the custom item a name");return;}
                if(!(Number(pwQty)>0)||!(Number(pwRate)>0)){alert("Enter your metres and your rate");return;}
                setPwLines(p=>[...p,{id:Date.now(),plot:pwPlot.trim(),opId:"custom",name:pwCustomName.trim(),unit:pwCustomUnit,formula:"qty",qty:pwQty,rate:pwRate,custom:true}]);
                setPwCustomName("");setPwCustomOpen(false);setPwQty("");setPwRate("");
                return;
              }
              if(!selOp){alert("Pick an operation");return;}
              if(isFlat){
                setPwLines(p=>[...p,{id:Date.now(),plot:pwPlot.trim(),opId:selOp.id,name:selOp.name,unit:selOp.unit,formula:"flat",flatPrice:selOp.flatPrice}]);
              }else{
                if(!(Number(pwQty)>0)||!(Number(pwRate)>0)){alert("Enter your metres and your rate");return;}
                setPwLines(p=>[...p,{id:Date.now(),plot:pwPlot.trim(),opId:selOp.id,name:selOp.name,unit:selOp.unit,formula:"qty",qty:pwQty,rate:pwRate}]);
              }
              pushPwRecent(selOp.id);setPwRecent(getPwRecent());
              setPwOpId("");setPwQty("");setPwRate("");
            };
            return (
            <div>
              <div style={{background:isDark?"#12261c":"#F0FAF4",border:"1px solid "+(isDark?"#2f6b4a":"#A8D5B5"),padding:"18px 16px",marginBottom:16,position:"sticky",top:50,zIndex:50,borderRadius:"0 0 16px 16px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:GR}}>💷 Price Work · {site}</div>
                    <div style={{fontSize:12,color:MT,marginTop:1}}>{tOut?("Clocked out "+tOut):(fSec(elapsed)+" · Started "+tIn)}</div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>setSc("home")} style={{background:W,border:"1.5px solid "+BD,borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK}}>‹ Home</button>
                    {tOut
                      ? <button onClick={()=>setSc("pwsummary")} style={{background:GR,border:"none",borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:W}}>‹ Back to sheet</button>
                      : <button onClick={clockOut} style={{background:RD,border:"none",borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:W}}>Clock Out</button>
                    }
                  </div>
                </div>
              </div>

              {/* Running total */}
              <div style={{...card,border:"2px solid "+GR,background:isDark?"#12261c":"#F0FAF4",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".8px"}}>Your earnings so far</div>
                  <div style={{fontSize:11,color:MT,marginTop:2}}>{pwLines.length} {pwLines.length===1?"line":"lines"}</div>
                </div>
                <div style={{fontSize:28,fontWeight:900,color:GR}}>{gbp(sheetTotal)}</div>
              </div>

              {/* Add line */}
              <div style={{...card,border:"2px solid "+GR}}>
                <div style={{fontWeight:800,fontSize:15,color:GR,marginBottom:8}}>+ Add Price Work Line</div>
                <div style={{marginBottom:10}}>
                  <label style={lbl}>Plot No.</label>
                  <input style={{...inp,padding:"9px 8px"}} value={pwPlot} onChange={e=>setPwPlot(e.target.value)} placeholder="120" inputMode="numeric"/>
                </div>

                {/* v18.26 — OFFICE BOOKED pull. Shows what the office tagged for this   */}
                {/* plot so the worker can confirm or override. Pull-only. Colour + text.*/}
                {pwPlot.trim() && site.trim() && (()=>{
                  const key=site.trim()+"||"+pwPlot.trim();
                  const ready=pwOfficeFor===key;
                  if(pwOfficeLoading && !ready){
                    return (<div style={{border:"1px solid "+BD,borderRadius:10,padding:"10px 12px",marginBottom:10,background:isDark?"#0f1a2e":"#F5F8FC",fontSize:12,color:MT}}>Checking office booking for Plot {pwPlot.trim()}…</div>);
                  }
                  if(!ready) return null;
                  if(pwOfficeBooked.length===0){
                    return (<div style={{border:"1px dashed "+BD,borderRadius:10,padding:"10px 12px",marginBottom:10,background:isDark?"#0f1a2e":"#F5F8FC",fontSize:12,color:MT}}>
                      <span style={{fontWeight:800,color:MT,letterSpacing:".4px"}}>NO OFFICE BOOKING</span> — the office hasn't tagged Plot {pwPlot.trim()} on {site}. Enter your lines as normal below.
                    </div>);
                  }
                  const alreadyOn=(name)=>pwLines.some(l=>String(l.plot)===pwPlot.trim() && (l.name||"").toLowerCase().trim()===(name||"").toLowerCase().trim());
                  return (
                    <div style={{border:"2px solid "+GR,borderRadius:12,padding:"12px",marginBottom:12,background:isDark?"#12261c":"#F0FAF4"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                        <span style={{fontSize:12,fontWeight:800,color:GR,textTransform:"uppercase",letterSpacing:".6px"}}>📋 Office booked · Plot {pwPlot.trim()}</span>
                        <span style={{fontSize:11,color:MT,fontWeight:700}}>{pwOfficeBooked.length} {pwOfficeBooked.length===1?"item":"items"}</span>
                      </div>
                      <div style={{fontSize:11,color:MT,marginBottom:8,lineHeight:1.4}}>Tap <strong style={{color:GR}}>Use this</strong> to load the office quantity, then confirm or change your metres and enter your rate.</div>
                      {pwOfficeBooked.map((ob,ix)=>{
                        const isFlatOb=ob.formula_type==="flat";
                        const qtyTxt=isFlatOb?"flat":( (ob.office_qty!=null?ob.office_qty:0)+(ob.unit?(" "+ob.unit):"") );
                        const on=alreadyOn(ob.operation);
                        return (
                          <div key={ix} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,background:isDark?"#0f1f16":"#FFFFFF",border:"1px solid "+(isDark?"#2f6b4a":"#CBEBD6"),borderRadius:8,padding:"8px 10px",marginBottom:6}}>
                            <div style={{minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:700,color:DK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ob.operation}</div>
                              <div style={{fontSize:11,color:MT}}>Office qty: <strong style={{color:DK}}>{qtyTxt}</strong></div>
                            </div>
                            {on ? (
                              <span style={{flexShrink:0,fontSize:11,fontWeight:800,color:GR,border:"1px solid "+GR,borderRadius:7,padding:"6px 9px"}}>✓ ADDED</span>
                            ) : (
                              <button onClick={()=>{
                                const matched=PRICE_WORK_OPS.find(o=>o.name.toLowerCase().trim()===(ob.operation||"").toLowerCase().trim());
                                if(isFlatOb){
                                  // flat item (e.g. SVP) — add straight in at the office flat price
                                  setPwLines(p=>[...p,{id:Date.now(),plot:pwPlot.trim(),opId:matched?matched.id:"custom",name:ob.operation,unit:ob.unit||"",formula:"flat",flatPrice:(Number(ob.flat_price)||0),custom:!matched}]);
                                  if(matched){pushPwRecent(matched.id);setPwRecent(getPwRecent());}
                                  return;
                                }
                                // qty item — prefill the add-line form so he confirms metres + enters his rate
                                setPwCustomOpen(!matched);
                                if(matched){ setPwOpId(matched.id); }
                                else { setPwCustomName(ob.operation); setPwCustomUnit(ob.unit||"unit"); }
                                setPwQty(ob.office_qty!=null?String(ob.office_qty):"");
                                setPwRate("");
                              }} style={{flexShrink:0,background:GR,color:W,border:"none",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Use this →</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                <label style={lbl}>Operation</label>
                {!pwCustomOpen ? (
                  <div style={{position:"relative",marginBottom:10}}>
                    <button type="button" onClick={()=>setPwOpMenuOpen(true)} style={{...inp,padding:"11px 10px",background:"#fff",color:"#000",textAlign:"left",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",fontFamily:"inherit"}}>
                      <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{(()=>{if(!pwOpId)return "— Pick what you did —";const o=orderedOps.find(x=>x.id===pwOpId);return o?o.name+(o.formula==="flat"?" (flat "+gbp(o.flatPrice)+")":" · per "+o.unit):"— Pick what you did —";})()}</span>
                      <span style={{color:"#666",marginLeft:8,fontSize:12}}>▼</span>
                    </button>
                    {pwOpMenuOpen && (
                      <div onClick={()=>setPwOpMenuOpen(false)} style={{position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,.4)"}}>
                        <div onClick={e=>e.stopPropagation()} style={{position:"absolute",left:"50%",top:"50%",transform:"translate(-50%,-50%)",width:"min(92vw,420px)",maxHeight:"80vh",overflowY:"auto",background:"#fff",borderRadius:14,boxShadow:"0 10px 40px rgba(0,0,0,.5)",WebkitOverflowScrolling:"touch"}}>
                          <div style={{padding:"14px 16px",fontSize:13,fontWeight:800,color:"#666",textTransform:"uppercase",letterSpacing:".6px",borderBottom:"1px solid #eee",position:"sticky",top:0,background:"#fff"}}>Pick what you did</div>
                          {orderedOps.map(o=>{
                            const sel=pwOpId===o.id;
                            return (
                              <div key={o.id} onClick={()=>{setPwOpId(o.id);setPwOpMenuOpen(false);}} style={{padding:"14px 16px",fontSize:16,color:"#000",background:sel?"#EAF4FF":"#fff",borderBottom:"1px solid #f0f0f0",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}>
                                <span>{o.name}{o.formula==="flat"?" (flat "+gbp(o.flatPrice)+")":" · per "+o.unit}</span>
                                {sel && <span style={{color:"#2563eb",fontWeight:900,fontSize:18}}>✓</span>}
                              </div>
                            );
                          })}
                          <div onClick={()=>{setPwCustomOpen(true);setPwOpId("");setPwOpMenuOpen(false);}} style={{padding:"14px 16px",fontSize:16,color:"#000",background:"#fff",borderTop:"1px solid #eee",cursor:"pointer",fontWeight:700}}>➕ Add custom item…</div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{border:"1.5px dashed "+GR,borderRadius:10,padding:10,marginBottom:10,background:isDark?"#12261c":"#F0FAF4"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <span style={{fontSize:12,fontWeight:800,color:GR,textTransform:"uppercase",letterSpacing:".6px"}}>CUSTOM ITEM</span>
                      <button onClick={()=>{setPwCustomOpen(false);setPwCustomName("");}} style={{background:"none",border:"none",color:MT,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                    </div>
                    <input style={{...inp,padding:"9px 8px",marginBottom:8}} value={pwCustomName} onChange={e=>setPwCustomName(e.target.value)} placeholder="e.g. Cabin frame"/>
                    <label style={lbl}>Unit</label>
                    <select value={pwCustomUnit} onChange={e=>setPwCustomUnit(e.target.value)} style={{...inp,padding:"9px 8px",appearance:"auto"}}>
                      <option value="unit">per unit</option>
                      <option value="m²">per m²</option>
                      <option value="LM">per LM (metre)</option>
                      <option value="house">per house</option>
                    </select>
                  </div>
                )}

                {/* Metres + rate — hidden for flat-price (SVP) */}
                {!isFlat && (
                  <div style={{display:"flex",gap:8,marginBottom:10}}>
                    <div style={{flex:1}}>
                      <label style={lbl}>Your {(pwCustomOpen?pwCustomUnit:(selOp?selOp.unit:"qty"))==="LM"?"metres":(pwCustomOpen?pwCustomUnit:selOp&&selOp.unit)||"qty"}</label>
                      <input style={{...inp,padding:"9px 8px"}} value={pwQty} onChange={e=>setPwQty(e.target.value)} placeholder="100" inputMode="decimal"/>
                    </div>
                    <div style={{flex:1}}>
                      <label style={lbl}>Your rate £</label>
                      <input style={{...inp,padding:"9px 8px"}} value={pwRate} onChange={e=>setPwRate(e.target.value)} placeholder="2.80" inputMode="decimal"/>
                    </div>
                  </div>
                )}

                {/* Live line preview */}
                {(isFlat || (Number(pwQty)>0 && Number(pwRate)>0)) && (
                  <div style={{fontSize:13,fontWeight:700,color:GR,marginBottom:10,textAlign:"center",padding:"8px",background:isDark?"#12261c":"#F0FAF4",borderRadius:8}}>
                    {isFlat ? "Flat price = "+gbp(selOp.flatPrice) : (pwQty+" × "+gbp(pwRate)+" = "+gbp((Number(pwQty)||0)*(Number(pwRate)||0)))}
                  </div>
                )}

                <button onClick={addLine} style={{width:"100%",padding:12,background:GR,color:W,border:"none",borderRadius:10,fontWeight:800,fontSize:15,cursor:"pointer",fontFamily:"inherit"}}>+ Add Line</button>
              </div>

              {/* Lines list */}
              {pwLines.length>0 && (
                <div style={card}>
                  <div style={{fontWeight:800,fontSize:14,marginBottom:10,color:DK}}>Lines ({pwLines.length})</div>
                  {pwLines.map(ln=>(
                    <div key={ln.id} style={{background:isDark?"#12261c":"#F0FFF4",borderRadius:10,padding:"9px 12px",marginBottom:7}}>
                      {pwEditId===ln.id && ln.formula!=="flat" ? (
                        <div>
                          <div style={{fontWeight:800,fontSize:13,color:GR,marginBottom:6}}>PLT {ln.plot} · {ln.name}{ln.custom?" · CUSTOM":""}</div>
                          <div style={{display:"flex",gap:8,marginBottom:6}}>
                            <input style={{...inp,flex:1,fontSize:13,padding:"6px 8px"}} defaultValue={ln.qty} onChange={ev=>setPwLines(p=>p.map(x=>x.id===ln.id?{...x,qty:ev.target.value}:x))} placeholder="Qty" inputMode="decimal"/>
                            <input style={{...inp,flex:1,fontSize:13,padding:"6px 8px"}} defaultValue={ln.rate} onChange={ev=>setPwLines(p=>p.map(x=>x.id===ln.id?{...x,rate:ev.target.value}:x))} placeholder="Rate £" inputMode="decimal"/>
                          </div>
                          <button onClick={()=>setPwEditId(null)} style={{width:"100%",padding:"7px",background:GR,color:W,border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>✅ Done</button>
                        </div>
                      ) : (
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <div style={{flex:1,cursor:"pointer"}} onClick={()=>{setPwEditId(null);setSc("pwsummary");}}>
                            <div style={{fontWeight:800,fontSize:13,color:GR}}>PLT {ln.plot} · {ln.name}{ln.custom?" ·":""}{ln.custom?<span style={{color:MT,fontWeight:700}}> CUSTOM</span>:null}</div>
                            <div style={{fontSize:13,marginTop:2,color:DK}}>
                              {ln.formula==="flat" ? "Flat "+gbp(ln.flatPrice) : (ln.qty+" "+ln.unit+" × "+gbp(ln.rate))}
                            </div>
                            <div style={{fontSize:11,marginTop:3,color:MT,fontWeight:600}}>👁 Tap to view sheet</div>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8,paddingLeft:8}}>
                            <div style={{fontWeight:900,fontSize:15,color:GR}}>{gbp(lineTotal(ln))}</div>
                            {ln.formula!=="flat" && <button onClick={()=>{setPwEditId(ln.id);setSc("pwsummary");}} style={{background:"none",border:"none",cursor:"pointer",fontSize:15,padding:2}}>✏️</button>}
                            <button onClick={()=>setPwLines(p=>p.filter(x=>x.id!==ln.id))} style={{background:"none",border:"none",cursor:"pointer",color:MT,fontSize:20,padding:2}}>×</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8,paddingTop:10,borderTop:"1.5px solid "+BD}}>
                    <span style={{fontWeight:800,fontSize:14,color:DK}}>Sheet total</span>
                    <span style={{fontWeight:900,fontSize:20,color:GR}}>{gbp(sheetTotal)}</span>
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* v18.14 — PRICE WORK REVIEW (clock-out lands here in pwMode)      */}
          {/* Mirrors the timesheet review: click through, edit, submit.      */}
          {/* ONE SHEET PER PLOT — a plot may span several days.              */}
          {/* ══════════════════════════════════════════════════════════════ */}
          {sc==="pwsummary" && (() => {
            const lineTot=(ln)=> ln.formula==="flat" ? (ln.flatPrice||0) : (Number(ln.qty)||0)*(Number(ln.rate)||0);
            // Group lines by plot (preserve first-seen order).
            const order=[]; const byPlot={};
            pwLines.forEach(ln=>{const k=String(ln.plot);if(!byPlot[k]){byPlot[k]=[];order.push(k);}byPlot[k].push(ln);});
            const grandTotal=pwLines.reduce((a,ln)=>a+lineTot(ln),0);
            return (
            <div>
              <div style={{background:isDark?"#12261c":"#F0FAF4",border:"1px solid "+(isDark?"#2f6b4a":"#A8D5B5"),padding:"18px 16px",marginBottom:16,position:"sticky",top:50,zIndex:50,borderRadius:"0 0 16px 16px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:16,fontWeight:800,color:GR}}>💷 Price Work — Review & Submit</div>
                    <div style={{fontSize:12,color:MT,marginTop:2}}>{sheetSite||site} · {tIn}–{tOut}</div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>setSc("home")} style={{background:W,border:"1.5px solid "+BD,borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK}}>‹ Home</button>
                    <button onClick={()=>setSc("pricework")} style={{background:W,border:"1.5px solid "+BD,borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK}}>+ Add line</button>
                    <button onClick={()=>{setPwWeekOffset(0);setSc("pwweek");}} style={{background:W,border:"1.5px solid "+GR,borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:GR}}>📅 Price Work</button>
                  </div>
                </div>
              </div>

              {pwLines.length===0 && (
                <div style={{...card,textAlign:"center",padding:24}}>
                  <div style={{fontSize:14,color:MT,marginBottom:14}}>No price work lines yet.</div>
                  <button onClick={()=>setSc("pricework")} style={{padding:"11px 18px",background:GR,color:W,border:"none",borderRadius:10,fontWeight:800,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>+ Add lines</button>
                </div>
              )}

              {order.map(plot=>{
                const lines=byPlot[plot];
                const plotTotal=lines.reduce((a,ln)=>a+lineTot(ln),0);
                const sent=pwSubmittedPlots[plot];
                const _pwSheetSite=sheetSite||site;
                const _pwC={border:"1px solid #444",padding:"4px 6px",fontFamily:"Arial,sans-serif",fontSize:12,color:"#111",verticalAlign:"middle",background:"#fff"};
                const _pwCB={..._pwC,background:"#BDD6F1",fontWeight:700};
                const _pwSheetNum=(sent&&sent.sheetNumber)?sent.sheetNumber:"PW-2026-XXXX";
                return (
                  <div key={plot} style={{...card,border:"2px solid "+(sent?"#A8D5B5":GR)}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                      <div style={{fontWeight:900,fontSize:16,color:DK}}>Plot {plot}</div>
                      <div style={{fontWeight:900,fontSize:18,color:GR}}>{gbp(plotTotal)}</div>
                    </div>

                    {/* v18.21 — worker's own invoice number (their series, separate from BAC sheet number) */}
                    {!sent && (
                      <div style={{marginBottom:12}}>
                        <label style={{...lbl,marginBottom:3}}>Your invoice number (your own reference)</label>
                        <input style={{...inp,fontSize:14}} value={pwInvoiceNos[plot]||""} onChange={ev=>setPwInvoiceNos(p=>({...p,[plot]:ev.target.value}))} placeholder="e.g. INV-2026-001"/>
                      </div>
                    )}

                    {/* v18.20 — FULL A4 PRICE WORK SHEET scaled to fit the screen. All columns fit, DATE always visible, no cut-off. */}
                    <div style={{fontWeight:700,fontSize:11,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:8}}>Price Work Sheet</div>
                    <div style={{width:"100%",overflow:"hidden",marginBottom:14,position:"relative",height:(pwSheetHs[plot]?Math.round(pwSheetHs[plot]*scale):Math.round((300+lines.length*30)*scale))+"px"}}>
                      <div style={{transformOrigin:"top left",transform:"scale("+scale+")",width:SHEET_W,position:"absolute",top:0,left:0}}>
                      <div ref={el=>{if(el){const h=el.offsetHeight;if(h&&pwSheetHs[plot]!==h)setPwSheetHs(p=>({...p,[plot]:h}));}}} style={{width:SHEET_W,background:"#fff",border:"1.5px solid #333",padding:"8px 12px 10px",fontFamily:"Arial,sans-serif",boxSizing:"border-box"}}>
                        <div style={{display:"flex",alignItems:"center",marginBottom:8,gap:14}}>
                          <div style={{flexShrink:0,width:60,height:60}}><img src={LOGO} alt="SiteVouch" style={{width:"100%",height:"100%",objectFit:"contain",display:"block"}}/></div>
                          <div style={{flex:1,textAlign:"center"}}><span style={{fontWeight:900,fontSize:20,letterSpacing:2,textDecoration:"underline",color:"#111"}}>PRICE WORK SHEET</span></div>
                          <div style={{textAlign:"right"}}><div style={{fontSize:17,fontWeight:900,color:"#1a3f88"}}>{_pwSheetNum}</div><div style={{fontSize:10,color:"#888"}}>Sheet Number</div></div>
                        </div>
                        <table style={{width:"100%",borderCollapse:"collapse",marginBottom:6}}><tbody>
                          <tr>
                            <td rowSpan={2} style={{..._pwCB,width:"20%",textAlign:"center",fontSize:11}}>SUB-CONTRACTOR NAME</td>
                            <td rowSpan={2} style={{..._pwC,width:"30%",fontSize:15,fontWeight:700,textAlign:"center"}}>{name}</td>
                            <td style={{..._pwCB,width:"15%",fontSize:11}}>DATE</td>
                            <td style={{..._pwC,width:"35%",fontSize:13}}>{sheetDate?new Date(sheetDate+"T00:00:00").toLocaleDateString("en-GB"):new Date().toLocaleDateString("en-GB")}</td>
                          </tr>
                          <tr>
                            <td style={{..._pwCB,fontSize:11}}>PLOT</td>
                            <td style={{..._pwC,fontSize:13,fontWeight:700}}>{plot}</td>
                          </tr>
                          <tr>
                            <td style={{..._pwCB,fontSize:11}}>SITE LOCATION</td>
                            <td style={{..._pwC,fontSize:13}}>{LC[_pwSheetSite]||_pwSheetSite}</td>
                            <td style={{..._pwCB,fontSize:11}}>CONTRACTOR</td>
                            <td style={{..._pwC,fontSize:13}}>{CT[_pwSheetSite]||ctr||""}</td>
                          </tr>
                          <tr>
                            <td style={{..._pwCB,fontSize:11}}>YOUR INVOICE No.</td>
                            <td colSpan={3} style={{..._pwC,fontSize:13,fontWeight:700}}>{pwInvoiceNos[plot]||"—"}</td>
                          </tr>
                        </tbody></table>
                        <table style={{width:"100%",borderCollapse:"collapse",marginBottom:6}}><tbody>
                          <tr>
                            <td style={{..._pwCB,fontSize:12,textAlign:"left"}}>OPERATION</td>
                            <td style={{..._pwCB,fontSize:12,width:"18%",textAlign:"center"}}>QTY</td>
                            <td style={{..._pwCB,fontSize:12,width:"18%",textAlign:"center"}}>RATE £</td>
                            <td style={{..._pwCB,fontSize:12,width:"20%",textAlign:"right"}}>TOTAL £</td>
                          </tr>
                          {lines.map(ln=>(
                            <tr key={ln.id}>
                              <td style={{..._pwC,fontSize:13}}>{ln.name}{ln.custom?" (CUSTOM)":""}</td>
                              <td style={{..._pwC,fontSize:13,textAlign:"center"}}>{ln.formula==="flat"?"—":(ln.qty+" "+ln.unit)}</td>
                              <td style={{..._pwC,fontSize:13,textAlign:"center"}}>{ln.formula==="flat"?"flat":gbp(ln.rate).replace("£","")}</td>
                              <td style={{..._pwC,fontSize:13,textAlign:"right",fontWeight:700}}>{gbp(lineTot(ln)).replace("£","")}</td>
                            </tr>
                          ))}
                          {Array.from({length:Math.max(0,6-lines.length)}).map((_,i)=>(
                            <tr key={"blank"+i}>
                              <td style={{..._pwC,height:26}}>&nbsp;</td>
                              <td style={{..._pwC}}>&nbsp;</td>
                              <td style={{..._pwC}}>&nbsp;</td>
                              <td style={{..._pwC}}>&nbsp;</td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan={3} style={{..._pwCB,fontSize:14,textAlign:"right"}}>PLOT TOTAL</td>
                            <td style={{..._pwCB,fontSize:15,textAlign:"right",fontWeight:900}}>{gbp(plotTotal)}</td>
                          </tr>
                        </tbody></table>
                        <table style={{width:"100%",borderCollapse:"collapse"}}><tbody><tr>
                          <td style={{..._pwCB,width:"30%",fontSize:11}}>SiteVouch Supervisor Signature</td>
                          <td style={{..._pwC,width:"30%",padding:"16px"}}>&nbsp;</td>
                          <td style={{..._pwCB,width:"10%",fontSize:11}}>Date</td>
                          <td style={{..._pwC,width:"30%",padding:"16px"}}>&nbsp;</td>
                        </tr></tbody></table>
                      </div>
                      </div>
                    </div>

                    <button onClick={()=>printPwSheet(plot)} style={{width:"100%",marginBottom:12,padding:11,background:W,color:GR,border:"1.5px solid "+GR,borderRadius:10,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>🖨 Print / Save as PDF</button>

                    {!sent && <div style={{fontSize:11,color:MT,marginBottom:8,fontWeight:600}}>Check the sheet above. Tap a line below to change a quantity or rate before you submit.</div>}
                    {lines.map(ln=>(
                      <div key={ln.id} style={{background:isDark?"#12261c":"#F0FFF4",borderRadius:10,padding:"9px 12px",marginBottom:7}}>
                        {pwEditId===ln.id && ln.formula!=="flat" ? (
                          <div>
                            <div style={{fontWeight:800,fontSize:13,color:GR,marginBottom:6}}>{ln.name}{ln.custom?" · CUSTOM":""}</div>
                            <div style={{display:"flex",gap:8,marginBottom:6}}>
                              <div style={{flex:1}}>
                                <label style={{...lbl,marginBottom:3}}>Quantity ({ln.unit})</label>
                                <div style={{display:"flex",alignItems:"center",gap:6}}>
                                  <input style={{...inp,flex:1,fontSize:13,padding:"6px 8px"}} defaultValue={ln.qty} onChange={ev=>setPwLines(p=>p.map(x=>x.id===ln.id?{...x,qty:ev.target.value}:x))} placeholder="Qty" inputMode="decimal"/>
                                  <span style={{fontSize:13,fontWeight:700,color:MT,minWidth:24}}>{ln.unit}</span>
                                </div>
                              </div>
                              <div style={{flex:1}}>
                                <label style={{...lbl,marginBottom:3}}>Rate (£)</label>
                                <div style={{display:"flex",alignItems:"center",gap:6}}>
                                  <span style={{fontSize:13,fontWeight:700,color:MT}}>£</span>
                                  <input style={{...inp,flex:1,fontSize:13,padding:"6px 8px"}} defaultValue={ln.rate} onChange={ev=>setPwLines(p=>p.map(x=>x.id===ln.id?{...x,rate:ev.target.value}:x))} placeholder="Rate" inputMode="decimal"/>
                                </div>
                              </div>
                            </div>
                            <button onClick={()=>setPwEditId(null)} style={{width:"100%",padding:"7px",background:GR,color:W,border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>✅ Done</button>
                          </div>
                        ) : (
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                            <div style={{flex:1}}>
                              <div style={{fontWeight:800,fontSize:13,color:GR}}>{ln.name}{ln.custom?<span style={{color:MT,fontWeight:700}}> · CUSTOM</span>:null}</div>
                              <div style={{fontSize:13,marginTop:2,color:DK}}>{ln.formula==="flat"?"Flat "+gbp(ln.flatPrice):(ln.qty+" "+ln.unit+" × "+gbp(ln.rate))}</div>
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:6,paddingLeft:8}}>
                              <div style={{fontWeight:900,fontSize:14,color:GR}}>{gbp(lineTot(ln))}</div>
                              {!sent && ln.formula!=="flat" && <button onClick={()=>setPwEditId(ln.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:15,padding:2}}>✏️</button>}
                              {!sent && <button onClick={()=>setPwLines(p=>p.filter(x=>x.id!==ln.id))} style={{background:"none",border:"none",cursor:"pointer",color:MT,fontSize:20,padding:2}}>×</button>}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {sent ? (
                      <div style={{marginTop:8,background:"#F0FAF4",border:"1px solid #A8D5B5",borderRadius:12,padding:11,fontSize:13,fontWeight:700,color:GR,textAlign:"center"}}>
                        ✅ Sent to Office · {sent.sheetNumber}
                      </div>
                    ) : (
                      <button onClick={()=>submitPwToOffice(plot)} disabled={pwSubmitting} style={{width:"100%",marginTop:8,padding:13,background:pwSubmitting?"#D1D5DB":GR,color:W,border:"none",borderRadius:12,fontSize:15,fontWeight:800,cursor:pwSubmitting?"not-allowed":"pointer",fontFamily:"inherit"}}>{pwSubmitting?"Sending…":"📤 Send Plot "+plot+" to Office"}</button>
                    )}
                  </div>
                );
              })}

              {pwLines.length>0 && (
                <div style={{...card,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontWeight:800,fontSize:14,color:DK}}>All plots total</span>
                  <span style={{fontWeight:900,fontSize:20,color:GR}}>{gbp(grandTotal)}</span>
                </div>
              )}

              <button onClick={()=>{setPwMode(false);newDay();}} style={{width:"100%",padding:14,background:W,border:"1.5px solid "+BD,borderRadius:14,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK,marginBottom:20}}>Done — Start New Day</button>
            </div>
            );
          })()}

          {/* v18.25 — Price Work sends straight to Office (it's an invoice, not a daywork sign-off). No supervisor picker. */}

          {/* v18.30 — MY WEEK: read-only rollup of this worker's submitted price work
              claims for a Mon–Sun window. Staples the week's plot sheets into one
              priced summary. Deliberately NOT a tax invoice: no CIS, no VAT, no
              deductions are calculated here — records, never decisions. */}
          {sc==="pwweek" && (() => {
            const win=pwWeekWindow(pwWeekOffset);
            const linesByClaim={};
            pwWeekLines.forEach(ln=>{(linesByClaim[ln.claim_id]=linesByClaim[ln.claim_id]||[]).push(ln);});
            const claimTot=(c)=>{const ls=linesByClaim[c.id];if(ls&&ls.length)return ls.reduce((a,ln)=>a+(Number(ln.value)||0),0);return Number(c.sheet_total)||0;};
            const grossTotal=pwWeekClaims.reduce((a,c)=>a+claimTot(c),0);
            const wkBtn=(on)=>({flex:1,padding:"8px 10px",borderRadius:8,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",border:"1.5px solid "+(on?GR:BD),background:on?GR:W,color:on?W:DK});
            return (
            <div>
              <div style={{background:isDark?"#12261c":"#F0FAF4",border:"1px solid "+(isDark?"#2f6b4a":"#A8D5B5"),padding:"18px 16px",marginBottom:12,position:"sticky",top:50,zIndex:50,borderRadius:"0 0 16px 16px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <div>
                    <div style={{fontSize:16,fontWeight:800,color:GR}}>📅 Price Work</div>
                    <div style={{fontSize:12,color:MT,marginTop:2}}>{win.label} · {name}</div>
                  </div>
                  <button onClick={()=>setSc("home")} style={{background:W,border:"1.5px solid "+BD,borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK}}>‹ Home</button>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setPwWeekOffset(0)} style={wkBtn(pwWeekOffset===0)}>This week</button>
                  <button onClick={()=>setPwWeekOffset(1)} style={wkBtn(pwWeekOffset===1)}>Last week</button>
                </div>
              </div>

              <div style={{...card,background:isDark?"#2a2410":"#FFF8E1",border:"1.5px solid #F59E0B",padding:"10px 14px",marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:800,color:isDark?"#FCD34D":"#92400E"}}>⚠️ Summary only — NOT a tax invoice</div>
                <div style={{fontSize:11,color:isDark?"#FCD34D":"#92400E",marginTop:3,lineHeight:1.5}}>This staples together the price work sheets you already submitted. No CIS deduction or VAT is calculated here. Your official pay and CIS statement come from the contractor.</div>
              </div>

              {!pwWeekLoading && !pwWeekErr && pwWeekClaims.length>0 && (
                <div style={{...card,padding:14,marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:pwEditHeader?10:0}}>
                    <div style={{fontSize:13,fontWeight:800,color:DK}}>Sheet details</div>
                    <button onClick={()=>setPwEditHeader(v=>!v)} style={{background:"none",border:"1px solid "+BD,borderRadius:8,padding:"5px 10px",fontSize:12,fontWeight:700,color:BL,cursor:"pointer",fontFamily:"inherit"}}>{pwEditHeader?"Done":"✎ Edit header"}</button>
                  </div>

                  {pwEditHeader && (
                    <div style={{marginBottom:12}}>
                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>Trading name (person or company)</label>
                      <input value={pwTradeName} onChange={e=>{setPwTradeName(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_trade_name",e.target.value);}catch(x){}}} placeholder={name||"e.g. SiteVouch Ltd"} style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:10}}/>

                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>Contractor (who you're billing)</label>
                      <input value={pwContractor} onChange={e=>{setPwContractor(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_contractor",e.target.value);}catch(x){}}} placeholder="e.g. SiteVouch Ltd" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:10}}/>

                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>Bill To — their contact name (optional)</label>
                      <input value={pwBillContact} onChange={e=>{setPwBillContact(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_bill_contact",e.target.value);}catch(x){}}} placeholder="e.g. Sam Rivers" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:10}}/>

                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>Bill To — their email (optional)</label>
                      <input value={pwBillEmail} onChange={e=>{setPwBillEmail(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_bill_email",e.target.value);}catch(x){}}} placeholder="e.g. info@sitevouch.io" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:10}}/>

                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>Your address (optional)</label>
                      <textarea value={pwAddress} onChange={e=>{setPwAddress(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_address",e.target.value);}catch(x){}}} placeholder={"14 Marine Parade\nBrighton\nEast Sussex BN2 1TL"} rows={3} style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:10,resize:"vertical"}}/>

                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>Your phone (optional)</label>
                      <input value={pwPhone} onChange={e=>{setPwPhone(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_phone",e.target.value);}catch(x){}}} placeholder="e.g. 07700 900123" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:10}}/>

                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>Your email (optional)</label>
                      <input value={pwEmail} onChange={e=>{setPwEmail(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_email",e.target.value);}catch(x){}}} placeholder="e.g. info@sitevouch.io" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:10}}/>

                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>Logo (optional — appears on the sheet)</label>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        {pwLogo && <img src={pwLogo} alt="logo" style={{height:40,maxWidth:120,objectFit:"contain",border:"1px solid "+BD,borderRadius:6,padding:2,background:"#fff"}}/>}
                        <label style={{display:"inline-block",background:W,border:"1.5px solid "+BD,borderRadius:8,padding:"8px 12px",fontSize:13,fontWeight:700,color:DK,cursor:"pointer",fontFamily:"inherit"}}>
                          {pwLogo?"Change logo":"Upload logo"}
                          <input type="file" accept="image/*" onChange={e=>{onPwLogoPick(e.target.files&&e.target.files[0]);e.target.value="";}} style={{display:"none"}}/>
                        </label>
                        {pwLogo && <button onClick={()=>{setPwLogo("");setPwWeekPdf(null);try{localStorage.removeItem("bac_pw_logo");}catch(x){}}} style={{background:"none",border:"none",color:RD,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>}
                      </div>
                      <div style={{fontSize:11,color:MT,marginTop:8,lineHeight:1.5}}>Saved on this device and reused each week. Header prints: <b>{(pwTradeName||name)||"—"}</b>{pwContractor?" → "+pwContractor:""}.</div>

                      <div style={{borderTop:"1px solid "+BD,margin:"14px 0 12px"}}></div>

                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>UTR (optional)</label>
                      <input value={pwUtr} onChange={e=>{setPwUtr(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_utr",e.target.value);}catch(x){}}} placeholder="e.g. 1234567890" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:10}}/>

                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>Company No. (optional)</label>
                      <input value={pwCoNo} onChange={e=>{setPwCoNo(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_co_no",e.target.value);}catch(x){}}} placeholder="e.g. 12345678" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:10}}/>

                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>Payment details (optional — bank / sort code / account)</label>
                      <textarea value={pwPayInfo} onChange={e=>{setPwPayInfo(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_pay_info",e.target.value);}catch(x){}}} placeholder={"Bank name\nAccount: 00000000\nSort code: 00-00-00"} rows={3} style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:12,resize:"vertical"}}/>

                      <div style={{borderTop:"1px solid "+BD,margin:"2px 0 12px"}}></div>

                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:pwCisOn?8:12}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:800,color:DK}}>CIS deduction {pwCisOn?"— ON":"— OFF"}</div>
                          <div style={{fontSize:11,color:MT}}>Deducted from labour. Most sole traders leave this on.</div>
                        </div>
                        <button onClick={()=>{const v=!pwCisOn;setPwCisOn(v);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_cis_on",v?"1":"0");}catch(x){}}} style={{background:pwCisOn?GR:W,color:pwCisOn?W:DK,border:"1.5px solid "+(pwCisOn?GR:BD),borderRadius:8,padding:"7px 12px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",minWidth:64}}>{pwCisOn?"ON":"OFF"}</button>
                      </div>
                      {pwCisOn && (
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                          <label style={{fontSize:12,fontWeight:700,color:MT}}>CIS rate %</label>
                          <input value={pwCisRate} onChange={e=>{setPwCisRate(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_cis_rate",e.target.value);}catch(x){}}} inputMode="decimal" style={{width:70,border:"1px solid "+BD,borderRadius:8,padding:"7px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box"}}/>
                        </div>
                      )}

                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:pwVatOn?8:0}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:800,color:DK}}>VAT {pwVatOn?"— ON":"— OFF"}</div>
                          <div style={{fontSize:11,color:MT}}>Only if VAT registered (rare).</div>
                        </div>
                        <button onClick={()=>{const v=!pwVatOn;setPwVatOn(v);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_vat_on",v?"1":"0");}catch(x){}}} style={{background:pwVatOn?GR:W,color:pwVatOn?W:DK,border:"1.5px solid "+(pwVatOn?GR:BD),borderRadius:8,padding:"7px 12px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",minWidth:64}}>{pwVatOn?"ON":"OFF"}</button>
                      </div>
                      {pwVatOn && (
                        <div style={{marginTop:8}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                            <label style={{fontSize:12,fontWeight:700,color:MT}}>VAT rate %</label>
                            <input value={pwVatRate} onChange={e=>{setPwVatRate(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_vat_rate",e.target.value);}catch(x){}}} inputMode="decimal" style={{width:70,border:"1px solid "+BD,borderRadius:8,padding:"7px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box"}}/>
                          </div>
                          <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:4}}>VAT number</label>
                          <input value={pwVatNo} onChange={e=>{setPwVatNo(e.target.value);setPwWeekPdf(null);try{localStorage.setItem("bac_pw_vat_no",e.target.value);}catch(x){}}} placeholder="e.g. 123456789" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box"}}/>
                        </div>
                      )}
                    </div>
                  )}

                  <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:6}}>Your invoice number (optional — prints on the PDF)</label>
                  <input value={pwWeekInvNo} onChange={e=>{setPwWeekInvNo(e.target.value);setPwWeekPdf(null);}} placeholder="e.g. INV-2026-014" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box",marginBottom:10}}/>
                  <div style={{display:"flex",gap:10,marginBottom:10}}>
                    <div style={{flex:1}}>
                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:6}}>Invoice date</label>
                      <input value={pwInvDate} onChange={e=>{setPwInvDate(e.target.value);setPwWeekPdf(null);}} placeholder="02/07/2026" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box"}}/>
                    </div>
                    <div style={{flex:1}}>
                      <label style={{fontSize:12,fontWeight:700,color:MT,display:"block",marginBottom:6}}>Due date</label>
                      <input value={pwDueDate} onChange={e=>{setPwDueDate(e.target.value);setPwWeekPdf(null);}} placeholder="10/07/2026" style={{width:"100%",border:"1px solid "+BD,borderRadius:8,padding:"9px 10px",fontSize:14,fontFamily:"inherit",color:DK,background:W,boxSizing:"border-box"}}/>
                    </div>
                  </div>
                  {!pwWeekPdf ? (
                    <button onClick={buildWeekPDF} style={{width:"100%",padding:12,background:"#1C1C1E",color:W,border:"none",borderRadius:10,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>📄 Generate PDF</button>
                  ) : (
                    <div>
                      <a href={pwWeekPdf} download={"Price-Work-Summary-"+((pwTradeName||name)||"worker").replace(/ /g,"-")+"-"+win.label.replace(/ /g,"").replace(/–/g,"_")+".html"} style={{display:"block",width:"100%",padding:12,background:GR,color:W,border:"none",borderRadius:10,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",textAlign:"center",textDecoration:"none",boxSizing:"border-box"}}>⬇️ Download sheet</a>
                      <div style={{fontSize:11,color:MT,textAlign:"center",marginTop:6}}>Open the file → Print → Save as PDF <button onClick={()=>setPwWeekPdf(null)} style={{background:"none",border:"none",color:BL,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>↺ Redo</button></div>
                    </div>
                  )}
                </div>
              )}

              {pwWeekLoading && <div style={{...card,textAlign:"center",padding:24,fontSize:14,color:MT}}>Loading your week…</div>}
              {!pwWeekLoading && pwWeekErr && <div style={{...card,textAlign:"center",padding:24,fontSize:13,color:RD}}>⚠️ {pwWeekErr}</div>}
              {!pwWeekLoading && !pwWeekErr && pwWeekClaims.length===0 && (
                <div style={{...card,textAlign:"center",padding:24}}>
                  <div style={{fontSize:14,color:MT}}>No price work submitted {pwWeekOffset===0?"this week":"last week"} yet.</div>
                  <div style={{fontSize:12,color:MT,marginTop:6}}>Sheets appear here automatically once you submit them.</div>
                </div>
              )}

              {!pwWeekLoading && !pwWeekErr && pwWeekClaims.map(c=>{
                const ls=linesByClaim[c.id]||[];
                const dLbl=c.claim_date?new Date(c.claim_date+"T00:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"}):"";
                return (
                  <div key={c.id} style={{...card,padding:14,marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                      <div>
                        <div style={{fontWeight:800,fontSize:14,color:DK}}>Plot {c.plot_number} · {LC[c.site_name]||c.site_name}</div>
                        <div style={{fontSize:11,color:MT,marginTop:2}}>{dLbl}{c.sheet_number?" · "+c.sheet_number:""}</div>
                      </div>
                      <div style={{fontWeight:900,fontSize:15,color:GR}}>{gbp(claimTot(c))}</div>
                    </div>
                    {ls.map((ln,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:DK,padding:"3px 0",borderTop:i===0?"1px solid "+BD:"none"}}>
                        <span style={{color:MT}}>{ln.operation}{ln.formula_type==="flat"?" · flat":" · "+(Number(ln.qty)||0)+" "+(ln.unit||"")+" × "+gbp(ln.rate)}</span>
                        <span style={{fontWeight:700}}>{gbp(ln.value)}</span>
                      </div>
                    ))}
                  </div>
                );
              })}

              {!pwWeekLoading && !pwWeekErr && pwWeekClaims.length>0 && (
                <div style={{...card,display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",border:"2px solid "+GR,marginBottom:20}}>
                  <span style={{fontWeight:800,fontSize:14,color:DK}}>WEEK TOTAL (gross, before any deductions)</span>
                  <span style={{fontWeight:900,fontSize:20,color:GR}}>{gbp(grossTotal)}</span>
                </div>
              )}
            </div>
            );
          })()}

          {sc==="summary" && (() => {
            // v17.49 — Force light palette on Review & Submit. The daywork sheet is a paper document and looks bad surrounded by dark chrome.
            const W="#fff", BG="#EEF2F7", MT="#6B7280", DK="#111827", BL="#2F80ED", GR="#27AE60";
            const BD="#D1D5DB", F2="#F2F2F7", SBL="#BDD6F1";
            const C={border:"1px solid #444",padding:"3px 6px",fontFamily:"Arial,sans-serif",fontSize:11,color:"#111",verticalAlign:"middle"};
            const CB={...C,background:SBL,fontWeight:700};
            return (
            <div style={{background:BG,color:DK,margin:"-16px",padding:"16px",minHeight:"100vh"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <button onClick={()=>setSc("sheet")} style={{background:F2,border:"none",borderRadius:8,padding:"7px 12px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK}}>‹ Back</button>
                <div style={{fontWeight:800,fontSize:16}}>Review & Submit</div>
              </div>
              <div style={{...card,background:"#F0FAF4",border:"1px solid #A8D5B5",marginBottom:8,padding:14}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}><span style={{fontSize:18}}>✅</span><span style={{fontWeight:800,fontSize:16}}>Shift Ended</span></div>
                    <div style={{fontSize:13,color:MT}}>{today} · {tIn}–{tOut}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:22,fontWeight:900,color:GR}}>{(()=>{if(!tIn||!tOut)return fMin(mgM+siM);const [h1,m1]=tIn.split(":").map(Number);const [h2,m2]=tOut.split(":").map(Number);let d=(h2*60+m2)-(h1*60+m1);if(d<0)d+=1440;return fMin(d);})()}</div>
                  </div>
                </div>
              </div>

              {/* ── DAYWORK SHEET PREVIEW ── */}
              <div style={{fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:6}}>Your Daywork Sheet</div>
              {/* ── Correct date / site before submitting ── */}
              <div style={{background:"#FFF8E7",border:"1px solid #F6D860",borderRadius:10,padding:"10px 12px",marginBottom:10,display:"flex",flexDirection:"column",gap:8}}>
                <div style={{fontSize:12,fontWeight:700,color:"#7B5E00",marginBottom:2}}>Correct before submitting</div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <label style={{fontSize:12,fontWeight:600,color:MT,minWidth:36}}>Date</label>
                  <input type="date" value={sheetDate||todayISO} onChange={e=>setSheetDate(e.target.value)} style={{flex:1,border:"1px solid "+BD,borderRadius:7,padding:"6px 8px",fontSize:13,fontFamily:"inherit",color:DK,background:W}}/>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <label style={{fontSize:12,fontWeight:600,color:MT,minWidth:36}}>Site</label>
                  {sheetSiteCustom ? (
                    <input type="text" placeholder="Type site name" value={sheetSite} onChange={e=>setSheetSite(e.target.value)} style={{flex:1,border:"1px solid "+BD,borderRadius:7,padding:"6px 8px",fontSize:13,fontFamily:"inherit",color:DK,background:W}}/>
                  ) : (
                    <select value={SITES.includes(sheetSite||site)?(sheetSite||site):""} onChange={e=>{if(e.target.value==="__custom__"){setSheetSiteCustom(true);setSheetSite("");}else{setSheetSite(e.target.value);}}} style={{flex:1,border:"1px solid "+BD,borderRadius:7,padding:"6px 8px",fontSize:13,fontFamily:"inherit",color:DK,background:W}}>
                      <option value="" disabled>Select a site…</option>
                      {SITES.map(s=><option key={s} value={s}>{LC[s]||s}</option>)}
                      <option value="__custom__">➕ New custom site…</option>
                    </select>
                  )}
                </div>
                {sheetSiteCustom && (
                  <button onClick={()=>{setSheetSiteCustom(false);setSheetSite("");}} style={{alignSelf:"flex-start",background:"none",border:"none",color:BL,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",padding:"0 0 0 44px"}}>↩ Pick from list instead</button>
                )}
              </div>
              <div style={{width:"100%",overflow:"hidden",marginBottom:4,position:"relative",height:Math.round(summarySheetH)+"px"}}>
                <div style={{transformOrigin:"top left",transform:"scale("+scale+")",width:SHEET_W,position:"absolute",top:0,left:0}}>
                  <div ref={summaryInnerRef} style={{width:SHEET_W,background:"#fff",border:"1.5px solid #333",padding:"4px 10px 6px",fontFamily:"Arial,sans-serif",boxSizing:"border-box"}}>
                    {/* HEADER — thinner: logo 56px, title alongside */}
                    <div style={{display:"flex",alignItems:"center",marginBottom:3,gap:12}}>
                      <div style={{flexShrink:0,width:56,height:56}}><img src={LOGO} alt="SiteVouch" style={{width:"100%",height:"100%",objectFit:"contain",display:"block"}}/></div>
                      <div style={{flex:1,textAlign:"center"}}><span style={{fontWeight:900,fontSize:16,letterSpacing:2,textDecoration:"underline",color:"#111"}}>DAYWORK SHEET</span></div>
                    </div>
                    <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                      <tr>
                        <td rowSpan={3} style={{...CB,width:"18%",textAlign:"center",fontSize:9,padding:"1px 3px"}}>SUB-CONTRACTOR NAME</td>
                        <td rowSpan={3} style={{...C,width:"25%",fontSize:13,fontWeight:700,textAlign:"center",padding:"1px 6px"}}>{name}</td>
                        <td style={{...CB,width:"19%",fontSize:9,padding:"1px 4px"}}>DATE</td>
                        <td style={{...C,width:"38%",fontSize:11,padding:"1px 4px"}}>{sheetDate?new Date(sheetDate+"T00:00:00").toLocaleDateString("en-GB"):today}</td>
                      </tr>
                      <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>SITE LOCATION</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{LC[sheetSite||site]||(sheetSite||site)}</td></tr>
                      <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>CONTRACTOR</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{CT[sheetSite||site]||ctr}</td></tr>
                    </tbody></table>
                    {/* WORK SHEET — bigger */}
                    <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                      <tr><td colSpan={2} style={{...CB,fontSize:11,padding:"2px 4px"}}>Work Sheet - Please Include Plot Numbers</td></tr>
                      <tr>
                        <td style={{...C,fontSize:9,color:"#CC0000",fontWeight:700,fontStyle:"italic",padding:"1px 4px"}}>Please confirm how long you spent in each plot next to your plot number</td>
                        <td style={{...C,width:140,textAlign:"right",fontWeight:700,fontSize:14,padding:"1px 4px"}}>Hours: {mgH}</td>
                      </tr>
                      <tr><td colSpan={2} style={{...C,height:380,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{mgTxt}</td></tr>
                    </tbody></table>
                    {/* SITE INSTRUCTION — smaller */}
                    <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                      <tr>
                        <td style={{...CB,fontSize:11,padding:"2px 4px"}}>Site Instruction Works - Please Include Plot Numbers</td>
                        <td style={{...CB,width:140,textAlign:"right",fontSize:14,padding:"2px 4px"}}>Hours: {siH}</td>
                      </tr>
                      <tr><td colSpan={2} style={{...C,height:140,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{siTxt}</td></tr>
                    </tbody></table>
                    <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                      <tr><td style={{...CB,fontSize:10,padding:"1px 4px"}}>Materials/Plant/Access Equipment Used</td></tr>
                      <tr><td style={{...C,height:30,verticalAlign:"top",paddingTop:2,fontSize:11}}>{mats}</td></tr>
                    </tbody></table>
                    <table style={{width:"45%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                      <tr>
                        <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME IN</td>
                        <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME OUT</td>
                      </tr>
                      <tr>
                        <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>
                          <input type="time" value={tIn} onChange={e=>setTIn(e.target.value)} style={{border:"none",background:"transparent",fontSize:14,fontWeight:700,textAlign:"center",width:"100%",fontFamily:"inherit",color:"inherit",cursor:"pointer"}}/>
                        </td>
                        <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>
                          <input type="time" value={tOut} onChange={e=>setTOut(e.target.value)} style={{border:"none",background:"transparent",fontSize:14,fontWeight:700,textAlign:"center",width:"100%",fontFamily:"inherit",color:"inherit",cursor:"pointer"}}/>
                        </td>
                      </tr>
                    </tbody></table>
                    <table style={{width:"100%",borderCollapse:"collapse"}}><tbody>
                      <tr>
                        <td style={{...CB,width:"28%",fontSize:9,padding:"2px 4px"}}>SiteVouch Supervisor Signature</td>
                        <td style={{...C,width:"20%",padding:"2px 4px"}}>&nbsp;</td>
                        <td style={{...CB,width:"10%",fontSize:9,padding:"2px 4px"}}>Date</td>
                        <td style={{...C,width:"42%",padding:"2px 4px"}}>&nbsp;</td>
                      </tr>
                    </tbody></table>
                  </div>
                </div>
              </div>

              {/* ── ACTION BUTTONS — SWAPPED: Generate PDF top-right, Submit bottom-left ── */}
              <div style={{display:"flex",gap:8,marginBottom:6}}>
                <button onClick={goEdit} style={{flex:1,padding:11,background:W,border:"2px solid "+BL,borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",color:BL}}>✏️ Edit ({entries.length})</button>
                {!pdfUrl ? (
                  <button onClick={buildPDF} style={{flex:1,padding:11,background:"#1C1C1E",color:W,border:"none",borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>📄 Generate PDF</button>
                ) : (
                  <a href={pdfUrl} download={"SiteVouch-Daywork-"+site.replace(/ /g,"-")+"-"+today.replace(/\//g,"-")+".html"} style={{flex:1,padding:11,background:"#1C1C1E",color:W,border:"none",borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6,textDecoration:"none",boxSizing:"border-box"}}>⬇️ Download</a>
                )}
              </div>
              {pdfUrl&&<div style={{fontSize:11,color:MT,textAlign:"center",marginBottom:6}}>Open downloaded file → Print → Save as PDF <button onClick={()=>setPdfUrl(null)} style={{background:"none",border:"none",color:BL,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>↺ Redo</button></div>}
              
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                {!submitted
                  ? <button onClick={openSubmitPicker} style={{flex:1,padding:11,background:GR,color:W,border:"none",borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>📤 Submit</button>
                  : <div style={{flex:1,background:"#F0FAF4",border:"1px solid #A8D5B5",borderRadius:12,padding:11,fontSize:13,fontWeight:700,color:GR,textAlign:"center"}}>{(()=>{try{const last=localStorage.getItem("bac_last_supervisor_id");const sup=supervisors.find(s=>s.id===last);return sup?"✅ Sent to "+sup.label:"✅ Submitted";}catch(e){return"✅ Submitted";}})()}</div>
                }
                <button onClick={newDay} style={{flex:1,padding:11,background:"#1C1C1E",color:W,border:"none",borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>New Day ✓</button>
              </div>
              {!isOnline&&!submitted&&<div style={{fontSize:11,color:"#F59E0B",textAlign:"center",marginBottom:6}}>📵 Offline — will sync when back online</div>}

              {/* ── GPS LOG — at the very bottom ── */}
              {(gpsIn||gpsOut) && (
                <div style={{...card,marginBottom:8,padding:12}}>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:6}}>📍 GPS Log</div>
                  {gpsIn && <div style={{fontSize:11,marginBottom:4}}><strong>Clock In:</strong> {gpsIn.lat}, {gpsIn.lng} ±{gpsIn.acc}m <a href={"https://maps.google.com/?q="+gpsIn.lat+","+gpsIn.lng} target="_blank" rel="noreferrer" style={{color:BL}}>View map</a></div>}
                  {gpsOut && <div style={{fontSize:11}}><strong>Clock Out:</strong> {gpsOut.lat}, {gpsOut.lng} ±{gpsOut.acc}m <a href={"https://maps.google.com/?q="+gpsOut.lat+","+gpsOut.lng} target="_blank" rel="noreferrer" style={{color:BL}}>View map</a></div>}
                </div>
              )}
            </div>
            );
          })()}

          {/* ── v17.18 — SUBMIT TO SUPERVISOR — bottom-sheet picker ── */}
          {showSupPicker && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:400,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={()=>!submitting&&setShowSupPicker(false)}>
              <div onClick={e=>e.stopPropagation()} style={{background:W,width:"100%",maxWidth:480,borderTopLeftRadius:18,borderTopRightRadius:18,padding:"14px 14px 20px",maxHeight:"85vh",overflowY:"auto"}}>
                <div style={{width:40,height:4,background:"#D1D5DB",borderRadius:2,margin:"0 auto 12px"}}/>
                <div style={{textAlign:"center",marginBottom:14}}>
                  <div style={{fontSize:18,fontWeight:800,color:DK,marginBottom:4}}>Send to which supervisor?</div>
                  <div style={{fontSize:12,color:MT}}>Pick the supervisor who needs to sign off this sheet</div>
                </div>
                <div style={{marginBottom:14}}>
                  {supervisors.length===0 && <div style={{textAlign:"center",color:MT,padding:24,fontSize:14}}>No supervisors set up yet. Ask the office.</div>}
                  {supervisors.map(s=>{
                    const isSelected=pickedSupId===s.id;
                    return (
                      <button key={s.id} onClick={()=>setPickedSupId(s.id)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",marginBottom:8,background:isSelected?"#F0FAF4":FA,border:"1.5px solid "+(isSelected?GR:BD),borderRadius:12,cursor:"pointer",fontFamily:"inherit"}}>
                        <div style={{display:"flex",alignItems:"center",gap:12}}>
                          <div style={{width:36,height:36,borderRadius:18,background:isSelected?GR:"#E5E7EB",color:isSelected?W:MT,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:14,flexShrink:0}}>
                            {(s.first_name?s.first_name[0]:"")+(s.surname?s.surname[0]:"")}
                          </div>
                          <div style={{textAlign:"left"}}>
                            <div style={{fontSize:15,fontWeight:700,color:DK}}>{s.label}</div>
                            {s.label!==s.full_name && <div style={{fontSize:11,color:MT}}>{s.full_name}</div>}
                          </div>
                        </div>
                        {isSelected && <div style={{color:GR,fontSize:20,fontWeight:900}}>✓</div>}
                      </button>
                    );
                  })}
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setShowSupPicker(false)} disabled={submitting} style={{flex:"0 0 auto",padding:"14px 20px",background:W,border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontWeight:700,color:DK,cursor:submitting?"not-allowed":"pointer",fontFamily:"inherit"}}>Cancel</button>
                  <button onClick={confirmSubmitToSupervisor} disabled={!pickedSupId||submitting} style={{flex:1,padding:14,background:pickedSupId&&!submitting?GR:"#D1D5DB",color:W,border:"none",borderRadius:12,fontSize:15,fontWeight:800,cursor:pickedSupId&&!submitting?"pointer":"not-allowed",fontFamily:"inherit"}}>
                    {submitting?"Sending…":(pickedSupId?"📤 Send to "+(supervisors.find(s=>s.id===pickedSupId)||{}).label:"Pick a supervisor")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── v17.18 — SIGNED SHEET DETAIL — actual daywork sheet, scaled to fit phone ── */}
          {openSignedSheet && (() => {
            // Pre-compute display data from the open signed sheet
            const _name = openSignedSheet.worker_name || name;
            const _date = openSignedSheet.shift_date ? new Date(openSignedSheet.shift_date).toLocaleDateString("en-GB") : "";
            const _signedDate = openSignedSheet.signed_at ? new Date(openSignedSheet.signed_at).toLocaleDateString("en-GB") : "";
            const _site = openSignedSheet.site_name || "";
            const _location = LC[_site] || _site;
            const _contractor = CT[_site] || "";
            const _mgEntries = signedSheetEntries.filter(e => e.section === "mg");
            const _siEntries = signedSheetEntries.filter(e => e.section === "si");
            const _groupSigned = (rows) => {
              const m = {};
              rows.forEach(e => {
                const k = String(e.plot_number||"").padStart(8,"0");
                if (!m[k]) m[k] = { plot: e.plot_number||"", items: [], mins: 0 };
                m[k].items.push({ desc: e.description, mins: e.time_spent_mins });
                m[k].mins += e.time_spent_mins || 0;
              });
              return Object.keys(m).sort().map(k => m[k]);
            };
            const _mgGroups = _groupSigned(_mgEntries);
            const _siGroups = _groupSigned(_siEntries);
            const _mgTxt = _mgGroups.map(g => (g.plot?"PLT "+g.plot+" - ":"")+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
            const _siTxt = _siGroups.map(g => (g.plot?"PLT "+g.plot+" - ":"")+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
            const _mgMins = _mgEntries.reduce((a,e)=>a+(e.time_spent_mins||0),0);
            const _siMins = _siEntries.reduce((a,e)=>a+(e.time_spent_mins||0),0);
            const _mgH = _mgMins ? fMin(_mgMins) : "";
            const _siH = _siMins ? fMin(_siMins) : "";
            return (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:300,display:"flex",flexDirection:"column"}}>
                <div style={{background:W,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid "+BD,flexShrink:0}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:800,fontSize:16}}>Signed Daywork Sheet</div>
                    {openSignedSheet.signed_at && <div style={{fontSize:11,color:MT,marginTop:2}}>Signed by {openSignedSheet.signed_by_name||"supervisor"} · {new Date(openSignedSheet.signed_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</div>}
                  </div>
                  <button onClick={closeSignedSheet} style={{background:F2,border:"none",borderRadius:8,padding:"7px 14px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                </div>

                <div ref={wrapRef} style={{flex:1,overflowY:"auto",padding:8,background:"#F9FAFB"}}>
                  {signedSheetLoading && <div style={{textAlign:"center",color:MT,padding:24,fontSize:13}}>Loading sheet…</div>}
                  {!signedSheetLoading && (
                    <div style={{width:"100%",overflow:"hidden",position:"relative",height:Math.round(SHEET_H*scale)+"px"}}>
                      <div style={{transformOrigin:"top left",transform:"scale("+scale+")",width:SHEET_W,position:"absolute",top:0,left:0}}>
                        <div style={{width:SHEET_W,background:"#fff",border:"1.5px solid #333",padding:"4px 10px 6px",fontFamily:"Arial,sans-serif",boxSizing:"border-box"}}>

                          {/* HEADER */}
                          <div style={{display:"flex",alignItems:"center",marginBottom:3,gap:12}}>
                            <div style={{flexShrink:0,width:56,height:56}}>
                              <img src={LOGO} alt="SiteVouch" style={{width:"100%",height:"100%",objectFit:"contain",display:"block"}}/>
                            </div>
                            <div style={{flex:1,textAlign:"center"}}>
                              <span style={{fontWeight:900,fontSize:16,letterSpacing:2,textDecoration:"underline",color:"#111"}}>DAYWORK SHEET</span>
                            </div>
                          </div>

                          {/* WORKER / DATE / SITE / CONTRACTOR */}
                          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                            <tr>
                              <td rowSpan={3} style={{...CB,width:"18%",textAlign:"center",fontSize:9,padding:"1px 3px"}}>SUB-CONTRACTOR NAME</td>
                              <td rowSpan={3} style={{...C,width:"25%",fontSize:13,fontWeight:700,textAlign:"center",padding:"1px 6px"}}>{_name}</td>
                              <td style={{...CB,width:"19%",fontSize:9,padding:"1px 4px"}}>DATE</td>
                              <td style={{...C,width:"38%",fontSize:11,padding:"1px 4px"}}>{_date}</td>
                            </tr>
                            <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>SITE LOCATION</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{_location}</td></tr>
                            <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>CONTRACTOR</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{_contractor}</td></tr>
                          </tbody></table>

                          {/* WORK SHEET */}
                          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                            <tr><td colSpan={2} style={{...CB,fontSize:11,padding:"2px 4px"}}>Work Sheet - Please Include Plot Numbers</td></tr>
                            <tr>
                              <td style={{...C,fontSize:9,color:"#CC0000",fontWeight:700,fontStyle:"italic",padding:"1px 4px"}}>Please confirm how long you spent in each plot next to your plot number</td>
                              <td style={{...C,width:140,textAlign:"right",fontWeight:700,fontSize:14,padding:"1px 4px"}}>Hours: {_mgH}</td>
                            </tr>
                            <tr><td colSpan={2} style={{...C,height:380,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{_mgTxt}</td></tr>
                          </tbody></table>

                          {/* SITE INSTRUCTION */}
                          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                            <tr>
                              <td style={{...CB,fontSize:11,padding:"2px 4px"}}>Site Instruction Works - Please Include Plot Numbers</td>
                              <td style={{...CB,width:140,textAlign:"right",fontSize:14,padding:"2px 4px"}}>Hours: {_siH}</td>
                            </tr>
                            <tr><td colSpan={2} style={{...C,height:140,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{_siTxt}</td></tr>
                          </tbody></table>

                          {/* MATERIALS — empty for signed sheets, kept for layout consistency */}
                          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                            <tr><td style={{...CB,fontSize:10,padding:"1px 4px"}}>Materials/Plant/Access Equipment Used</td></tr>
                            <tr><td style={{...C,height:30,verticalAlign:"top",paddingTop:2,fontSize:11}}>&nbsp;</td></tr>
                          </tbody></table>

                          {/* TIME IN / OUT */}
                          <table style={{width:"45%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                            <tr>
                              <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME IN</td>
                              <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME OUT</td>
                            </tr>
                            <tr>
                              <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{openSignedSheet.time_in||""}</td>
                              <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{openSignedSheet.time_out||""}</td>
                            </tr>
                          </tbody></table>

                          {/* SUPERVISOR SIGNATURE — POPULATED with the signed image */}
                          <table style={{width:"100%",borderCollapse:"collapse"}}><tbody>
                            <tr>
                              <td style={{...CB,width:"28%",fontSize:9,padding:"2px 4px"}}>SiteVouch Supervisor Signature</td>
                              <td style={{...C,width:"20%",padding:"2px 4px",textAlign:"center",height:50}}>
                                {openSignedSheet.signature_data && <img src={openSignedSheet.signature_data} alt="signature" style={{maxHeight:46,maxWidth:"100%",objectFit:"contain",verticalAlign:"middle"}}/>}
                              </td>
                              <td style={{...CB,width:"10%",fontSize:9,padding:"2px 4px"}}>Date</td>
                              <td style={{...C,width:"42%",padding:"2px 4px",fontSize:11,textAlign:"center"}}>{_signedDate}</td>
                            </tr>
                          </tbody></table>

                          {/* Signed-by stamp */}
                          {openSignedSheet.signed_by_name && (
                            <div style={{marginTop:4,padding:"3px 6px",background:"#F0FAF4",border:"1px solid #A8D5B5",fontSize:10,color:"#15803D",textAlign:"center",fontWeight:600}}>
                              ✓ Signed by {openSignedSheet.signed_by_name}
                            </div>
                          )}

                          {/* Supervisor notes */}
                          {openSignedSheet.supervisor_notes && (
                            <div style={{marginTop:4,padding:"6px 8px",background:"#EBF3FD",border:"1px solid #BDD6F1",fontSize:11,color:"#1E40AF"}}>
                              <strong>Supervisor Notes:</strong> {openSignedSheet.supervisor_notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom action bar */}
                <div style={{background:W,padding:"12px 14px",borderTop:"1px solid "+BD,display:"flex",gap:8,flexShrink:0}}>
                  {!signedSheetUrl ? (
                    <button onClick={buildSignedDownload} disabled={signedSheetLoading} style={{flex:1,padding:13,background:signedSheetLoading?"#D1D5DB":"#1C1C1E",color:W,border:"none",borderRadius:12,fontSize:14,fontWeight:800,cursor:signedSheetLoading?"not-allowed":"pointer",fontFamily:"inherit"}}>⬇️ Save for Invoicing</button>
                  ) : (
                    <a href={signedSheetUrl} onClick={()=>markSignedDownloaded(openSignedSheet.id)} download={"SiteVouch-Daywork-SIGNED-"+(openSignedSheet.site_name||"").replace(/ /g,"-")+"-"+(openSignedSheet.shift_date||"").replace(/-/g,"")+".html"} style={{flex:1,padding:13,background:GR,color:W,border:"none",borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6,textDecoration:"none",boxSizing:"border-box"}}>⬇️ Download</a>
                  )}
                  <button onClick={closeSignedSheet} style={{flex:"0 0 auto",padding:"13px 22px",background:W,border:"1.5px solid "+BD,borderRadius:12,fontSize:14,fontWeight:700,color:DK,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
                </div>
                {signedSheetUrl && <div style={{fontSize:11,color:MT,textAlign:"center",padding:"6px 14px 10px",background:W,borderTop:"1px solid "+BD}}>Open downloaded file → Print → Save as PDF</div>}
                {/* v17.57 — Delete from my list */}
                <div style={{background:W,padding:"4px 14px 12px",textAlign:"center",borderTop:signedSheetUrl?"none":"1px solid "+BD}}>
                  <button onClick={requestDismissSignedSheet} style={{background:"none",border:"none",color:MT,fontSize:12,fontWeight:600,textDecoration:"underline",cursor:"pointer",fontFamily:"inherit",padding:"6px 10px"}}>🗑️ Delete from my list</button>
                </div>

                {/* v17.57 — Dismiss confirmation (when sheet hasn't been downloaded yet) */}
                {showDismissConfirm && (
                  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:20,zIndex:400}}>
                    <div style={{background:W,borderRadius:14,padding:18,maxWidth:340,width:"100%",boxShadow:"0 8px 24px rgba(0,0,0,.25)"}}>
                      <div style={{fontSize:32,textAlign:"center",marginBottom:6}}>⚠️</div>
                      <div style={{fontSize:16,fontWeight:800,color:DK,textAlign:"center",marginBottom:6}}>Delete this signed sheet?</div>
                      <div style={{fontSize:13,color:MT,textAlign:"center",marginBottom:16,lineHeight:1.4}}>You haven't downloaded it yet. Once deleted you won't see it in your list again — the office still keeps a copy.</div>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>setShowDismissConfirm(false)} style={{flex:1,padding:12,background:W,border:"1.5px solid "+BD,borderRadius:10,fontSize:14,fontWeight:700,color:DK,cursor:"pointer",fontFamily:"inherit"}}>Keep it</button>
                        <button onClick={confirmDismissSignedSheet} style={{flex:1,padding:12,background:"#DC2626",color:W,border:"none",borderRadius:10,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Delete</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── SHEET PREVIEW MODAL (tap entry to view) ── */}
          {showPreview && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:300,display:"flex",flexDirection:"column"}}>
              <div style={{background:W,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid "+BD}}>
                <div style={{fontWeight:800,fontSize:16}}>Daywork Sheet Preview</div>
                <button onClick={()=>setShowPreview(false)} style={{background:F2,border:"none",borderRadius:8,padding:"7px 14px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✕ Close</button>
              </div>
              <div style={{fontSize:12,color:MT,textAlign:"center",padding:"6px 0",background:"#F9FAFB",borderBottom:"1px solid "+BD}}>Tap ✏️ on an entry to edit</div>
              <div ref={previewWrapRef} style={{flex:1,overflowY:"auto",overflowX:"hidden",padding:12}}>
                <div style={{width:SHEET_W*previewScale,margin:"0 auto",position:"relative",height:previewH}}>
                  <div ref={previewInnerRef} style={{width:SHEET_W,transform:`scale(${previewScale})`,transformOrigin:"top left",position:"absolute",top:0,left:0}}>
                    <div style={{width:680,background:"#fff",border:"1.5px solid #333",padding:"4px 10px 6px",fontFamily:"Arial,sans-serif",boxSizing:"border-box"}}>
                      <div style={{display:"flex",alignItems:"center",marginBottom:3,gap:12}}>
                        <div style={{flexShrink:0,width:56,height:56}}><img src={LOGO} alt="SiteVouch" style={{width:"100%",height:"100%",objectFit:"contain",display:"block"}}/></div>
                        <div style={{flex:1,textAlign:"center"}}><span style={{fontWeight:900,fontSize:16,letterSpacing:2,textDecoration:"underline",color:"#111"}}>DAYWORK SHEET</span></div>
                      </div>
                      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                        <tr>
                          <td rowSpan={3} style={{...CB,width:"18%",textAlign:"center",fontSize:9,padding:"1px 3px"}}>SUB-CONTRACTOR NAME</td>
                          <td rowSpan={3} style={{...C,width:"25%",fontSize:13,fontWeight:700,textAlign:"center",padding:"1px 6px"}}>{name}</td>
                          <td style={{...CB,width:"19%",fontSize:9,padding:"1px 4px"}}>DATE</td>
                          <td style={{...C,width:"38%",fontSize:11,padding:"1px 4px"}}>{today}</td>
                        </tr>
                        <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>SITE LOCATION</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{LC[site]||site}</td></tr>
                        <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>CONTRACTOR</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{ctr}</td></tr>
                      </tbody></table>
                      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                        <tr><td colSpan={2} style={{...CB,fontSize:10,padding:"1px 4px"}}>Work Sheet - Please Include Plot Numbers</td></tr>
                        <tr>
                          <td style={{...C,fontSize:9,color:"#CC0000",fontWeight:700,fontStyle:"italic",padding:"1px 4px"}}>Please confirm how long you spent in each plot next to your plot number</td>
                          <td style={{...C,width:140,textAlign:"right",fontWeight:700,fontSize:14,padding:"1px 4px"}}>Hours: {mgH}</td>
                        </tr>
                        <tr><td colSpan={2} style={{...C,height:380,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{mgTxt}</td></tr>
                      </tbody></table>
                      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                        <tr>
                          <td style={{...CB,fontSize:10,padding:"1px 4px"}}>Site Instruction Works - Please Include Plot Numbers</td>
                          <td style={{...CB,width:140,textAlign:"right",fontSize:14,padding:"1px 4px"}}>Hours: {siH}</td>
                        </tr>
                        <tr><td colSpan={2} style={{...C,height:140,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{siTxt}</td></tr>
                      </tbody></table>
                      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                        <tr><td style={{...CB,fontSize:11,fontWeight:800,textAlign:"right",padding:"2px 4px"}}>TOTAL HOURS: {fMin((mgM||0)+(siM||0))||"0"}</td></tr>
                      </tbody></table>
                      <table style={{width:"45%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                        <tr><td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME IN</td><td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME OUT</td></tr>
                        <tr><td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{tIn||""}</td><td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{tOut||""}</td></tr>
                      </tbody></table>
                      <table style={{width:"100%",borderCollapse:"collapse"}}><tbody>
                        <tr><td style={{...CB,fontSize:10,padding:"1px 4px"}}>Materials/Plant/Access Equipment Used</td></tr>
                        <tr><td style={{...C,height:60,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11}}>{mats}</td></tr>
                      </tbody></table>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{padding:"12px 16px 24px",borderTop:"1px solid "+BD,background:W,display:"flex",gap:8}}>
                <button onClick={()=>setShowPreview(false)} style={{flex:1,padding:14,background:F2,color:DK,border:"none",borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>‹ Back to entries</button>
                <button onClick={()=>{setShowPreview(false);}} style={{flex:1,padding:14,background:BL,color:W,border:"none",borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✅ Looks good</button>
              </div>
            </div>
          )}

          {/* ── SITE NOT LISTED MODAL ── */}
          {showSiteRequest && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",padding:"24px 20px 40px"}}>
                {siteRequestSent ? (
                  <div style={{textAlign:"center",padding:"20px 0"}}>
                    <div style={{fontSize:48,marginBottom:12}}>✅</div>
                    <div style={{fontWeight:800,fontSize:18,marginBottom:6}}>Request sent</div>
                    <div style={{fontSize:14,color:MT,marginBottom:24}}>Office will add your site shortly. You're clocked in.</div>
                    <button onClick={()=>setShowSiteRequest(false)} style={{width:"100%",padding:14,background:BL,color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Got it</button>
                  </div>
                ) : (
                  <div>
                    <div style={{fontWeight:900,fontSize:18,marginBottom:4}}>⚠️ Site not listed</div>
                    <div style={{fontSize:13,color:MT,marginBottom:16}}>You're clocked in. Tell the office which site you're on:</div>
                    <textarea style={{...{width:"100%",padding:"11px 13px",border:"1.5px solid "+BD,borderRadius:10,fontSize:15,boxSizing:"border-box",background:W,fontFamily:"inherit",outline:"none",color:DK},minHeight:80,resize:"vertical"}} placeholder="e.g. Haslemere — new Bellway site on London Road" value={siteRequestNote} onChange={e=>setSiteRequestNote(e.target.value)}/>
                    <button onClick={()=>{
                      const r={id:Date.now(),workerName:name,type:"wrong_site",note:"Site not listed: "+(siteRequestNote||"No details given"),date:today,submitted:new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}),status:"pending",gps:gpsIn||null};
                      try{const ex=JSON.parse(localStorage.getItem("bac_requests")||"[]");ex.push(r);localStorage.setItem("bac_requests",JSON.stringify(ex));}catch(e){}
                      // Push to Supabase
                      pushRequest({workerId,workerName:name,type:"wrong_site",note:"Site not listed: "+(siteRequestNote||"No details given"),gps:gpsIn||null});
                      setSiteRequestSent(true);
                    }} style={{width:"100%",marginTop:12,padding:14,background:GR,color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Send to Office</button>
                    <button onClick={()=>{setShowSiteRequest(false);setSc("home");}} style={{width:"100%",marginTop:8,padding:12,background:F2,border:"none",borderRadius:12,fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"inherit",color:DK}}>Skip — I'll sort it later</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {showEdit && editShift && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"90vh",overflowY:"auto",paddingBottom:40}}>
                {editDone ? (
                  <div style={{padding:40,textAlign:"center"}}>
                    <div style={{fontSize:64,marginBottom:16}}>👍</div>
                    <div style={{fontWeight:800,fontSize:18,marginBottom:8}}>Edit request sent</div>
                    <div style={{fontSize:14,color:MT,marginBottom:24}}>Sent for manager approval</div>
                    <button onClick={()=>setShowEdit(false)} style={{width:"100%",padding:16,background:BL,color:W,border:"none",borderRadius:14,fontSize:17,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Got it</button>
                  </div>
                ) : (
                  <div style={{padding:"20px 20px 0"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
                      <button onClick={()=>setShowEdit(false)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:BL,padding:0}}>‹</button>
                      <div style={{fontWeight:900,fontSize:20}}>Edit Shift</div>
                    </div>
                    <div style={card}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Job</span>
                        <span style={{fontWeight:700,fontSize:14,background:"#E8F5E9",color:"#2E7D32",padding:"4px 10px",borderRadius:20}}>{LC[editShift.site]||editShift.site}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Date</span>
                        <span style={{fontWeight:700,fontSize:14}}>{editShift.date}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Clock In</span>
                        <input type="time" value={editIn} onChange={e=>setEditIn(e.target.value)} style={{border:"none",fontSize:18,fontWeight:700,color:BL,background:"transparent",outline:"none"}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Clock Out</span>
                        <input type="time" value={editOut} onChange={e=>setEditOut(e.target.value)} style={{border:"none",fontSize:18,fontWeight:700,color:BL,background:"transparent",outline:"none"}}/>
                      </div>
                      <div>
                        <div style={{color:MT,fontSize:13,marginBottom:6}}>📝 Note to manager</div>
                        <textarea style={{...inp,minHeight:80,resize:"vertical"}} value={editNote} onChange={e=>setEditNote(e.target.value)} placeholder="Attach a note to your request"/>
                      </div>
                    </div>
                    <div style={{fontSize:12,color:MT,textAlign:"center",marginBottom:16}}>All requests will be sent for manager approval</div>
                    <button onClick={submitEdit} style={{width:"100%",padding:16,background:GR,color:W,border:"none",borderRadius:14,fontSize:17,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Send for Approval</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── WEEKLY SUMMARY MODAL ── */}
          {weekSummary&&(
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"90vh",overflowY:"auto",paddingBottom:40}}>
                <div style={{padding:"20px 20px 0"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                    <div>
                      <div style={{fontWeight:900,fontSize:20}}>📋 Week Summary</div>
                      <div style={{fontSize:13,color:MT,marginTop:2}}>{weekSummary.weekLabel}</div>
                    </div>
                    <button onClick={()=>setWeekSummary(null)} style={{background:F2,border:"none",borderRadius:8,padding:"6px 12px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
                  </div>

                  {/* Total hours card */}
                  <div style={{background:LBL,borderRadius:14,padding:16,marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:13,color:MT,fontWeight:600}}>Total hours this week</div>
                      <div style={{fontSize:32,fontWeight:900,color:BL}}>{fMin(weekSummary.totalMins)}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:13,color:MT,fontWeight:600}}>Shifts</div>
                      <div style={{fontSize:32,fontWeight:900,color:BL}}>{weekSummary.shifts.length}</div>
                    </div>
                  </div>

                  {/* Day by day breakdown */}
                  {Object.values(weekSummary.days).sort((a,b)=>{
                    const [da,ma,ya]=a.date.split("/");const [db,mb,yb]=b.date.split("/");
                    return new Date(ya,ma-1,da)-new Date(yb,mb-1,db);
                  }).map(day=>(
                    <div key={day.date} style={{...card,marginBottom:10}}>
                      <div style={{fontWeight:800,fontSize:15,marginBottom:8,color:DK}}>{day.date}</div>
                      {day.shifts.map((s,i)=>(
                        <div key={s.id} style={{paddingBottom:i<day.shifts.length-1?10:0,marginBottom:i<day.shifts.length-1?10:0,borderBottom:i<day.shifts.length-1?"1px solid "+BD:"none"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                            <div>
                              <div style={{fontWeight:700,fontSize:13}}>{LC[s.site]||s.site}</div>
                              <div style={{fontSize:12,color:MT}}>{s.tIn} – {s.tOut}</div>
                              {s.mgH&&<div style={{fontSize:11,color:MT}}>WS: {s.mgH}hr{s.siH?" · SI: "+s.siH+"hr":""}</div>}
                            </div>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontWeight:800,fontSize:15,color:BL}}>{fMin(s.totalMins)}</div>
                              {s.gpsIn&&<a href={"https://maps.google.com/?q="+s.gpsIn.lat+","+s.gpsIn.lng} target="_blank" rel="noreferrer" style={{fontSize:10,color:BL}}>📍 Map</a>}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div style={{marginTop:8,paddingTop:8,borderTop:"1px solid "+BD,display:"flex",justifyContent:"space-between"}}>
                        <span style={{fontSize:12,color:MT,fontWeight:600}}>Day total</span>
                        <span style={{fontSize:13,fontWeight:800,color:GR}}>{fMin(day.shifts.reduce((a,s)=>a+(s.totalMins||0),0))}</span>
                      </div>
                    </div>
                  ))}

                  {/* Sites worked this week */}
                  <div style={card}>
                    <div style={{fontWeight:800,fontSize:14,marginBottom:10}}>Sites this week</div>
                    {[...new Set(weekSummary.shifts.map(s=>s.site))].map(s=>(
                      <div key={s} style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                        <span style={{fontSize:13}}>{LC[s]||s}</span>
                        <span style={{fontSize:13,fontWeight:700,color:BL}}>{fMin(weekSummary.shifts.filter(x=>x.site===s).reduce((a,x)=>a+(x.totalMins||0),0))}</span>
                      </div>
                    ))}
                  </div>

                  <button onClick={()=>setWeekSummary(null)} style={{width:"100%",padding:14,background:"#1C1C1E",color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
                </div>
              </div>
            </div>
          )}

          {showAdd && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"90vh",overflowY:"auto",paddingBottom:40}}>
                {addDone ? (
                  <div style={{padding:40,textAlign:"center"}}>
                    <div style={{fontSize:64,marginBottom:16}}>👍</div>
                    <div style={{fontWeight:800,fontSize:18,marginBottom:8}}>Shift added</div>
                    <div style={{fontSize:14,color:MT,marginBottom:24}}>Sent for manager approval</div>
                    <button onClick={()=>setShowAdd(false)} style={{width:"100%",padding:16,background:BL,color:W,border:"none",borderRadius:14,fontSize:17,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Got it</button>
                  </div>
                ) : (
                  <div style={{padding:"20px 20px 0"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
                      <button onClick={()=>setShowAdd(false)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:BL,padding:0}}>‹</button>
                      <div style={{fontWeight:900,fontSize:20}}>Add Shift</div>
                    </div>
                    <div style={card}>
                      <div style={{paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <div style={{color:MT,fontSize:13,marginBottom:6}}>Job / Site</div>
                        <button onClick={()=>setShowAddSite(true)} style={{width:"100%",padding:"10px 14px",background:F2,border:"1.5px solid "+BD,borderRadius:10,fontSize:14,fontWeight:700,textAlign:"left",cursor:"pointer",fontFamily:"inherit",color:addSite?"#2E7D32":MT}}>
                          {addSite||"Select site..."}
                        </button>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Date</span>
                        <input type="date" value={addDate} onChange={e=>setAddDate(e.target.value)} style={{border:"none",fontSize:14,fontWeight:700,color:BL,background:"transparent",outline:"none"}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Clock In</span>
                        <input type="time" value={addIn} onChange={e=>setAddIn(e.target.value)} style={{border:"none",fontSize:18,fontWeight:700,color:BL,background:"transparent",outline:"none"}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Clock Out</span>
                        <input type="time" value={addOut} onChange={e=>setAddOut(e.target.value)} style={{border:"none",fontSize:18,fontWeight:700,color:BL,background:"transparent",outline:"none"}}/>
                      </div>
                      <div>
                        <div style={{color:MT,fontSize:13,marginBottom:6}}>📝 Note</div>
                        <textarea style={{...inp,minHeight:70,resize:"vertical"}} value={addNote} onChange={e=>setAddNote(e.target.value)} placeholder="e.g. New site, forgot to clock in..."/>
                      </div>
                    </div>
                    <div style={{fontSize:12,color:MT,textAlign:"center",marginBottom:16}}>Manual shifts will be sent for manager approval</div>
                    <button onClick={submitAdd} style={{width:"100%",padding:16,background:GR,color:W,border:"none",borderRadius:14,fontSize:17,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Send for Approval</button>
                  </div>
                )}
              </div>
              {showAddSite && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:400,display:"flex",alignItems:"flex-end"}}>
                  <div style={{background:W,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"80vh",display:"flex",flexDirection:"column"}}>
                    <div style={{padding:"16px 20px 12px",borderBottom:"1px solid "+BD}}>
                      <div style={{textAlign:"center",fontSize:18,fontWeight:700,marginBottom:12}}>Select Site</div>
                      <input style={{...inp,background:F2}} placeholder="Search sites..." value={addSrch} onChange={e=>setAddSrch(e.target.value)}/>
                    </div>
                    <div style={{overflowY:"auto",flex:1}}>
                      {addFilt.map(r=>(
                        <button key={r} onClick={()=>{setAddSite(r);setShowAddSite(false);setAddSrch("");}} style={{width:"100%",padding:"14px 20px",background:W,border:"none",borderBottom:"1px solid "+BD,cursor:"pointer",display:"flex",alignItems:"center",gap:14,fontSize:15,color:DK,fontFamily:"inherit",textAlign:"left"}}>
                          <div style={{width:44,height:44,borderRadius:6,background:W,border:"2px solid "+DOTS[SITES.indexOf(r)%DOTS.length],flexShrink:0,overflow:"hidden",padding:2,boxSizing:"border-box"}}>
                            <img src={LOGO} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/>
                          </div>
                          <div style={{flex:1}}>{r}</div>
                          {PC[r] && <span style={{fontSize:12,color:MT}}>{PC[r]}</span>}
                        </button>
                      ))}
                    </div>
                    <div style={{padding:"12px 20px 24px",borderTop:"1px solid "+BD}}>
                      <button onClick={()=>{setShowAddSite(false);setAddSrch("");}} style={{width:"100%",padding:14,background:F2,border:"none",borderRadius:12,fontSize:17,color:DK,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* v18.49 — worker settings sheet removed: the Profile page (sc==="profile") now holds signed-in identity, My Cards, PIN change and sign-out. The supervisor branch keeps its own settings sheet. */}

          {myCardsModal}

          {/* ── v18.46 — BOTTOM NAV (worker): Home / My Week / Profile.
               v18.49 — Profile is now a real page (sc==="profile"), not the
               settings sheet: cards are surfaced on it rather than buried. */}
          <div style={{position:"fixed",left:0,right:0,bottom:0,zIndex:250,display:"flex",background:W,borderTop:"1px solid "+BD,boxShadow:"0 -2px 10px rgba(0,0,0,0.06)",paddingBottom:"env(safe-area-inset-bottom)"}}>
            {[
              {k:"home",icon:"🏠",lbl:"Home",go:()=>{setShowSettings(false);setSc("home");}},
              {k:"pwweek",icon:"📅",lbl:"Price Work",go:()=>{setShowSettings(false);setPwWeekOffset(0);setSc("pwweek");}},
              {k:"profile",icon:"👤",lbl:"Profile",go:()=>{setShowSettings(false);setSc("profile");}},
            ].map(b=>{
              const active=sc===b.k&&!showSettings;
              return (
                <button key={b.k} onClick={b.go} style={{flex:1,padding:"8px 0 6px",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",gap:1}}>
                  <span style={{fontSize:20,filter:active?"none":"grayscale(1) opacity(0.55)"}}>{b.icon}</span>
                  <span style={{fontSize:10.5,fontWeight:700,color:active?GR:MT}}>{b.lbl}</span>
                </button>
              );
            })}
          </div>

          {/* ── PIN PROMPT MODAL (after first login on temp PIN) ── */}
          {showPinPrompt && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:400,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",padding:"24px 20px 40px"}}>
                <div style={{textAlign:"center",fontSize:40,marginBottom:8}}>🔐</div>
                <div style={{fontWeight:900,fontSize:20,textAlign:"center",marginBottom:8}}>Welcome, {name.split(" ")[0]}</div>
                <div style={{fontSize:14,color:MT,textAlign:"center",marginBottom:6,lineHeight:1.5}}>Your PIN is private to you. Would you like to change it now to one only you know?</div>
                <div style={{fontSize:12,color:MT,textAlign:"center",marginBottom:20,fontStyle:"italic"}}>You can change it any time from Settings.</div>
                <button onClick={()=>{setNewPin1("");setNewPin2("");setPinChangeError("");setPinStep(1);setShowPinChange(true);}} style={{width:"100%",padding:14,background:GR,color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"inherit",marginBottom:8}}>Yes, set my own PIN</button>
                <button onClick={keepTempPin} style={{width:"100%",padding:14,background:W,color:DK,border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Keep this PIN for now</button>
              </div>
            </div>
          )}

          {/* ── PIN CHANGE MODAL ── */}
          {showPinChange && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:500,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",padding:"20px 20px 40px",maxHeight:"95vh",overflowY:"auto"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{fontWeight:900,fontSize:20}}>Change PIN</div>
                  <button onClick={()=>{setShowPinChange(false);setNewPin1("");setNewPin2("");setPinChangeError("");setPinStep(1);}} style={{background:F2,border:"none",borderRadius:8,padding:"6px 12px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                </div>
                <div style={{fontSize:13,color:MT,marginBottom:12,textAlign:"center"}}>
                  {pinStep===1?"Enter a new 4-digit PIN":"Confirm your new PIN"}
                </div>
                {pinDots(pinStep===1?newPin1:newPin2,4)}
                {pinChangeError && <div style={{textAlign:"center",color:RD,fontSize:13,fontWeight:600,marginBottom:12}}>{pinChangeError}</div>}
                <PinPad
                  value={pinStep===1?newPin1:newPin2}
                  onPress={d=>{
                    if(pinStep===1){
                      if(newPin1.length>=4)return;
                      const next=newPin1+d;
                      setNewPin1(next);
                      // Auto-advance to confirm step at 4 digits
                      if(next.length===4)setTimeout(()=>setPinStep(2),200);
                    } else {
                      if(newPin2.length>=4)return;
                      setNewPin2(p=>p+d);
                    }
                    setPinChangeError("");
                  }}
                  onClear={()=>{
                    if(pinStep===1)setNewPin1("");
                    else setNewPin2("");
                    setPinChangeError("");
                  }}
                  onBack={()=>{
                    if(pinStep===1)setNewPin1(p=>p.slice(0,-1));
                    else setNewPin2(p=>p.slice(0,-1));
                    setPinChangeError("");
                  }}
                />
                {pinStep===2 && (
                  <div style={{display:"flex",gap:8,marginTop:16}}>
                    <button onClick={()=>{setNewPin2("");setPinStep(1);}} style={{flex:1,padding:14,background:W,color:DK,border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>‹ Back</button>
                    {newPin2.length>=4 && (
                      <button onClick={savePinChange} style={{flex:2,padding:14,background:GR,color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>✅ Save</button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── v17.85 — RAMS SIGN MODAL ── */}
          {ramsToSign && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:500,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
              <div style={{background:W,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"95vh",overflowY:"auto",padding:20,boxSizing:"border-box"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{fontWeight:900,fontSize:18,color:DK}}>📋 RAMS Sign-Off</div>
                  <button onClick={cancelRamsSign} style={{background:F2,border:"none",borderRadius:8,padding:"6px 12px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                </div>
                <div style={{fontSize:13,color:MT,marginBottom:14}}>Sign before clocking into {ramsPendingSite}.</div>

                <div style={{background:F2,borderRadius:12,padding:14,marginBottom:14}}>
                  <div style={{fontWeight:800,fontSize:15,color:DK,marginBottom:6}}>{ramsToSign.title}</div>
                  {ramsToSign.file_url && (
                    <a href={ramsToSign.file_url} target="_blank" rel="noopener noreferrer" style={{display:"inline-block",padding:"8px 14px",background:BL,color:W,borderRadius:8,fontSize:13,fontWeight:700,textDecoration:"none"}}>📄 View RAMS PDF</a>
                  )}
                </div>

                <label style={{display:"flex",alignItems:"flex-start",gap:10,padding:12,background:F2,borderRadius:10,marginBottom:14,cursor:"pointer"}}>
                  <input type="checkbox" checked={ramsRead} onChange={e=>setRamsRead(e.target.checked)} style={{marginTop:3,width:18,height:18,flexShrink:0}}/>
                  <span style={{fontSize:13,color:DK,lineHeight:1.4}}>I have read and understood this Risk Assessment & Method Statement, and I will follow it on site.</span>
                </label>

                <div style={{fontSize:12,color:MT,fontWeight:700,marginBottom:6}}>SIGNATURE</div>
                <div style={{border:"1.5px solid "+BD,borderRadius:10,background:W,marginBottom:8,overflow:"hidden"}}>
                  <canvas
                    ref={ramsSigCanvasRef}
                    width={600}
                    height={200}
                    onPointerDown={onRamsSigStart}
                    onPointerMove={onRamsSigMove}
                    onPointerUp={onRamsSigEnd}
                    onPointerCancel={onRamsSigEnd}
                    style={{width:"100%",height:160,touchAction:"none",display:"block",background:W}}
                  />
                </div>
                <button onClick={clearRamsSig} style={{padding:"8px 14px",background:W,color:MT,border:"1.5px solid "+BD,borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:14}}>Clear</button>

                <button
                  onClick={submitRamsSig}
                  disabled={!ramsRead || !ramsSigData || ramsSigning}
                  style={{width:"100%",padding:14,background:(ramsRead&&ramsSigData&&!ramsSigning)?GR:MT,color:W,border:"none",borderRadius:12,fontSize:15,fontWeight:800,cursor:(ramsRead&&ramsSigData&&!ramsSigning)?"pointer":"default",fontFamily:"inherit"}}
                >
                  {ramsSigning?"Submitting…":"✓ Sign & Continue"}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}